import type { IPublicClientApplication } from '@azure/msal-browser';
import { apiRequest, stilleErneuerung } from './authConfig';
import type { NeaStand, SimarisStand } from './seiten/elektro/nea-simaris';
import type { BlitzschutzStand } from './seiten/elektro/blitzschutz';
import type { FunktionserhaltStand } from './seiten/elektro/funktionserhalt';
import type { VersammlungsstaetteStand } from './seiten/elektro/versammlungsstaette';
import type { SicherheitstechnikStand } from './seiten/elektro/sicherheitstechnik';
import type { BmaStand } from './seiten/elektro/bma';
import type { Kabelstand } from './seiten/ElektroKabelliste';
import type { SaaStand } from './seiten/elektro/sprachalarm';
import type { PvStand } from './seiten/elektro/pv';
import type { ZutrittStand } from './seiten/elektro/zutritt';
import type { SicherheitsstromStand } from './seiten/elektro/sicherheitsstrom';
import type { UsvStand } from './seiten/elektro/usv';
import type { VerbraucherStand } from './seiten/elektro/verbraucher';
import type { EventstromStand } from './seiten/elektro/eventstrom';
import { dateinameAusAntwort } from './dateiname';
import { hausAbfrage } from './mehrhaus';

export interface CompletionFinding {
  id: string;
  scopeKey: string;
  entityType: string;
  entityKey: string;
  findingType: string;
  severity: string;
  summary: string;
  details?: string | null;
  componentUniqueId?: string | null;
  system?: string | null;
  systemType?: string | null;
  nearbyNetwork?: string | null;
  room?: string | null;
  level?: string | null;
  expectedConnectionType?: string | null;
  sourceModel?: string | null;
  sourceModelVersion?: string | null;
  openConnector?: { elementUniqueId: string; connectorId: string; domain?: string | null; direction?: string | null; nominalDiameterMm?: number | null } | null;
  connectedConnectors?: { elementUniqueId: string; connectorId: string; domain?: string | null; direction?: string | null; nominalDiameterMm?: number | null }[];
  provenance?: string[];

  // ─── Ordnungsrahmen des jüngsten Koordinationsstands (Dokument 36,
  // Abschnitt A/B) — additiv, von Befundadressierung befüllt. Nur für
  // offene Anschlüsse und Kollisionen; Bauteil-Netzrolle-/Netzbrücke-
  // Befunde tragen hier nur die Vorgabewerte (zugeordnet=true, sonst null),
  // siehe CompletionFinding.cs. ─────────────────────────────────────────
  abschnitt?: string | null;
  quadrant?: string | null;
  gewerk?: string | null;
  medium?: string | null;
  achsadresseText?: string | null;
  achseX?: string | null;
  achseY?: string | null;
  achseAbstandMm?: number | null;
  /** `false`, wenn kein Koordinationsstand vorlag oder sich kein Bauteil zur UniqueId fand — siehe `zugeordnetGrund`. */
  zugeordnet: boolean;
  zugeordnetGrund?: string | null;
  /** offen · vorgeschlagen · freigegeben · ausgefuehrt · gescheitert. */
  planStatus: string;

  // ─── Einstufung VOR dem Klick (Fachplaner-Beschwerde 14.09.2026: "die
  // liste sagt nichts darüber aus, ob etwas lösbar ist oder nicht") —
  // additiv, aus dem jüngsten, nicht überholten Plan berechnet, siehe
  // BefundEinstufung.cs / CompletionEndpoints.PlanAuskunftJeBefundAsync.
  // Dieselben Felder wie das Add-in (CompletionFinding, gemeinsame
  // Domain-Baugruppe) — Frontend und Add-in zeigen denselben Text. ────────

  /** `"loesbar"` · `"unloesbar"` · `"nicht_geplant"`. */
  einstufung: string;
  /** Klartext-Grund, gesetzt sobald `einstufung !== "loesbar"`. */
  einstufungGrund?: string | null;
  planVorhanden: boolean;
  /** Rohcode der Aktionsart des jüngsten Plans, z. B. `"ManualReviewRequired"`. */
  aktionsart?: string | null;
  /** Klartext, z. B. "Anschlüsse verbinden" statt "ConnectConnectors". */
  aktionsartText?: string | null;
  /** Eigene Bewertung in Prozent — KEINE gemessene Erfolgswahrscheinlichkeit. */
  vertrauenswertProzent?: number | null;
  /** Klartext: gering/mittel/hoch/kritisch. */
  risikostufe?: string | null;
  pruefungBestanden?: boolean | null;
  /** Klartext von `ModelActionPlan.ApprovalMode`. */
  policyEntscheidung?: string | null;
}

/**
 * Antwort von `GET .../ai-completion/findings` - die Befunde plus die
 * Kappungsauskunft ausschließlich für die offenen Heizungsanschlüsse (siehe
 * `CompletionEndpoints.CompletionFindingsPage`, die anderen Befundarten
 * tragen weiterhin ihre eigene, unveränderte Grenze ohne Auskunft).
 *
 * `gesamtanzahl`/`seite`/`seitengroesse` stehen IMMER, wenn die Antwort über
 * den HTTP-Endpunkt kam (Dokument 36, Abschnitt B) — der Endpunkt baut den
 * Filter jetzt immer auf, auch ohne gesetzte Parameter (Vorgabe Seite 1,
 * Seitengröße 100). Nur der interne Serverbefehl-Aufruf ohne Filter (CLI
 * 'completion-befunde') liefert sie nicht — der läuft nicht über diese
 * TS-Schnittstelle.
 */
export interface CompletionFindingsAntwort {
  findings: CompletionFinding[];
  offeneAnschluesseGekappt: boolean;
  gesamtanzahl: number;
  seite: number;
  seitengroesse: number;
  offeneAnschluesseHoechstzahl: number;
  offeneAnschluesseGeladenGesamt: number;
}

export interface CompletionPlan {
  id: string;
  projectId: string;
  modelId: string;
  modelVersion: string;
  findingId?: string | null;
  issueId?: string | null;
  discipline: string;
  actionType: string;
  source: string;
  planner: string;
  createdAt?: string;
  confidence: number;
  riskLevel: string;
  approvalMode: string;
  autonomyLevel: string;
  operations: {
    operationId: string; actionType: string; reason?: string | null; targetElements: string[];
    /**
     * Nur bei `actionType === 'ClashAssessment'` (KI-Kollisionsbewertung,
     * siehe KollisionsBewerter.cs) gefüllt — die strukturierte KI-Antwort,
     * zusätzlich zum Klartext in `reason`. `pruefpunkteJson` trägt eine
     * JSON-codierte Liste von Zeichenketten.
     */
    parameters?: { extensions?: Record<string, string | null> } | null;
  }[];
  preconditions?: { code: string; description: string; value?: string | null }[];
  constraints?: { code: string; description: string; isHard: boolean; limitMm?: number | null }[];
  expectedResult?: { openConnectorsBefore?: number | null; openConnectorsAfter?: number | null; newElementCount?: number | null; changedElementCount?: number | null; description?: string | null };
  validationRules?: { code: string; description: string; isRequired: boolean }[];
  rollbackStrategy?: { kind: string; required: boolean; description?: string | null };
  provenance?: string[];
}

/** Ein Wegpunkt einer geprüften Route (Planungsversuch). */
export interface CompletionVersuchRoutenpunkt {
  kennzeichen: string;
  x: number;
  y: number;
  z: number;
}

/** Ein Treffer der Kollisionsprüfung innerhalb eines Fehlschlags. */
export interface CompletionVersuchTreffer {
  hindernisUniqueId: string;
  segment: number;
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
  eindringtiefeX: number;
  eindringtiefeY: number;
  eindringtiefeZ: number;
}

/** Ein Fehlschlag (nicht bestandener Prüfcode) innerhalb eines Versuchs. */
export interface CompletionVersuchFehlschlag {
  code: string;
  meldung: string;
  treffer: CompletionVersuchTreffer[];
}

/**
 * Ein einzelner Planungsversuch (Direktverbindung oder KI-Runde).
 *
 * Von einer parallelen Erweiterung der API vorgesehen, heute aber noch NICHT
 * geliefert — alle Verbraucher müssen mit einem fehlenden `versuche`-Feld
 * zurechtkommen (siehe AiCompletion.tsx).
 */
export interface CompletionVersuch {
  nummer: number;
  herkunft: 'direkt' | 'ki';
  aktionsart: string;
  wegpunkte: number | null;
  route: CompletionVersuchRoutenpunkt[];
  bestanden: string[];
  fehlschlaege: CompletionVersuchFehlschlag[];
  hindernisseNachgeladen: number | null;
  hindernisseNeuErgaenzt: number | null;
  fehlervermerk: string | null;
}

export interface CompletionPlanItem {
  plan: CompletionPlan;
  status: string;
  updatedAt: string;
  updatedBy?: string | null;
  runtime?: {
    jobId: string;
    jobNumber?: string | null;
    jobStatus: string;
    failureReason?: string | null;
    issues: { level: string; code: string; message: string; createdAt: string }[];
    acceptance?: {
      runId: string;
      caseId: string;
      caseType: string;
      status: string;
      reasons: string[];
      reportStorageKey?: string | null;
      reportSha256?: string | null;
      evaluatedAt?: string | null;
    } | null;
  } | null;
  /** Siehe CompletionVersuch — heute noch nicht von der API geliefert. */
  versuche?: CompletionVersuch[];
  abbruchgrund?: string | null;
  kiAnbieter?: string | null;
  kiModell?: string | null;
  kiVersuche?: number | null;
}

/** Nachladezahlen je KI-Versuch, wie CreateProposal (`ki.versucheDetail`) sie liefert. */
export interface CompletionKiVersuchDetail {
  nummer: number;
  hindernisseNachgeladen: number | null;
  hindernisseNeuErgaenzt: number | null;
}

/** `ki` aus der Vorschlagsantwort — `null`, wenn kein KI-Weg lief (Regelplaner). */
export interface CompletionKiInfo {
  anbieter: string;
  modell: string;
  versuche: number;
  abbruchgrund: string | null;
  versucheDetail: CompletionKiVersuchDetail[];
}

/** `hindernisse` aus der Vorschlagsantwort — die Prüfgrundlage der Routenprüfung. */
export interface CompletionHindernisInfo {
  geladen: number;
  gefundenImSuchradius: number | null;
  gedeckelt: boolean;
  suchradiusMm: number;
  zusaetzlichEntlangErwarteterRoute: number;
  zusaetzlichEntlangErwarteterRouteGedeckelt: boolean;
}

/** `offeneAnschluesse` aus der Vorschlagsantwort — mögliche Anschlusspartner. */
export interface CompletionOffeneAnschluesseInfo {
  geladen: number;
  gefundenImModell: number | null;
  gedeckelt: boolean;
  hoechstzahl: number;
}

/** Antwort von POST .../ai-completion/proposals. */
export interface CompletionProposalAntwort {
  item: CompletionPlanItem;
  policy: { decision: string; reasons: string[] };
  ki: CompletionKiInfo | null;
  hindernisse: CompletionHindernisInfo;
  offeneAnschluesse: CompletionOffeneAnschluesseInfo;
}

/**
 * Ein Knoten des Completion-Baums (Dokument 36, Abschnitt B) — derselbe
 * Vertrag wie der Planerarbeitsplatz-Baum der Koordination
 * (`KoordinationEndpoints.Baumknoten`): Geschoss → Abschnitt → Quadrant →
 * Medium, mit den fünf Plan-Kennzahlen (offen/vorgeschlagen/freigegeben/
 * ausgefuehrt/gescheitert) in `jeStatus`.
 */
export interface CompletionBaumknoten {
  schluessel: string;
  bezeichnung: string;
  ebene: 'geschoss' | 'abschnitt' | 'quadrant' | 'medium';
  offen: number;
  gesamt: number;
  jeStatus: Record<string, number> | null;
  kinder: CompletionBaumknoten[];
}

/** Antwort von `GET .../completion/baum`. */
export interface CompletionBaumAntwort {
  projectId: string;
  modelId: string;
  befundart: 'alle' | 'anschluss' | 'kollision';
  gesamt: number;
  wurzel: CompletionBaumknoten[];
}

/**
 * Serverseitiger Filter- und Seitensatz von `GET .../ai-completion/findings`
 * (Dokument 36, Abschnitt B) — siehe `CompletionEndpoints.CompletionFindingsFilter`.
 * `geschoss`/`abschnitt`/`quadrant`/`medium` kommen entweder aus einem
 * gewählten Baumknoten oder aus der Filterleiste; `undefined` heißt "kein
 * Kriterium", nicht "leerer Text".
 */
export interface CompletionFindingsFilterParams {
  modelId?: string;
  geschoss?: string;
  abschnitt?: string;
  quadrant?: string;
  medium?: string;
  gewerk?: string;
  befundart?: string;
  planstatus?: string;
  nennweiteVonMm?: number;
  nennweiteBisMm?: number;
  suche?: string;
  seite?: number;
  seitengroesse?: number;
}

/**
 * Ergebnis EINER Kollision aus `POST .../ai-completion/kollisionen/bewertung`
 * (Dokument 36, Abschnitt C) — `item` ist `null`, wenn `ok` falsch ist
 * (unbekannte oder fremde Kollision, siehe `CompletionEndpoints.RequestKollisionsbewertungenAsync`).
 */
export interface KollisionsBewertungErgebnis {
  kollisionId: string;
  ok: boolean;
  item: CompletionPlanItem | null;
  fehler: string | null;
}

/** Antwort von `POST .../ai-completion/kollisionen/bewertung` — ein Ergebnis je angefragter Kollision, in derselben Reihenfolge. */
export interface KollisionsBewertungAntwort {
  ergebnisse: KollisionsBewertungErgebnis[];
}

/**
 * Höchstzahl der Kollisions-Ids, die der Server in EINEM Aufruf von
 * `POST .../ai-completion/kollisionen/bewertung` annimmt (Dokument 36,
 * Abschnitt C) — siehe `CompletionEndpoints.KollisionsbewertungHoechstzahlJeAufruf`
 * für die Begründung (KI-Zeitlimit je Kollision × Proxy-Zeitfenster). Die
 * Koordinationsseite nutzt diesen Wert nicht aus: ein "Stapel über eine
 * Auswahl" ruft {@link ApiClient.koordinationKiBewertungAnfordern} WIEDERHOLT
 * mit je EINER Kennung auf, nicht mit einem größeren Feld.
 */
export const KOLLISIONSBEWERTUNG_HOECHSTZAHL_JE_AUFRUF = 2;
import type {
  MsrUebersicht, MsrBaum, MsrAspZeile, MsrAspDetail, MsrAnlageZeile,
  MsrAnlageDetail, MsrBeschreibung, MsrFeldgeraetZeile, MsrFeldgeraetDetail,
  MsrDatenpunktZeile, MsrFunktionZeile, MsrRaumZeile, MsrAksAufbauZeile,
  MsrAksProtokollZeile, MsrGrundlagen, MsrVorlage, MsrFunktionsvorlage,
  MsrFestlegungen, MsrMbus, MsrKlemmenfreigabe,
  MsrAspregel, MsrEinstellung, MsrLauf, MsrSeite, MsrVdiFunktionsliste,
  MsrVdiGrundlagen, MsrVdiZeilenvorlage, MsrKlemmenbelegung,
  MsrEinstellwert, MsrEinstellwerteAntwort, MsrEinstellwerteVorschlagErgebnis,
  MsrEinstellwertvorlage, MsrDokument, MsrDokumentart, MsrDokumentlauf,
  MsrBefundliste, MsrKabelliste, MsrSchrankbilanz, MsrRechenstand, MsrAnnahmenliste,
} from './seiten/msr/typen';

/**
 * Zugriff auf die API.
 *
 * Das Token wird bei jedem Aufruf frisch geholt - MSAL gibt ein
 * zwischengespeichertes zurueck, solange es gilt, und erneuert es sonst still.
 * Ein einmal beschafftes Token festzuhalten waere naheliegend, fuehrt aber nach
 * einer Stunde zu Fehlern, die niemand mit der Anmeldung in Verbindung bringt.
 */
/**
 * Eine Zeile der Anlagenliste.
 *
 * `einheiten` ist der wichtigste Wert: Null heißt, dass für diese Anlage kein
 * Datenblatt eingelesen ist — und damit kennt die Rohrnetzberechnung ihre
 * Register nicht.
 */
export interface Anlagenzeile {
  id: string;
  anlage: string;
  aksSchluessel: string | null;
  ebene: string | null;
  ebeneIndex: number | null;
  raumnummer: string | null;
  raumname: string | null;
  state: string | null;
  einheiten: number;
  datenblaetter: number;
  fabrikat: string | null;
  geraetebezeichnung: string | null;
  angebot: string | null;
  zuluft: string | null;
  abluft: string | null;
  /** Der öffentliche Verweis. Wird gebildet, nicht abgelegt. */
  verweis: string | null;
}

/**
 * Ein Betriebspunkt einer Funktionseinheit.
 *
 * Die Werte liegen als JSON und nicht als Felder: Welche es gibt, hängt an der
 * Art der Einheit — ein Change-Over hat Kondensatmenge, ein Erhitzer nicht. Die
 * Schlüssel beginnen mit `bp_`.
 */
export interface Anlagenpunkt {
  id: string;
  reihenfolge: number;
  modus: string | null;
  modusDe: string | null;
  stimmig: boolean | null;
  daten: string | null;
  herkunft: string | null;
}

/**
 * Eine Funktionseinheit.
 *
 * `daten` trägt die Eigenschaften als LISTE, nicht als Objekt: Mehrere tragen
 * denselben Namen — `Volumenstrom` steht dreimal, für Luft, Haupt- und
 * Alternativpunkt. Ein Objekt hätte sie überschrieben.
 */
export interface Anlageneinheit {
  id: string;
  reihenfolge: number;
  art: string;
  bezeichnung: string;
  druckverlustPa: number | null;
  componentKey: string | null;
  herkunft: string;
  daten: string | null;
  quelltext: string | null;
  betriebspunkte: Anlagenpunkt[];
}

/**
 * Ein Luftstrom mit seiner Einheitenfolge.
 *
 * `summeOhneVentilator` ist die Druckverlustbilanz: Weicht sie vom internen
 * Druckverlust ab, den der Ventilator meldet, fehlt eine Einheit im
 * Datenblattauszug. Die API rechnet sie, damit es an einer Stelle geschieht.
 */
export interface Anlagenstrom {
  luftstrom: string;
  einheiten: Anlageneinheit[];
  summeOhneVentilator: number;
}

/** Alle Daten einer Anlage für die Anlagenseite. */
export interface Anlagendaten {
  id: string;
  anlage: string;
  anlageQuelle: string | null;
  aksSchluessel: string | null;
  ebene: string | null;
  ebeneIndex: number | null;
  raumnummer: string | null;
  raumname: string | null;
  aksTeilprojekt: string | null;
  aksGewerk: string | null;
  aksGruppe: string | null;
  aksGeschoss: string | null;
  aksArt: string | null;
  aksNummer: number | null;
  /** Nur intern. */
  state: string | null;
  gesehenAm: string | null;
  verweis: string | null;
  elemente: {
    id: string; elementGuid: string; ifcGuid: string | null;
    familie: string | null; typ: string | null; rolle: string | null;
    ebene: string | null;
    posX: number | null; posY: number | null; posZ: number | null;
  }[];
  gruppen: {
    gruppe: string;
    felder: {
      fieldKey: string; label: string; unit: string | null;
      dataType: string; wert: string; herkunft: string | null;
      /** gruen, gelb, blau oder grau — an einer Stelle im Backend entschieden. */
      farbe: string;
    }[];
  }[];
  stroeme: Anlagenstrom[];
  kanalsysteme: {
    id: string; systemname: string; abkuerzung: string | null;
    luftart: string | null; klassifizierung: string | null;
    anlagenAnzahl: number | null; istVerbundnetz: boolean;
    aksSchluessel: string | null;
  }[];
  datenblatt: {
    id: string; hersteller: string | null; geraetebezeichnung: string | null;
    angebotsnummer: string | null; datenblattDatum: string | null;
    aktuelleDatenversion: string | null; dateiname: string | null;
    skizzeDateiname: string | null; herstellerUrl: string | null;
    datenblattVerweis: string | null; zustand: string | null;
  } | null;

  /**
   * Der Kreislaufverbund — nur bei den Bowl-Anlagen belegt.
   *
   * Ein KVS hat auf Geräteebene keinen Wirkungsgrad; er entsteht erst im
   * Verbund zweier Geräte über den Glykolkreis. Deshalb stehen die Kennzahlen
   * hier und nicht bei den Gerätefeldern.
   */
  kvs: {
    systemgradProzent: number | null;
    energiegradProzent: number | null;
    gruppe: string | null;
    quelle: string | null;
    frostAustrittC: number | null;
    instanzen: {
      anlage: string; ausrichtung: string | null; saison: string;
      aussentempC: number | null; glykolProzent: number | null;
      zuluftM3h: number | null; zuluftKw: number | null;
      abluftM3h: number | null; abluftKw: number | null;
      rueckwaermzahlTrockenPct: number | null;
      energiewirkungsgradPct: number | null; datenstatus: string;
    }[];
    /** Plätze im Schema, ohne Gerätezuordnung — sie ist nicht belegt. */
    geraeteplaetze: {
      anlage: string; geraeteslot: string; luftart: string;
      volumenstromM3h: number | null; leistungKw: number | null;
      eintrittC: number | null; austrittC: number | null;
    }[];
    typdatenblatt: {
      angebotsnummer: string; bezeichnung: string; variante: string | null;
      volumenstromM3h: number | null; ventilatorAnzahl: number | null;
      ventilatorLeistungKw: number | null; systemwirkungsgradPct: number | null;
      sfpWsM3: number | null; euroventWinter: string | null;
      euroventSommer: string | null;
    }[] | null;
    /** istWrg trennt Rückgewinnung von Klimatisierung. */
    register: {
      angebotsnummer: string; luftart: string; komponente: string;
      istWrg: boolean; betriebspunkt: string | null;
      nennleistungKw: number | null; eintrittC: number | null;
      austrittC: number | null; mediumTemp1C: number | null;
      mediumTemp2C: number | null; glykolProzent: number | null;
    }[] | null;
  } | null;
}


// --- Druckverlust Lüftung ---------------------------------------------------

export interface DruckverlustAnlage {
  anlage: string;
  /** zuluft | abluft — ein Gerät hat zwei Ventilatoren und zwei Wege. */
  weg: string;
  kreis: string | null;
  bauteile: number;
  auslaesse: number;
  volumenstromM3h: number | null;
  druckverlustPa: number | null;
  reibungPa: number | null;
  einzelPa: number | null;
  laengeM: number | null;
  fehlendeBeiwerte: number;
  istRingnetz: boolean;
  massgebenderAuslass: number | null;
  massgebenderRaum: string | null;
  gerechnetAm: string | null;
}

export interface DruckverlustPosten {
  nummer: number;
  elementId: number;
  art: string;
  bezeichnung: string | null;
  bauform: string | null;
  volumenstromM3h: number | null;
  laengeM: number | null;
  querschnittM2: number | null;
  geschwindigkeitMs: number | null;
  geschwindigkeitsdruckPa: number | null;
  lambda: number | null;
  reynolds: number | null;
  zeta: number | null;
  weg: string | null;
  druckverlustPa: number | null;
  summePa: number;
  herkunft: string;
  hinweis: string;
}

export interface KuecheQuellmodell {
  id: string;
  linkInstanzUniqueId: string;
  linkTypName: string | null;
  linkInstanzName: string | null;
  dateiname: string | null;
  dateipfad: string | null;
  kuerzel: string | null;
  istKuechenquelle: boolean;
  freigegeben: boolean;
  freigegebenVon: string | null;
  freigegebenAm: string | null;
  geladen: boolean | null;
  elementanzahl: number | null;
  wurzelbauteile: number | null;
  zuletztGelesenAm: string | null;
  zustand: string;
}

export interface KuecheAufnahmestand {
  konfig: {
    hostModell: string;
    erwarteteQuellmodelle: number;
    markerFamilie: string;
    markerKantenlaengeM: number;
    markerVersatzUeberEbeneM: number;
    mappingVollstaendigNoetig: boolean;
    erwarteteDateienJson: string | null;
  } | null;
  quellen: KuecheQuellmodell[];
  freigegeben: number;
  erwartet: number;
  quellenVollstaendig: boolean;
  bestand: {
    bauteile: number;
    lueftungsdecken: number;
    leuchten: number;
    ungeprueft: number;
    ohneRaum: number;
    ohneEbene: number;
    altbestand: number;
  };
  pdf: { beobachtungen: number; offen: number; plaene: number };
  letzterLauf: {
    id: string; laufart: string; zustand: string;
    gestartet: string; beendet: string | null; ergebnisJson: string | null;
  } | null;
}

export interface KuecheZuordnungslauf {
  laufId: string;
  beobachtungen: number;
  mitKandidaten: number;
  automatisch: number;
  zurPruefung: number;
  ohneKandidat: number;
  konflikte: number;
  hinweise: string[];
}

export interface KuecheKandidat {
  komponenteId: string;
  bewertung: number;
  rang: number;
  abstandXyM: number | null;
  abstandBboxM: number | null;
  innerhalbBbox: boolean | null;
  gleicheEbene: boolean | null;
  gleicherRaum: boolean | null;
  positionTrifft: boolean | null;
  etage: string;
  geraet: string | null;
  kucaFamilie: string | null;
  kucaTyp: string | null;
  bauteilklasse: string;
  positionIdModell: string | null;
  quellmodellId: string | null;
}

export interface KuecheBeobachtung {
  id: string;
  beobachtungsart: string;
  rohtext: string | null;
  positionText: string | null;
  raumText: string | null;
  abschnittText: string | null;
  planId: string | null;
  hostXKandidatM: number | null;
  hostYKandidatM: number | null;
  hostKoordinateStatus: string;
  technikJson: string | null;
  kandidaten: KuecheKandidat[];
}

export interface KuecheOffeneZuordnungen {
  gesamt: number;
  gezeigt: number;
  beobachtungen: KuecheBeobachtung[];
}

export interface KuecheLeuchte {
  id: string;
  komponenteId: string;
  traegerKomponenteId: string | null;
  elektroBauteilId: string | null;
  elektroBauteilart: string | null;
  bezugsstatus: string;
  abstandM: number | null;
  begruendung: string | null;
  leistungW: number | null;
  spannungV: string | null;
  etage: string;
  kucaFamilie: string | null;
  kucaTyp: string | null;
  beobachtetXM: number | null;
  beobachtetYM: number | null;
}

export interface KuecheLeuchtenstand {
  gesamt: number;
  jeStatus: Record<string, number>;
  leuchten: KuecheLeuchte[];
}

export interface KuecheGatebefund {
  status: string;
  bereit: boolean;
  quellmodelleErwartet: number;
  quellmodelleFreigegeben: number;
  quellmodelleGelesen: number;
  quellmodelleUngeladen: number;
  bauteile: number;
  bauteileUngeprueft: number;
  lueftungsdecken: number;
  leuchten: number;
  leuchtenOhneElektrobezug: number;
  beobachtungen: number;
  beobachtungenTechnisch: number;
  beobachtungenOffen: number;
  beobachtungenZugeordnet: number;
  beobachtungenAusgeschlossen: number;
  beobachtungenOhneZiel: number;
  mehrfachzuordnungen: number;
  plaeneVerwendet: number;
  plaeneOhneRegistrierung: number;
  deckungProzent: number;
  blocker: string[];
  hinweise: string[];
  markerProbelauf: boolean;
}

export interface SchallAnlage {
  anlage: string;
  weg: string;
  auslaesse: number;
  volumenstromM3h: number | null;
  druckverlustPa: number | null;
  straenge: number;
  mitSchall: number;
  lpaMax: number | null;
  lpaMin: number | null;
  lwaMax: number | null;
  daempfung: number | null;
  massgeblich: string | null;
  ueberZiel: number;

  /**
   * Fehlt einem geräuscherzeugenden Bauteil dieses Weges der Kennwert, ist
   * der ausgewiesene Pegel eine untere Schranke — kein Nachweis.
   */
  ohneKennwerte: boolean;
}

export interface SchallUebersicht {
  anzahlWege: number;
  mitSchall: number;
  ohneKennwerte: number;
  ueberZielWege: number;
  anlagen: SchallAnlage[];
  /** TV7a (§22 d): die offene Eigentümerfrage LUE-39, wörtlich aus der Serverkonstante. */
  lue39: string;
}

export interface SchallLauf {
  anzahlAnlagen: number;
  netze: number;
  ringnetze: number;
  hinweise: string[];
  uebersicht: SchallUebersicht;
}

export interface DruckverlustTeilstrecke {
  nummer: number;
  auslassId: number;
  raumnummer: string | null;
  ebene: string | null;
  volumenstromM3h: number | null;
  druckverlustPa: number;
  laengeM: number | null;
  bauteile: number;
  abgleichPa: number;
  lwaAuslassDb: number | null;
  lpaDirektDb: number | null;
  zielpegelDbA: number | null;
  lwOktaven: number[] | null;
  massgeblich: string | null;
  daempfungDb: number | null;
}

export interface DruckverlustStrang extends DruckverlustAnlage {
  strang: DruckverlustPosten[];
  teilstrecken: DruckverlustTeilstrecke[];
  hinweise: string[];
}

export interface LueftungBefund {
  anlage: string | null;
  weg: string | null;
  elementId: number;
  art: string | null;
  bauform: string | null;
  kreis: string | null;
  raumnummer: string | null;
  regel: string;
  istwert: number | null;
  grenzwert: number | null;
  einheit: string;
  /** hinweis | warnung | fehler */
  schwere: string;
  text: string;
  gerechnetAm: string;
}

export interface LueftungBefundRegel {
  regel: string;
  grenzwert: number | null;
  einheit: string;
  anzahl: number;
  fehler: number;
  warnungen: number;
  hoechster: number | null;
}

export interface LueftungBefundliste {
  befunde: LueftungBefund[];
  /** Die Liste ist gekürzt; die Summen daneben zählen trotzdem alles. */
  gekuerzt: boolean;
  summe: {
    gesamt: number;
    fehler: number;
    warnungen: number;
    hinweise: number;
  };
  jeRegel: LueftungBefundRegel[];
  stand: string | null;
}

/**
 * Ein Laufparameter der Lüftung — der Wert ist TEXT und keine Zahl.
 *
 * Die Einstellung eines Ventils ist „mitte" und keine Größe, die sich
 * addieren lässt; wer ein Zahlenfeld nimmt, kann sie nicht ablegen und
 * erfindet dann eine 15.
 */
export interface LueftungLaufparameter {
  schluessel: string;
  wert: string;
  einheit: string | null;
  bezeichnung: string | null;
  quelle: string | null;
  begruendung: string | null;
  istBestaetigt: boolean;
  bestaetigtVon: string | null;
  bestaetigtAm: string | null;
  /** Ob dieser Wert ein Projektwert ist und den hausweiten schlägt. */
  istProjektwert: boolean;
}

/** Ein Luftzustand, wie die Rechnung ihn aus den Parametern bildet. */
export interface LueftungLuftzustand {
  temperaturC: number;
  dichteKgM3: number;
  kinViskositaetM2s: number;
}

export interface LueftungGrundlagen {
  laufparameter: LueftungLaufparameter[];
  medienwerte: {
    kreis: string;
    zustand: string;
    bezeichnung: string | null;
    temperaturC: number | null;
    dichteKgM3: number;
    kinViskositaetM2s: number | null;
    istAuslegung: boolean;
    herkunft: string;
    quelle: string | null;
    geprueft: boolean;
    begruendung: string | null;
  }[];
  raumakustik: {
    schluessel: string;
    bezeichnung: string;
    nachhallzeitS: number;
    zielpegelDbA: number | null;
    raumkategorie: string | null;
    herkunft: string;
    quelle: string | null;
    istBestaetigt: boolean;
    bestaetigtVon: string | null;
    bestaetigtAm: string | null;
    hinweis: string | null;
  }[];
  hygieneregeln: {
    regel: string;
    bezeichnung: string;
    pruefart: string;
    luftstrom: string | null;
    art: string | null;
    stufe: number | null;
    klasseGruppe: string | null;
    klasseProzent: number | null;
    schwere: string;
    quelle: string | null;
    begruendung: string | null;
    istBestaetigt: boolean;
  }[];
  /**
   * Was die Rechnung aus den Parametern bilden WÜRDE, wenn der Lauf jetzt
   * startete — nicht gespeichert, sondern gerechnet.
   */
  gerechnet: {
    aussenluft: LueftungLuftzustand | null;
    fortluft: LueftungLuftzustand | null;
  };
}

export interface LueftungLaufparameterAntwort {
  schluessel: string;
  wert: string;
  einheit: string | null;
  quelle: string | null;
  istBestaetigt: boolean;
  /** Der hausweite Wert, den der Projektwert schlägt. */
  hausweit: string;
  hinweis: string;
}

/**
 * Eine Teilstrecke als ZEILE — ein Verbraucher des Wegs (Befund L-16).
 *
 * `posten` nennt, wie viele Bauteile dieser Teilstrecke im Verzeichnis
 * `lueftung_teilstrecke_posten` stehen. Seit dem 15.09.2026 trägt JEDE
 * Teilstrecke ihre Posten; vorher war es nur der maßgebende Strang, und auch
 * der nicht immer — teilen sich mehrere Anlagen dieselben Bauteile (bei der
 * Bowl zehn Anlagen einen Ring), bekam sie die Anlage mit dem größten
 * Druckverlust, und die übrigen gingen leer aus.
 *
 * `abgleichPa`, `lwaAuslassDb`, `lpaDirektDb`, `daempfungDb` und
 * `massgeblich` stehen als Spalten an `lueftung_teilstrecken`; für Läufe von
 * vor Migration 0486 fallen sie auf die abgelegte Verbraucherliste der
 * Anlagenberechnung zurück. `schallQuelle` sagt, welcher Weg gegangen wurde:
 * `tabelle` | `anlagenberechnung` | `keine`.
 */
export interface LueftungWegteilstrecke {
  id: string;
  kennzeichen: string;
  nummer: number;
  auslassId: number | null;
  vonElementId: number | null;
  raumnummer: string | null;
  ebene: string | null;
  auslassBauform: string | null;
  auslassTyp: string | null;
  volumenstromM3h: number | null;
  druckverlustPa: number | null;
  druckverlustReibungPa: number | null;
  druckverlustEinzelPa: number | null;
  laengeM: number | null;
  bauteile: number | null;
  summeZeta: number | null;
  istMassgebend: boolean;
  herkunft: string | null;
  hinweis: string | null;
  posten: number;
  abgleichPa: number | null;
  lwaAuslassDb: number | null;
  lpaDirektDb: number | null;
  daempfungDb: number | null;
  massgeblich: string | null;
  zielpegelDbA: number | null;
  ueberZiel: boolean;
  raumpegelDbA: number | null;
  raumUeberschreitung: number | null;
}

export interface LueftungWegteilstrecken {
  anlage: string;
  weg: string;
  praefix: string;
  kreis: string | null;
  volumenstromM3h: number | null;
  druckverlustPa: number | null;
  reibungPa: number | null;
  einzelPa: number | null;
  laengeM: number | null;
  bauteile: number | null;
  auslaesse: number | null;
  fehlendeBeiwerte: number | null;
  istRingnetz: boolean;
  massgebenderAuslass: number | null;
  massgebenderRaum: string | null;
  stand: string | null;
  /** anlagenberechnung | keine */
  schallQuelle: string;
  summe: {
    strecken: number;
    mitPosten: number;
    massgebende: number;
    hoechsterPa: number | null;
    niedrigsterPa: number | null;
    groessterAbgleichPa: number | null;
    ueberZiel: number;
    raeume: number;
  };
  teilstrecken: LueftungWegteilstrecke[];
}

/**
 * Ein Posten des Strangs — ein Bauteil mit seinen Eingangsgrößen
 * (Befund L-18).
 *
 * `volumenstromHerkunft` und `querschnittHerkunft` sind der Kern: 21.014
 * Volumenströme und 24.377 Querschnitte im Bestand sind aus dem Nachbarn
 * ergänzt und nicht gemessen. Ein ergänzter Wert ist kein Modellwert.
 */
export interface LueftungPosten {
  nummer: number | null;
  elementId: number;
  art: string;
  familie: string | null;
  typ: string | null;
  bauform: string | null;
  volumenstromM3h: number | null;
  /** modell | nachbar */
  volumenstromHerkunft: string | null;
  querschnittM2: number | null;
  /** modell | nachbar | datenblatt */
  querschnittHerkunft: string | null;
  hydraulischerDurchmesser: number | null;
  durchmesserMm: number | null;
  breiteMm: number | null;
  hoeheMm: number | null;
  laengeM: number | null;
  geschwindigkeitMs: number | null;
  geschwindigkeitsdruckPa: number | null;
  reynolds: number | null;
  lambda: number | null;
  zeta: number | null;
  /** datenblatt | bauform | gerechnet | keine */
  beiwertHerkunft: string | null;
  /** Durchgang | Abzweig | unbekannt */
  weg: string | null;

  /**
   * Der Beitrag AUF DIESEM STRANG — die Differenz zweier aufeinander
   * folgender laufender Summen.
   *
   * Nicht der Wert aus der Spalte am Bauteil: Die Einzelbewertung kennt den
   * Weg durch das Bauteil nicht und rechnet am T-Stück mit dem
   * ungünstigeren Beiwert. Am Weg RLT.T.EG.6 zuluft gehen beide an 19 von
   * 150 Posten auseinander, bis zu 1,99 Pa.
   */
  druckverlustPa: number | null;

  /** Der Wert der Einzelbewertung aus der Spalte — zum Vergleich. */
  druckverlustEinzelbewertungPa: number | null;

  summePa: number | null;
  raumnummer: string | null;
  hinweis: string | null;
}

export interface LueftungTeilstreckenposten {
  teilstrecke: {
    id: string;
    kennzeichen: string;
    bezeichnung: string | null;
    nummer: number;
    kreis: string | null;
    vonElementId: number | null;
    nachElementId: number | null;
    laengeM: number | null;
    bauteile: number | null;
    summeZeta: number | null;
    volumenstromM3h: number | null;
    druckverlustPa: number | null;
    druckverlustReibungPa: number | null;
    druckverlustEinzelPa: number | null;
    istMassgebend: boolean;
    herkunft: string | null;
    hinweis: string | null;
  };
  /** verzeichnis | lueftung_kanaele | keine — woher die Posten kommen. */
  quelle: string;
  /** Warum keine Posten da sind — null, wenn welche da sind. */
  grund: string | null;
  summe: {
    posten: number;
    summePa: number | null;
    ohneBeiwert: number;
    mitReynolds: number;
    mitWeg: number;
    /** Posten, deren Einzelbewertung vom Beitrag auf dem Strang abweicht. */
    abweichendeEinzelwerte: number;
  };
  posten: LueftungPosten[];
}

/**
 * Der Pegel EINES Raums nach VDI 2081 Blatt 1.
 *
 * `lpaDirektMassgebendDb` ist der Direktschall des LAUTESTEN Auslasses —
 * unter ihm liegt der Aufpunkt. `lpaRaumDb` ist er zuzüglich des
 * Diffusfelds ALLER Auslässe.
 *
 * `lpaDirektSummeDb` ist die energetische Summe der Direktanteile aller
 * Auslässe, jeder in 1,5 m Abstand. Das war bis zum 20.09.2026 der
 * ausgewiesene Raumpegel und setzt einen Hörer voraus, der gleichzeitig
 * unter jedem Auslass steht; die Zahl bleibt als OBERE SCHRANKE stehen
 * (PB32, S-01).
 *
 * Ist `absorptionHerkunft` gleich „fehlt“, gibt es keinen Raumpegel —
 * geraten wird nichts.
 */
export interface LueftungRaumschallzeile {
  raumnummer: string;
  raumname: string | null;
  ebene: string | null;
  nutzungsart: string | null;
  anzahlAuslaesse: number;
  wegeGesamt: number;
  /** Obere Schranke: alle Direktanteile in je 1,5 m summiert. */
  lpaDirektSummeDb: number | null;
  /** Direktschall des maßgebenden (lautesten) Auslasses. */
  lpaDirektMassgebendDb: number | null;
  lpaRaumDb: number | null;
  diffusZuschlagDb: number | null;
  zielpegelDbA: number | null;
  ueberschreitung: number | null;
  /** Festlegung F36: um wie viel der Raumpegel sinken muss. */
  zusatzdaempfungDb: number | null;
  /** Das Oktavband mit dem größten A-bewerteten Beitrag. */
  massgebendesBandHz: number | null;
  /** Dämpfung allein in diesem Band; null, wenn es nicht reicht. */
  zusatzdaempfungBandDb: number | null;
  massnahmeAuslassId: number | null;
  massnahme: string | null;
  raumvolumenM3: number | null;
  /** raumbuch | flaeche_hoehe | fehlt */
  raumvolumenHerkunft: string | null;
  nachhallzeitS: number | null;
  absorptionsflaecheM2: number | null;
  /** sabine | fehlt | … */
  absorptionHerkunft: string;
  /** Auslasszuordnung trägt nicht — kein Befund gegen den Zielpegel. */
  zuordnungUnsicher: boolean;
  /** Wetterschutzgitter und Dachhauben, die nicht eingehen. */
  aussenterminalsAusgelassen: number;
  /**
   * EIN TEIL der Auslässe ohne Eigengeräuschquelle: Der Pegel steht und ist
   * zu klein.
   */
  istUntereSchranke: boolean;
  /**
   * KEIN Auslass mit Eigengeräusch — es gibt keinen Raumpegel, und es steht
   * auch keine 0 da (PB45). Schließt `istUntereSchranke` aus.
   */
  nichtBerechenbar: boolean;
  auslaesseOhneEigengeraeusch: number;
  lautesterAuslassId: number | null;
  lautesterAuslassDb: number | null;
  hinweis: string | null;
  gerechnetAm: string;
}

export interface LueftungRaumschallliste {
  raeume: LueftungRaumschallzeile[];
  gekuerzt: boolean;
  /** TV7a (§22 d): die offene Eigentümerfrage LUE-39, wörtlich aus der Serverkonstante. */
  lue39: string;
  summe: {
    gesamt: number;
    ueberZiel: number;
    ohneZiel: number;
    ohneAbsorption: number;
    auslaesse: number;
    hoechster: number | null;
    groessteUeberschreitung: number | null;
    /** Räume ohne JEDEN Auslasskennwert — keine Zahl, auch keine 0. */
    nichtBerechenbar: number;
    /** Räume, in denen ein Teil der Auslässe fehlt: Pegel zu klein. */
    pegelZuKlein: number;
    /** Auslässe ohne Kennwert insgesamt. */
    auslaesseOhneKennwert: number;
    /** Räume, in denen KEIN Auslass einen Kennwert trägt. */
    ohneJedenKennwert: number;
  };
  jeEbene: {
    ebene: string | null;
    anzahl: number;
    ueberZiel: number;
    hoechster: number | null;
  }[];
  stand: string | null;
}

export interface LueftungRaumauslass {
  teilstreckeId: string;
  kennzeichen: string;
  nummer: number;
  anlage: string | null;
  weg: string | null;
  auslassId: number;
  volumenstromM3h: number | null;
  druckverlustPa: number | null;
  laengeM: number | null;
  bauteile: number | null;
  istMassgebend: boolean;
  auslassBauform: string | null;
  abgleichPa: number | null;
  lwaAuslassDb: number | null;
  lpaDirektDb: number | null;
  daempfungDb: number | null;
  massgeblich: string | null;
  istLautester: boolean;
}

export interface LueftungRaumschallRaum {
  raum: LueftungRaumschallzeile & {
    /** Direktschall des maßgebenden Auslasses je Oktavband. */
    oktaven: (number | null)[];
    /** Gesamtpegel je Oktavband: Direkt plus Diffus. */
    oktavenRaum: (number | null)[];
  };
  /** Die lautesten zuerst; ab 200 Zeilen gekürzt. */
  auslaesse: LueftungRaumauslass[];
  auslaesseGesamt: number;
  gekuerzt: boolean;
  /** anlagenberechnung | keine */
  schallQuelle: string;
}

/**
 * Eine Zeile des Soll-Ist-Vergleichs der Luftmengen (Befund L-01, Stufe 1).
 *
 * Eine Zeile ist ENTWEDER ein Raum (`roomId` gesetzt) ODER ein Bereich, der
 * als Ganzes ausgelegt wird (`bereich` gesetzt) — im Bestand die Bowl mit 76
 * Rangblöcken und 22.222 Personen aus der MBBM-Simulation.
 */
export interface LueftungLuftmenge {
  roomId: string | null;
  raumnummer: string | null;
  raumname: string | null;
  ebene: string | null;
  bereich: string | null;
  raeumeAnzahl: number | null;
  nutzungsart: string | null;
  personen: number | null;
  /** modell | hand | abgeleitet | standard | extern */
  personenHerkunft: string | null;
  flaecheM2: number | null;
  volumenM3: number | null;
  sollPersonenM3h: number | null;
  sollFlaecheM3h: number | null;
  sollLuftwechselM3h: number | null;
  sollM3h: number | null;
  sollHerkunft: string | null;
  istZuluftM3h: number | null;
  istAbluftM3h: number | null;
  istM3h: number | null;
  istQuelle: string | null;
  abweichungProzent: number | null;
  /** fehlt | kein_soll | unter_soll | im_rahmen | ueber_soll | unplausibel */
  bewertung: string;
  kennzeichnungen: string | null;
  hinweis: string | null;
  gerechnetAm: string;
}

export interface LueftungLuftmengenliste {
  zeilen: LueftungLuftmenge[];
  /** Die Liste ist gekürzt; die Zahlen daneben zählen trotzdem alles. */
  gekuerzt: boolean;
  summe: {
    gesamt: number;
    raeume: number;
    bereiche: number;
    /** Ohne die Bereichszeilen — sonst zählte die Bowl doppelt. */
    sollSumme: number;
    istSumme: number;
  };
  jeBewertung: { bewertung: string; anzahl: number }[];
  jeNutzungsart: {
    nutzungsart: string;
    anzahl: number;
    unterSoll: number;
    ohneIst: number;
    ohneSoll: number;
  }[];
  stand: string | null;
}

// --- TV8: Brandschutzklappenliste (Seite lueftung/brandschutzklappen, PDF LUE-BKL) ---------------------------------

/** Eine Brandschutzklappe - genau die Zeile des Berichts LUE-BKL. */
export interface BrandschutzklappeZeile {
  anlage: string;
  hatAnlage: boolean;
  ebene: string;
  hatEbene: boolean;
  aksSchluessel: string | null;
  aksKuerzel: string | null;
  revitId: string | null;
  raum: string | null;
  baureihe: string | null;
  typ: string | null;
  groesse: string | null;
  ausloesung: string | null;
  feuerwiderstand: string | null;
  brandabschnitt: string | null;
  /** aks | baureihe */
  erkennung: string;
  /** „über AKS erkannt“ | „über Baureihe erkannt“ */
  herkunft: string;
  /** ohne_aks_schluessel, aks_abweichend, ohne_anlage, ohne_ausloesung, ohne_feuerwiderstand, ohne_brandabschnitt */
  luecken: string[];
}

export interface BrandschutzklappenMenge {
  baureihe: string;
  groesse: string;
  klappen: number;
  ohneAksSchluessel: number;
}

export interface BrandschutzklappenAnlagenmenge {
  anlage: string;
  hatAnlage: boolean;
  klappen: number;
  ueberAks: number;
  ueberBaureihe: number;
  ohneAksSchluessel: number;
  ebenen: number;
}

export interface BrandschutzklappenAnnahme {
  parameter: string;
  wert: string | null;
  einheit: string | null;
  herkunft: string;
  quelle: string | null;
  bestaetigt: boolean;
  normbezug: string | null;
}

export interface BrandschutzklappenModell {
  /** gesamt | anlage | knoten */
  umfang: string;
  gegenstand: string;
  klappen: number;
  ueberAks: number;
  ueberBaureihe: number;
  anlagen: number;
  luecken: {
    ohneAksSchluessel: number;
    aksAbweichend: number;
    ohneAnlage: number;
    ausloesungOffen: number;
    feuerwiderstandOffen: number;
    brandabschnittOffen: number;
  };
  jeBaureihe: BrandschutzklappenMenge[];
  jeAnlage: BrandschutzklappenAnlagenmenge[];
  zeilen: BrandschutzklappeZeile[];
  annahmen: BrandschutzklappenAnnahme[];
}

export interface BrandschutzklappenAntwort {
  /** Gesetzt, wenn keine Klappe erkennbar ist (Regel bsk_liste fehlt oder ist unvollständig). */
  hinweis: string | null;
  datenhash: string | null;
  /** Vorabzug | Zwischenstand */
  status: string;
  modellstand: string | null;
  modell: BrandschutzklappenModell | null;
  /** Je Zeile das Bauteil (ventilation_components.Id), Zeile für Zeile. */
  ziele: string[];
  vorbehalte: { art: 'annahmen' | 'fachfrage' | 'raumstand' | 'veraltet' | 'sonstiges'; text: string; verweis: string | null }[];
}

/**
 * Eine Heizfläche nach DIN EN 442-2 — Befunde H-01 und H-13 der Normprüfung
 * Heizung/Kälte vom 16.09.2026.
 *
 * Die drei Leistungen stehen NEBENEINANDER und nicht übereinander: Der
 * Bestand führt für dieselbe Heizfläche eine Modellleistung, eine
 * Normleistung und einen Massenstrom, und höchstens eine davon kann stimmen.
 */
export interface HeizungHeizflaeche {
  id: string;
  bauteilId: string;
  elementId: number | null;
  bauart: string;
  typbezeichnung: string | null;
  geschoss: string | null;
  raumnummer: string | null;
  raumname: string | null;
  /** Typenschild bei 75/65/20. */
  normleistungW: number | null;
  normUebertemperaturK: number | null;
  vorlaufC: number | null;
  ruecklaufC: number | null;
  raumtemperaturC: number | null;
  betriebUebertemperaturK: number | null;
  exponent: number | null;
  /** bauteil | ansatz | fehlt. */
  exponentHerkunft: string | null;
  betriebsleistungW: number | null;
  massenstromKgH: number | null;
  leistungAusMassenstromW: number | null;
  modellleistungW: number | null;
  heizlastRaumW: number | null;
  geraeteImRaum: number | null;
  /** Raumbezogen: Summe der Heizflächen des Raums gegen dessen Heizlast. */
  deckungProzent: number | null;
  /** Modellleistung geteilt durch die Leistung aus dem Massenstrom (H-13). */
  verhaeltnis: number | null;
  /** unter | deckt | ueber | widerspruch | ohne_kennwert. */
  bewertung: string;
  /** Hersteller und Reihe der Kennlinie, `null` = kein Katalogeintrag. */
  baureihe: string | null;
  /** Die Baugröße des Herstellers, z. B. KERMI-PTV-T22-H405-L605. */
  baureiheKennung: string | null;
  /** kennwert | muster | fehlt. */
  baureiheHerkunft: string | null;
  /** bauteil | katalog | fehlt. */
  normleistungHerkunft: string | null;
  /**
   * arithmetisch | logarithmisch — mit welcher Definition der Übertemperatur
   * gerechnet wurde. Sie gehört an die ZEILE: Zwei Hersteller dürfen zwei
   * Definitionen mitbringen, und die Ergebnisse liegen 4,5 % auseinander.
   */
  uebertemperaturArt: string | null;
  /** Aus der Betriebsleistung gerechnet (Festlegung Frage 9). */
  massenstromBerechnetKgH: number | null;
  spreizungK: number | null;
  /** gedeckt | unterdeckt | ueberdeckt | ohne_heizlast — der Befund des RAUMS. */
  deckungBefund: string | null;
  hinweis: string | null;
  gerechnetAm: string;
}

export interface HeizungHeizflaechenansatz {
  schluessel: string;
  bezeichnung: string;
  wert: number;
  einheit: string | null;
  art: string;
  herkunft: string;
  quelle: string | null;
  istBestaetigt: boolean;
  hinweis: string | null;
}

export interface HeizungWiderspruch {
  betroffen: number;
  geprueft: number;
  mittleresVerhaeltnis: number;
  kleinstesVerhaeltnis: number;
  groesstesVerhaeltnis: number;
  bauarten: string[];
  text: string;
}

export interface HeizungHeizflaechenliste {
  zeilen: HeizungHeizflaeche[];
  /** Die Liste ist gekürzt; die Summen daneben zählen trotzdem alles. */
  gekuerzt: boolean;
  summe: {
    gesamt: number;
    raeume: number;
    normleistungW: number;
    betriebsleistungW: number;
    leistungAusMassenstromW: number;
    modellleistungW: number;
    heizlastRaeumeW: number;
    /**
     * Installierte Betriebsleistung über Norm-Heizlast, über alle Räume mit
     * Heizlast (V-L1). `null` heißt: kein Heizlastlauf — eine 0 stünde dort
     * für „nichts gedeckt" und wäre das Gegenteil der Wahrheit.
     */
    deckungGebaeudeProzent: number | null;
    /** Heizflächen, deren Typbezeichnung keinen Katalogeintrag trifft. */
    ohneBaureihe: number;
    /** Heizflächen mit Baugröße aus dem Katalog — Normleistung UND n. */
    mitKennwert: number;
  };
  jeBewertung: { stufe: string; anzahl: number }[];
  /** Die Deckung OHNE den Vorrang des Widerspruchs, je Raum gezählt. */
  jeDeckung: { stufe: string; anzahl: number }[];
  widerspruch: HeizungWiderspruch | null;
  ansaetze: HeizungHeizflaechenansatz[];
  /** Die Deckung je Geschoss — Heizlast je Raum EINMAL gezählt. */
  jeGeschoss: {
    geschoss: string;
    raeume: number;
    betriebsleistungW: number;
    heizlastW: number;
    deckungProzent: number | null;
  }[];
  stand: string | null;
}

/**
 * Der Abgleich der Kälteleistung gegen die Referenz des Fachplaners —
 * Befund H-20.
 */
export interface KuehllastReferenzzeile {
  szenario: string;
  referenzKw: number;
  datenstatus: string | null;
  istAktuell: boolean;
  quelle: string | null;
  toleranzProzent: number;
  anlagenKaelteleistungKw: number;
  gebaeudespitzeKw: number;
  spitzeStunde: number | null;
  abweichungAnlageProzent: number;
  abweichungSpitzeProzent: number;
  ueberToleranz: boolean;
}

export interface KuehllastReferenzvergleich {
  anlagenKaelteleistungKw: number;
  gebaeudespitzeKw: number;
  spitzeStunde: number | null;
  summeRaumspitzenKw: number;
  zeilen: KuehllastReferenzzeile[];
  vorbehalt: string;
}

export interface DruckverlustUebersicht {
  anlagen: DruckverlustAnlage[];
  stand: string | null;
  summe: {
    anlagen: number;
    ringnetze: number;
    fehlendeBeiwerte: number;
    ohneErgebnis: number;
  };
}

/**
 * Die Fehlermeldung aus einer Antwort ziehen — eine Stelle für alle Wege.
 *
 * Vorher stand dieser Block elfmal in dieser Datei, jedes Mal mit derselben
 * Regel: `if (k?.message) text = k.message`. Als die API am 20.08.2026 auf
 * ProblemDetails umgestellt wurde, hätten alle elf mitziehen müssen — der Text
 * steht dort in `detail`. Beim Anwender wäre sonst „HTTP 409“ angekommen statt
 * des Satzes, der ihm sagt, was zu tun ist.
 *
 * Zwei der elf standen mit einzeiligem `catch` da und wären bei einer Suche
 * nach dem mehrzeiligen Muster übrig geblieben — es waren genau die zwei
 * PDF-Wege, um die es ging.
 *
 * Die Reihenfolge der Felder ist kein Geschmack:
 *
 *   detail   ProblemDetails der API — dort steht der Satz.
 *   message  die alten Endpunkte, und der Fachfehler doppelt es dorthin,
 *            solange installierte Addins darauf lesen.
 *   fehler   ältere Endpunkte in der Koordination.
 *   title    nur als Notnagel: „Nicht gefunden“ ist wahr, aber unspezifisch.
 *
 * `abhilfe` wird angehängt statt ersetzt — die Meldung sagt, was los ist, die
 * Abhilfe sagt, was hilft, und der Anwender braucht beides.
 */
async function fehlertext(antwort: Response): Promise<string> {
  // HR-1: Eine 403 der Anmeldeprüfung hat keinen Rumpf - die Person soll lesen, was fehlt, nicht „HTTP 403“.
  const ohneText = antwort.status === 403 ? ROLLE_FEHLT : `HTTP ${antwort.status}`;
  try {
    const k = await antwort.json();

    const meldung = k?.detail ?? k?.message ?? k?.fehler ?? k?.title;
    if (!meldung) return ohneText;

    return k?.abhilfe ? `${meldung}\n\n${k.abhilfe}` : String(meldung);
  } catch {
    // Keine JSON-Antwort — dann bleibt der Statuscode (bzw. bei 403 der Hinweis auf die Rolle).
    return ohneText;
  }
}

/**
 * K3 (F447): Fehlercode von `POST /api/jobs`, wenn keine angemeldete Verarbeitungsmaschine die Revit-Version des Modells
 * kann (Server: `RevitVersionAbdeckung.Fehlercode`). Der Auftrag entsteht dann nicht - vorher blieb er still „wartend“.
 */
export const FEHLER_REVIT_VERSION_OHNE_MASCHINE = 'REVIT_VERSION_OHNE_MASCHINE';

/**
 * K3 (F447): Fehler einer Anfrage mit Statuscode und - falls der Server einen mitgibt - dem Fehlercode (`code` der
 * ProblemDetails). `message` ist derselbe Text wie bisher (`fehlertext`, samt Abhilfe): Jede Stelle, die `e.message`
 * zeigt, zeigt ihn unverändert.
 */
export class ApiFehler extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(meldung: string, status: number, code: string | null) {
    super(meldung);
    this.status = status;
    this.code = code;
  }
}

/** K3: `fehlertext` plus Statuscode und Fehlercode; der Rumpf wird dafür einmal geklont. */
async function apiFehler(antwort: Response): Promise<ApiFehler> {
  let code: string | null = null;
  try {
    const k = await antwort.clone().json();
    if (typeof k?.code === 'string') code = k.code;
  } catch {
    // Kein JSON-Rumpf (oder eine Attrappe ohne clone) - dann ohne Fehlercode.
  }
  return new ApiFehler(await fehlertext(antwort), antwort.status, code);
}

/** HR-1 (F104/F112): Text einer 403 ohne Meldung des Servers - schreibende Heizlast- und MSR-Aktionen verlangen eine Rolle. */
export const ROLLE_FEHLT = 'Für diese Aktion fehlt die Rolle Projektadmin.';

/** Aussparungen ohne Geschoss - sie stehen auf keinem Aussparungsplan (v0.45.2). */
export type OhneGeschoss = {
  stueck: number;
  ebenen: { ebene: string; stueck: number }[];
};

/**
 * Der Grund eines Abbruchs aus der Oberfläche (AUF3) - dieselben Werte wie
 * `Auftragsabbruch.cs` auf dem Server: höchstens 500 Zeichen (die Spalte fasst
 * 512), ohne Angabe der Standardtext.
 */
export const ABBRUCHGRUND_HOECHSTLAENGE = 500;
export const ABBRUCHGRUND_STANDARD = 'Abgebrochen in der Oberfläche';

/**
 * Welche Bauteile eine Liste oder Tabelle umfasst: eine Anlage, ein Verbundnetz, ein Geschoss - und seit TV7a
 * (§22 a/b) auch die Bauteile OHNE Geschoss (`ohneEbene`) und ein Knoten „Nicht zugeordnet“ (`zuordnung` mit
 * `traeger: 'offen'`).
 */
export interface Bauteilumfang {
  plantId?: string;
  systemId?: string;
  ebene?: string;
  ohneEbene?: boolean;
  zuordnung?: string;
  traeger?: string;
}

/** Der Umfang als Abfrage - leere Angaben fallen weg. */
export function umfangSuche(u: Bauteilumfang): URLSearchParams {
  const suche = new URLSearchParams();
  if (u.plantId) suche.set('plantId', u.plantId);
  if (u.systemId) suche.set('systemId', u.systemId);
  if (u.ebene) suche.set('ebene', u.ebene);
  if (u.ohneEbene) suche.set('ohneEbene', 'true');
  if (u.zuordnung) suche.set('zuordnung', u.zuordnung);
  if (u.traeger) suche.set('traeger', u.traeger);
  return suche;
}

export class ApiClient {
  constructor(private msal: IPublicClientApplication) {}

  /**
   * Ein laufender Token-Erwerb. Wird von allen wartenden Anfragen geteilt.
   *
   * Ohne das lösen zwei gleichzeitige Anfragen zwei Erneuerungen aus. MSAL
   * benutzt dafür ein verstecktes Fenster, und zwei davon gleichzeitig gehen
   * schief - im Browser erscheint dann „Failed to fetch", und nach dem Neuladen
   * geht es, weil der Token dann im Zwischenspeicher liegt.
   */
  private laufenderErwerb: Promise<string> | null = null;

  private async token(): Promise<string> {
    if (this.laufenderErwerb) return this.laufenderErwerb;

    this.laufenderErwerb = this.erwerbeToken()
      .finally(() => { this.laufenderErwerb = null; });

    return this.laufenderErwerb;
  }

  private async erwerbeToken(): Promise<string> {
    const konto = this.msal.getAllAccounts()[0];
    if (!konto) throw new Error('Nicht angemeldet.');

    try {
      const antwort = await this.msal.acquireTokenSilent({
        ...apiRequest,
        account: konto,

        /*
         * Eine eigene Rückkehradresse für das versteckte iframe.
         *
         * Ohne sie nimmt MSAL die aus der Grundeinstellung — also die Anwendung —
         * und lädt sie vollständig in das iframe. nginx verweigert das mit
         * `X-Frame-Options: DENY`, die Erneuerung scheitert, und MSAL fällt auf
         * das Popup unten zurück. Der Browser sperrt es, und sichtbar wird nur
         * `popup_window_error` — eine Meldung, die die Ursache nicht nennt.
         *
         * `anmeldung.html` ist leer und darf eingebettet werden. Als einzige
         * Adresse der Anwendung.
         */
        redirectUri: stilleErneuerung,
      });

      return antwort.accessToken;
    } catch (fehler) {
      // Schlägt die stille Erneuerung fehl - etwa weil die Sitzung abgelaufen
      // ist -, hilft nur eine sichtbare Anmeldung. Ohne diesen Zweig blieben
      // alle weiteren Anfragen erfolglos, bis jemand die Seite neu lädt.
      //
      // HIER die Grundadresse, nicht die leere Seite: Ein Popup ist ein echtes
      // Fenster, und dort muss MSAL laufen, um die Antwort zu verarbeiten.
      const antwort = await this.msal.acquireTokenPopup({
        ...apiRequest,
        account: konto,
      });

      return antwort.accessToken;
    }
  }

  /**
   * Ruft ab und versucht es bei einem Netzwerkfehler ein zweites Mal.
   *
   * „Failed to fetch" heißt nicht, dass der Server etwas abgelehnt hat - die
   * Verbindung kam nicht zustande. Das passiert im WLAN, beim Aufwachen des
   * Rechners oder wenn der Server gerade neu startet. Ein zweiter Versuch
   * genügt fast immer, und der Person bleibt eine Meldung erspart, mit der sie
   * nichts anfangen kann.
   */
  private async abrufen(pfad: string, optionen: RequestInit): Promise<Response> {
    try {
      return await fetch(pfad, optionen);
    } catch (fehler) {
      await new Promise((r) => setTimeout(r, 600));
      return fetch(pfad, optionen);
    }
  }

  private async hole<T>(pfad: string): Promise<T> {
    const antwort = await this.abrufen(pfad, {
      headers: { Authorization: `Bearer ${await this.token()}` },
    });

    if (!antwort.ok) {
      // Die Meldung der API mitnehmen, sofern vorhanden - sie sagt oft mehr
      // als der Statuscode.
      throw new Error(await fehlertext(antwort));
    }

    return antwort.json() as Promise<T>;
  }

  private async sende<T>(pfad: string, koerper: unknown): Promise<T> {
    const antwort = await this.abrufen(pfad, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await this.token()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(koerper),
    });

    // K3 (F447): ApiFehler statt Error - gleiche Meldung, dazu Status und Fehlercode des Servers.
    if (!antwort.ok) throw await apiFehler(antwort);
    return antwort.json() as Promise<T>;
  }

  private async schicke<T>(pfad: string, verfahren: string, koerper?: unknown): Promise<T | void> {
    const antwort = await this.abrufen(pfad, {
      method: verfahren,
      headers: {
        Authorization: `Bearer ${await this.token()}`,
        ...(koerper ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(koerper ? { body: JSON.stringify(koerper) } : {}),
    });

    if (!antwort.ok) {
      throw new Error(await fehlertext(antwort));
    }

    // 204 hat keinen Inhalt - ein json() darauf wirft.
    if (antwort.status === 204) return;
    return antwort.json() as Promise<T>;
  }

  zustand = () => this.hole<Zustand>('/api/status');
  /**
   * FV1: Version, Commit und Bauzeit des laufenden Stands (nur angemeldet; ohne Anmeldung 401). `release` ist nie
   * leer: ohne gesetzten Bau „Entwicklungsstand“ (`gesetzt` = false).
   */
  systemversion = () => this.hole<Systemversion>('/api/system/version');
  /**
   * TD-WR1: was die angemeldete Person selbst darf - nur Wahrheitswerte, keine Rollen (ohne Anmeldung 401). Die
   * Oberfläche blendet damit Knöpfe aus, die der Server ohnehin abweist; die Sperre bleibt der Server. Nicht direkt
   * aufrufen, sondern über `useModellSchreibrecht` (rechte.ts): eine Abfrage je Anmeldung.
   */
  systemrechte = () => this.hole<Systemrechte>('/api/system/rechte');
  maschinen = () => this.hole<Maschine[]>('/api/status/maschinen');
  projekte = () => this.hole<Projekt[]>('/api/projects');
  /**
   * BIM12 (F147/F148): das Modellregister des BIM-Kerns, nur lesend. Ohne freigeschalteten BIM-Kern antwortet der
   * Server 404 (die Meldung kommt als Fehler an).
   */
  bimModelle = (projektId: string) =>
    this.hole<BimModell[]>(`/api/projects/${encodeURIComponent(projektId)}/bim-modelle`);
  /**
   * MP-6 (F324): mit `projectId` bindet der Server die Abfrage an die Datenbank des Projekts; ohne sieht man nur die
   * Dienst-DB. Die Auftragsliste der Oberfläche fragt deshalb immer mit dem aktiven Projekt.
   */
  auftraege = (anzahl = 25, projectId?: string) => this.hole<Auftrag[]>(
    `/api/jobs?take=${anzahl}${projectId ? `&projectId=${encodeURIComponent(projectId)}` : ''}`,
  );
  goldenAuftraege = (anzahl = 200, projectId?: string) => this.hole<Auftrag[]>(
    `/api/jobs/golden-audit?take=${anzahl}${projectId ? `&projectId=${encodeURIComponent(projectId)}` : ''}`,
  );
  auftrag = (id: string) => this.hole<AuftragDetail>(`/api/jobs/${id}`);
  /**
   * Einen Auftrag abbrechen (AUF3). Der Grund ist freiwillig; ohne ihn trägt der
   * Server {@link ABBRUCHGRUND_STANDARD} ein, als Auslöser den angemeldeten
   * Benutzer.
   *
   * Über `schicke`, nicht `sende`: Die Route antwortet 204 ohne Inhalt, und
   * `sende` las daraus JSON - der Abbruch war geschehen, der Dialog meldete
   * trotzdem einen Fehler.
   */
  auftragAbbrechen = async (id: string, grund?: string): Promise<void> => {
    const g = grund?.trim();
    await this.schicke<void>(`/api/jobs/${id}/cancel`, 'POST', g ? { grund: g } : {});
  };

  /**
   * Einen Auftrag pausieren.
   *
   * Wirkt nur in der Warteschlange. Ein Teilauftrag, den eine Maschine schon
   * übernommen hat, läuft dort weiter — Revit hält nicht an, weil hier ein
   * Kennzeichen gesetzt wird.
   */
  auftragPausieren = (id: string) =>
    this.schicke<{ message: string }>(`/api/jobs/${id}/pausieren`, 'POST', {});

  auftragFortsetzen = (id: string) =>
    this.schicke<{ message: string }>(`/api/jobs/${id}/fortsetzen`, 'POST', {});

  /** Nur in der Warteschlange. Abgeschlossene bleiben als Nachweis stehen. */
  auftragLoeschen = (id: string) =>
    this.schicke<{ message: string }>(`/api/jobs/${id}`, 'DELETE', {});

  /**
   * `f.modelId` grenzt auf EIN Modell ein - siehe REGEL im Klassenkommentar
   * von `CompletionEndpoints.ExistingFindings`: ohne Modellwahl kommen
   * hoechstens die offenen Anschluesse aller aktiven Modelle, nie eine
   * willkuerliche Auswahl ueber ein einzelnes Modell hinaus.
   *
   * Die übrigen Felder sind der serverseitige Filter- und Seitensatz aus
   * Dokument 36, Abschnitt B — die Route teilt IMMER in Seiten auf (Vorgabe
   * Seite 1, Seitengröße 100); ohne diesen Aufruf komplett zu ersetzen wäre
   * nur die erste Seite sichtbar, ohne dass es jemand merkt.
   */
  completionFindings = (projectId: string, f?: CompletionFindingsFilterParams) => {
    const p = new URLSearchParams();

    if (f?.modelId) p.set('modelId', f.modelId);
    if (f?.geschoss) p.set('geschoss', f.geschoss);
    if (f?.abschnitt) p.set('abschnitt', f.abschnitt);
    if (f?.quadrant) p.set('quadrant', f.quadrant);
    if (f?.medium) p.set('medium', f.medium);
    if (f?.gewerk) p.set('gewerk', f.gewerk);
    if (f?.befundart) p.set('befundart', f.befundart);
    if (f?.planstatus) p.set('planstatus', f.planstatus);
    if (f?.nennweiteVonMm !== undefined) p.set('nennweiteVonMm', String(f.nennweiteVonMm));
    if (f?.nennweiteBisMm !== undefined) p.set('nennweiteBisMm', String(f.nennweiteBisMm));
    if (f?.suche) p.set('suche', f.suche);
    if (f?.seite !== undefined) p.set('seite', String(f.seite));
    if (f?.seitengroesse !== undefined) p.set('seitengroesse', String(f.seitengroesse));

    const frage = p.toString();

    return this.hole<CompletionFindingsAntwort>(
      `/api/projects/${projectId}/ai-completion/findings${frage ? `?${frage}` : ''}`);
  };

  /**
   * Der Baum der KI-Ansicht (Dokument 36, Abschnitt B) — Geschoss →
   * Abschnitt → Quadrant → Medium, mit Plan-Kennzahlen je Knoten.
   * `modelId` ist am Server ein Pflichtparameter (anders als bei
   * {@link completionFindings}): ohne gewähltes Modell lässt sich kein Baum
   * bilden, siehe `CompletionEndpoints.ReadBaumAsync`.
   */
  completionBaum = (projectId: string, modelId: string, befundart?: 'alle' | 'anschluss' | 'kollision') => {
    const p = new URLSearchParams({ modell: modelId });
    if (befundart) p.set('befundart', befundart);

    return this.hole<CompletionBaumAntwort>(
      `/api/projects/${projectId}/completion/baum?${p.toString()}`);
  };

  completionPlans = (projectId: string) =>
    this.hole<CompletionPlanItem[]>(`/api/projects/${projectId}/ai-completion/plans`);
  completionProposal = (projectId: string, findingId: string) =>
    this.sende<CompletionProposalAntwort>(
      `/api/projects/${projectId}/ai-completion/proposals`, { findingId });
  completionStatus = (projectId: string, planId: string, action: 'approve-sandbox' | 'reject' | 'retry' | 'manual') =>
    this.sende<CompletionPlanItem | { item: CompletionPlanItem; job?: { id: string; jobNumber: string } | null }>(
      `/api/projects/${projectId}/ai-completion/plans/${planId}/${action}`, {});

  /**
   * Dokument 36, Abschnitt C: KI-Bewertung für eine oder mehrere Kollisionen
   * aus der Koordination anfordern — derselbe Weg wie {@link completionProposal}
   * mit einer Kollisions-Id. Der Server nimmt höchstens
   * {@link KOLLISIONSBEWERTUNG_HOECHSTZAHL_JE_AUFRUF} Kennungen je Aufruf an
   * (siehe `CompletionEndpoints.KollisionsbewertungHoechstzahlJeAufruf`) —
   * ein "Stapel über eine Auswahl" ruft diese Methode deshalb WIEDERHOLT mit
   * je EINER Kennung auf, siehe `Koordination.tsx`.
   */
  koordinationKiBewertungAnfordern = (projectId: string, kollisionIds: string[]) =>
    this.sende<KollisionsBewertungAntwort>(
      `/api/projects/${projectId}/ai-completion/kollisionen/bewertung`, { kollisionIds });

  projekt = (id: string) => this.hole<ProjektDetail>(`/api/projects/${id}`);

  // --- PT-0a Projektrechte (Projekttrennung, F39) -----------------------------

  /**
   * Mitglieder, Zugang und bekannte Benutzer eines Projekts. Nur mit Pflegerecht
   * (`ProjektDetail.rechte.mitgliederVerwalten`); sonst 403.
   */
  projektMitglieder = (projektId: string) =>
    this.hole<Mitgliederliste>(`/api/projects/${projektId}/mitglieder`);

  /** Ein Mitglied anlegen oder ändern. Der Grund ist Pflicht und steht im Protokoll. */
  mitgliedSetzen = (projektId: string, benutzerId: string,
    werte: { canExport: boolean; canAdminister: boolean; grund: string }) =>
    this.schicke<{ benutzerId: string; canExport: boolean; canAdminister: boolean }>(
      `/api/projects/${projektId}/mitglieder/${benutzerId}`, 'PUT', werte);

  /** Ein Mitglied entfernen (204). Der Grund ist Pflicht. */
  mitgliedEntfernen = (projektId: string, benutzerId: string, grund: string) =>
    this.schicke<void>(
      `/api/projects/${projektId}/mitglieder/${benutzerId}?grund=${encodeURIComponent(grund)}`, 'DELETE');

  /** „Offen für alle Angemeldeten“ schalten - nur SystemAdmin. */
  projektZugangSetzen = (projektId: string, offenFuerAlle: boolean, grund: string) =>
    this.schicke<{ offenFuerAlle: boolean; aktiveMitglieder: number }>(
      `/api/projects/${projektId}/zugang`, 'PUT', { offenFuerAlle, grund });

  /** Fachbereichskatalog. Ändert sich nur bei einer Verteilung. */
  /** Der gemeinsame Katalog: was es überhaupt gibt. */
  fachbereiche = () => this.hole<Fachbereich[]>('/api/disciplines');

  /**
   * Die Fachbereiche DIESES Projekts.
   *
   * Der Katalog sagt, was es gibt — dieser Aufruf, was hier gilt. Getrennt,
   * weil Projekte in eigenen Containern laufen, aber den Quelltext teilen: Ein
   * neuer Import oder eine eigene Gewerkeseite kann damit im gemeinsamen Code
   * liegen und nur dort erreichbar sein, wo sie gebraucht wird.
   */
  projektFachbereiche = (projektId: string) =>
    this.hole<Fachbereich[]>(`/api/projects/${projektId}/disciplines`);

  /** Die vollständige Liste der freigeschalteten Fachbereiche setzen. */
  projektFachbereicheSetzen = (projektId: string, rollen: string[]) =>
    this.schicke<{ freigeschaltet: number }>(
      `/api/projects/${projektId}/disciplines`, 'PUT', { rollen });

  // --- Lüftung --------------------------------------------------------------

  /** Anlagen, Verbundnetze und Zählwerte. Ohne Bauteile - die kommen je Träger. */
  lueftung = (projektId: string) =>
    this.hole<LueftungBestand>(`/api/projects/${projektId}/ventilation`);

  ausschreibungLueftung = (projektId: string) =>
    this.hole<AusschreibungsUebersicht>(
      `/api/projects/${projektId}/ausschreibung/lueftung`);

  ausschreibungLueftungGaeb = (projektId: string, projektnummer: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/ausschreibung/lueftung/gaeb`,
      `${projektnummer}_Lueftung_LV.X83`);

  ausschreibungLueftungPdf = (projektId: string, projektnummer: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/ausschreibung/lueftung/pdf`,
      `${projektnummer}_Lueftung_LV.pdf`);

  // --- Das LV-Modul (Ausschreibung L1/L2) ----------------------------------
  //
  // Diese Endpunkte lesen den ABGELEITETEN Stand aus lv_*. Die Pfade darueber
  // (ausschreibungLueftung*) liefern seit Schritt L2 DASSELBE Dokument - der
  // alte, bei jedem Abruf neu rechnende Dienst ist entfallen.

  lvUebersicht = (projektId: string) =>
    this.hole<LvKopf[]>(`/api/projects/${projektId}/ausschreibung/lv`);

  lvDokument = (projektId: string, gewerk: string) =>
    this.hole<LvDokument>(
      `/api/projects/${projektId}/ausschreibung/lv/${gewerk}`);

  lvGrundlagen = (projektId: string, gewerk: string) =>
    this.hole<LvGrundlagen>(
      `/api/projects/${projektId}/ausschreibung/lv/grundlagen/${gewerk}`);

  lvPositionselemente = (projektId: string, gewerk: string, positionId: string) =>
    this.hole<LvPositionselement[]>(
      `/api/projects/${projektId}/ausschreibung/lv/${gewerk}` +
      `/positionen/${positionId}/elemente`);

  lvGaebPruefung = (projektId: string, gewerk: string) =>
    this.hole<LvGaebPruefung>(
      `/api/projects/${projektId}/ausschreibung/lv/${gewerk}/gaeb-pruefung`);

  lvGaeb = (
    projektId: string, gewerk: string, art: 'x83' | 'x81',
    projektnummer: string, fassung: number,
  ) =>
    this.dateiLaden(
      `/api/projects/${projektId}/ausschreibung/lv/${gewerk}/gaeb.${art}`,
      `${projektnummer}_${gewerk}_LV_F${fassung}.${art.toUpperCase()}`);

  lvPdf = (projektId: string, gewerk: string, projektnummer: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/ausschreibung/lv/${gewerk}/bericht.pdf`,
      `${projektnummer}_${gewerk}_LV.pdf`);

  // --- Das Gesamt-LV (Schritt L10) -----------------------------------------

  lvGesamt = (projektId: string) =>
    this.hole<LvGesamt>(`/api/projects/${projektId}/ausschreibung/lv/gesamt`);

  lvGesamtGaeb = (
    projektId: string, art: 'x83' | 'x81', projektnummer: string, fassung: number,
  ) =>
    this.dateiLaden(
      `/api/projects/${projektId}/ausschreibung/lv/gesamt/gaeb.${art}`,
      `${projektnummer}_GES_LV_F${fassung}.${art.toUpperCase()}`);

  lvGesamtPdf = (projektId: string, projektnummer: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/ausschreibung/lv/gesamt/bericht.pdf`,
      `${projektnummer}_GES_LV.pdf`);

  /**
   * Der Probelauf: rechnet alles, schreibt NICHTS und liefert je Titel und
   * je Einheit die Zahlen vorher und nachher.
   *
   * Er ist der einzige Weg, die Wirkung einer geänderten Mengenregel zu
   * sehen, bevor sie im Dokument steht.
   */
  lvProbelauf = (projektId: string, gewerk: string) =>
    this.sende<LvLauf>(
      `/api/projects/${projektId}/ausschreibung/lv/${gewerk}/probelauf`, {});

  /** Derselbe Lauf, diesmal schreibend. */
  lvAbleiten = (projektId: string, gewerk: string) =>
    this.sende<LvLauf>(
      `/api/projects/${projektId}/ausschreibung/lv/${gewerk}/ableiten`, {});

  lvTextbausteinPflegen = (
    projektId: string, gewerk: string, schluessel: string,
    eingabe: LvTextbausteinpflege,
  ) =>
    this.schicke<{ id: string; istBestaetigt: boolean; bestaetigtAm: string | null }>(
      `/api/projects/${projektId}/ausschreibung/lv/grundlagen/${gewerk}` +
      `/textbaustein/${encodeURIComponent(schluessel)}`,
      'PUT', eingabe);

  lvVorbemerkungPflegen = (
    projektId: string, gewerk: string, abschnitt: string,
    eingabe: LvVorbemerkungspflege,
  ) =>
    this.schicke<{ id: string; istBestaetigt: boolean; bestaetigtAm: string | null }>(
      `/api/projects/${projektId}/ausschreibung/lv/grundlagen/${gewerk}` +
      `/vorbemerkung/${encodeURIComponent(abschnitt)}`,
      'PUT', eingabe);

  /**
   * Die Bauteile eines Trägers.
   *
   * Genau einer von plantId, systemId oder ebene ist nötig: Der Gesamtbestand
   * wird nicht ausgeliefert. Bei 6622 Bauteilen läge die Antwort weit über dem
   * nginx-Proxypuffer, und der schneidet stillschweigend ab.
   */
  lueftungBauteile = (
    projektId: string,
    auswahl: Bauteilumfang,
  ) => {
    const suche = umfangSuche(auswahl);

    return this.hole<LueftungBauteil[]>(
      `/api/projects/${projektId}/ventilation/components?${suche}`);
  };

  /** Ein Bauteil mit allen Werten und dem Feldkatalog seiner Bauart. */
  lueftungBauteil = (id: string) =>
    this.hole<LueftungBauteilDetail>(`/api/ventilation/components/${id}`);

  /** Eine Anlage mit Elementen, Systemen und dem Feldkatalog. */
  lueftungAnlage = (id: string) =>
    this.hole<LueftungAnlagenDetail>(`/api/ventilation/plants/${id}`);

  lueftungAnlageSpeichern = (id: string, werte: Record<string, string>) =>
    this.schicke<void>(`/api/ventilation/plants/${id}/values`, 'PUT', werte);

  // --- Datenblätter ---------------------------------------------------------

  // --- Datenblattimporte ----------------------------------------------------

  /**
   * Importdatei prüfen und ablegen.
   *
   * Legt das Dokument gleich ab — dieselbe Datei zweimal ist kein Fehler,
   * sondern ein Aufruf ohne Wirkung. Die Prüfsumme entscheidet, nicht der Name.
   */
  async importPruefen(datei: File) {
    const inhalt = new FormData();
    inhalt.append('datei', datei);

    const antwort = await fetch('/api/datasheet-imports/pruefen', {
      method: 'POST',
      headers: { Authorization: `Bearer ${await this.token()}` },
      body: inhalt,
    });

    // Auch bei 400 steht im Körper das Prüfergebnis mit den Fehlern — es gehört
    // angezeigt, nicht in eine Ausnahme verwandelt.
    const k = await antwort.json().catch(() => null);

    if (!antwort.ok && !k?.fehler) {
      throw new Error(k?.message ?? `HTTP ${antwort.status}`);
    }

    return k as Importpruefung;
  }

  importe = () => this.hole<Datenblattimport[]>('/api/datasheet-imports');

  importDetail = (id: string) =>
    this.hole<Importdetail>(`/api/datasheet-imports/${id}`);

  importPunkte = (id: string, tabelle: string, sizeKey?: string) => {
    const s = new URLSearchParams({ tabelle });
    if (sizeKey) s.set('sizeKey', sizeKey);

    return this.hole<{ tabelle: string; punkte: Importpunkt[] }>(
      `/api/datasheet-imports/${id}/punkte?${s}`);
  };

  // --- Projektgrunddaten ----------------------------------------------------

  /** Die Grunddaten eines Projekts - alle Felder, auch die ohne Wert. */
  grunddaten = (projektId: string) =>
    this.hole<Grunddatenfeld[]>(`/api/projects/${projektId}/grunddaten`);

  grunddatenSpeichern = (projektId: string, werte: Record<string, string | null>) =>
    this.schicke<{ geaendert: number; message: string }>(
      `/api/projects/${projektId}/grunddaten`, 'PUT', { werte });

  /**
   * Felder fixieren oder freigeben.
   *
   * Fixiert heißt: Die Plattform gewinnt, ein Auslesen überschreibt nicht. Bei neun
   * Modellen ist das der einzige Schutz — sonst entscheidet, welches Modell zuletzt
   * gelesen wurde.
   *
   * Ohne Feldangabe gilt es für alle.
   */
  grunddatenFixieren = (
    projektId: string, fixieren: boolean, felder?: string[],
  ) => this.schicke<{ anzahl: number; message: string }>(
    `/api/projects/${projektId}/grunddaten/fixieren`, 'POST', { fixieren, felder });

  /** Für ein fixiertes Feld, bei dem das Modell doch das Richtige trägt. */
  grunddatenModellUebernehmen = (projektId: string, schluessel: string) =>
    this.schicke<{ wert: string | null; message: string }>(
      `/api/projects/${projektId}/grunddaten/${schluessel}/modell-uebernehmen`,
      'POST', {});

  // --- Heizung und Kaelte ---------------------------------------------------

  // === Elektro ==============================================================

  elektroAnlagengruppen = () =>
    this.hole<Elektroanlagengruppe[]>('/api/elektro/anlagengruppen');

  // === Normwerte ===========================================================

  /**
   * Alle Normwerte mit ihrem Prüfstand.
   *
   * Der Stand entscheidet, ob ein Verteilerplan den Vorabzugsvermerk trägt: Solange
   * ein verwendeter Wert ungeprüft ist, meldet das Qualitätsgate einen BLOCKER.
   */
  normwerte = () =>
    this.hole<{
      stand: { gesamt: number; geprueft: number; alsUnsicher: number };
      belastbarkeit: {
        id: string; verlegeart: string; belasteteAdern: number;
        werkstoff: string; isolierung: string; querschnitt: number;
        iz: number; normbezug: string | null; herkunft: string;
        geprueft: boolean; geprueftAm: string | null; geprueftVon: string | null;
        bemerkung: string | null;
      }[];
      faktoren: {
        id: string; art: string; kennwert: number; bedingung: string | null;
        faktor: number; normbezug: string | null; herkunft: string;
        geprueft: boolean; geprueftAm: string | null; geprueftVon: string | null;
      }[];
      grenzwerte: {
        id: string; schluessel: string; bedingung: string | null; wert: number;
        einheit: string | null; bezeichnung: string; normbezug: string | null;
        herkunft: string; geprueft: boolean; geprueftAm: string | null;
        geprueftVon: string | null; bemerkung: string | null;
      }[];
      anschlusswerte: {
        id: string; schluessel: string; bezeichnung: string;
        bauart: string | null;
        spannung: number; phasen: number; nennstrom: number;
        polzahl: number | null;
        anschlussleistung: number; bedarfsansatz: number | null;
        leistungsfaktor: number | null;
        gleichzeitigEinzel: number | null; gleichzeitigGruppe: number | null;
        brauchtRcd: boolean;
        normbezug: string | null; herkunft: string; geprueft: boolean;
        geprueftAm: string | null; geprueftVon: string | null;
        bemerkung: string | null;
      }[];
    }>('/api/normwerte');

  /**
   * Einen Normwert berichtigen und freigeben.
   *
   * Der Weg ist `belastbarkeit/{id}`, `faktoren/{id}` oder `grenzwerte/{id}` — die
   * drei Tabellen haben verschiedene Spalten, aber dieselbe Freigabelogik.
   */
  /**
   * Einen Anschlusswert pflegen.
   *
   * Die Anschlussleistung wird vom Server NACHGERECHNET — S = U × I bzw. √3 × U × I.
   * Sie folgt aus dem Nennstrom, und wer sie von Hand ändert, widerspricht der Physik.
   *
   * Der Bedarfsansatz und die Gleichzeitigkeitsfaktoren sind Erfahrungswerte und
   * werden übernommen, wie sie kommen.
   */
  anschlusswertSetzen = (id: string, werte: Record<string,
    string | number | boolean | null | undefined>) =>
    this.schicke<{ anschlussleistung: number; message: string }>(
      `/api/normwerte/anschlusswerte/${id}`, 'PUT', werte);

  normwertSetzen = (weg: string, angabe: {
    wert: number | null;
    normbezug: string | null;
    geprueft: boolean | null;
    bemerkung: string | null;
  }) =>
    this.schicke<{ message: string }>(`/api/normwerte/${weg}`, 'PUT', angabe);

  /** Alle Freigaben zurücknehmen. */
  normwerteFreigabeZuruecknehmen = () =>
    this.schicke<{ zurueckgenommen: number; message: string }>(
      '/api/normwerte/freigabe-zuruecknehmen', 'POST', {});

  elektroBauteilarten = () =>
    this.hole<Elektrobauteilart[]>('/api/elektro/bauteilarten');

  elektroFelder = (bauteilart?: string) =>
    this.hole<Elektrofeld[]>(
      `/api/elektro/felder${bauteilart ? `?bauteilart=${encodeURIComponent(bauteilart)}` : ''}`);

  elektroUebersicht = (projektId: string) =>
    this.hole<Elektrouebersicht>(`/api/projects/${projektId}/elektro/uebersicht`);

  /**
   * Die Baumansicht in einer von vier Sichten.
   *
   * Die Gestalt der Antwort hängt von der Sicht ab — deshalb `unknown` und
   * Auflösung in der Seite. Ein gemeinsamer Typ für vier verschiedene Bäume wäre
   * eine Vereinheitlichung, die keinem der vier gerecht wird.
   */
  elektroBaum = (
    projektId: string,
    nach: 'anlage' | 'raum' | 'verteiler' | 'typ',
    kostengruppe?: string,
  ) => {
    const p = new URLSearchParams({ nach });
    if (kostengruppe) p.set('kostengruppe', kostengruppe);

    return this.hole<unknown>(
      `/api/projects/${projektId}/elektro/baum?${p.toString()}`);
  };

  elektroBauteile = (projektId: string, filter: {
    bauteilart?: string; kostengruppe?: string; raum?: string; geschoss?: string;
    verteilerId?: string; stromkreisId?: string; typennummer?: string;
    suche?: string; seite?: number; proSeite?: number;
  }) => {
    const p = new URLSearchParams();

    Object.entries(filter).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
    });

    return this.hole<Elektroseite>(
      `/api/projects/${projektId}/elektro/bauteile?${p.toString()}`);
  };

  elektroBauteil = (id: string) =>
    this.hole<Elektrobauteil>(`/api/elektro/bauteile/${id}`);

  elektroWerteSpeichern = (id: string, werte: Record<string, string | null>) =>
    this.schicke<{
      gesetzt: number; geleert: number; abgewiesen: string[]; message: string;
    }>(`/api/elektro/bauteile/${id}/werte`, 'PUT', werte);

  // --- Die Verteiler --------------------------------------------------------

  /**
   * Die Verteiler des Projekts.
   *
   * OHNE die IT-Verteiler: Ein LAN-Verteiler mit Netzwerkdosen ist keine
   * Unterverteilung und steht im eigenen Abschnitt (Auftrag Fachplaner
   * 18.09.2026). Wer beides braucht, ruft mit `auswahl = 'alle'`.
   */
  elektroVerteiler = (projektId: string, auswahl?: 'strom' | 'it' | 'alle') =>
    this.hole<Elektroverteilerliste>(
      `/api/projects/${projektId}/elektro/verteiler`
      + (auswahl ? `?art=${auswahl}` : ''));

  /** Die IT-Schränke mit Bestückung, Höheneinheiten und Abgängen nach Art. */
  elektroItSchraenke = (projektId: string) =>
    this.hole<{ schraenke: ItSchrank[] }>(
      `/api/projects/${projektId}/elektro/it-schraenke`);

  /** Der Schrankaufriss als Objekt-URL (ein <img src="/api/..."> hätte kein
   *  Token und bekäme 401 — dasselbe Muster wie bei den MSR-Schemen). */
  elektroItSchrankblattSvg = (verteilerId: string) =>
    this.msrSchemaBlob(
      `/api/elektro/verteiler/${verteilerId}/it-schrankblatt`);

  elektroItSchrankblattPdf = (verteilerId: string, kennzeichen: string) =>
    this.dateiLaden(
      `/api/elektro/verteiler/${verteilerId}/it-schrankblatt-pdf`,
      `ELT-IT-${kennzeichen}.pdf`);

  /**
   * Das Anschlussschema ELT-IT eines Schrankes als mehrseitiges Planblatt-PDF
   * (Komponenten- und Fehlstellenblätter).
   */
  elektroItAnschlussschemaPdf = (verteilerId: string, kennzeichen: string) =>
    this.dateiLaden(
      `/api/elektro/verteiler/${verteilerId}/it-anschlussschema-pdf`,
      `ELT-IT-${kennzeichen}-Anschlussschema.pdf`);

  // ── Video / CCTV (Migration 0825) ─────────────────────────────────────────

  /** Die Kameras mit Schrank, Kabel, PoE und Status. Gefiltert wird auf der
   *  Seite; die API nimmt dieselben Filter als Abfrageparameter. */
  elektroCctv = (projektId: string) =>
    this.hole<CctvListe>(`/api/projects/${projektId}/elektro/cctv`);

  elektroCctvDetail = (projektId: string, id: string) =>
    this.hole<CctvDetail>(`/api/projects/${projektId}/elektro/cctv/${id}`);

  elektroCctvWerte = (projektId: string, id: string, werte: CctvWerte) =>
    this.schicke<{ message: string }>(
      `/api/projects/${projektId}/elektro/cctv/${id}/werte`, 'PUT', werte,
    ) as Promise<{ message: string }>;

  /** Ohne `schreiben` ein Probelauf. */
  elektroCctvBilden = (projektId: string, schreiben: boolean) =>
    this.sende<CctvLaufergebnis>(
      `/api/projects/${projektId}/elektro/cctv/bilden?schreiben=${schreiben}`, {});

  elektroCctvBefunde = (projektId: string) =>
    this.hole<CctvBefundliste>(`/api/projects/${projektId}/elektro/cctv/befunde`);

  elektroCctvKameraliste = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/cctv/kameraliste.xlsx`, 'CCTV_Kameraliste.xlsx');

  elektroCctvKabelliste = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/cctv/kabelliste.xlsx`, 'CCTV_Kabelliste.xlsx');

  // ── Video / CCTV als PDF (Etappe 3) ───────────────────────────────────────
  // Den Dateinamen bestimmt die API: Er trägt die Dokumentnummer aus dem
  // Berichtsregister vorn, die die Oberfläche nicht kennt.

  /** Der Gesamtbericht ELT-CCTV (Heft mit den Schemablättern als Anhang). */
  elektroCctvBerichtPdf = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/elektro/cctv/bericht.pdf`);

  elektroCctvKameralistePdf = (projektId: string, gruppierung: CctvGruppierung = 'geschoss') =>
    this.pdfLaden(
      `/api/projects/${projektId}/elektro/cctv/kameraliste.pdf?gruppierung=${gruppierung}`);

  elektroCctvKabellistePdf = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/elektro/cctv/kabelliste.pdf`);

  /** Alle Schemablätter: je Geschoss die Übersicht, je IT-Schrank das Schema. */
  elektroCctvSchemaPdf = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/elektro/cctv/schema.pdf`);

  /** Die Geschoss-Lagepläne (Etappe 4): je Geschoss ein maßstäbliches Planblatt. */
  elektroCctvLageplanPdf = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/elektro/cctv/lageplan.pdf`);

  elektroCctvSchrankSchemaPdf = (projektId: string, itSchrankId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/elektro/cctv/schema/${itSchrankId}.pdf`);

  /**
   * Ein Schemablatt eines IT-Schranks als Objekt-URL samt Blattzahl — dasselbe
   * Blob-Muster wie der Schrankaufriss (ein <img src="/api/..."> hätte kein
   * Token). Die Vorschau verbraucht keine Dokumentnummer.
   */
  elektroCctvSchrankSchemaSvg = async (
    projektId: string, itSchrankId: string, blatt = 1,
  ): Promise<CctvSchemaVorschau> => {
    const antwort = await this.abrufen(
      `/api/projects/${projektId}/elektro/cctv/schema/${itSchrankId}.svg?blatt=${blatt}`,
      { headers: { Authorization: `Bearer ${await this.token()}` } },
    );

    if (!antwort.ok) throw new Error(await fehlertext(antwort));

    const blaetter = Number(antwort.headers.get('X-Cctv-Blaetter') ?? '1');
    const blob = await antwort.blob();

    return {
      url: URL.createObjectURL(blob),
      blatt,
      blaetter: Number.isFinite(blaetter) && blaetter > 0 ? blaetter : 1,
    };
  };

  /**
   * ED-2/ED-3: Einlinienschema oder Schrankansicht eines Verteilers als
   * Objekt-URL samt Blattzahl — dieselbe Zeichnung wie im Verteilerplan-PDF,
   * als Vorschau OHNE Registernummer (Muster wie das CCTV-Schema).
   */
  elektroVerteilerZeichnungSvg = async (
    verteilerId: string,
    art: 'einlinienschema' | 'schrankansicht' | 'netzschema' | 'lageplan' | 'installationsplan', blatt = 1,
    projektId?: string, geschoss?: string | null,
  ): Promise<CctvSchemaVorschau> => {
    const antwort = await this.abrufen(
      art === 'netzschema'
        ? `/api/projects/${projektId}/elektro/netzschema.svg?blatt=${blatt}`
        : art === 'lageplan' || art === 'installationsplan'
          ? `/api/projects/${projektId}/elektro/${art === 'lageplan' ? 'verteilerlageplan' : 'installationsplan'}.svg?blatt=${blatt}`
            + (geschoss ? `&geschoss=${encodeURIComponent(geschoss)}` : '')
          : `/api/elektro/verteiler/${verteilerId}/${art}.svg?blatt=${blatt}`,
      { headers: { Authorization: `Bearer ${await this.token()}` } },
    );

    if (!antwort.ok) throw new Error(await fehlertext(antwort));

    const blaetter = Number(antwort.headers.get('X-Zeichnung-Blaetter') ?? '1');
    const blob = await antwort.blob();

    return {
      url: URL.createObjectURL(blob),
      blatt,
      blaetter: Number.isFinite(blaetter) && blaetter > 0 ? blaetter : 1,
    };
  };

  /** ED-4: der Plansatz Netzschema (ohne Registernummer). */
  elektroNetzschemaLaden = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/netzschema.pdf`, 'Netzschema.pdf');

  /** ED-8: der Installationsplan eines Geschosses (ohne Registernummer). */
  elektroInstallationsplanLaden = (projektId: string, geschoss?: string | null) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/installationsplan.pdf`
        + (geschoss ? `?geschoss=${encodeURIComponent(geschoss)}` : ''),
      `Installationsplan${geschoss ? `_${geschoss}` : ''}.pdf`);

  /** ED-8: der Verteiler-Lageplan aller Geschosse (ohne Registernummer). */
  elektroVerteilerLageplanLaden = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/verteilerlageplan.pdf`, 'Verteiler-Lageplan.pdf');

  /** Dasselbe Anschlussschema, für alle IT-Schränke des Projekts in einer Mappe. */
  elektroItAnschlussschemaGesamtPdf = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/it-anschlussschema-pdf`,
      'ELT-IT-Anschlussschema-gesamt.pdf');

  /**
   * Verteiler aus allen Verteilerbereichen des Raumbuchs anlegen.
   *
   * Bei über zwanzig Bereichen je Gewerk ist Einzelanlegen zu mühsam. Erst prüfen,
   * dann anlegen — die Prüfung ändert nichts.
   */
  /**
   * Die automatisch angelegten Verteiler entfernen.
   *
   * Von Hand angelegte bleiben. Danach neu anlegen und die Zuordnung erneuern.
   */
  elektroVerteilerZuruecksetzen = (projektId: string, nurPruefen: boolean) =>
    this.schicke<{
      entfernt: number; ohneBereich: number;
      betroffeneBauteile: number; betroffeneKreise: number; message: string;
    }>(`/api/projects/${projektId}/elektro/verteiler-zuruecksetzen`
       + `?nurPruefen=${nurPruefen}`, 'POST', {});

  elektroVerteilerAusRaumbuch = (
    projektId: string, nurPruefen: boolean, mitObergruppen = true,
  ) =>
    this.schicke<{
      angelegt: number; uebersprungen: number; bereiche: number;
      namen: string[]; message: string;
    }>(`/api/projects/${projektId}/elektro/verteiler-aus-raumbuch`, 'POST',
       { nurPruefen, mitObergruppen });

  elektroVerteilerAnlegen = (projektId: string, angabe: Elektroverteilerangabe) =>
    this.schicke<{ id: string; message: string }>(
      `/api/projects/${projektId}/elektro/verteiler`, 'POST', angabe);

  elektroVerteilerAendern = (id: string, angabe: Elektroverteilerangabe) =>
    this.schicke<{ message: string }>(
      `/api/elektro/verteiler/${id}`, 'PUT', angabe);

  elektroVerteilerEntfernen = (id: string) =>
    this.schicke<{ message: string }>(`/api/elektro/verteiler/${id}`, 'DELETE');

  // --- Die Abgänge ----------------------------------------------------------

  elektroStromkreise = (verteilerId: string) =>
    this.hole<Elektrostromkreis[]>(
      `/api/elektro/verteiler/${verteilerId}/stromkreise`);

  /**
   * Stückliste, Klemmenplan und Wärmebilanz eines Verteilers (V-E2).
   *
   * GET und nicht POST: Die Ableitung rechnet und schreibt nichts. Ein
   * Seitenaufruf darf den Datenstand nicht ändern.
   */
  elektroStueckliste = (verteilerId: string) =>
    this.hole<Elektroverteilerdoku>(
      `/api/elektro/verteiler/${verteilerId}/stueckliste`);

  /**
   * Bestückung, Kreise und Kennzahlen eines Verteilers (Paket VT3).
   *
   * GET und nicht POST: Liest den letzten geschriebenen Lauf der
   * Verteilerbestückung (Paket VT1), rechnet und schreibt nichts.
   */
  elektroVerteilerplanbestueckung = (verteilerId: string) =>
    this.hole<Elektroverteilerplandaten>(
      `/api/elektro/verteiler/${verteilerId}/bestueckung`);

  /**
   * Die Trafobelüftung nach AGI J11, je Aufstellraum (Paket EG2).
   *
   * GET: Der Lauf rechnet und schreibt nichts. Ein gespeichertes Ergebnis
   * würde veralten, sobald jemand einen der sieben Ansätze ändert — dann
   * stünde eine Fläche in der Datenbank, die zu ihren eigenen Eingaben
   * nicht mehr passt.
   */
  elektroTrafobelueftung = (projektId: string) =>
    this.hole<Trafobelueftungslauf>(
      `/api/projects/${projektId}/elektro/trafobelueftung`);

  /**
   * Das Trafo-Datenblatt ELT-TRA als PDF — ein Blatt je Transformator des
   * Projekts (Stammdaten, Verluste, Kurzschluss, Belüftung, Bilanz,
   * Herstellerdaten, je Wert mit Herkunft).
   *
   * Über pdfLaden, nicht dateiLaden: Der Bericht nummeriert und benennt
   * die Datei selbst, dasselbe Muster wie beim Bilanzbericht ELT-BIL.
   */
  elektroTrafodatenblattLaden = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/elektro/trafodatenblatt`);

  /**
   * Den Verteilerplan ELT-VTP als PDF herunterladen (Aufbauzeichnung,
   * Bestückungs- und Kreistabellen; die Stromlaufplanblätter aus Paket VT2
   * folgen beim Zusammenführen).
   *
   * Über `dateiLaden`, nicht über einen Verweis - derselbe Grund wie beim
   * alten Verteilerplan: der Weg braucht die Anmeldung.
   */
  elektroVerteilerplanberichtLaden = (verteilerId: string, kennzeichen: string) =>
    this.dateiLaden(
      `/api/elektro/verteiler/${verteilerId}/verteilerplanbericht`,
      `Verteilerplanbericht_${kennzeichen}.pdf`);

  elektroStromkreisAnlegen = (
    verteilerId: string, angabe: Elektrostromkreisangabe,
  ) =>
    this.schicke<{ id: string; message: string }>(
      `/api/elektro/verteiler/${verteilerId}/stromkreise`, 'POST', angabe);

  elektroStromkreisAendern = (id: string, angabe: Elektrostromkreisangabe) =>
    this.schicke<{ message: string }>(
      `/api/elektro/stromkreise/${id}`, 'PUT', angabe);

  elektroStromkreisEntfernen = (id: string) =>
    this.schicke<{ message: string }>(`/api/elektro/stromkreise/${id}`, 'DELETE');

  /**
   * Die Bauteile am Verteiler ohne Abgang, nach Raum gruppiert.
   *
   * Je Raum, nicht je Bauteil: Ein Kreis nimmt ganze Räume, und wer zwanzig
   * Leuchten einzeln anhaken muss, macht es nicht.
   */
  elektroNichtZugeordnet = (verteilerId: string) =>
    this.hole<{
      verteiler: {
        id: string; kennzeichen: string; bezeichnung: string | null;
        quadrant: string | null; geschoss: string | null;
        verteilernummer: string | null;
      };
      gesamt: number;
      raeume: number;
      jeRaum: {
        raumnummer: string | null;
        raumname: string | null;
        anzahl: number;
        arten: {
          bauteilart: string; bezeichnung: string;
          kostengruppe: string | null; anzahl: number; ids: string[];
        }[];
      }[];
    }>(`/api/elektro/verteiler/${verteilerId}/nicht-zugeordnet`);

  /**
   * Abgänge nach Regeln bilden.
   *
   * Mit `nurPruefen` wird gerechnet, ohne anzulegen — die Antwort sagt dann, wie
   * viele Abgänge und Teilungseinheiten entstünden und ob ein zusätzliches
   * Verteilerfeld nötig wäre.
   */
  elektroAbgaengeBilden = (verteilerId: string, nurPruefen: boolean) =>
    this.schicke<{
      angelegt: number;
      bauteile: number;
      ohneRegel: number;
      teVorhanden: number;
      teNeu: number;
      teBedarf: number;
      teJeFeld: number;
      felderBedarf: number;
      felderVorhanden: number;
      zusaetzlicheFelder: number;
      kreise: {
        verwendung: string; schutzorgan: string | null;
        raeume: string; bauteile: number; te: number;
      }[];
      hinweise: string[];
      message: string;
    }>(`/api/elektro/verteiler/${verteilerId}/abgaenge-bilden`
       + `?nurPruefen=${nurPruefen}`, 'POST', {});

  /**
   * Die Verteiler-Datensätze mit den Schränken im Modell verknüpfen.
   *
   * Über die Bezeichnung des Bauteils: „UV_2.0.14", „NSHV_2.1.0 (Nordost)". Damit
   * bekommt der Datensatz Raum und Koordinaten — die Voraussetzung für die
   * Trassenlängen.
   */
  elektroVerteilerVerknuepfen = (projektId: string, nurPruefen: boolean) =>
    this.schicke<{
      verknuepft: number; ohneBauteil: number; ohneKennung: number;
      hinweise: string[]; message: string;
    }>(`/api/projects/${projektId}/elektro/verteiler-verknuepfen`
       + `?nurPruefen=${nurPruefen}`, 'POST', {});

  /**
   * Die Leistungsbilanz kaskadieren: UV → NSHV → Trafo → MS-Anlage, mit
   * einem eigenen Gleichzeitigkeitsfaktor je Ebene (Migration 0548).
   *
   * `nurPruefen = true` rechnet und meldet, schreibt aber nichts — dieselbe
   * Regel wie bei den anderen Ableitungsläufen.
   */
  elektroBilanzRechnen = (projektId: string, nurPruefen: boolean) =>
    this.schicke<{
      gerechnet: number;
      stand: { art: string; anlagen: number; bedarfKW: number; anschlussKVA: number }[];
      trafoAuslastung: {
        kennzeichen: string; bemessungsleistungKva: number;
        bedarfKW: number; auslastungProzent: number | null;
      }[];
      msrAbgaenge: { kennzeichen: string; abgaenge: number; leistungW: number }[];

      /**
       * Der Arbeitsvorrat, je Gruppe gezählt und mit Kennzeichen.
       *
       * `anzahl` ist die volle Zahl, `kennzeichen` die (gekürzte) Liste —
       * „33 UV ohne NSHV" versetzt niemanden in die Lage, es zu beheben.
       */
      luecken: {
        schluessel: string; titel: string; anzahl: number;
        kennzeichen: string[]; erklaerung: string;
      }[];

      /**
       * Die Leistungsbilanz der Zentralbatterieanlagen — eine EIGENE Stufe.
       *
       * Getrennt von der Normalstrombilanz, weil die SV-Verteiler
       * absichtlich an keiner NSHV hängen. `auslastungProzent === null`
       * heißt „nicht zu bilden" (kein Typenschild-VA) und nicht „null".
       */
      svBilanzen: {
        zentraleId: string; kennzeichen: string; bezeichnung: string | null;
        verteiler: number; leuchten: number;
        leuchtenMitGepflegtemWert: number; leuchtenAusAnnahme: number;
        bedarfW: number; bemessungsleistungVa: number | null;
        auslastungProzent: number | null; kette: string;
      }[];

      hinweise: string[];
      message: string;
    }>(`/api/projects/${projektId}/elektro/bilanz-rechnen`
       + `?nurPruefen=${nurPruefen}`, 'POST', {});

  /** Der Bilanzbericht ELT-BIL — liest nur, was bilanz-rechnen geschrieben hat. */
  elektroBilanzberichtLaden = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/elektro/bilanzbericht`);

  /** Der Übersichtsschaltplan Gebäude — ein Blatt, MS-Anlage bis Unterverteilung. */
  elektroUebersichtsschaltplanLaden = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/elektro/uebersichtsschaltplan`);

  /**
   * Das Mittelspannungsschema (Paket EG2) — MS-Anlagen, Kupplung,
   * Trafoabzweige und die NSHV darunter.
   *
   * Das Blatt UNTER dem Übersichtsschaltplan: dort die Topologie bis zur
   * Unterverteilung, hier die Feldebene der Mittelspannung. Für ms, trafo
   * und nshv ist es das Schema, das es gibt — einen Verteiler-Stromlaufplan
   * (ELT-VTP) kann es dort nicht geben, weil er Abgänge zeichnet und eine
   * Einspeiseebene keine Bestückung trägt.
   */
  elektroMittelspannungsschemaLaden = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/elektro/mittelspannungsschema`);

  /** Der Bestand der gebildeten DALI-Linien (Paket H2, Migration 0575). */
  elektroDaliLinien = (projektId: string) =>
    this.hole<{
      linien: {
        id: string; kennzeichen: string; nummer: number; verteiler: string;
        teilnehmer: number; maxTeilnehmer: number; reserveProzent: number;
        herkunft: string; hinweis: string | null;
      }[];
      leuchtenGesamt: number;
      leuchtenOhneVerteiler: number;
      leuchtenZugeordnet: number;
    }>(`/api/projects/${projektId}/elektro/dali-linien`);

  /**
   * DALI-Linien bilden und Kurz-/Gruppenadressen vergeben.
   *
   * Deterministisch: derselbe Bestand ergibt dieselben Adressen, unabhaengig
   * davon, wie oft der Lauf wiederholt wird. `nurPruefen = true` rechnet und
   * meldet, schreibt aber nichts.
   */
  elektroDaliLinienBilden = (projektId: string, nurPruefen: boolean) =>
    this.schicke<{
      linien: number; zugeordnet: number; ohneVerteiler: number;
      ohneGruppe: number; hinweise: string[];
      proben: { kennzeichen: string; nummer: number; teilnehmer: number; maxTeilnehmer: number }[];
      message: string;
    }>(`/api/projects/${projektId}/elektro/dali-linien-bilden`
       + `?nurPruefen=${nurPruefen}`, 'POST', {});

  /** Die Vorgabeszenen je Raum-Nutzungsart (Fachplanerfrage 278). */
  elektroDaliSzenenvorlagen = () =>
    this.hole<{
      nutzungsart: string;
      szenen: { szeneNr: number; bezeichnung: string; dimmwertProzent: number; farbtemperatur: number | null }[];
    }[]>('/api/elektro/dali-szenenvorlagen');

  /** Lichtszenen eines Raums, oder des ganzen Projekts ohne `raum`. */
  elektroLichtszenen = (projektId: string, raum?: string) =>
    this.hole<{
      id: string; raumnummer: string; szeneNr: number; bezeichnung: string;
      zielGruppe: number | null; zielBauteilId: string | null;
      dimmwertProzent: number; farbtemperatur: number | null; herkunft: string;
    }[]>(`/api/projects/${projektId}/elektro/lichtszenen`
       + (raum ? `?raum=${encodeURIComponent(raum)}` : ''));

  /** Die Vorgabeszenen der Raum-Nutzungsart fuer einen Raum uebernehmen. */
  elektroLichtszenenVorgabeUebernehmen = (
    projektId: string, raum: string, ueberschreiben: boolean) =>
    this.schicke<{
      nutzungsart: string; uebernommen: number; ohneGruppe: boolean; message: string;
    }>(`/api/projects/${projektId}/elektro/lichtszenen/vorgabe-uebernehmen`
       + `?raum=${encodeURIComponent(raum)}&ueberschreiben=${ueberschreiben}`,
       'POST', {});

  /** Dimmwert oder Farbtemperatur einer Lichtszene von Hand aendern. */
  elektroLichtszeneAendern = (
    projektId: string, id: string,
    eingabe: { bezeichnung?: string; dimmwertProzent?: number; farbtemperatur?: number }) =>
    this.schicke<{ id: string; message: string }>(
      `/api/projects/${projektId}/elektro/lichtszenen/${id}`, 'PUT', eingabe);

  /** Der Bericht ELT-DALI — Linienliste, Adressliste, Szenenblatt je Raum. */
  elektroDaliBerichtLaden = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/elektro/dali-bericht`);

  /**
   * Die Trassenlängen aus den Bauteilkoordinaten ermitteln.
   *
   * Manhattan-Distanz vom Verteilerort zum weitesten Bauteil des Abgangs, plus
   * 15 % für Umwege. Keine Luftlinie — eine Leitung läuft senkrecht, waagerecht
   * und quer, nicht diagonal.
   */
  elektroLaengenErmitteln = (verteilerId: string, nurPruefen: boolean) =>
    this.schicke<{
      abgaenge: number; ohneLage: number; hinweise: string[]; message: string;
    }>(`/api/elektro/verteiler/${verteilerId}/laengen-ermitteln`
       + `?nurPruefen=${nurPruefen}`, 'POST', {});

  // === Meldelinien =========================================================

  /** Zentralen, Linienregeln und noch nicht zugeordnete Bauteile. */
  elektroZentralen = (projektId: string) =>
    this.hole<{
      zentralen: {
        id: string; art: string; kennzeichen: string; bezeichnung: string | null;
        hersteller: string | null; typ: string | null;
        baureihe: string | null;
        maxLinien: number | null; maxJeLinie: number | null;
        kanalLeistung: number | null; kanaele: number | null;
        felder: number | null;
        svArt: string | null; bemessungsleistung: number | null;
        betriebsdauer: number | null; umschaltzeit: number | null;
        batterieart: string | null; maxEndstromkreise: number | null;
        kreiseJeModul: number | null; ueberwachung: string | null;
        bauteilId: string | null; raumnummer: string | null;
        geschoss: string | null;
        lageX: number | null; lageY: number | null; lageZ: number | null;
        versorgteQuadranten: string | null; versorgteGeschosse: string | null;
        verteilerId: string | null; stromkreisId: string | null;
        hinweis: string | null; herkunft: string;
        linien: number; teilnehmer: number;
      }[];
      regeln: {
        linienArt: string; bezeichnung: string;
        maxTeilnehmer: number | null; maxLaenge: number | null;
        maxLeistung: number | null; maxJeGruppe: number | null;
        maxFlaeche: number | null;
        artenTrennen: boolean; istRing: boolean; zweiStraenge: boolean;
        leitungsart: string | null; querschnitt: number | null;
        funktionserhalt: string | null;
        normbezug: string | null; begruendung: string | null;
      }[];
      ohneZentrale: { linienArt: string; bauteilart: string; anzahl: number }[];
    }>(`/api/projects/${projektId}/elektro/zentralen`);

  /**
   * Zentralen im Modell anhand der Bezeichnung erkennen, nach Raum gruppiert. SA-4e Teil 2: zweistufig - die Probe
   * nennt in `gefunden` die Zentralen, die neu angelegt würden, mit Token; geschrieben wird nur mit diesem Token.
   */
  elektroZentralenErkennen = (projektId: string, nurPruefen: boolean, token?: string) =>
    this.schicke<{
      gefunden: {
        art: string; kennzeichen: string; bezeichnung: string | null;
        raumnummer: string | null; raumname: string | null;
        geschoss: string | null; quadrant: string | null;
        felder: number; bauteilart: string;
      }[];
      angelegt: number; fehlend: string[]; message: string;
      /** SA-4e Teil 2: das Token der Probe - geschrieben wird nur damit. */
      token: string | null;
      zusammenfassung: Erkennungszusammenfassung | null;
    }>(`/api/projects/${projektId}/elektro/zentralen-erkennen`
       + `?nurPruefen=${nurPruefen}`
       + (token ? `&token=${encodeURIComponent(token)}` : ''), 'POST', {});

  /**
   * Bauteile den Zentralen zuordnen.
   *
   * `hand` ist der Standard: Er nutzt den gepflegten Versorgungsbereich und ordnet
   * nichts zu, was nicht eindeutig ist. `quadrant` und `naehe` sind Ableitungen.
   */
  elektroZentralenZuordnen = (projektId: string,
    verfahren: 'hand' | 'quadrant' | 'naehe', nurPruefen: boolean, token?: string) =>
    this.schicke<{
      verfahren: string; zugeordnet: number; offen: number;
      vorgeschlagen: string[]; hinweise: string[]; message: string;
      /** SA-4e: das Token der Probe - geschrieben wird nur damit. */
      token: string | null;
      zusammenfassung: Zuordnungszusammenfassung | null;
    }>(`/api/projects/${projektId}/elektro/zentralen-zuordnen`
       + `?verfahren=${verfahren}&nurPruefen=${nurPruefen}`
       + (token ? `&token=${encodeURIComponent(token)}` : ''), 'POST', {});

  /** Eine Zentrale von Hand anlegen. */
  elektroZentraleAnlegen = (projektId: string, werte: Record<string,
    string | number | null | undefined>) =>
    this.schicke<{ id: string; kennzeichen: string; message: string }>(
      `/api/projects/${projektId}/elektro/zentralen`, 'POST', werte);

  /** Eine Zentrale ändern. */
  elektroZentraleSetzen = (id: string, werte: Record<string,
    string | number | null | undefined>) =>
    this.schicke<{ message: string }>(
      `/api/elektro/zentralen/${id}`, 'PUT', werte);

  /** Eine Zentrale entfernen. */
  elektroZentraleEntfernen = (id: string) =>
    this.schicke<{ message: string }>(
      `/api/elektro/zentralen/${id}`, 'DELETE', {});

  /**
   * SV-Verteiler den Sicherheitsstromversorgungen zuordnen.
   *
   * `gleichmaessig` ist der Standard: Die Kreise werden durch die Zahl der Anlagen
   * geteilt, und das ist die Grenze — nicht die 88 des Schranks. Damit bleibt Reserve
   * in JEDER Anlage.
   *
   * `kapazitaet` füllt bis zur Schrankgrenze. Bei 178 Kreisen auf drei Anlagen ergibt
   * das 88 / 88 / 2 — technisch richtig, aber jede Nachrüstung in den ersten beiden
   * Bereichen braucht dann einen neuen Schrank.
   *
   * Beide durchlaufen die Verteiler nach Quadrant und Geschoss, damit jede Anlage
   * einen zusammenhängenden Bereich versorgt.
   */
  elektroSvZuordnen = (projektId: string,
    verfahren: 'hand' | 'naehe' | 'kapazitaet' | 'gleichmaessig',
    nurPruefen: boolean, token?: string) =>
    this.schicke<{
      verfahren: string; zielJeAnlage: number | null;
      zugeordnet: number; offen: number;
      stand: {
        kennzeichen: string; baureihe: string | null;
        endstromkreise: number; grenze: number | null;
        /** NULL, wenn die Kreise je Modul unbekannt sind (seit v0.47.1 keine angenommene 4). */
        module: number | null; verteiler: number;
      }[];
      hinweise: string[]; message: string;
      /** SA-4f: das Token der Probe - geschrieben wird nur damit. */
      token: string | null;
      zusammenfassung: SvZuordnungszusammenfassung | null;
    }>(`/api/projects/${projektId}/elektro/sv-zuordnen`
       + `?verfahren=${verfahren}&nurPruefen=${nurPruefen}`
       + (token ? `&token=${encodeURIComponent(token)}` : ''), 'POST', {});

  /** Die bekannten Baureihen als Vorlage. */
  elektroBaureihen = () =>
    this.hole<{
      id: string; art: string; hersteller: string; baureihe: string;
      maxRinge: number | null; steckplaetze: number | null;
      maxRingadressen: number | null; maxNetzwerk: number | null;
      ringeJeModul: number | null; maxJeModul: number | null;
      maxModule: number | null; kanalleistung: number | null;
      ausloeselinien: number | null; antriebsgruppen: number | null;
      antriebsstrom: number | null;
      notstrom: string | null; zertifizierung: string | null;
      netzwerk: string | null; quelle: string | null; hinweis: string | null;
    }[]>('/api/elektro/zentralenbaureihen');

  /**
   * Die Bestückung nach Ring- oder Kreiszahl bilden.
   *
   * Bei der BMZ ein Ringmodul je Ring, bei SAA ein SPKR-8-Modul je acht Kreise, bei
   * RWA ein Taster je Etage. Komponenten, deren Menge aus einer Rechnung folgt —
   * Verstärker, Netzteile, Akkus —, werden mit Menge 1 und einem Vermerk angelegt.
   */
  elektroBestueckungBilden = (zentraleId: string, nurPruefen: boolean,
    mitOptionalen: boolean, token?: string) =>
    this.schicke<{
      einheiten: number; teilnehmer: number;
      angelegt: string[]; uebergangen: string[];
      hinweise: string[]; message: string;
      /** SA-4f: das Token der Probe - geschrieben wird nur damit. */
      token: string | null;
      zusammenfassung: Bestueckungszusammenfassung | null;
    }>(`/api/elektro/zentralen/${zentraleId}/bestueckung-bilden`
       + `?nurPruefen=${nurPruefen}&mitOptionalen=${mitOptionalen}`
       + (token ? `&token=${encodeURIComponent(token)}` : ''), 'POST', {});

  /**
   * Die Baugruppen einer Zentrale mit Auslastungsbefund.
   *
   * `ausBestueckung` ist die Zahl der Ringe, die die steckenden Module tragen —
   * eine andere Zahl als `maxRingeBaureihe`, und beide begrenzen.
   */
  elektroBaugruppen = (zentraleId: string) =>
    this.hole<{
      zentrale: {
        id: string; kennzeichen: string; art: string;
        bezeichnung: string | null; hersteller: string | null; typ: string | null;
        baureihe: string | null;
        maxLinien: number | null; maxJeLinie: number | null;
        maxRingeBaureihe: number | null; maxRingadressen: number | null;
        netzwerk: string | null; netzwerkNummer: number | null;
        kanalLeistung: number | null; kanaele: number | null;
        felder: number | null;
      };
      baugruppen: {
        id: string; art: string; bezeichnung: string;
        hersteller: string | null; typ: string | null;
        artikelnummer: string | null; anzahl: number;
        ringeJeStueck: number | null;
        leistung: number | null; kapazitaet: number | null;
        hinweis: string | null;
      }[];
      ausBestueckung: number; linien: number; teilnehmer: number;
      befunde: string[];
    }>(`/api/elektro/zentralen/${zentraleId}/baugruppen`);

  /** Eine Baugruppe hinzufügen. */
  elektroBaugruppeAnlegen = (zentraleId: string, werte: Record<string,
    string | number | null | undefined>) =>
    this.schicke<{ id: string; message: string }>(
      `/api/elektro/zentralen/${zentraleId}/baugruppen`, 'POST', werte);

  /** Eine Baugruppe ändern. */
  elektroBaugruppeSetzen = (id: string, werte: Record<string,
    string | number | null | undefined>) =>
    this.schicke<{ message: string }>(
      `/api/elektro/baugruppen/${id}`, 'PUT', werte);

  /** Eine Baugruppe entfernen. */
  elektroBaugruppeEntfernen = (id: string) =>
    this.schicke<{ message: string }>(
      `/api/elektro/baugruppen/${id}`, 'DELETE', {});

  /**
   * Meldelinien und Meldergruppen bilden.
   *
   * `geschossweise` hält jede Linie in einem Quadranten und Geschoss — mehr Linien,
   * aber kurze Wege. `dicht` füllt bis zur Grenze und läuft über Geschossgrenzen.
   */
  elektroLinienBilden = (projektId: string,
    verfahren: 'geschossweise' | 'dicht', nurPruefen: boolean, token?: string) =>
    this.schicke<{
      linien: number; meldergruppen: number; zugeordnet: number; offen: number;
      hinweise: string[]; verfahren: string; message: string;
      proben: {
        kennzeichen: string; art: string; zentrale: string;
        teilnehmer: number; leistung: number | null; gruppen: number;
        bereich: string | null;
      }[];
      /** SA-4d: das Token der Probe über den Eingangsstand - geschrieben wird nur damit. */
      token: string | null;
      zusammenfassung: Linienzusammenfassung | null;
    }>(`/api/projects/${projektId}/elektro/linien-bilden`
       + `?verfahren=${verfahren}&nurPruefen=${nurPruefen}`
       + (token ? `&token=${encodeURIComponent(token)}` : ''), 'POST', {});

  /** Die Trassenlängen der Linien aus den Koordinaten - zweistufig wie das Bilden (SA-4d). */
  elektroLinienLaengen = (projektId: string, nurPruefen: boolean, token?: string) =>
    this.schicke<{
      linien: number; ohneLage: number; hinweise: string[]; message: string;
      token: string | null;
      zusammenfassung: Laengenzusammenfassung | null;
    }>(`/api/projects/${projektId}/elektro/linien-laengen`
       + `?nurPruefen=${nurPruefen}`
       + (token ? `&token=${encodeURIComponent(token)}` : ''), 'POST', {});

  /**
   * Die Meldelinien einer Zentrale prüfen — ohne PDF.
   *
   * Bestückung, Baureihe, Ringadressen, Längen und Meldergruppen. Eine ungeprüfte
   * Längengrenze meldet WARNUNG, nicht FEHLER: Von vier Grenzen war nur eine belegt.
   */
  elektroZentralePruefen = (zentraleId: string) =>
    this.hole<{
      freigabefaehig: boolean;
      linien: number; meldergruppen: number; teilnehmer: number;
      ausBestueckung: number;
      befunde: string[];
      zusammenfassung: {
        blocker: number; fehler: number; warnungen: number; hinweise: number;
      };
      message: string;
    }>(`/api/elektro/zentralen/${zentraleId}/pruefen`);

  /**
   * Den Loopplansatz einer Zentrale herunterladen.
   *
   * Übersicht, je Linie ein Blatt mit der Ringdarstellung, Meldergruppenliste,
   * Melderlisten und Befundliste. Solange Angaben fehlen, trägt jede Seite
   * „VORABZUG — NICHT ZUR AUSFÜHRUNG". Seit BM-5 die registrierte Fassung ELT-LOP:
   * Der Dateiname kommt vom Server und trägt die Dokumentnummer; der hier
   * übergebene ist nur der Rückfall.
   */
  elektroLoopplanLaden = (zentraleId: string, kennzeichen: string) =>
    this.dateiLaden(
      `/api/elektro/zentralen/${zentraleId}/loopplan`,
      `Loopplan_${kennzeichen}.pdf`);

  /**
   * Die Melderliste ELT-MLD: alle Melder projektweit, je Zentrale.
   *
   * Der Loopplan zeichnet eine Zentrale, ELT-MLD listet das ganze Projekt —
   * die durchsuchbare Gegenstelle zum gezeichneten Plansatz. Der Dateiname
   * kommt vom Server, weil er die Dokumentnummer aus dem Berichtsregister
   * trägt; der hier übergebene ist nur der Rückfall.
   */
  elektroMelderlisteLaden = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/melderliste`,
      'Melderliste.pdf');

  // ── BMA und RWA vertieft (W2-D1) ────────────────────────────────────────
  // Brandfallsteuermatrix, Notstrom BMZ/RWA, Ringnachweis, RWA-Linien. Die
  // JSON-Route liest nur; jeder PDF-Abruf legt eine NEUE Berichtsfassung an
  // (R25, bis F11). Den Dateinamen der PDF bestimmt die API.

  elektroSicherheitstechnik = (projektId: string) =>
    this.hole<SicherheitstechnikStand>(`/api/projects/${projektId}/elektro/sicherheitstechnik`);

  elektroSicherheitstechnikMatrixLaden = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/sicherheitstechnik/steuermatrix.pdf`,
      'Brandfallsteuermatrix.pdf');

  elektroSicherheitstechnikNachweisLaden = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/sicherheitstechnik/nachweis.pdf`,
      'Notstrom_BMA_RWA.pdf');

  elektroSicherheitstechnikListen = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/sicherheitstechnik/listen.xlsx`,
      'BMA_RWA.xlsx');

  // ── Brandmeldeanlage (BM-1) ──────────────────────────────────────────────
  // Stand der Seite (nur lesend), Heft ELT-BMA als Gesamtbericht oder mit
  // gewählten Kapiteln (F11: gleicher Stand und gleiche Auswahl = abgelegte
  // Fassung) und die BMA-Listen über den Excel-Kern. Den Dateinamen setzt
  // der Server.

  elektroBma = (projektId: string) =>
    this.hole<BmaStand>(`/api/projects/${projektId}/elektro/bma`);

  /** `kapitel` leer = Gesamtbericht, sonst z. B. "6,10" (siehe `auswahlParameter`). */
  elektroBmaBerichtLaden = (projektId: string, kapitel = '') =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/bma/bericht.pdf${kapitel ? `?kapitel=${encodeURIComponent(kapitel)}` : ''}`,
      'Brandmeldeanlage.pdf');

  elektroBmaListen = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/bma/listen.xlsx`,
      'BMA-Listen.xlsx');

  // ── Sprachalarmanlage (W2-D2, SA-1) ───────────────────────────────────────
  // Stand der Seite (nur lesend), Heft ELT-SAH als Gesamtbericht oder mit
  // gewählten Kapiteln, Nachweis ELT-SPA, Lautsprecherliste ELT-LSL und die
  // SAA-Listen über den Excel-Kern. Jeder PDF-Abruf liefert bei gleichem
  // Stand die abgelegte Fassung (F11); nur ein geänderter Stand legt eine neue
  // an. Den Dateinamen setzt der Server.

  elektroSaa = (projektId: string) =>
    this.hole<SaaStand>(`/api/projects/${projektId}/elektro/sicherheitstechnik/saa`);

  /** `kapitel` leer = Gesamtbericht, sonst z. B. "5,7" (siehe `auswahlParameter`). */
  elektroSaaBerichtLaden = (projektId: string, kapitel = '') =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/sicherheitstechnik/saa/bericht.pdf${kapitel ? `?kapitel=${encodeURIComponent(kapitel)}` : ''}`,
      'Sprachalarmanlage.pdf');

  elektroSaaNachweisLaden = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/sicherheitstechnik/saa/nachweis.pdf`,
      'SAA_Nachweis.pdf');

  elektroSaaListeLaden = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/sicherheitstechnik/saa/lautsprecherliste.pdf`,
      'Lautsprecherliste.pdf');

  elektroSaaListen = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/sicherheitstechnik/saa/listen.xlsx`,
      'SAA-Listen.xlsx');
  // W2-A3 PV-Anlage (16.10.2026): Stand lesen, PDF ELT-PV (neue Fassung je Abruf, R25 bis F11)
  // und Excel-Mappe.

  elektroPv = (projektId: string) =>
    this.hole<PvStand>(`/api/projects/${projektId}/elektro/pv`);

  elektroPvNachweisLaden = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/pv/nachweis.pdf`, 'PV-Anlage.pdf');

  elektroPvListen = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/pv/listen.xlsx`, 'PV-Anlage.xlsx');

  // ── Zutritt, Fluchttür, Türüberwachung und Sprechstellen (W2-D3) ─────────
  // Türabgleich Konzept/Türbuch gegen Modell, Zuordnung Bauteil → Tür, Versorgung. Die
  // JSON-Route liest nur; jeder PDF-Abruf legt eine NEUE Berichtsfassung an (R25, bis F11).

  elektroZutritt = (projektId: string) =>
    this.hole<ZutrittStand>(`/api/projects/${projektId}/elektro/sicherheitstechnik/zutritt`);

  elektroZutrittNachweisLaden = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/sicherheitstechnik/zutritt/nachweis.pdf`,
      'Tuerabgleich_Zutritt.pdf');

  elektroZutrittListen = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/sicherheitstechnik/zutritt/listen.xlsx`,
      'Tuerabgleich_Zutritt.xlsx');
  // W2-A2 Sicherheitsstrom ZSV/CPS (16.10.2026): Stand lesen, PDF ELT-ZSV (neue Fassung je
  // Abruf, R25 bis F11) und Excel-Mappe.

  elektroSicherheitsstrom = (projektId: string) =>
    this.hole<SicherheitsstromStand>(`/api/projects/${projektId}/elektro/sicherheitsstrom`);

  elektroSicherheitsstromNachweisLaden = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/sicherheitsstrom/nachweis.pdf`,
      'Sicherheitsstrom_ZSV.pdf');

  elektroSicherheitsstromListen = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/sicherheitsstrom/listen.xlsx`,
      'Sicherheitsstrom.xlsx');

  // W2-A1 USV-Bedarfsbemessung (16.10.2026): Stand lesen, PDF ELT-USV (neue Fassung je Abruf, R25
  // bis F11) und Excel-Mappe.

  elektroUsv = (projektId: string) =>
    this.hole<UsvStand>(`/api/projects/${projektId}/elektro/usv`);

  elektroUsvNachweisLaden = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/usv/nachweis.pdf`, 'USV.pdf');

  elektroUsvListen = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/usv/listen.xlsx`, 'USV.xlsx');

  // W2-B2 Verbraucherschnittstellen (16.10.2026): Stand lesen, PDF ELT-VBS (neue Fassung je
  // Abruf, R25 bis F11) und Excel-Mappe. Nur lesend - die Leistungsbilanz bleibt unberuehrt.

  elektroVerbraucher = (projektId: string) =>
    this.hole<VerbraucherStand>(`/api/projects/${projektId}/elektro/verbraucher`);

  elektroVerbraucherNachweisLaden = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/verbraucher/nachweis.pdf`,
      'Verbraucherschnittstellen_VBS.pdf');

  elektroVerbraucherListen = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/verbraucher/listen.xlsx`,
      'Verbraucherschnittstellen.xlsx');

  // W2-B1 Eventstromversorgung (16.10.2026): Stand lesen, PDF ELT-EVS (neue Fassung je Abruf, R25 bis
  // F11) und Excel-Mappe. Nur lesend - die Leistungsbilanz bleibt unberuehrt.

  elektroEventstrom = (projektId: string) =>
    this.hole<EventstromStand>(`/api/projects/${projektId}/elektro/eventstrom`);

  elektroEventstromNachweisLaden = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/eventstrom/nachweis.pdf`,
      'Eventstrom_EVS.pdf');

  elektroEventstromListen = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/eventstrom/listen.xlsx`,
      'Eventstrom.xlsx');

  /** Der Bestand der Meldelinien und Meldergruppen. */
  elektroLinien = (projektId: string) =>
    this.hole<{
      linien: {
        id: string; zentraleId: string; art: string; kennzeichen: string;
        bezeichnung: string | null; nummer: number | null;
        strang: string | null; leitungsart: string | null;
        querschnitt: number | null; laenge: number | null;
        funktionserhalt: string | null; teilnehmer: number;
        summeLeistung: number | null;
        quadrant: string | null; geschoss: string | null;
        hinweis: string | null; herkunft: string;
        zentrale: string | null; gruppen: number;
      }[];
      meldergruppen: {
        id: string; zentraleId: string; kennzeichen: string;
        nummer: number | null; bezeichnung: string | null;
        melderart: string; brandabschnitt: string | null;
        geschoss: string | null; quadrant: string | null;
        melder: number; linien: number | null; flaeche: number | null;
        raeume: string | null; hinweis: string | null;
      }[];
    }>(`/api/projects/${projektId}/elektro/linien`);

  /**
   * Die Nachweisdaten eines Verteilers pflegen.
   *
   * Nur mitgeschickte Felder werden gesetzt — `undefined` heißt „unverändert".
   * Danach gilt der Datensatz als `gepflegt`, und das Qualitätsgate meldet ihn nicht
   * mehr als Vorschlag.
   */
  elektroVerteilerNachweis = (verteilerId: string, werte: Record<string,
    string | number | null | undefined>) =>
    this.schicke<{ message: string }>(
      `/api/elektro/verteiler/${verteilerId}/nachweisdaten`, 'PUT', werte);

  /** Die Nachweisdaten eines Abgangs pflegen. */
  elektroKreisNachweis = (stromkreisId: string, werte: Record<string,
    string | number | null | undefined>) =>
    this.schicke<{ message: string }>(
      `/api/elektro/stromkreise/${stromkreisId}/nachweisdaten`, 'PUT', werte);

  /**
   * Den Verteilerplan als A3-PDF herunterladen.
   *
   * Über `dateiLaden`, nicht über einen Verweis: Der Weg braucht die Anmeldung, und
   * ein `<a href>` schickt den Bearer nicht mit.
   *
   * Solange das Qualitätsgate einen BLOCKER oder FEHLER meldet, trägt jede Seite
   * diagonal „VORABZUG — NICHT ZUR AUSFÜHRUNG".
   */
  elektroVerteilerplanLaden = (verteilerId: string, kennzeichen: string) =>
    this.dateiLaden(
      `/api/elektro/verteiler/${verteilerId}/verteilerplan`,
      `Verteilerplan_${kennzeichen}.pdf`);

  /**
   * Die Anschlussleistung je Abgang aus den Bauteilen summieren.
   *
   * Keine Annahme, sondern eine Rechnung: Die Leistung steht an den Bauteilen als
   * Typwert am Fabrikat oder als Bauteilwert am Exemplar. Ohne sie gibt es keinen
   * Betriebsstrom und damit keinen Nachweis.
   */
  elektroLeistungSummieren = (verteilerId: string, nurPruefen: boolean) =>
    this.schicke<{
      abgaenge: number; bauteile: number; ohneWert: number;
      hinweise: string[]; message: string;
    }>(`/api/elektro/verteiler/${verteilerId}/leistung-summieren`
       + `?nurPruefen=${nurPruefen}`, 'POST', {});

  /**
   * Vorschlagswerte für die Nachweisdaten.
   *
   * Übliche Ansätze für einen Büroneubau, abgeleitet aus dem, was am Abgang schon
   * steht. Sie werden nicht geschrieben — erst die Übernahme tut das, und sie
   * vermerkt die Herkunft.
   */
  elektroVorschlag = (verteilerId: string) =>
    this.hole<{
      anzahl: number;
      verteiler: {
        feld: string; bezeichnung: string; bisher: string | null;
        neu: string; begruendung: string;
      }[];
      kreise: {
        id: string; kennzeichen: string; verwendung: string | null;
        werte: {
          feld: string; bezeichnung: string; bisher: string | null;
          neu: string; begruendung: string;
        }[];
      }[];
      /** Werte, zu denen bewusst nichts vorgeschlagen wird, mit Begründung. */
      offen: string[];
      message: string;
    }>(`/api/elektro/verteiler/${verteilerId}/vorschlag`);

  /** Die Vorschlagswerte übernehmen — mit Vermerk der Herkunft. */
  elektroVorschlagUebernehmen = (verteilerId: string) =>
    this.schicke<{ verteiler: number; kreise: number; message: string }>(
      `/api/elektro/verteiler/${verteilerId}/vorschlag-uebernehmen`, 'POST', {});

  /**
   * Die Plandaten eines Verteilers prüfen.
   *
   * Die Prüfliste nach Abschnitt 8 der Planvorgabe: BLOCKER, FEHLER, WARNUNG,
   * HINWEIS, BESTANDEN. Solange ein BLOCKER oder FEHLER besteht, trägt jede
   * Planseite „VORABZUG — NICHT ZUR AUSFÜHRUNG".
   *
   * Fehlende Eingaben werden als BLOCKER gemeldet und nicht durch Annahmen
   * ersetzt — eine Rechnung mit erfundenen Eingaben sieht aus wie ein Nachweis,
   * ist aber keiner.
   */
  elektroPlandatenPruefen = (verteilerId: string) =>
    this.hole<{
      freigabefaehig: boolean;
      vermerk: string;
      pruefhash: string;
      zusammenfassung: {
        blocker: number; fehler: number; warnungen: number;
        hinweise: number; bestanden: number;
      };
      befunde: {
        stufe: string; regelId: string; gegenstand: string; meldung: string;
        normbezug: string | null; rechenweg: string | null;
      }[];
      kreise: {
        id: string; kennzeichen: string; bezeichnung: string | null;
        ib: number | null; in: number | null; iz: number | null;
        spannungsfall: number | null; spannungsfallGrenze: number | null;
        stufe: string; meldungen: string[];
      }[];
    }>(`/api/elektro/verteiler/${verteilerId}/plandaten-pruefen`);

  /**
   * Die Bauteile eines Abgangs GENAU setzen.
   *
   * Anders als `elektroKreisZuweisen`, das hinzufügt: Was nicht in der Liste steht,
   * wird vom Abgang gelöst. So lässt sich abwählen.
   */
  elektroKreisMengeSetzen = (stromkreisId: string, bauteilIds: string[]) =>
    this.schicke<{
      gesetzt: number; geloest: number; bauteile: number; message: string;
    }>(`/api/elektro/stromkreise/${stromkreisId}/bauteile`, 'PUT', { bauteilIds });

  /** Bauteile einem Abgang zuweisen — über Kennungen oder über eine Auswahl. */
  elektroKreisZuweisen = (stromkreisId: string, anfrage: {
    bauteilIds?: string[]; bauteilart?: string; raum?: string;
  }) =>
    this.schicke<{ betroffen: number; message: string }>(
      `/api/elektro/stromkreise/${stromkreisId}/bauteile`, 'POST', anfrage);

  /**
   * Die Verteilerzuordnung aus dem Raumbuch erneuern.
   *
   * Erst prüfen, dann übernehmen — die Prüfung ändert nichts.
   */
  elektroZuordnungErneuern = (projektId: string, nurPruefen: boolean) =>
    this.schicke<{
      geprueft: number; neu: number; unveraendert: number;
      ohneRaum: number; ohneBereich: number;
      aufSvVerteiler: number; message: string;
    }>(`/api/projects/${projektId}/elektro/zuordnung-erneuern?nurPruefen=${nurPruefen}`,
       'POST', {});

  // --- Die Datenpflege je Fabrikat ------------------------------------------

  elektroTypen = (projektId: string, filter?: {
    bauteilart?: string; nurOhneDatenblatt?: boolean;
  }) => {
    const p = new URLSearchParams();
    if (filter?.bauteilart) p.set('bauteilart', filter.bauteilart);
    if (filter?.nurOhneDatenblatt) p.set('nurOhneDatenblatt', 'true');

    return this.hole<Elektrotyp[]>(
      `/api/projects/${projektId}/elektro/typen?${p.toString()}`);
  };

  elektroTypwerteSpeichern = (
    projektId: string, familie: string, typ: string,
    werte: Record<string, string | null>,
  ) =>
    this.schicke<{
      gesetzt: number; geleert: number; betroffen: number;
      abgewiesen: string[]; message: string;
    }>(`/api/projects/${projektId}/elektro/typwerte`, 'PUT',
       { familie, typ, werte });

  /**
   * Ein PDF-Datenblatt je Fabrikat hochladen.
   *
   * Der Verweis, den die Antwort liefert, ist ohne Anmeldung erreichbar und geht
   * beim nächsten Zurückschreiben ins Modell.
   */
  elektroDatenblattHochladen = async (
    projektId: string, datei: File, familie: string | null, typ: string | null,
  ) => {
    const koerper = new FormData();
    koerper.append('datei', datei, datei.name);
    koerper.append('familie', familie ?? '');
    koerper.append('typ', typ ?? '');

    const antwort = await this.abrufen(
      `/api/projects/${projektId}/elektro/datenblatt-hochladen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await this.token()}` },
        body: koerper,
      });

    return antwort.json() as Promise<{
      datasheetId: string; verweis: string; betroffen: number; message: string;
    }>;
  };

  /**
   * Bauteildaten je Fabrikat aus einer JSON-Datei einlesen.
   *
   * Erst prüfen, dann einlesen — von Hand gepflegte Werte bleiben in beiden Fällen
   * unberührt.
   */
  elektroDatenImport = async (
    projektId: string, datei: File, nurPruefen: boolean,
    familie?: string | null, typ?: string | null,
  ) => {
    const koerper = new FormData();
    koerper.append('datei', datei, datei.name);
    koerper.append('nurPruefen', nurPruefen ? 'true' : 'false');

    // Sind Familie und Typ gesetzt, darf die Datei flach sein — das Fabrikat ist
    // auf dem Bildschirm schon gewählt, und es in die Datei zu schreiben wäre
    // eine Fehlerquelle ohne Gewinn.
    if (familie) {
      koerper.append('familie', familie);
      koerper.append('typ', typ ?? '');
    }

    const antwort = await this.abrufen(
      `/api/projects/${projektId}/elektro/daten-import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await this.token()}` },
        body: koerper,
      });

    return antwort.json() as Promise<{
      fabrikate: number; gesetzt: number; unveraendert: number;
      betroffeneBauteile: number;
      unbekannteFabrikate: string[]; unbekannteFelder: string[];
      abgewiesen: string[];
      /** Fabrikate der Datei, die wegen des Zeilenbezugs nicht eingelesen wurden. */
      uebergangen: string[];
      message: string;
    }>;
  };

  /** Eine Importvorlage mit den Fabrikaten des Projekts. */
  elektroDatenVorlage = (projektId: string, bauteilart?: string) =>
    this.hole<{
      hinweis: string;
      fabrikate: {
        bauteilart: string; familie: string | null; typ: string | null;
        imProjekt: number; werte: Record<string, string>;
      }[];
    }>(`/api/projects/${projektId}/elektro/daten-vorlage${
      bauteilart ? `?bauteilart=${encodeURIComponent(bauteilart)}` : ''}`);

  heizungBauteilarten = () =>
    this.hole<Heizungbauteilart[]>('/api/heizung/bauteilarten');

  heizungFelder = (bauteilart?: string) =>
    this.hole<Heizungfeld[]>(
      `/api/heizung/felder${bauteilart ? `?bauteilart=${bauteilart}` : ''}`);

  heizungUebersicht = (projektId: string) =>
    this.hole<Heizunguebersicht>(
      `/api/projects/${projektId}/heizung/uebersicht`);

  // --- Die Sollseite: die Geräteliste des Fachplaners (HK1) ----------------
  //
  // Sie steht NEBEN dem Bestand und nicht darin: `heizungBauteile` sagt, was
  // im Modell steht, `sollgeraete` sagt, was gebaut wird. Wer beides in eine
  // Liste zieht, sieht den Unterschied nicht mehr — und der Unterschied ist
  // der ganze Zweck.

  sollgeraete = (
    projektId: string,
    f?: { quelle?: string; system?: string; weg?: string },
  ) => {
    const p = new URLSearchParams();
    if (f?.quelle) p.set('quelle', f.quelle);
    if (f?.system) p.set('system', f.system);
    if (f?.weg) p.set('weg', f.weg);

    return this.hole<Sollgeraeteliste>(
      `/api/projects/${projektId}/sollgeraete?${p}`);
  };

  sollgeraeteSollseite = (projektId: string) =>
    this.hole<Sollseite>(`/api/projects/${projektId}/sollgeraete/sollseite`);

  sollgeraeteAbweichung = (projektId: string) =>
    this.hole<Sollabweichung>(
      `/api/projects/${projektId}/sollgeraete/abweichung`);

  // --- Sanitär -------------------------------------------------------------

  sanitaerUebersicht = (projektId: string) =>
    this.hole<Sanitaeruebersicht>(
      `/api/projects/${projektId}/sanitaer/uebersicht`);

  sanitaerOffeneEnden = (
    projektId: string, einstufung?: string, kreis?: string,
  ) => {
    const p = new URLSearchParams();
    if (einstufung) p.set('einstufung', einstufung);
    if (kreis) p.set('kreis', kreis);

    return this.hole<{ zeilen: SanitaerOffenesEnde[]; gesamt: number }>(
      `/api/projects/${projektId}/sanitaer/offene-enden?${p}`);
  };

  /**
   * Der Bestand als Baum: Geschoss → Raum → Bauteilart.
   *
   * Er trägt nur Struktur und Zahlen. Die Bauteile eines Raums kommen über
   * `sanitaerRaum` auf Abruf — ein Baum mit allen 24.000 Elementen sprengt
   * den nginx-Puffer um ein Vielfaches.
   */
  sanitaerBaum = (projektId: string, nurGeraete = false) =>
    this.hole<{
      baum: SanitaerBaumknoten[];
      geschosse: number; raeume: number; elemente: number;
    }>(`/api/projects/${projektId}/sanitaer/baum`
       + (nurGeraete ? '?nurGeraete=true' : ''));

  sanitaerRaum = (projektId: string, raum?: string, geschoss?: string) => {
    const p = new URLSearchParams();
    if (raum) p.set('raum', raum);
    if (geschoss) p.set('geschoss', geschoss);

    return this.hole<SanitaerRaum>(
      `/api/projects/${projektId}/sanitaer/raum?${p}`);
  };

  // --- Die Sanitärberechnungen ---------------------------------------------

  sanitaerBerechnungUebersicht = (projektId: string) =>
    this.hole<SanitaerBerechnungsuebersicht>(
      `/api/projects/${projektId}/sanitaer/berechnung/uebersicht`);

  /**
   * Alle Zeilen eines Bereichs.
   *
   * Ohne Gattung alle — die Seite gruppiert selbst. Mit Gattung nur eine, für
   * die eigene Reiterseite der Hebeanlagen: Sie ist keine Strangrechnung und
   * gehört nicht zwischen 350 Netzteile.
   */
  sanitaerBerechnungBereich = (
    projektId: string, bereich: string, gattung?: string,
  ) =>
    this.hole<SanitaerBerechnungsbereich>(
      `/api/projects/${projektId}/sanitaer/berechnung/${bereich}`
      + (gattung ? `?gattung=${encodeURIComponent(gattung)}` : ''));

  sanitaerRechnen = (projektId: string) =>
    this.schicke<SanitaerRechenlauf>(
      `/api/projects/${projektId}/sanitaer/berechnung`, 'POST', {}) as
      Promise<SanitaerRechenlauf>;

  sanitaerGegenstandsuebersicht = (projektId: string) =>
    this.hole<SanitaerGegenstandsuebersicht>(
      `/api/projects/${projektId}/sanitaer/gegenstandsuebersicht`);

  sanitaerAnlagen = (projektId: string) =>
    this.hole<SanitaerAnlagen>(
      `/api/projects/${projektId}/sanitaer/anlagen`);

  /** Die Übersichten als PDF: `gegenstaende` oder `anlagen`. */
  sanitaerUebersichtPdf = (projektId: string, was: string) =>
    this.pdfLaden(
      `/api/projects/${projektId}/sanitaer/uebersicht/${was}/bericht.pdf`);

  sanitaerBerichtPdf = (projektId: string, bereich: string) =>
    this.pdfLaden(
      `/api/projects/${projektId}/sanitaer/berechnung/${bereich}/bericht.pdf`);

  sanitaerSchemaPdf = (projektId: string, bereich: string) =>
    this.pdfLaden(
      `/api/projects/${projektId}/sanitaer/schema/${bereich}/bericht.pdf`);

  // --- TS11: die Excel-Listen über den Excel-Kern (Dateiname vom Server; der hier ist nur der Rückfall) --------

  /** Der jüngste Lauf eines Sanitärbereichs als Excel - alle Zeilen, alle Teilstrecken. */
  sanitaerBerechnungExcel = (projektId: string, bereich: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/sanitaer/berechnung/${encodeURIComponent(bereich)}/listen.xlsx`,
      `SAN_${bereich}.xlsx`);

  /** Die Sanitärgegenstände als Excel - je Gegenstand und Summen je Geschoss und Art. */
  sanitaerGegenstaendeExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/sanitaer/gegenstaende/listen.xlsx`, 'SAN_Gegenstaende.xlsx');

  /** Der jüngste Lauf eines Außenbereichs als Excel - alle Zeilen. */
  aussenBerechnungExcel = (projektId: string, bereich: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/aussenanlagen/berechnung/${encodeURIComponent(bereich)}/listen.xlsx`,
      `AUS_${bereich}.xlsx`);

  // --- Die Aufschlüsselung nach Teilstrecken (Schritt 3c) ------------------
  //
  // Sie lesen aus `sanitaer_teilstrecken` und nicht aus den JSON-Spalten der
  // Berechnungszeile. Das ist der ganze Zweck des Befunds S-22: Was in einem
  // Blob steht, ist nicht filterbar und nicht je Kreis aggregierbar — der
  // maßgebende Trinkwasserweg war ein 6,78-MB-Blob.

  sanitaerStraenge = (
    projektId: string, o: { bereich?: string; kreis?: string } = {},
  ) => {
    const p = new URLSearchParams();
    if (o.bereich) p.set('bereich', o.bereich);
    if (o.kreis) p.set('kreis', o.kreis);
    const q = p.toString();

    return this.hole<SanitaerStraenge>(
      `/api/projects/${projektId}/sanitaer/straenge${q ? `?${q}` : ''}`);
  };

  sanitaerStrangteilstrecken = (projektId: string, schluessel: string) =>
    this.hole<SanitaerStrangteilstrecken>(
      `/api/projects/${projektId}/sanitaer/straenge/`
      + `${encodeURIComponent(schluessel)}/teilstrecken`);

  sanitaerTeilstreckenposten = (projektId: string, teilstreckeId: string) =>
    this.hole<SanitaerTeilstreckenposten>(
      `/api/projects/${projektId}/sanitaer/teilstrecken/`
      + `${teilstreckeId}/posten`);

  sanitaerNetzteile = (
    projektId: string, o: { kreis?: string; bereich?: string } = {},
  ) => {
    const p = new URLSearchParams();
    if (o.kreis) p.set('kreis', o.kreis);
    if (o.bereich) p.set('bereich', o.bereich);
    const q = p.toString();

    return this.hole<SanitaerNetzteile>(
      `/api/projects/${projektId}/sanitaer/netzteile${q ? `?${q}` : ''}`);
  };

  sanitaerBerechnungsanlagen = (projektId: string) =>
    this.hole<SanitaerBerechnungsanlagen>(
      `/api/projects/${projektId}/sanitaer/berechnung/anlagen`);

  /**
   * Je Bereich der NEUESTE Lauf, mit seinen Kennzahlen.
   *
   * Die Kennwertkarten der Teilstreckenseite stehen bereichsübergreifend —
   * Spitzendurchfluss kalt und warm, die Warmwasserzapfstellen ohne Netz und
   * die Deckung der Notentwässerung liegen in DREI verschiedenen Läufen. Drei
   * Einzelabrufe wären drei Runden über die Leitung.
   */
  sanitaerBerechnungslaeufe = (projektId: string) =>
    this.hole<SanitaerBereichslauf[]>(
      `/api/projects/${projektId}/sanitaer/berechnung`);

  // --- Die Sanitärprodukte -------------------------------------------------

  // --- Die Aussparungen ----------------------------------------------------

  aussparungenUebersicht = (projektId: string) =>
    this.hole<AussparungsUebersicht>(
      `/api/projects/${projektId}/aussparungen/uebersicht`);

  aussparungen = (projektId: string, o: {
    seite?: number; proSeite?: number; geschoss?: string;
    zustand?: string; gewerk?: string; ohneAnforderung?: boolean;
    planungsbereich?: string;
    /** true zeigt die STILLGELEGTEN statt der aktiven (F87). */
    entfernt?: boolean;
  } = {}) => {
    const p = new URLSearchParams();
    if (o.seite) p.set('seite', String(o.seite));
    if (o.proSeite) p.set('proSeite', String(o.proSeite));
    if (o.geschoss) p.set('geschoss', o.geschoss);
    if (o.zustand) p.set('zustand', o.zustand);
    if (o.gewerk) p.set('gewerk', o.gewerk);
    if (o.ohneAnforderung) p.set('ohneAnforderung', 'true');
    if (o.planungsbereich) p.set('planungsbereich', o.planungsbereich);
    if (o.entfernt) p.set('entfernt', 'true');

    return this.hole<{
      gesamt: number; seite: number; proSeite: number; zeilen: Aussparung[];
    }>(`/api/projects/${projektId}/aussparungen?${p}`);
  };

  /**
   * Der Abgleich conVoid gegen das Architekturmodell.
   *
   * Der Freigabestand steht in conVoid, die Anmerkung im ARDU-Modell. Diese
   * Abfrage bringt beides nebeneinander.
   */
  /**
   * Der Abgleich — seitenweise, mit Summen über der ganzen Menge.
   *
   * Hier stand bis AS8 ein `Take(500)` über 6.601 Paaren, und die Seite
   * nannte keine Gesamtzahl: Der Fachplaner sah ein Zwölftel und konnte es
   * nicht wissen. Jetzt trägt jede Antwort `gesamt`, `seite` und `seiten`.
   */
  aussparungAbgleich = (projektId: string, f?: {
    befund?: string; stufe?: string; geschoss?: string; bereich?: string;
    herkunft?: string; entscheidung?: string; bestaetigt?: boolean;
    seite?: number; groesse?: number;
  }) => {
    const p = new URLSearchParams();
    if (f?.befund) p.set('befund', f.befund);
    if (f?.stufe) p.set('stufe', f.stufe);
    if (f?.geschoss) p.set('geschoss', f.geschoss);
    if (f?.bereich) p.set('bereich', f.bereich);
    if (f?.herkunft) p.set('herkunft', f.herkunft);
    if (f?.entscheidung) p.set('entscheidung', f.entscheidung);
    if (f?.bestaetigt != null) p.set('bestaetigt', String(f.bestaetigt));
    if (f?.seite) p.set('seite', String(f.seite));
    if (f?.groesse) p.set('groesse', String(f.groesse));

    const q = p.toString();

    return this.hole<AussparungAbgleichDaten>(
      `/api/projects/${projektId}/aussparungen/abgleich`
      + (q ? `?${q}` : ''));
  };

  /**
   * Die Adresse des Abgleichberichts — wahlweise je Geschoss.
   *
   * Ein Knopf je Geschoss: Wer G02 abarbeitet, braucht die 196 Zeilen
   * dieses Geschosses und nicht die 511 des Projekts.
   */
  aussparungAbgleichPdfPfad = (projektId: string, geschoss?: string) =>
    `/api/projects/${projektId}/aussparungen/abgleich.pdf`
    + (geschoss ? `?geschoss=${encodeURIComponent(geschoss)}` : '');

  /** Die Adresse der CSV-Datei mit ALLEN gefilterten Paaren — ohne Seite. */
  aussparungAbgleichCsvPfad = (projektId: string, f?: {
    befund?: string; stufe?: string; geschoss?: string; bereich?: string;
    herkunft?: string; entscheidung?: string;
  }) => {
    const p = new URLSearchParams();
    if (f?.befund) p.set('befund', f.befund);
    if (f?.stufe) p.set('stufe', f.stufe);
    if (f?.geschoss) p.set('geschoss', f.geschoss);
    if (f?.bereich) p.set('bereich', f.bereich);
    if (f?.herkunft) p.set('herkunft', f.herkunft);
    if (f?.entscheidung) p.set('entscheidung', f.entscheidung);

    const q = p.toString();

    return `/api/projects/${projektId}/aussparungen/abgleich.csv`
         + (q ? `?${q}` : '');
  };

  /**
   * Die Aussparungen als Baum: Geschoss, Bauart, Aussparung mit Medien.
   *
   * 5.668 Zeilen in einer flachen Liste sind nicht prüfbar. Abgearbeitet
   * wird geschossweise, und innerhalb eines Geschosses nach Bauart — die
   * bestimmt das Schott.
   */
  aussparungBaum = (projektId: string, geschoss?: string,
                    geaendert?: boolean) => {
    const p = new URLSearchParams();
    if (geschoss) p.set('geschoss', geschoss);
    if (geaendert) p.set('geaendert', 'true');

    const q = p.toString();

    return this.hole<AussparungBaum>(
      `/api/projects/${projektId}/aussparungen/baum` + (q ? `?${q}` : ''));
  };

  aussparungSchottZuordnen = (projektId: string) =>
    this.schicke<{
      durchgaenge: number; zugeordnet: number; ohneRegel: number;
      ohneBauart: number; ohneAnforderung: number;
      jeSystem: Record<string, number>; hinweise: string[];
    }>(`/api/projects/${projektId}/aussparungen/schott`, 'POST');

  brandschutzZulassungen = () =>
    this.hole<BrandschutzZulassung[]>('/api/aussparungen/zulassungen');

  /**
   * Der Baum nach Geschoss und Planungsbereich — die Arbeitsansicht.
   *
   * `geaendert` zeigt nur, was sich SEIT DER LETZTEN AUFNAHME geändert hat:
   * neue Aussparungen und solche mit einer Verlaufszeile der Plattform
   * (Stilllegung, Reaktivierung, Abgleichsentscheidung) ab dem
   * Aufnahmestand. Der Stand steht in der Antwort — ohne ihn wäre die Zahl
   * nicht nachrechenbar.
   */
  aussparungBaumBereich = (projektId: string, geaendert?: boolean) =>
    this.hole<any>(
      `/api/projects/${projektId}/aussparungen/baum-bereich`
      + (geaendert ? '?geaendert=true' : ''));

  /** Eine Aussparung mit allem: Verortung, Medien, Verlauf, Zulassungen. */
  aussparungVollstaendig = (id: string) =>
    this.hole<any>(`/api/aussparungen/${id}/vollstaendig`);

  aussparungBereiche = (projektId: string) =>
    this.hole<{
      planungsbereich: string | null; quadrant: string | null;
      stueck: number; ohneAnforderung: number;
    }[]>(`/api/projects/${projektId}/aussparungen/bereiche`);

  aussparungAnforderungen = (projektId: string, bereich?: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/aussparungen/anforderungen`
      + (bereich ? `?planungsbereich=${encodeURIComponent(bereich)}` : ''));

  /** Die Geschosse mit ihren offenen Anforderungen (AS4). */
  aussparungGeschosseOffen = (projektId: string) =>
    this.hole<{
      geschoss: string | null; stueck: number;
      ohneAnforderung: number; vorhaltungen: number;
    }[]>(`/api/projects/${projektId}/aussparungen/geschosse-offen`);

  /**
   * Die Liste für die Architektur — je Planungsbereich UND je Geschoss.
   *
   * Der Geschossfilter kam mit AS4 dazu: Die August-Vorgabe „PDF-Liste je
   * Geschoss" war nie gebaut, den Filter trug nur der JSON-Endpunkt.
   */
  aussparungAnforderungenPdf = (
    projektId: string, bereich?: string, geschoss?: string,
  ) => {
    const teile = [
      bereich ? `planungsbereich=${encodeURIComponent(bereich)}` : '',
      geschoss ? `geschoss=${encodeURIComponent(geschoss)}` : '',
    ].filter(Boolean);

    return this.pdfLaden(
      `/api/projects/${projektId}/aussparungen/anforderungen.pdf`
      + (teile.length > 0 ? `?${teile.join('&')}` : ''));
  };

  /**
   * Die Ausschnitte, für die es ein Aussparungsplanblatt gibt (AS7).
   *
   * Sieben Geschosse und 42 Paare aus Planungsbereich und Geschoss — ein
   * Bereich spannt alle sieben Ebenen, ein Blatt darüber legte sie
   * übereinander.
   */
  aussparungPlanblaetter = (projektId: string) =>
    this.hole<{
      geschosse: { geschoss: string; stueck: number }[];
      bereiche: { bereich: string; geschoss: string; stueck: number }[];
      /** Seit v0.45.2: was auf keinem Blatt steht, mit der Ebene im Modell. */
      ohneGeschoss?: OhneGeschoss;
    }>(`/api/projects/${projektId}/aussparungen/planblaetter`);

  /**
   * Der Aussparungsplan als Planblatt (AS7) — je Geschoss oder je
   * Planungsbereich eines Geschosses.
   *
   * Das Geschoss ist PFLICHT: Ein Blatt über alle sieben legte dieselbe Lage
   * siebenmal übereinander.
   */
  aussparungPlanPdf = (projektId: string, geschoss: string, bereich?: string) =>
    this.pdfLaden(
      `/api/projects/${projektId}/aussparungen/plan.pdf`
      + `?geschoss=${encodeURIComponent(geschoss)}`
      + (bereich ? `&bereich=${encodeURIComponent(bereich)}` : ''));

  // --- Sprinkler ---------------------------------------------------------

  sprinklerUebersicht = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/sprinkler/uebersicht`);

  sprinklerNetze = (projektId: string, art?: string) =>
    this.hole<any[]>(
      `/api/projects/${projektId}/sprinkler/netze`
      + (art ? `?art=${encodeURIComponent(art)}` : ''));

  sprinklerKoepfe = (projektId: string, o: {
    seite?: number; geschoss?: string; nurOhneNetz?: boolean;
  } = {}) => {
    const p = new URLSearchParams();
    if (o.seite) p.set('seite', String(o.seite));
    if (o.geschoss) p.set('geschoss', o.geschoss);
    if (o.nurOhneNetz) p.set('nurOhneNetz', 'true');

    return this.hole<any>(
      `/api/projects/${projektId}/sprinkler/koepfe?${p}`);
  };

  sprinklerAnlagentechnik = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/sprinkler/anlagentechnik`);

  /**
   * Der Baum Ebene → Raum → Köpfe mit der Prüfrechnung je Raum.
   *
   * Die Rechnung kommt vom Server und wird hier nicht wiederholt: Sie
   * entscheidet, ob ein Raum als beanstandet gilt, und zwei Rechnungen
   * weichen ab, sobald eine von ihnen angefasst wird.
   */
  sprinklerBaum = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/sprinkler/baum`);

  /** Die Rohrnetzberechnung ausfuehren und festschreiben. */
  sprinklerRechnen = (projektId: string) =>
    this.schicke<any>(
      `/api/projects/${projektId}/sprinkler/rechnen`, 'POST');

  /**
   * Das Ergebnis mit der Strangansicht.
   *
   * Der Bereich unterscheidet Sprinklernetz und nasse Steigleitung. Sie
   * werden nach verschiedenen Regelwerken bemessen und müssen einzeln
   * vorlegbar sein — deshalb zwei Berechnungen und nicht zwei Abschnitte
   * derselben.
   */
  sprinklerBerechnung = (projektId: string, bereich = 'rohrnetz') =>
    this.hole<any>(
      `/api/projects/${projektId}/sprinkler/berechnung`
        + `?bereich=${encodeURIComponent(bereich)}`);

  /** Berechnung und Anlagendaten als PDF. */
  sprinklerBerichtPdf = (projektId: string, bereich = 'rohrnetz') =>
    this.dateiLaden(
      `/api/projects/${projektId}/sprinkler/bericht.pdf`
        + `?bereich=${encodeURIComponent(bereich)}`,
      bereich === 'steigleitung'
        ? 'Steigleitung-Berechnung.pdf' : 'Sprinkler-Berechnung.pdf');

  // ── Die Aufschlüsselung (Schritt 4c) ──────────────────────────────────
  //
  // Anlage → Station → Wirkfläche → Weg → Teilstrecke → Posten. Jede Stufe
  // ein eigener Aufruf: Der Baum lädt den nächsten Ast erst, wenn er
  // aufgeklappt wird — 5.351 Köpfe und 303 Posten im Voraus zu laden wäre
  // eine halbe Minute Wartezeit für eine Ansicht, die meist auf der ersten
  // Ebene bleibt.

  /** Anlage, Stationen und die Bilanz des gerechneten Anteils. */
  sprinklerAnlage = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/sprinkler/anlage`);

  /** Die maßgebende Wirkfläche mit jedem Kopf auf ihr. */
  sprinklerWirkflaeche = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/sprinkler/wirkflaeche`);

  /** Der Weg zu einem Sprinkler, in Teilstrecken. */
  sprinklerWeg = (projektId: string, kopfElementId: number) =>
    this.hole<any>(
      `/api/projects/${projektId}/sprinkler/wege/${kopfElementId}`);

  /** Die Posten einer Teilstrecke dieses Wegs. */
  sprinklerWegposten = (
    projektId: string, kopfElementId: number, nr: number,
  ) =>
    this.hole<any>(
      `/api/projects/${projektId}/sprinkler/wege/${kopfElementId}`
        + `/teilstrecken/${nr}`);

  /** Die nasse Steigleitung in Teilstrecken und Posten. */
  sprinklerSteigstrang = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/sprinkler/steigstrang`);

  /** Die Sprinkler einer Alarmventilstation - mit und ohne Weg. */
  sprinklerStationskoepfe = (
    projektId: string, stationElementId: number,
    o: { ohneWeg?: boolean; seite?: number; proSeite?: number } = {},
  ) => {
    const p = new URLSearchParams();
    if (o.ohneWeg) p.set('ohneWeg', 'true');
    if (o.seite) p.set('seite', String(o.seite));
    if (o.proSeite) p.set('proSeite', String(o.proSeite));

    return this.hole<any>(
      `/api/projects/${projektId}/sprinkler/stationen/`
        + `${stationElementId}/koepfe?${p}`);
  };

  /** Stationen ohne Sprinkler, Bilanz, Pumpen, Modellstand. */
  sprinklerBefundliste = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/sprinkler/befundliste`);

  /** Die Geraete der Sprinkleranlage mit ihrem oeffentlichen Verweis. */
  sprinklerGeraete = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/sprinkler/geraete`);

  /**
   * Ein einzelnes Sprinklergerät — in der Form der Heizungs-Produktseite.
   *
   * Derselbe Typ und damit derselbe Baustein: Gruppen mit Feldern, jedes
   * Feld mit seiner Herkunftsfarbe.
   */
  sprinklerGeraet = (id: string) =>
    this.hole<Produktdaten>(`/api/sprinkler/geraet/${id}`);

  /** Ein einzelnes Außenanlagenbauteil, ebenso. */
  aussenGeraet = (id: string) =>
    this.hole<Produktdaten>(`/api/aussenanlagen/geraet/${id}`);

  sprinklerWirkflaechen = () =>
    this.hole<any[]>('/api/sprinkler/wirkflaechen');

  /**
   * Anforderung ableiten, Schott zuordnen, Nummer vergeben — in EINEM Zug.
   *
   * Die drei Schritte bauen aufeinander auf. Wer nur zuordnet, bekommt
   * dasselbe Ergebnis wie vorher: Die Anforderung fehlt ja noch. Genau das
   * ist einmal passiert — 5.664 durchbrochene Bauteile lagen vor, und die
   * Zuordnung fand 141 Treffer.
   */
  aussparungSchottkette = (projektId: string) =>
    this.schicke<any>(
      `/api/projects/${projektId}/aussparungen/schott-kette`, 'POST');

  /** Das Brandschottkataster als PDF, wahlweise je Abschnitt oder Geschoss. */
  katasterPdf = (projektId: string, o?: {
    planungsbereich?: string; geschoss?: string;
  }) => {
    const p = new URLSearchParams();
    if (o?.planungsbereich) p.set('planungsbereich', o.planungsbereich);
    if (o?.geschoss) p.set('geschoss', o.geschoss);

    const frage = p.toString();

    return this.dateiLaden(
      `/api/projects/${projektId}/aussparungen/kataster.pdf`
        + (frage ? `?${frage}` : ''),
      'Brandschottkataster.pdf');
  };

  /**
   * Der Schottnachweis als PDF -- das Blatt fuer den Pruefsachverstaendigen
   * (Paket AS3).
   *
   * Nicht dasselbe wie das Kataster: Das Kataster ist die Liste, nach der
   * eingebaut wird. Der Nachweis nennt das Verfahren mit seinen Normen, die
   * Freigabekriterien in Zahlen, die Befunde mit Fundstelle, die Annahmen mit
   * Herkunft und den Abgleich gegen LV, Architekturmodell und Nummernzaehler.
   *
   * Ohne `bereich` entsteht das Gesamtblatt ueber alle Planungsabschnitte.
   */
  schottnachweisPdf = (projektId: string, bereich?: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/aussparungen/schottnachweis.pdf`
        + (bereich ? `?bereich=${encodeURIComponent(bereich)}` : ''),
      'Schottnachweis.pdf');

  // ─── Kollision und Koordination ────────────────────────────────────

  /**
   * Bereiche, Regeln, Toleranzen und der zu prüfende Bestand -- seit Batch 7
   * zusätzlich Lösungsarten, Einstellungen und die Höhenbänder des
   * (optional wählbaren) Arbeitsstands.
   */
  koordinationGrundlagen = (projektId: string, standId?: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/koordination/grundlagen`
        + (standId ? `?standId=${standId}` : ''));

  /**
   * Die Kollisionssuche starten.
   *
   * Sie laeuft ueber den Rasterindex: 173.639 Bauteile ergaeben 15 Milliarden
   * Paare. Zwei Bauteile koennen sich nur durchdringen, wenn sie eine Zelle
   * teilen — das verwirft kein Paar, das sich ueberlappt.
   */
  koordinationSuchen = (projektId: string, standId?: string) =>
    this.sende<any>(
      `/api/projects/${projektId}/koordination/suchen`
      + (standId ? `?standId=${standId}` : ''), {});

  /**
   * Stufe 2 — Hoehenbaender bilden und Ausweichvorschlaege rechnen.
   *
   * Die Baender ZUERST: 'Die Heizungsleitung 120 mm tiefer legen' ist keine
   * Loesung, wenn 80 mm darunter die Abhangdecke sitzt.
   */
  koordinationVorschlagen = (projektId: string, standId?: string) =>
    this.sende<any>(
      `/api/projects/${projektId}/koordination/vorschlagen`
      + (standId ? `?standId=${standId}` : ''), {});

  /**
   * Der Belegungsplan — wo im Deckenhohlraum noch Platz ist.
   *
   * Gerechnet aus der Sicht EINES Gewerks: Wer eine Leitung ergaenzt, plant
   * neben seine eigenen -- dort ist er selbst im Bilde. Was er nicht sieht,
   * sind die anderen.
   */
  koordinationBelegung = (
    projektId: string, geschoss: string, gewerk?: string,
  ) => {
    const p = new URLSearchParams({ geschoss });
    if (gewerk) p.set('gewerk', gewerk);

    return this.hole<any>(
      `/api/projects/${projektId}/koordination/belegung?${p}`);
  };

  // ═════════════════════════════════════════════════════════════════════
  // SAMMELFUNKTIONEN -- IMMER MIT LISTE ZUR DURCHSICHT
  // ═════════════════════════════════════════════════════════════════════

  /**
   * Was eine Sammelaktion treffen wuerde -- als Liste, VOR jeder Ausfuehrung.
   *
   * Ein Sammelknopf ohne Vorschau ist ein Sammelrisiko: Vierzehn Bauteile auf
   * einmal zu verschieben ohne Vorschau ist ein Vertrauensverlust, den man
   * einmal hat.
   */
  sammelVorschau = (projektId: string, w: {
    bereich?: string; geschoss?: string; gewerk?: string;
    gegenGewerk?: string; richtung?: string; betragBisMm?: number;
    befund?: string; nurUnstrittig?: boolean; ohneMitzieher?: boolean;
  }) => this.sende<any>(
    `/api/projects/${projektId}/koordination/sammel/vorschau`, w);

  /**
   * Sammelweise ablehnen -- die sichere Sorte.
   *
   * Braucht keine Geometrie: Es ist eine Entscheidung. Der Grund ist Pflicht
   * und muss aus der Liste kommen, sonst waeren 200 Kollisionen weg und
   * niemand wuesste, warum.
   */
  sammelAblehnen = (projektId: string, w: {
    bereich?: string; geschoss?: string; gewerk?: string;
    gegenGewerk?: string; richtung?: string; betragBisMm?: number;
    befund?: string; nurUnstrittig?: boolean; ohneMitzieher?: boolean;
    abgewaehlt?: string[]; grundschluessel: string; angabe?: string;
  }) => this.sende<any>(
    `/api/projects/${projektId}/koordination/sammel/ablehnen`, w);

  /**
   * Die Ablehngruende zur Auswahl -- mit ihrer Nutzungshaeufigkeit.
   *
   * Vom SERVER und nicht als Aufzaehlung im Frontend: Eine zweite Liste dort
   * waere die, die nach dem ersten neuen Grund veraltet, und niemand merkt es,
   * weil beide plausibel aussehen.
   */
  koordinationAblehngruende = (projektId: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/koordination/ablehngruende`);

  /** Das Protokoll -- bei Automatik die einzige Spur. */
  koordinationProtokoll = (
    projektId: string, f: { ergebnis?: string; take?: number } = {},
  ) => {
    const p = new URLSearchParams();
    if (f.ergebnis) p.set('ergebnis', f.ergebnis);
    if (f.take) p.set('take', String(f.take));

    return this.hole<any>(
      `/api/projects/${projektId}/koordination/protokoll?${p}`);
  };

  /** Die Ansicht fuer die Projektleitung -- mit den Blockaden zuerst. */
  koordinationProjektleitung = (projektId: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/koordination/projektleitung`);

  // ═════════════════════════════════════════════════════════════════════
  // DER NAECHTLICHE LAUF
  // ═════════════════════════════════════════════════════════════════════

  /**
   * Der Tagesbericht je Planer.
   *
   * 'nurMitInhalt' ist der Vorgabefall: Wer nichts Neues hat, bekommt nichts.
   * Eine taegliche Meldung ohne Inhalt trainiert das Wegklicken.
   */
  koordinationTagesbericht = (
    projektId: string,
    f: { gewerk?: string; bereich?: string; nurMitInhalt?: boolean } = {},
  ) => {
    const p = new URLSearchParams();
    if (f.gewerk) p.set('gewerk', f.gewerk);
    if (f.bereich) p.set('bereich', f.bereich);
    if (f.nurMitInhalt) p.set('nurMitInhalt', 'true');

    return this.hole<any>(
      `/api/projects/${projektId}/koordination/tagesbericht?${p}`);
  };

  /**
   * Wer fuer welchen Bereich und welches Gewerk zustaendig ist.
   *
   * Ohne diese Zuordnung hat der Tagesbericht keinen Empfaenger und die
   * Zuweisung einer Kollision keinen Adressaten. Die Antwort traegt deshalb
   * 'ohneAdresse' mit -- die Zahl, an der man sieht, ob die Automatisierung
   * ueberhaupt jemanden erreicht.
   */
  koordinationZustaendigkeiten = (projektId: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/koordination/zustaendigkeiten`);

  /** Einen Planer hinterlegen -- leer loeschen ist erlaubt. */
  koordinationZustaendigkeitSetzen = (
    projektId: string,
    id: string,
    w: { planerName?: string | null; planerMail?: string | null },
  ) =>
    this.schicke<any>(
      `/api/projects/${projektId}/koordination/zustaendigkeiten/${id}`,
      'PUT', w);

  /** Annahmequoten je Vorschlagsart -- Automatik und Regelpflege. */
  koordinationVorschlagsguete = (projektId: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/koordination/vorschlagsguete`);

  /** Eine Vorschlagsart zur automatischen Uebernahme freischalten. */
  koordinationFreischalten = (projektId: string, w: {
    gewerk?: string; gegenGewerk?: string; richtung?: string;
    frei: boolean; von?: string;
  }) => this.sende<any>(
    `/api/projects/${projektId}/koordination/vorschlagsguete/freischalten`, w);

  /** Welches Fremdmodell sich geaendert hat -- und welches nicht. */
  koordinationModellstaende = (projektId: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/koordination/modellstaende`);

  // ═════════════════════════════════════════════════════════════════════
  // DIE RESERVIERUNGEN -- KOORDINATION VOR DEM ZEICHNEN
  // ═════════════════════════════════════════════════════════════════════

  /** Die reservierten Korridore. */
  reservierungen = (
    projektId: string,
    f: { gewerk?: string; geschoss?: string; zustand?: string;
         mitZellen?: boolean } = {},
  ) => {
    const p = new URLSearchParams();
    if (f.gewerk) p.set('gewerk', f.gewerk);
    if (f.geschoss) p.set('geschoss', f.geschoss);
    if (f.zustand) p.set('zustand', f.zustand);
    if (f.mitZellen) p.set('mitZellen', 'true');

    return this.hole<any>(
      `/api/projects/${projektId}/koordination/reservierungen?${p}`);
  };

  /**
   * Einen Korridor reservieren.
   *
   * Gibt bei 409 den KOERPER zurueck und wirft nicht: Die Ueberschneidungen
   * stehen darin, und die Oberflaeche braucht sie, um 'trotzdem?' fragen zu
   * koennen. Ein Fehler mit dem Text 'HTTP 409' koennte das nicht.
   */
  reservierungAnlegen = async (projektId: string, wunsch: {
    gewerk: string; planerName: string; zweck: string; geschoss?: string;
    hoeheVonMm: number; hoeheBisMm: number; zellen: number[][];
    fristTage?: number; trotzdem?: boolean; bemerkung?: string;
  }): Promise<{ ok: boolean; besetzt: boolean; daten: any }> => {
    const antwort = await this.abrufen(
      `/api/projects/${projektId}/koordination/reservierungen`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.token()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(wunsch),
      });

    const daten = await antwort.json().catch(() => null);

    if (!antwort.ok && antwort.status !== 409)
      throw new Error(daten?.fehler ?? daten?.message
                      ?? `HTTP ${antwort.status}`);

    return {
      ok: antwort.ok,
      besetzt: antwort.status === 409,
      daten,
    };
  };

  /** Vorpruefen, ohne anzulegen — damit die Karte sofort meldet. */
  reservierungPruefen = (projektId: string, wunsch: {
    gewerk: string; hoeheVonMm: number; hoeheBisMm: number;
    zellen: number[][];
  }) => this.sende<any>(
    `/api/projects/${projektId}/koordination/reservierungen/pruefen`,
    { ...wunsch, planerName: '', zweck: '' });

  reservierungVerlaengern = (projektId: string, id: string, tage?: number) =>
    this.sende<any>(
      `/api/projects/${projektId}/koordination/reservierungen/`
      + `${id}/verlaengern`, { tage: tage ?? null });

  reservierungAufgeben = (
    projektId: string, id: string, bemerkung?: string,
  ) => this.sende<any>(
    `/api/projects/${projektId}/koordination/reservierungen/${id}/aufgeben`,
    { bemerkung: bemerkung ?? null });

  /** Den Abgleich gegen den Bestand anstossen. */
  reservierungAbgleich = (projektId: string) =>
    this.sende<any>(
      `/api/projects/${projektId}/koordination/reservierungen/abgleich`, {});

  reservierungMeldungen = (projektId: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/koordination/reservierungen/meldungen`);

  reservierungMeldungGesehen = (projektId: string, id: string) =>
    this.sende<any>(
      `/api/projects/${projektId}/koordination/reservierungen/meldungen/`
      + `${id}/gesehen`, {});

  /**
   * Die Abstandsmaengel — zu eng, ohne Durchdringung.
   *
   * Eine eigene Frage und eine eigene Liste: Solange beides zusammenstand,
   * beantwortete ein Treffer zwei Fragen auf einmal, und die zweite verdeckte
   * die erste.
   */
  koordinationAbstaende = (projektId: string, geschoss?: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/koordination/abstaende`
      + (geschoss ? `?geschoss=${encodeURIComponent(geschoss)}` : ''));

  /** Die Vorschlaege und die Hoehenbaender lesen. */
  /**
   * Die Ausweichvorschlaege — filterbar und geblaettert.
   *
   * Geschoss und Bereich kommen aus der KOLLISION, nicht aus dem Vorschlag:
   * Der Planer arbeitet einen Abschnitt in einem Geschoss ab, und eine Liste
   * ueber das ganze Bauwerk ist keine Arbeitsliste.
   */
  koordinationVorschlaege = (
    projektId: string, f?: {
      befund?: string; gewerk?: string; geschoss?: string;
      bereich?: string; richtung?: string;
      seite?: number; proSeite?: number;
    },
  ) => {
    const p = new URLSearchParams();

    if (f?.befund) p.set('befund', f.befund);
    if (f?.gewerk) p.set('gewerk', f.gewerk);
    if (f?.geschoss) p.set('geschoss', f.geschoss);
    if (f?.bereich) p.set('bereich', f.bereich);
    if (f?.richtung) p.set('richtung', f.richtung);
    if (f?.seite) p.set('seite', String(f.seite));
    if (f?.proSeite) p.set('proSeite', String(f.proSeite));

    return this.hole<any>(
      `/api/projects/${projektId}/koordination/vorschlaege?${p}`);
  };

  /** Die Lösungsquote: automatisch/Planer, je Lösungsart, je Gewerkpaar. */
  koordinationLoesungen = (projektId: string, standId?: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/koordination/loesungen`
      + (standId ? `?standId=${standId}` : ''));

  /** Anweisungen und Planeraufgaben aus den Ausweichvorschlägen ableiten. */
  koordinationLoesungenAbleiten = (
    projektId: string, standId?: string, ki?: boolean,
  ) => {
    const p = new URLSearchParams();
    if (standId) p.set('standId', standId);
    if (ki) p.set('ki', 'true');

    return this.sende<any>(
      `/api/projects/${projektId}/koordination/loesungen-ableiten?${p}`, {});
  };

  /** Die Planeraufgaben eines Standes -- ein Cluster ohne Rechenlösung. */
  koordinationPlaneraufgaben = (
    projektId: string, f?: { standId?: string; status?: string },
  ) => {
    const p = new URLSearchParams();
    if (f?.standId) p.set('standId', f.standId);
    if (f?.status) p.set('status', f.status);

    return this.hole<any>(
      `/api/projects/${projektId}/koordination/planeraufgaben?${p}`);
  };

  /** Eine Planeraufgabe im Detail, mit ihren Kollisionen. */
  koordinationPlaneraufgabe = (projektId: string, id: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/koordination/planeraufgaben/${id}`);

  /**
   * Eine KI-Erläuterung für genau diese Planeraufgabe erzeugen -- Kurzfassung,
   * Empfehlung, Risiken, Rückfragen. Kein Aufruf ohne Anthropic-Schlüssel,
   * derselbe Prompt-Hash wie beim letzten Mal löst keinen neuen Aufruf aus.
   */
  koordinationPlaneraufgabeErlaeutern = (projektId: string, id: string) =>
    this.sende<any>(
      `/api/projects/${projektId}/koordination/planeraufgaben/${id}`
      + '/erlaeutern', {});

  /** Eine Planeraufgabe entscheiden -- setzt die zugehörigen Anweisungen. */
  koordinationPlaneraufgabeEntscheiden = (
    projektId: string, id: string,
    w: { status: string; entscheidung: string },
  ) => this.sende<any>(
    `/api/projects/${projektId}/koordination/planeraufgaben/${id}/entscheiden`,
    w);

  /** Eine Anweisung entscheiden -- angenommen/umgesetzt/abgelehnt. */
  koordinationAnweisungStatus = (
    id: string, w: { status: string; ablehngrund?: string; angabe?: string },
  ) => this.sende<any>(
    `/api/koordination/anweisungen/${id}/status`, w);

  /** Netzersatzanlagen der Grundlagenmappe (EG4): Stand, Auslastung, Prüfungen. */
  elektroNea = (projektId: string) =>
    this.hole<NeaStand>(`/api/projects/${projektId}/elektro/nea`);

  /** EK: der jüngste Lauf von kabel-bemessen - nur lesend. */
  elektroKabelliste = (projektId: string) =>
    this.hole<Kabelstand>(`/api/projects/${projektId}/elektro/kabelliste`);

  elektroKabellisteExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/kabelliste.xlsx`, 'ELT_Kabelliste.xlsx');

  /**
   * Der Nachweis ELT-NEA als PDF (ES, 16.10.2026). Jeder Aufruf legt eine
   * neue Berichtsfassung mit Dokumentnummer an (Befund J16, R25; F11 offen) -
   * die Seite sagt das neben dem Knopf.
   */
  elektroNeaPdf = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/elektro/nea/nachweis.pdf`);

  /** Die NEA-Auswertung als Excel - Arbeitsstand, keine Berichtsfassung. */
  elektroNeaExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/nea/auswertung.xlsx`, 'ELT_NEA_Auswertung.xlsx');

  /** SIMARIS (EG3): jüngster Lauf gegen das Modell und die eigene Rechnung, mit Prüfungen. */
  elektroSimaris = (projektId: string) =>
    this.hole<SimarisStand>(`/api/projects/${projektId}/elektro/simaris`);

  // ── Blitzschutz, Erdung, Ueberspannung (W2-C1/C2) ─────────────────────────
  // Die JSON- und die SVG-Route lesen nur; jeder PDF-Abruf legt eine NEUE
  // Berichtsfassung an (R25, bis F11). Den Dateinamen der PDF bestimmt die API.

  elektroBlitzschutz = (projektId: string) =>
    this.hole<BlitzschutzStand>(`/api/projects/${projektId}/elektro/blitzschutz`);

  /** Der Plan der Fangeinrichtung als SVG-TEXT (eingebettet, wie der Raumgrundriss). */
  elektroBlitzschutzPlanSvg = async (projektId: string): Promise<string> => {
    const antwort = await this.abrufen(
      `/api/projects/${projektId}/elektro/blitzschutz/plan.svg`,
      { headers: { Authorization: `Bearer ${await this.token()}` } },
    );

    if (!antwort.ok) throw new Error(await fehlertext(antwort));
    return antwort.text();
  };

  elektroBlitzschutzNachweisLaden = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/blitzschutz/nachweis.pdf`, 'Blitzschutz_Erdung.pdf');

  elektroBlitzschutzUeberspannungLaden = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/blitzschutz/ueberspannung.pdf`, 'Ueberspannung_PA.pdf');

  elektroBlitzschutzPlanLaden = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/blitzschutz/plan.pdf`, 'Plan_Fangeinrichtung.pdf');

  elektroBlitzschutzListen = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/blitzschutz/listen.xlsx`, 'Blitzschutz.xlsx');

  // ── Systemnachweis Funktionserhalt (W2-C3) ────────────────────────────────
  // Die JSON-Route liest nur; jeder PDF-Abruf legt eine NEUE Berichtsfassung an
  // (R25, bis F11).

  elektroFunktionserhalt = (projektId: string) =>
    this.hole<FunktionserhaltStand>(`/api/projects/${projektId}/elektro/funktionserhalt`);

  elektroFunktionserhaltPdfLaden = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/funktionserhalt/nachweis.pdf`, 'Funktionserhalt.pdf');

  elektroFunktionserhaltListen = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/funktionserhalt/listen.xlsx`, 'Funktionserhalt.xlsx');

  // ── Regelpruefung Versammlungsstaette (DIN VDE 0100-718, EK-Folgepaket) ─────
  // Die JSON-Route liest nur; jeder PDF-Abruf legt eine NEUE Berichtsfassung an
  // (R25, bis F11).

  elektroVersammlungsstaette = (projektId: string) =>
    this.hole<VersammlungsstaetteStand>(`/api/projects/${projektId}/elektro/versammlungsstaette`);

  elektroVersammlungsstaettePdfLaden = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/versammlungsstaette/nachweis.pdf`, 'Versammlungsstaette.pdf');

  elektroVersammlungsstaetteListen = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/versammlungsstaette/listen.xlsx`, 'Versammlungsstaette.xlsx');

  /** Der Nachweis ELT-KS als PDF - wie `elektroNeaPdf` eine neue Fassung je Abruf. */
  elektroSimarisPdf = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/elektro/simaris/nachweis.pdf`);

  /** Die SIMARIS-Auswertung als Excel - Arbeitsstand, keine Berichtsfassung. */
  elektroSimarisExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/elektro/simaris/auswertung.xlsx`, 'ELT_SIMARIS_Auswertung.xlsx');

  /**
   * Der Rauchmelderbedarf je Raum nach DIN VDE 0833-2.
   *
   * Ohne Kappung: 1.021 Zeilen sind vertretbar, und eine Melderliste, die
   * stillschweigend bei 500 endet, waere eine Aufforderung zum Fehler.
   */
  elektroMelderbedarf = (
    projektId: string,
    f?: { geschoss?: string; bereich?: string; befund?: string },
  ) => {
    const p = new URLSearchParams();

    if (f?.geschoss) p.set('geschoss', f.geschoss);
    if (f?.bereich) p.set('bereich', f.bereich);
    if (f?.befund) p.set('befund', f.befund);

    return this.hole<any>(
      `/api/projects/${projektId}/elektro/melderbedarf?${p}`);
  };

  /**
   * Der Beleuchtungsnachweis je Raum nach DIN EN 12464-1 (Paket V-E4).
   *
   * Geliefert wird der JUENGSTE Lauf mit seinen Zeilen, dazu der
   * Anforderungskatalog, die Rechenannahmen und die Leuchtentypen. Alles in
   * EINEM Aufruf: Die Seite muss zu jedem Sollwert sagen koennen, woher er
   * kommt, und eine zweite Abfrage dafuer kann man vergessen.
   *
   * Die Liste wird nicht gekappt - 995 Zeilen mit je zwei Dutzend Zahlen
   * sind vertretbar, ein Nachweis, der stillschweigend bei 500 endet, nicht.
   */
  elektroBeleuchtung = (
    projektId: string,
    f?: { laufId?: string; befund?: string; geschoss?: string },
  ) => {
    const p = new URLSearchParams();

    if (f?.laufId) p.set('laufId', f.laufId);
    if (f?.befund) p.set('befund', f.befund);
    if (f?.geschoss) p.set('geschoss', f.geschoss);

    return this.hole<any>(
      `/api/projects/${projektId}/elektro/beleuchtung?${p}`);
  };

  /** Der Beleuchtungsnachweis ELT-BEL als PDF. */
  elektroBeleuchtungPdf = (projektId: string, laufId?: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/beleuchtung.pdf`
      + (laufId ? `?laufId=${encodeURIComponent(laufId)}` : ''),
      'Beleuchtungsnachweis.pdf');

  /**
   * Das Punktverfahren (Paket H4, Raster 0,5 m) — Ergänzung des
   * Beleuchtungsnachweises um Ē, Emin und U0 je Raum. Baut auf dem
   * Wirkungsgradlauf (V-E4) auf, ersetzt ihn nicht.
   */
  elektroBeleuchtungPunktverfahren = (
    projektId: string,
    f?: { laufId?: string; befund?: string },
  ) => {
    const p = new URLSearchParams();

    if (f?.laufId) p.set('laufId', f.laufId);
    if (f?.befund) p.set('befund', f.befund);

    return this.hole<any>(
      `/api/projects/${projektId}/elektro/beleuchtung-punktverfahren?${p}`);
  };

  /** Der Grundriss eines Raumes mit Isolux-Linien — dasselbe Blob-Muster wie die MSR-Schemen. */
  elektroBeleuchtungPunktverfahrenRaumSvg = (
    projektId: string, roomId: string, laufId?: string,
  ) =>
    this.msrSchemaBlob(
      `/api/projects/${projektId}/elektro/beleuchtung-punktverfahren`
      + `/raeume/${roomId}.svg`
      + (laufId ? `?laufId=${encodeURIComponent(laufId)}` : ''));

  /** Punktverfahren ELT-BUP als PDF (je Raum ein Blatt). */
  elektroBeleuchtungPunktverfahrenPdf = (projektId: string, laufId?: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/beleuchtung-punktverfahren.pdf`
      + (laufId ? `?laufId=${encodeURIComponent(laufId)}` : ''),
      'Beleuchtung-Punktverfahren.pdf');

  /**
   * Rettungsweg und Sicherheitsbeleuchtung nach DIN EN 1838 (Paket V-E5).
   *
   * Der Rettungsweg kommt aus dem Türbuch (Raumfolge über belastbare
   * Türverbindungen, NICHT über Fluchtrichtung/Anschlag — die sind
   * projektweit gleich gesetzt). Geliefert wird der jüngste Lauf mit seinen
   * Zeilen und den EN-1838-Annahmen.
   */
  elektroSicherheitsbeleuchtung = (
    projektId: string,
    f?: { laufId?: string; befund?: string; geschoss?: string },
  ) => {
    const p = new URLSearchParams();

    if (f?.laufId) p.set('laufId', f.laufId);
    if (f?.befund) p.set('befund', f.befund);
    if (f?.geschoss) p.set('geschoss', f.geschoss);

    return this.hole<any>(
      `/api/projects/${projektId}/elektro/sicherheitsbeleuchtung?${p}`);
  };

  /** Der Sicherheitsbeleuchtungsnachweis ELT-SIB als PDF. */
  elektroSicherheitsbeleuchtungPdf = (projektId: string, laufId?: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/elektro/sicherheitsbeleuchtung.pdf`
      + (laufId ? `?laufId=${encodeURIComponent(laufId)}` : ''),
      'Sicherheitsbeleuchtung.pdf');

  /** Der Bericht mit den Gegenproben. */
  koordinationBericht = (projektId: string, standId?: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/koordination/bericht`
      + (standId ? `?standId=${standId}` : ''));

  /** Die Kollisionen des neuesten Laufs, nach Geschoss und Abschnitt. */
  /**
   * Der Arbeitsvorrat: was mit welcher Kollision zu tun ist.
   *
   * Ohne `korb` kommen nur die Zählwerte — acht Zeilen, eine je Korb, dazu
   * die Gewerkepaare und die Geschosse. Das ist der Regelfall und kostet
   * nichts: Gruppiert wird in der Datenbank.
   *
   * MIT `korb` kommen zusätzlich die Einzelfälle dieses einen Korbes,
   * seitenweise. Alle Körbe zugleich zu laden war die alte Lesart der
   * Kollisionsliste — sie holte 318.000 Zeilen, um 200 zu zeigen.
   */
  koordinationArbeitsvorrat = (
    projektId: string,
    f?: { standId?: string; korb?: string; geschoss?: string;
          seite?: number; groesse?: number },
  ) => {
    const p = new URLSearchParams();

    if (f?.standId) p.set('standId', f.standId);
    if (f?.korb) p.set('korb', f.korb);
    if (f?.geschoss) p.set('geschoss', f.geschoss);
    if (f?.seite !== undefined) p.set('seite', String(f.seite));
    if (f?.groesse !== undefined) p.set('groesse', String(f.groesse));

    const frage = p.toString();

    return this.hole<any>(
      `/api/projects/${projektId}/koordination/arbeitsvorrat`
        + (frage ? `?${frage}` : ''));
  };

  /**
   * Eine Kollision im Detail — beide Bauteile mit ihrer Kennung im Modell.
   *
   * Das ist die Antwort auf "man kann nichts zuordnen": Die Liste zeigte
   * "G04 | klt | seitlich_x | 569 mm" und damit nichts, womit man das Rohr
   * im Modell wiederfindet. Hier stehen Modellname, RevitElementId und
   * UniqueId beider Bauteile — und die Lösung in Worten.
   */
  koordinationKollision = (projektId: string, kollisionId: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/koordination/kollision/${kollisionId}`);

  /**
   * Das Vorschaubild einer Kollision (Ist und Soll, Grundriss und Schnitt)
   * als Objekt-URL — ein <img>-Tag kann sie direkt setzen.
   *
   * Ein einfacher <img src="/api/..."> sendet kein Token; die Anfrage würde
   * mit 401 abgewiesen (dasselbe Muster wie bei dateiLaden).
   */
  koordinationVorschauSvg = async (projektId: string, kollisionId: string) => {
    const antwort = await this.abrufen(
      `/api/projects/${projektId}/koordination/kollision/${kollisionId}`
        + '/vorschau.svg',
      { headers: { Authorization: `Bearer ${await this.token()}` } },
    );

    if (!antwort.ok) {
      throw new Error(await fehlertext(antwort));
    }

    const blob = await antwort.blob();
    return URL.createObjectURL(blob);
  };

  koordinationKollisionen = (
    projektId: string, standId?: string, einstufung?: string,
  ) => {
    const p = new URLSearchParams();
    if (standId) p.set('standId', standId);
    if (einstufung) p.set('einstufung', einstufung);

    const frage = p.toString();

    return this.hole<any>(
      `/api/projects/${projektId}/koordination/kollisionen`
        + (frage ? `?${frage}` : ''));
  };

  /**
   * Batch 7 — dieselbe Abfrage wie {@link koordinationKollisionen}, aber mit
   * dem vollen Filter- und Seitensatz des Planerarbeitsplatzes. Die Antwort
   * trägt zusätzlich `ZeilenGesamt`/`ZeilenSeite`/`Zeilen` — die
   * Zusammenfassung (`Gesamt`/`JeEinstufung`/`Geschosse`) bleibt unverändert
   * mit dabei.
   */
  koordinationKollisionenListe = (
    projektId: string,
    f?: {
      standId?: string; geschoss?: string; bereich?: string;
      quadrant?: string; gewerkWeicht?: string; gewerkBleibt?: string;
      loesungsart?: string; status?: string; befund?: string; korb?: string;
      mindestDurchdringung?: number; suche?: string;
      nurMitAnweisung?: boolean; nurAutomatisch?: boolean;
      sortierung?: string; seite?: number; groesse?: number;
    },
  ) => {
    const p = new URLSearchParams();

    if (f?.standId) p.set('standId', f.standId);
    if (f?.geschoss) p.set('geschoss', f.geschoss);
    if (f?.bereich) p.set('bereich', f.bereich);
    if (f?.quadrant) p.set('quadrant', f.quadrant);
    if (f?.gewerkWeicht) p.set('gewerkWeicht', f.gewerkWeicht);
    if (f?.gewerkBleibt) p.set('gewerkBleibt', f.gewerkBleibt);
    if (f?.loesungsart) p.set('loesungsart', f.loesungsart);
    if (f?.status) p.set('status', f.status);
    if (f?.befund) p.set('befund', f.befund);
    if (f?.korb) p.set('korb', f.korb);
    if (f?.mindestDurchdringung !== undefined)
      p.set('mindestDurchdringung', String(f.mindestDurchdringung));
    if (f?.suche) p.set('suche', f.suche);
    if (f?.nurMitAnweisung) p.set('nurMitAnweisung', 'true');
    if (f?.nurAutomatisch) p.set('nurAutomatisch', 'true');
    if (f?.sortierung) p.set('sortierung', f.sortierung);
    if (f?.seite !== undefined) p.set('seite', String(f.seite));
    if (f?.groesse !== undefined) p.set('groesse', String(f.groesse));

    const frage = p.toString();

    return this.hole<any>(
      `/api/projects/${projektId}/koordination/kollisionen`
        + (frage ? `?${frage}` : ''));
  };

  /**
   * Der Baum des Planerarbeitsplatzes (Batch 7) — Geschoss → Abschnitt →
   * Quadrant → Gewerkpaar, oder mit `art: 'planeraufgaben'` Geschoss →
   * Abschnitt → Aufgabe.
   *
   * Heißt 'arbeitsbaum' und nicht 'baum': Unter '.../koordination/baum'
   * steht bereits der Addin-Baum (Geschoss → Quadrant → Zelle, siehe
   * KoordinationsfensterEndpoints.cs) — derselbe Pfad hätte bei jeder
   * Anfrage eine Routenkollision ausgelöst (Batch 7b, 06.09.2026).
   */
  koordinationArbeitsbaum = (
    projektId: string,
    f?: { standId?: string; art?: 'kollisionen' | 'planeraufgaben' },
  ) => {
    const p = new URLSearchParams();
    if (f?.standId) p.set('standId', f.standId);
    if (f?.art) p.set('art', f.art);

    const frage = p.toString();

    return this.hole<any>(
      `/api/projects/${projektId}/koordination/arbeitsbaum`
        + (frage ? `?${frage}` : ''));
  };

  /**
   * Bis zu 200 Anweisungen auf einmal entscheiden (Batch 7) — dieselben
   * Regeln wie {@link koordinationAnweisungStatus}.
   */
  koordinationAnweisungenSammelstatus = (
    projektId: string,
    w: { ids: string[]; status: string; ablehngrund?: string; angabe?: string },
  ) => this.sende<any>(
    `/api/projects/${projektId}/koordination/anweisungen/sammelstatus`, w);

  // ─── Fremdmodelle > Küchenplanung ──────────────────────────────────
  //
  // Alles hier liest AUSSCHLIESSLICH aus der Datenbank. Das ist die
  // Anforderung des Moduls und keine Sparmaßnahme: Der Baum und die
  // Detailansicht müssen ohne laufenden Revit-Client vollständig lesbar
  // sein.

  /** Zählwerte und Status — die offenen Punkte zuerst. */
  kuecheUebersicht = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/kueche/uebersicht`);

  /**
   * Der Baum Ebene → Abschnitt → Raum, mit Zählwerten je Knoten.
   *
   * In einem Zug: 6 + 19 + 37 Knoten sind wenige Kilobyte, und Nachladen
   * je Klick wären drei Abfragen für Daten, die schon da sein könnten.
   * Die Komponenten kommen getrennt — 845 Zeilen mit HKLSE-JSON sind über
   * ein Megabyte.
   */
  kuecheBaum = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/kueche/baum`);

  /**
   * Die Komponenten eines Knotens.
   *
   * Ohne `herkunft` ist der BESTAND gemeint — die Bauteile aus den beiden
   * KUCA-Modellen. Die Planpositionen der Altauswertung holt man sich
   * ausdrücklich mit `herkunft: 'LEGACY_PREMODEL_SEED'`; `'alle'` gibt beides.
   * Vermischt waren sie der Grund, warum „446 ohne Raumzuordnung" als Aussage
   * über den Bestand gelesen wurde, obwohl kein einziges Modellbauteil ohne
   * Raum ist.
   */
  kuecheKomponenten = (projektId: string, filter: {
    etage?: string; abschnittId?: string; raumId?: string;
    ohneRaum?: boolean; status?: string; suche?: string;
    herkunft?: string; importStatus?: string;
  }) => {
    const p = new URLSearchParams();
    if (filter.etage) p.set('etage', filter.etage);
    if (filter.abschnittId) p.set('abschnittId', filter.abschnittId);
    if (filter.raumId) p.set('raumId', filter.raumId);
    if (filter.ohneRaum) p.set('ohneRaum', 'true');
    if (filter.status) p.set('status', filter.status);
    if (filter.suche) p.set('suche', filter.suche);
    if (filter.herkunft) p.set('herkunft', filter.herkunft);
    if (filter.importStatus) p.set('importStatus', filter.importStatus);

    const frage = p.toString();

    return this.hole<any>(
      `/api/projects/${projektId}/kueche/komponenten`
        + (frage ? `?${frage}` : ''));
  };

  /** Eine Komponente mit Übersicht, TGA-Daten, Modell, Quelle und Historie. */
  kuecheKomponente = (projektId: string, id: string) =>
    this.hole<any>(`/api/projects/${projektId}/kueche/komponenten/${id}`);

  /**
   * Fachlich ändern. `dbRevision` ist Pflicht — sie ist die optimistische
   * Sperre. Bei HTTP 409 hat jemand anders zwischenzeitlich geschrieben.
   */
  kuecheKomponenteAendern = (
    projektId: string, id: string, aenderung: Record<string, unknown>,
  ) => this.schicke<any>(
    `/api/projects/${projektId}/kueche/komponenten/${id}`, 'PATCH', aenderung);

  /** Die 610 Anschlusspunkte ohne Gerät — der offene Kern des Moduls. */
  kuecheOffeneAnschluesse = (
    projektId: string, etage?: string, gewerk?: string,
  ) => {
    const p = new URLSearchParams();
    if (etage) p.set('etage', etage);
    if (gewerk) p.set('gewerk', gewerk);

    const frage = p.toString();

    return this.hole<any>(
      `/api/projects/${projektId}/kueche/anschluesse/offen`
        + (frage ? `?${frage}` : ''));
  };

  kuecheRaeume = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/kueche/raeume`);

  /** Einen Raumbuchkandidaten bestätigen. */
  kuecheRaumAendern = (
    projektId: string, id: string, aenderung: Record<string, unknown>,
  ) => this.schicke<any>(
    `/api/projects/${projektId}/kueche/raeume/${id}`, 'PATCH', aenderung);

  kuechePlaene = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/kueche/plaene`);

  kuecheLaeufe = (projektId: string) =>
    this.hole<any>(`/api/projects/${projektId}/kueche/laeufe`);

  // ─── Der Fremdmodellcheck ──────────────────────────────────────────

  /** Die gelesenen Wochenstände, neueste zuerst. */
  fremdStaende = (projektId: string) =>
    this.hole<any[]>(`/api/projects/${projektId}/fremdmodelle/staende`);

  /**
   * Die Änderungen als Baum: Geschoss → Planungsabschnitt.
   *
   * Ohne Standangabe der NEUESTE Vergleich — nicht alle. Alle zusammen
   * wären die Summe seit Beginn, und die beantwortet keine Frage, die
   * jemand hat.
   */
  fremdBaum = (projektId: string, standId?: string, zustand?: string) => {
    const p = new URLSearchParams();
    if (standId) p.set('standId', standId);
    if (zustand) p.set('zustand', zustand);

    const frage = p.toString();

    return this.hole<any>(
      `/api/projects/${projektId}/fremdmodelle/baum`
        + (frage ? `?${frage}` : ''));
  };

  fremdPrioritaet = (id: string, prioritaet: string | null) =>
    this.schicke<unknown>(
      `/api/fremdmodelle/aenderungen/${id}/prioritaet`, 'POST',
      { prioritaet });

  fremdErledigt = (id: string, erledigt: boolean, bemerkung?: string) =>
    this.schicke<unknown>(
      `/api/fremdmodelle/aenderungen/${id}/erledigt`, 'POST',
      { erledigt, bemerkung });

  fremdRegeln = () => this.hole<any>('/api/fremdmodelle/regeln');

  /** Die Folgen im eigenen Bestand prüfen — der teuerste Lauf des Moduls. */
  fremdFolgenPruefen = (projektId: string, standId?: string) =>
    this.schicke<any>(
      `/api/projects/${projektId}/fremdmodelle/folgen-pruefen`
        + (standId ? `?standId=${standId}` : ''), 'POST', {});

  fremdFolgen = (projektId: string, zustand?: string) =>
    this.hole<any>(
      `/api/projects/${projektId}/fremdmodelle/folgen`
        + (zustand ? `?zustand=${zustand}` : ''));

  /** Der Fremdmodellbericht als PDF — Folgen zuerst, Änderungen als Nachweis. */
  fremdBerichtPdf = (projektId: string, standId?: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/fremdmodelle/bericht.pdf`
        + (standId ? `?standId=${standId}` : ''),
      'Fremdmodellbericht.pdf');

  fremdFolgeZustand = (id: string, zustand: string, bemerkung?: string) =>
    this.schicke<unknown>(
      `/api/fremdmodelle/folgen/${id}/zustand`, 'POST',
      { zustand, bemerkung });

  aussparungPaaren = (projektId: string) =>
    this.schicke<unknown>(
      `/api/projects/${projektId}/aussparungen/paaren`, 'POST');

  /**
   * Eine über die LAGE geratene Paarung von Hand bestätigen.
   *
   * Aus einer Vermutung wird damit eine Zuordnung. Nur das — der
   * Freigabestand bleibt unberührt.
   */
  aussparungPaarBestaetigen = (paarId: string) =>
    this.schicke<unknown>(
      `/api/aussparungen/paare/${paarId}/bestaetigen`, 'POST');

  /**
   * Welcher Stand gilt — die Entscheidung des Fachplaners (AS8).
   *
   * Nicht dasselbe wie `aussparungPaarBestaetigen`: Das sagt „das sind
   * dieselben zwei Löcher", dies sagt „dieser Stand gilt". Sie ändert KEIN
   * Modell — sie wird aufgeschrieben (F61; conVoid ist Paket AS9).
   */
  aussparungPaarEntscheiden = (
    paarId: string, entscheidung: string, begruendung?: string,
  ) => this.schicke<{
    id: string; entscheidung: string | null; entschiedenVon: string | null;
    entschiedenAm: string | null; begruendung: string | null;
    verlaufszeile: boolean; hinweis: string | null;
  }>(`/api/aussparungen/paare/${paarId}/entscheiden`, 'POST',
     { entscheidung, begruendung });

  // --- Die Außenanlagen ----------------------------------------------------

  aussenUebersicht = (projektId: string) =>
    this.hole<AussenUebersicht>(
      `/api/projects/${projektId}/aussenanlagen/uebersicht`);

  aussenSchaechte = (projektId: string, gewerk?: string) =>
    this.hole<AussenSchacht[]>(
      `/api/projects/${projektId}/aussenanlagen/schaechte`
      + (gewerk ? `?gewerk=${encodeURIComponent(gewerk)}` : ''));

  /**
   * Der jüngste Berechnungslauf eines Bereichs.
   *
   * `zeilen2` heißt so, weil `zeilen` am Lauf schon die ANZAHL ist. Ein
   * Feld, das an einer Stelle eine Zahl und an der anderen eine Liste ist,
   * wäre die Art Fehler, die erst im Browser auffällt.
   */
  aussenBerechnung = (projektId: string, bereich: string) =>
    this.hole<AussenBerechnungslauf>(
      `/api/projects/${projektId}/aussenanlagen/berechnung/${bereich}`);

  aussenRechnen = (projektId: string, bereich?: string) =>
    this.schicke<unknown>(
      `/api/projects/${projektId}/aussenanlagen/berechnung`
      + (bereich ? `?bereich=${bereich}` : ''), 'POST');

  aussenBerichtPdf = (projektId: string, bereich: string) =>
    this.pdfLaden(
      `/api/projects/${projektId}/aussenanlagen/berechnung/${bereich}/bericht.pdf`);

  /**
   * Die sieben Außenberichte mit Lauf- und Registerstand.
   *
   * Erzeugt NICHTS. Sie sagt, welcher Bereich gerechnet ist, welche
   * Dokumentnummer sein letzter Bericht trägt und ob dieser noch zum
   * jüngsten Lauf gehört — ohne dafür sieben PDFs zu bauen.
   */
  aussenBerichte = (projektId: string) =>
    this.hole<{ bereiche: AussenBerichtsstand[] }>(
      `/api/projects/${projektId}/aussenanlagen/berichte`);

  /**
   * Die Stränge eines Bereichs — die oberen zwei Ebenen des Baums.
   *
   * Ohne die Teilstrecken: Beim Freispiegel sind das 282 Zeilen mit je
   * siebzehn Feldern, und der Baum braucht sie erst beim Aufklappen.
   */
  aussenNachweisStraenge = (projektId: string, bereich = 'freispiegel') =>
    this.hole<AussenNachweisStraenge>(
      `/api/projects/${projektId}/aussenanlagen/nachweis/straenge`
      + `?bereich=${bereich}`);

  /** Die Teilstrecken EINES Strangs — die dritte Ebene. */
  aussenNachweisStrang = (projektId: string, zeileId: string) =>
    this.hole<AussenNachweisStrang>(
      `/api/projects/${projektId}/aussenanlagen/nachweis/straenge/${zeileId}`);

  /** Die Rigolen mit beiden Rechenwegen und der Deckung. */
  aussenNachweisVersickerung = (projektId: string) =>
    this.hole<AussenNachweisVersickerung>(
      `/api/projects/${projektId}/aussenanlagen/nachweis/versickerung`);

  /** Die Hydranten mit dem Abstand zum nächsten Nachbarn. */
  aussenNachweisHydranten = (projektId: string) =>
    this.hole<AussenNachweisHydranten>(
      `/api/projects/${projektId}/aussenanlagen/nachweis/hydranten`);

  /** Alle Befunde der jüngsten Läufe, über alle sechs Bereiche. */
  aussenNachweisBefunde = (projektId: string) =>
    this.hole<AussenNachweisBefunde>(
      `/api/projects/${projektId}/aussenanlagen/nachweis/befunde`);

  aussenUebergaenge = (projektId: string) =>
    this.hole<AussenUebergang[]>(
      `/api/projects/${projektId}/aussenanlagen/uebergaenge`);

  aussenVersickerung = (projektId: string) =>
    this.hole<AussenVersickerungsbauwerk[]>(
      `/api/projects/${projektId}/aussenanlagen/versickerung`);

  aussenLeitungen = (
    projektId: string, kreis?: string, seite = 1, proSeite = 100,
  ) => {
    const p = new URLSearchParams({
      seite: String(seite), proSeite: String(proSeite),
    });
    if (kreis) p.set('kreis', kreis);

    return this.hole<{
      gesamt: number; seite: number; proSeite: number;
      zeilen: AussenLeitung[];
    }>(`/api/projects/${projektId}/aussenanlagen/leitungen?${p}`);
  };

  /**
   * Die Leerrohre — seitenweise, weil es 5.322 sind.
   *
   * `laengeM` und `ohneTrasse` gelten für ALLE Zeilen, nicht für die
   * angezeigte Seite: Eine Gesamtlänge, die sich beim Blättern ändert, ist
   * keine.
   */
  aussenLeerrohre = (
    projektId: string, seite = 1, proSeite = 100, nurOhneTrasse = false,
  ) => {
    const p = new URLSearchParams({
      seite: String(seite), proSeite: String(proSeite),
    });
    if (nurOhneTrasse) p.set('nurOhneTrasse', 'true');

    return this.hole<{
      gesamt: number; seite: number; proSeite: number;
      laengeM: number | null; ohneTrasse: number;
      zeilen: AussenLeerrohr[];
    }>(`/api/projects/${projektId}/aussenanlagen/leerrohre?${p}`);
  };

  /** Die Geräte der Außenanlagen mit ihrem öffentlichen Verweis. */
  aussenGeraete = (projektId: string) =>
    this.hole<{
      bauwerke: any[]; hydranten: any[]; versickerung: any[];
    }>(`/api/projects/${projektId}/aussenanlagen/geraete`);

  aussenHydranten = (projektId: string) =>
    this.hole<AussenHydrant[]>(
      `/api/projects/${projektId}/aussenanlagen/hydranten`);

  /** Die Rückhaltung je Einleitstelle nach DWA-A 117 (Reiter Rückhaltung, TS9b2). */
  aussenRueckhaltung = (projektId: string) =>
    this.hole<AussenRueckhaltungszeile[]>(
      `/api/projects/${projektId}/aussenanlagen/rueckhaltung`);

  /**
   * Die Kreiszuordnung neu anwenden — OHNE Modelllauf.
   *
   * Das ist der Grund, warum die Zuordnung nicht in der Aufnahme steht: Eine
   * geänderte Regel soll keine zehn Minuten kosten.
   */
  aussenKreiseZuordnen = (projektId: string) =>
    this.schicke<unknown>(
      `/api/projects/${projektId}/aussenanlagen/kreise`, 'POST');

  aussenAnschluesseAufloesen = (projektId: string) =>
    this.schicke<unknown>(
      `/api/projects/${projektId}/aussenanlagen/anschluesse`, 'POST');

  sanitaerGeraete = (projektId: string) =>
    this.hole<SanitaerGeraet[]>(
      `/api/projects/${projektId}/sanitaer/geraete`);

  /**
   * Die Druckerhöhungsanlagen.
   *
   * Ein eigener Weg neben der Geräteliste, weil sie NICHT modelliert sind:
   * Sie folgen aus der Trinkwasserberechnung. Deshalb haben sie keine
   * RevitElementId und keinen öffentlichen Verweis — es gibt kein Bauteil,
   * auf das er zeigen könnte.
   */
  sanitaerDruckerhoehung = (projektId: string) =>
    this.hole<SanitaerDruckerhoehung[]>(
      `/api/projects/${projektId}/sanitaer/druckerhoehung`);

  /**
   * Die Druckminderervorschläge je Zone mit der Grenze `ruhedruck_max_bar`
   * (V-S3, Seite Druckzonen TS9b2). Die Grenze steht mit dabei: Ob sie geprüft
   * ist, entscheidet, wie ein Vorschlag zu lesen ist.
   */
  sanitaerDruckminderer = (projektId: string) =>
    this.hole<SanitaerDruckminderer>(
      `/api/projects/${projektId}/sanitaer/druckminderer`);

  /**
   * Die Absicherung nach DIN EN 1717 und die 3-Liter-Regel (V-S4, Seite
   * Absicherung TS9b2): je Gegenstand, dazu Summen je Sicherungsart und
   * Kategorie und der Katalog.
   */
  sanitaerAbsicherung = (projektId: string) =>
    this.hole<SanitaerAbsicherungsliste>(
      `/api/projects/${projektId}/sanitaer/absicherung`);

  /**
   * Die Produktseite eines Sanitärgeräts.
   *
   * Liefert `Produktdaten` — dieselbe Form wie die Heizung. Das ist der Kern
   * der Vereinheitlichung: Eine Anzeigekomponente bedient alle Gewerke, und
   * ein neues Feld erscheint überall statt in vier Dateien nachgezogen zu
   * werden.
   */
  sanitaerGeraet = (bauteilId: string) =>
    this.hole<Produktdaten>(`/api/sanitaer/geraete/${bauteilId}`);

  elektroGeraete = (projektId: string, bauteilart?: string) =>
    this.hole<ElektroGeraet[]>(
      `/api/projects/${projektId}/elektro/geraete`
      + (bauteilart ? `?bauteilart=${encodeURIComponent(bauteilart)}` : ''));

  /**
   * Die Bauteilarten mit ihren Zahlen.
   *
   * Zuerst die Gruppen, dann auf Klick die Bauteile: 30.218 Elektrobauteile
   * sind keine Liste. Die erste Fassung holte 5.000 und schnitt den Rest ab —
   * ohne es zu sagen.
   */
  elektroGruppen = (projektId: string) =>
    this.hole<ElektroGruppe[]>(
      `/api/projects/${projektId}/elektro/gruppen`);

  /**
   * Die Kabeltrassen, nach Stromart getrennt.
   *
   * Seit Fassung 1.6.90 liest sie der Elektrolauf selbst. Davor kamen sie aus
   * der Koordinationsaufnahme, die fuer die Kollision liest und dabei weder
   * Breite noch Hoehe mitnimmt — ausgezaehlt 0 von 55.964.
   */
  kabeltrassen = (projektId: string) =>
    this.hole<Kabeltrassenuebersicht>(
      `/api/projects/${projektId}/elektro/kabeltrassen`);

  /**
   * Der Baum: Ebene → Abschnitt → Trassenart.
   *
   * Nur die Knoten mit ihren Zahlen. Die Trassen selbst holt die Seite beim
   * Aufklappen — 55.964 Zeilen in einer Antwort waeren etwa 20 MB und keine
   * Ansicht.
   */
  kabeltrassenbaum = (projektId: string) =>
    this.hole<Kabeltrassenbaum>(
      `/api/projects/${projektId}/elektro/kabeltrassen/baum`);

  /**
   * Der Bestandsbaum: Kategorie → Ebene → Abschnitt → Raum.
   *
   * Die Kategorie ganz oben, weil sie die groebste Trennung ist: Eine
   * Kabelrinne, ein Leerrohr und ein Fundamenterder haben nichts miteinander
   * zu tun.
   */
  kabeltrassenbestand = (projektId: string) =>
    this.hole<Kabeltrassenbestand>(
      `/api/projects/${projektId}/elektro/kabeltrassen/bestand`);

  kabeltrassenliste = (projektId: string, filter: {
    stromart?: string; geschoss?: string; ebene?: string;
    planungsbereich?: string; gruppe?: string; raum?: string;
    seite?: number; proSeite?: number;
  } = {}) => {
    const p = new URLSearchParams();

    for (const [k, v] of Object.entries(filter))
      if (v !== undefined && v !== null && v !== '') p.set(k, String(v));

    return this.hole<Kabeltrassenblatt>(
      `/api/projects/${projektId}/elektro/kabeltrassen/liste?${p}`);
  };

  /** Der Feldkatalog: hellgruen aus Revit, hellgelb aus der Plattform. */
  kabeltrassenfelder = (projektId: string) =>
    this.hole<Kabeltrassenfelder>(
      `/api/projects/${projektId}/elektro/kabeltrassen/felder`);

  kabeltrassenwert = (projektId: string, eingabe: {
    trasseId?: string | null; typschluessel?: string | null;
    feldschluessel: string; wert?: string | null; zahlwert?: number | null;
    herkunft?: string; quelle?: string | null; geprueft?: boolean;
    begruendung?: string | null;
  }) =>
    this.sende<{ id: string }>(
      `/api/projects/${projektId}/elektro/kabeltrassen/wert`, eingabe);

  /**
   * Wo die Stromart im Modell stehen koennte — ausgezaehlt.
   *
   * Die wichtigste Auskunft dieses Bereichs, solange 37.932 Bauteile auf
   * 'unbestimmt' stehen.
   */
  kabeltrassenerhebung = (projektId: string) =>
    this.hole<Kabeltrassenerhebung>(
      `/api/projects/${projektId}/elektro/kabeltrassen/erhebung`);

  kabeltrassenprodukte = (projektId: string) =>
    this.hole<Kabeltrassenprodukte>(
      `/api/projects/${projektId}/elektro/kabeltrassen/produkte`);

  kabeltrassenUebernehmen = (projektId: string) =>
    this.sende<{
      gelesen: number; zugeordnet: number; unbestimmt: number;
      jeStromart: Record<string, number>; hinweise: string[];
    }>(`/api/projects/${projektId}/elektro/kabeltrassen/uebernehmen`, {});

  elektroProduktbauteile = (projektId: string, filter: {
    bauteilart?: string; raum?: string; geschoss?: string; suche?: string;
    seite?: number; proSeite?: number;
  } = {}) => {
    const p = new URLSearchParams();

    for (const [k, v] of Object.entries(filter))
      if (v !== undefined && v !== null && v !== '') p.set(k, String(v));

    return this.hole<ElektroBauteilseite>(
      `/api/projects/${projektId}/elektro/produktbauteile?${p}`);
  };

  elektroGeraet = (bauteilId: string) =>
    this.hole<Produktdaten>(`/api/elektro/geraete/${bauteilId}`);

  elektroWertPflegen = (bauteilId: string, feld: string, wert: string | null) =>
    this.schicke<SanitaerGepflegterWert>(
      `/api/elektro/geraete/${bauteilId}/werte/${encodeURIComponent(feld)}`,
      'PUT', { wert }) as Promise<SanitaerGepflegterWert>;

  sanitaerWertPflegen = (bauteilId: string, feld: string, wert: string | null) =>
    this.schicke<SanitaerGepflegterWert>(
      `/api/sanitaer/geraete/${bauteilId}/werte/${encodeURIComponent(feld)}`,
      'PUT', { wert }) as Promise<SanitaerGepflegterWert>;

  // --- Die Rohrnetzberechnung ---------------------------------------------

  /**
   * Den Lauf auslösen.
   *
   * Er ersetzt alle Wege und alle Ergebnisse des Projekts — deshalb ein
   * ausdrücklicher Aufruf und kein Nebeneffekt des Ladens.
   */
  heizungBerechnen = (projektId: string) =>
    this.schicke<Heizungberechnungslauf>(
      `/api/projects/${projektId}/heizung/berechnung`, 'POST');

  heizungBerechnung = (projektId: string, gruppe?: string) =>
    this.hole<Heizungberechnung>(
      `/api/projects/${projektId}/heizung/berechnung`
      + (gruppe ? `?gruppe=${encodeURIComponent(gruppe)}` : ''));

  /**
   * Die Kälte — Anlagen, Bilanz, Stoffwerte und die offenen Widersprüche.
   *
   * Zusammen und nicht in vier Aufrufen: Eine Kältebilanz ohne die
   * Widersprüche, die in ihren Eingangsgrößen stecken, ist eine Zahl, der
   * man mehr glaubt, als sie verdient.
   */
  kaelte = (projektId: string) =>
    this.hole<Kaelteseite>(`/api/projects/${projektId}/kaelte`);

  /**
   * Der Kältenachweis (TH-3): jüngster beendeter Lauf von `kaelte-rechnen` –
   * Erzeugung, Teillast, n−1, Rückkühlung, Eisspeicherprofil, freie Kühlung
   * und DIN EN 378, mit Annahmen und Normbezug. Dasselbe Datenmodell trägt
   * den Bericht KAE-KMN und (mit TX1) die Excel-Liste.
   */
  kaelteNachweis = (projektId: string) =>
    this.hole<KaeltenachweisAntwort>(`/api/projects/${projektId}/kaelte/nachweis`);

  /**
   * Der hydraulische Abgleich (TH-4): jüngster beendeter Lauf von
   * `heizung-abgleich` – Einstellwerte je Verbraucher, Strangarmaturen,
   * Pumpen (Betriebspunkt nach Abgleich, EEI), Annahmen und Normbezug.
   * Dasselbe Datenmodell trägt den Bericht HZG-ABG und (mit TX1) die
   * Excel-Einstellwerteliste.
   */
  heizungAbgleich = (projektId: string) =>
    this.hole<AbgleichAntwort>(`/api/projects/${projektId}/heizung/abgleich`);

  /**
   * Die Netzbrücken — was das Netz zusammenhält.
   *
   * Verbindungen, die im MODELL fehlen und trotzdem gerechnet werden. Nur
   * die aktiven wirken; ein Vorschlag ist keine Verbindung.
   */
  heizungNetzbruecken = (projektId: string) =>
    this.hole<HeizungNetzbrueckenliste>(
      `/api/projects/${projektId}/heizung/netzbruecken`);

  /**
   * Ein Weg mit allen Einzelstücken.
   *
   * Getrennt von der Liste: Die Einzelstücke sind je Weg einige Kilobyte. Bei
   * tausend Wegen wäre das ein Vielfaches dessen, was eine Übersicht braucht —
   * und die Seite lädt sie, bevor jemand einen einzelnen aufklappt.
   */
  heizungWeg = (wegId: string) =>
    this.hole<Heizungweg>(`/api/heizung/wege/${wegId}`);

  // --- Schritt 2c: Wege, Teilstrecken, Posten, Verbraucher ---------------
  //
  // Vier Abfragen, dieselbe Gliederung wie bei der Lüftung:
  // Gruppe → Klasse → Weg → Teilstrecke → Posten.

  /**
   * Die Wege einer Gruppe, wahlweise auf eine Verbraucherklasse eingegrenzt.
   *
   * Die Klasse ist die Verbraucherart des Wegs — Befund H-14: Ein
   * Lüftungsregister mit 50 kPa und ein Heizkörper mit 14 Pa an derselben
   * Pumpe sind nicht gegeneinander abzugleichen.
   */
  heizungWege = (
    projektId: string,
    was?: { gruppe?: string; klasse?: string; grenze?: number },
  ) => {
    const f = new URLSearchParams();
    if (was?.gruppe) f.set('gruppe', was.gruppe);
    if (was?.klasse) f.set('klasse', was.klasse);
    if (was?.grenze) f.set('grenze', String(was.grenze));

    const s = f.toString();

    return this.hole<HeizungWegliste>(
      `/api/projects/${projektId}/heizung/wege${s ? `?${s}` : ''}`);
  };

  /** Die Teilstrecken eines Wegs, in Fließrichtung. */
  heizungWegTeilstrecken = (wegId: string) =>
    this.hole<HeizungWegaufbau>(
      `/api/heizung/wege/${wegId}/teilstrecken`);

  /**
   * Die Posten einer Teilstrecke auf diesem Weg.
   *
   * `teilstrecke` nimmt eine Kennung, das Wort `ohne` für die Stücke ohne
   * Teilstreckenbezug, oder nichts — dann kommt der ganze Weg.
   */
  heizungWegPosten = (
    wegId: string, was?: { teilstrecke?: string; seite?: string },
  ) => {
    const f = new URLSearchParams();
    if (was?.teilstrecke) f.set('teilstrecke', was.teilstrecke);
    if (was?.seite) f.set('seite', was.seite);

    const s = f.toString();

    return this.hole<HeizungPostenliste>(
      `/api/heizung/wege/${wegId}/posten${s ? `?${s}` : ''}`);
  };

  /** Die Verbraucher einer Gruppe mit ihren drei Leistungszahlen. */
  heizungVerbraucher = (
    projektId: string,
    was?: { gruppe?: string; klasse?: string; grenze?: number },
  ) => {
    const f = new URLSearchParams();
    if (was?.gruppe) f.set('gruppe', was.gruppe);
    if (was?.klasse) f.set('klasse', was.klasse);
    if (was?.grenze) f.set('grenze', String(was.grenze));

    const s = f.toString();

    return this.hole<HeizungVerbraucherliste>(
      `/api/projects/${projektId}/heizung/verbraucher${s ? `?${s}` : ''}`);
  };

  /** Anlageninhalt und Absicherung nach DIN EN 12828 (H-06, H-07). */
  heizungAusdehnung = (projektId: string) =>
    this.hole<HeizungAusdehnungsliste>(
      `/api/projects/${projektId}/heizung/ausdehnung`);

  heizungZetaregeln = () =>
    this.hole<Heizungzetaregel[]>('/api/heizung/zetaregeln');

  /**
   * Der Dämmnachweis nach GEG Anlage 8 und gegen den Taupunkt (HZ4).
   *
   * Ohne `urteil` kommt die Arbeitsliste: die unterschrittenen UND die nicht
   * beurteilbaren Leitungen. Die erfüllten braucht niemand einzeln.
   */
  heizungDaemmung = (projektId: string, p?: { urteil?: string; grenze?: number }) => {
    const f = new URLSearchParams();
    if (p?.urteil) f.set('urteil', p.urteil);
    if (p?.grenze) f.set('grenze', String(p.grenze));
    const s = f.toString();

    return this.hole<HeizungDaemmnachweisliste>(
      `/api/projects/${projektId}/heizung/daemmung${s ? `?${s}` : ''}`);
  };

  /**
   * Der Nachweis als PDF.
   *
   * Ohne Gruppe die Übersicht, mit Gruppe der Einzelnachweis mit dem
   * vollständigen Rechenweg — Stück für Stück, so wie er geprüft wird.
   */
  heizungBerichtPdf = (projektId: string, gruppe?: string) =>
    this.pdfLaden(
      `/api/projects/${projektId}/heizung/berechnung/bericht.pdf`
      + (gruppe ? `?gruppe=${encodeURIComponent(gruppe)}` : ''));

  /**
   * Das Anlagenschema Heizung und Kälte.
   *
   * Die Erzeugerseite: Wärmeübertrager, Kältemaschinen, Rückkühler, Speicher
   * und Verteiler, dazu die abgehenden Kreise mit Förderhöhe ist und
   * korrigiert. Es beantwortet die Frage nach der ERZEUGUNG und braucht
   * deshalb keine Gruppe.
   */
  heizungAnlagenschemaPdf = (projektId: string) =>
    this.pdfLaden(
      `/api/projects/${projektId}/heizung/anlagenschema.pdf`);

  /**
   * Das Funktionsschema EINES Kreises.
   *
   * Die Schaltung: Verteiler, Pumpe, Regelarmatur, Verbraucher, Rücklauf —
   * Vorlauf durchgezogen, Rücklauf gestrichelt — mit den Auslegungsdaten von
   * der Leistung bis zur Ventilvoreinstellung. Das Blatt für den
   * Regeltechniker.
   */
  heizungFunktionsschemaPdf = (projektId: string, gruppe: string) =>
    this.pdfLaden(
      `/api/projects/${projektId}/heizung/funktionsschema.pdf`
      + `?gruppe=${encodeURIComponent(gruppe)}`);

  /**
   * Das Strangschema EINES Kreises.
   *
   * Der Netzbaum von der Quelle bis zu jedem Verbraucher. Jedes Bauteil steht
   * einmal, an der Stelle, an der es hängt — nicht je Verbraucher wiederholt.
   */
  heizungStrangschemaPdf = (projektId: string, gruppe: string) =>
    this.pdfLaden(
      `/api/projects/${projektId}/heizung/strangschema.pdf`
      + `?gruppe=${encodeURIComponent(gruppe)}`);

  /**
   * Die Bauteile, seitenweise.
   *
   * Schlank und ohne Werte: Viertausend Bauteile mit je dreißig Werten sprengen
   * den nginx-Puffer um ein Vielfaches.
   */
  /**
   * Die Bauteile als Baum — nur Struktur und Zahlen.
   *
   * Die Blätter kommen über heizungBauteile auf Abruf: 2.750 Bauteile in einem
   * Baum sprengen den nginx-Puffer von 65.536 Byte um ein Vielfaches.
   */
  heizungBaum = (projektId: string, nach: 'netz' | 'raum', kreis?: string) =>
    this.hole<Heizungbaum>(
      `/api/projects/${projektId}/heizung/baum?nach=${nach}`
      + (kreis ? `&kreis=${encodeURIComponent(kreis)}` : ''));

  heizungBauteile = (projektId: string, filter: {
    bauteilart?: string; raum?: string; geschoss?: string; kreis?: string;
    netz?: string; suche?: string;
    auchEntfernte?: boolean; seite?: number; proSeite?: number;
  } = {}) => {
    const frage = new URLSearchParams();

    for (const [k, v] of Object.entries(filter))
      if (v !== undefined && v !== '') frage.set(k, String(v));

    return this.hole<Heizungseite>(
      `/api/projects/${projektId}/heizung/bauteile`
      + (frage.toString() ? `?${frage}` : ''));
  };

  heizungBauteil = (id: string) =>
    this.hole<Heizungbauteil>(`/api/heizung/bauteile/${id}`);

  heizungWerteSpeichern = (id: string, werte: Record<string, string | null>) =>
    this.schicke<{ geaendert: number; message: string }>(
      `/api/heizung/bauteile/${id}/werte`, 'PUT', { werte });

  /**
   * Herstellerdaten als JSON einlesen.
   *
   * Zwei Gänge: erst prüfen, dann übernehmen. Ein Import über tausend Bauteile,
   * der zur Hälfte danebentrifft, ist schwerer zu berichtigen als einer, der gar
   * nicht erst lief.
   */
  heizungProduktimport = async (
    projektId: string, datei: File, nurPruefen: boolean,
  ) => {
    const koerper = new FormData();
    koerper.append('datei', datei, datei.name);
    koerper.append('nurPruefen', nurPruefen ? 'true' : 'false');

    const antwort = await this.abrufen(
      `/api/projects/${projektId}/heizung/produktimport`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await this.token()}` },
        body: koerper,
      });

    return antwort.json() as Promise<{
      nurPruefen: boolean; eintraege: number; getroffen: number;
      ohneTreffer: number; werte: number; hinweise: string[]; message: string;
    }>;
  };

  /**
   * Die Bauteiltypen für die Datenblattpflege.
   *
   * Nach Typ gruppiert und nicht nach Bauteil: 304 Ventilatorkonvektoren desselben
   * Typs teilen sich ein Datenblatt.
   */
  heizungTypen = (projektId: string, filter: {
    bauteilart?: string; nurOhneDatenblatt?: boolean;
  } = {}) => {
    const frage = new URLSearchParams();
    for (const [k, v] of Object.entries(filter))
      if (v !== undefined && v !== '') frage.set(k, String(v));

    return this.hole<Heizungtyp[]>(
      `/api/projects/${projektId}/heizung/typen`
      + (frage.toString() ? `?${frage}` : ''));
  };

  /**
   * Ein Datenblatt hochladen und der ganzen Gruppe zuweisen.
   *
   * Die Plattform legt das PDF ab, bildet einen anonymen Verweis und schreibt ihn
   * an alle Bauteile derselben Familie und desselben Typs — 304
   * Ventilatorkonvektoren teilen sich eines.
   */
  heizungDatenblattHochladen = async (
    projektId: string, datei: File, familie: string | null, typ: string | null,
  ) => {
    const koerper = new FormData();
    koerper.append('datei', datei, datei.name);
    koerper.append('familie', familie ?? '');
    koerper.append('typ', typ ?? '');

    const antwort = await this.abrufen(
      `/api/projects/${projektId}/heizung/datenblatt-hochladen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await this.token()}` },
        body: koerper,
      });

    return antwort.json() as Promise<{
      datasheetId: string; betroffen: number; verweis: string;
      groesse: number; message: string;
    }>;
  };

  heizungDatenblattZuweisen = (
    projektId: string, datasheetId: string, familie: string | null,
    typ: string | null,
  ) =>
    this.schicke<{ betroffen: number; verweis: string; message: string }>(
      `/api/projects/${projektId}/heizung/datenblatt-zuweisen`, 'POST',
      { datasheetId, familie, typ });

  heizungHerstellerUrl = (
    projektId: string, familie: string | null, typ: string | null, url: string,
  ) =>
    this.schicke<{ betroffen: number; message: string }>(
      `/api/projects/${projektId}/heizung/hersteller-url`, 'PUT',
      { familie, typ, url });

  // --- Produktdaten ---------------------------------------------------------

  heizungProduktreihen = (projektId: string) =>
    this.hole<Heizungproduktreihe[]>(
      `/api/projects/${projektId}/heizung/produktreihen`);

  /** Einen Produktkatalog als JSON einlesen. */
  heizungKatalogEinlesen = async (projektId: string, datei: File) => {
    const koerper = new FormData();
    koerper.append('datei', datei, datei.name);

    const antwort = await this.abrufen(
      `/api/projects/${projektId}/heizung/produktreihen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await this.token()}` },
        body: koerper,
      });

    return antwort.json() as Promise<{
      id: string; baugroessen: number; hinweise: string[]; message: string;
    }>;
  };

  /**
   * Eine Produktreihe einer Bauteilgruppe zuweisen.
   *
   * Zwischen den Baugrößen wird interpoliert, wenn die Reihe es erlaubt. Erst
   * prüfen, dann übernehmen — die Prüfung ändert nichts.
   */
  heizungReiheZuweisen = (
    projektId: string, reiheId: string, familie: string | null,
    typ: string | null, nurPruefen: boolean,
  ) =>
    this.schicke<{
      bauteile: number; zugewiesen: number; zuPruefen: number;
      werte: number; interpoliert: number; hinweise: string[]; message: string;
    }>(`/api/projects/${projektId}/heizung/produktreihe-zuweisen`, 'POST',
       { reiheId, familie, typ, nurPruefen });

  heizungNetze = (projektId: string) =>
    this.hole<Heizungnetz[]>(`/api/projects/${projektId}/heizung/netze`);

  /**
   * Wasserseitige AHU-Anschlusspunkte anhand ihrer Modelllage mit den
   * Lüftungsanlagen verbinden. Standardmäßig wird nur geprüft.
   */
  heizungLueftungsbezug = (
    projektId: string, probe = true, grenzeMm = 15000,
  ) => this.sende<LueftungsbezugErgebnis>(
    `/api/projects/${projektId}/heizung/lueftungsbezug`
    + `?probe=${probe}&grenzeMm=${grenzeMm}`, {});

  // --- Planstaende ----------------------------------------------------------

  /** Die Planstaende aller Modelle eines Projekts. */
  planuebersicht = (projektId: string) =>
    this.hole<Planmodellzeile[]>(`/api/projects/${projektId}/plaene`);

  modellplaene = (modelId: string, nurListe = false) =>
    this.hole<Planblatt[]>(
      `/api/models/${modelId}/plaene${nurListe ? '?nurListe=true' : ''}`);

  modellrevisionen = (modelId: string) =>
    this.hole<Planrevision[]>(`/api/models/${modelId}/revisionen`);

  modelluploads = (modelId: string) =>
    this.hole<Modellupload[]>(`/api/models/${modelId}/uploads`);

  revisionAendern = (id: string, werte: {
    datum?: string; beschreibung?: string; ersteller?: string; ausgegeben?: boolean;
  }) => this.schicke(`/api/revisionen/${id}`, 'PATCH', werte);

  revisionAnlegen = (modelId: string, werte: {
    datum: string; beschreibung?: string; ersteller?: string; ausgegeben?: boolean;
  }) => this.schicke(`/api/models/${modelId}/revisionen`, 'POST', werte);

  /** Freigeben heißt: Der Stand der App gilt, das Modell wird angeglichen. */
  /**
   * Einen Indexstand fixieren.
   *
   * Bis dahin ist er ein Entwurf und wird NICHT ins Modell geschrieben. In der
   * Bearbeitung ändert sich noch oft etwas.
   */
  revisionFixieren = (id: string) =>
    this.schicke<{ message: string }>(`/api/revisionen/${id}/fixieren`, 'POST', {});

  revisionLoesen = (id: string) =>
    this.schicke<{ message: string }>(`/api/revisionen/${id}/loesen`, 'POST', {});

  /** Nur aus der Plattform — im Modell bleibt der Stand stehen. */
  revisionEntfernen = (id: string) =>
    this.schicke<{ message: string }>(`/api/revisionen/${id}`, 'DELETE', {});

  uploadFixieren = (id: string) =>
    this.schicke<{ message: string }>(`/api/uploads/${id}/fixieren`, 'POST', {});

  uploadLoesen = (id: string) =>
    this.schicke<{ message: string }>(`/api/uploads/${id}/loesen`, 'POST', {});

  revisionFreigeben = (id: string) =>
    this.schicke(`/api/revisionen/${id}/freigeben`, 'POST', {});

  planFreigeben = (id: string) =>
    this.schicke(`/api/plaene/${id}/freigeben`, 'POST', {});

  /**
   * Alle Pläne in die Planliste aufnehmen oder entfernen.
   *
   * nurRevitliste=true beschränkt es auf die, die Revit in seiner Liste führt — der
   * häufigste Ausgangspunkt.
   */
  planlisteAuswahl = (
    modelId: string, aufnehmen: boolean, nurRevitliste?: boolean,
  ) => this.schicke<{ geaendert: number; inPlanliste: number; message: string }>(
    `/api/models/${modelId}/planliste/auswahl`, 'POST',
    { aufnehmen, nurRevitliste });

  /**
   * Ein Projektbild hochladen.
   *
   * Erscheint auf dem Dashboard rechts oben. Nur PNG oder JPEG — andere Formate kann
   * die PDF-Erzeugung nicht einbinden.
   */
  projektbildHochladen = async (projektId: string, datei: File) => {
    const koerper = new FormData();
    koerper.append('datei', datei, datei.name);

    const antwort = await this.abrufen(`/api/projects/${projektId}/bild`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${await this.token()}` },
      body: koerper,
    });

    return antwort.json() as Promise<{
      bildDateiname: string; bildGroesse: number; message: string;
    }>;
  };

  projektbildEntfernen = (projektId: string) =>
    this.schicke<{ message: string }>(`/api/projects/${projektId}/bild`, 'DELETE', {});

  /**
   * Das Modell-Dashboard als PDF, A3 quer.
   *
   * Zum Ansehen und Prüfen. Auf die Startansicht setzt es das Add-in — dort wird es
   * verknüpft eingefügt, damit die Platzierung beim Erneuern bleibt.
   */
  dashboardLaden = (modelId: string, format: 'a3' | 'a1' | 'a0' = 'a1') =>
    this.dateiLaden(`/api/models/${modelId}/dashboard.pdf?format=${format}`,
      `Dashboard_${format.toUpperCase()}.pdf`);

  /** Die Planliste als PDF oder Tabelle. */
  planlisteLaden = (modelId: string, art: 'pdf' | 'xlsx') =>
    this.dateiLaden(`/api/models/${modelId}/planliste.${art}`,
      `Planliste.${art}`);

  planAendern = (id: string, werte: {
    planname?: string; plandatum?: string; freigabestatus?: string;
    bauwerksteil?: string; bereich?: string; massstab?: string; bearbeiter?: string;
    inPlanliste?: boolean;
  }) => this.schicke(`/api/plaene/${id}`, 'PATCH', werte);

  schluesselVergeben = (modelId: string) =>
    this.schicke<{ modellschluessel: string; message: string }>(
      `/api/models/${modelId}/schluessel`, 'POST', {});

  uploadAnlegen = (modelId: string, werte: {
    datum?: string; leistungsphase?: string; ziel?: string;
    zielHinweis?: string; bemerkung?: string;
  }) => this.schicke(`/api/models/${modelId}/uploads`, 'POST', werte);

  uploadAendern = (id: string, werte: {
    datum?: string; leistungsphase?: string; ziel?: string;
    zielHinweis?: string; bemerkung?: string;
  }) => this.schicke(`/api/uploads/${id}`, 'PATCH', werte);

  uploadEntfernen = (id: string) =>
    this.schicke(`/api/uploads/${id}`, 'DELETE', {});

  // --- Anlagenimporte -------------------------------------------------------

  /**
   * Ein Anlagendokument prüfen.
   *
   * Ohne Ablage: Ein Anlagendokument gehört zu einer Anlage, und die ist hier noch
   * nicht gewählt. Es abzulegen und die Zuordnung nachzuholen würde Fassungen ohne
   * Datenblatt hinterlassen.
   */
  async anlagePruefen(datei: File) {
    const inhalt = new FormData();
    inhalt.append('datei', datei);

    const antwort = await fetch('/api/plant-imports/pruefen', {
      method: 'POST',
      headers: { Authorization: `Bearer ${await this.token()}` },
      body: inhalt,
    });

    const k = await antwort.json().catch(() => null);

    if (!antwort.ok && !k?.fehler) throw new Error(k?.message ?? `HTTP ${antwort.status}`);

    return k as Anlagenpruefung;
  }

  /**
   * Vorschau oder Übernahme — dieselbe Rechnung, ein Schalter.
   *
   * Zwei Endpunkte hätten zwei Fassungen derselben Rechnung bedeutet, und die wären
   * nach der dritten Änderung auseinandergelaufen.
   */
  async anlageUebernehmen(
    datei: File, projektId: string, plantId: string,
    schreiben: boolean, ueberschreiben = false,
  ) {
    const inhalt = new FormData();
    inhalt.append('datei', datei);
    inhalt.append('projectId', projektId);
    inhalt.append('plantId', plantId);
    inhalt.append('schreiben', String(schreiben));
    inhalt.append('ueberschreiben', String(ueberschreiben));

    const antwort = await fetch('/api/plant-imports/uebernehmen', {
      method: 'POST',
      headers: { Authorization: `Bearer ${await this.token()}` },
      body: inhalt,
    });

    const k = await antwort.json().catch(() => null);

    if (!antwort.ok) throw new Error(k?.message ?? `HTTP ${antwort.status}`);

    return k;
  }

  /** Alle Lüftungsanlagen des Projekts mit ihren Unterlagen. */
  anlagenliste = (projektId: string) =>
    this.hole<Anlagenzeile[]>(
      `/api/plant-imports/anlagen?projectId=${projektId}`);

  /**
   * Datenblatt oder Geräteskizze einer Anlage ablegen.
   *
   * Beim Hochladen wird der gleichbleibende, signierte Verweis gebildet und in das
   * Anlagenfeld geschrieben — nicht die Adresse des Objektspeichers, die läuft nach
   * Minuten ab.
   */
  async anlageUnterlage(plantId: string, datei: File, art: 'datenblatt' | 'skizze') {
    const inhalt = new FormData();
    inhalt.append('datei', datei);

    const antwort = await fetch(
      `/api/plant-imports/${plantId}/unterlage?art=${art}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${await this.token()}` },
        body: inhalt,
      });

    const k = await antwort.json().catch(() => null);

    if (!antwort.ok) throw new Error(k?.message ?? `HTTP ${antwort.status}`);

    return k as {
      id: string; art: string; dateiname: string; groesse: number;
      verweis: string | null; message: string;
    };
  }

  anlagenfassungen = (plantId: string) =>
    this.hole<Anlagenfassungen>(`/api/plant-imports/revisionen/${plantId}`);

  /** Die Zuordnungen je Bauteiltyp — die Arbeitsliste der JSON-Seite. */
  importZuordnungen = (projektId: string) =>
    this.hole<Zuordnungsliste>(
      `/api/datasheet-imports/zuordnungen?projectId=${projektId}`);

  /** Die Bauteiltypen des Projekts. */
  importTypen = (projektId: string, suche?: string) => {
    const s = new URLSearchParams({ projectId: projektId });
    if (suche) s.set('suche', suche);

    return this.hole<Importtyp[]>(`/api/datasheet-imports/typen?${s}`);
  };

  /**
   * Die Bauteile über ihre Luftmenge gegen die Tabelle abgleichen.
   *
   * Jedes Bauteil hat seine eigene geplante Luftmenge — bei 639 Tellerventilen
   * wäre eine einzige Auswahl für alle falsch.
   */
  importAbgleich = (
    id: string, projektId: string, familie: string, typ: string,
    tabelle: string, sizeKey: string | null,
    bedingungen: Record<string, number>,
    schreiben: boolean, ueberschreiben: boolean,
  ) => this.sende<Importabgleich>(`/api/datasheet-imports/${id}/abgleich`,
        { projectId: projektId, familie, typ, tabelle, sizeKey,
          bedingungen, schreiben, ueberschreiben });

  /** Was sich seit dem letzten Datenblattabgleich geändert hat. */
  abgleichstand = (projektId: string) =>
    this.hole<Abgleichstand>(`/api/projects/${projektId}/ventilation/abgleichstand`);

  /** Bezug entfernter Bauteile zum Datenblatt lösen. */
  abgleichAufraeumen = (projektId: string) =>
    this.sende<{ geloest: number; message: string }>(
      `/api/projects/${projektId}/ventilation/abgleich-aufraeumen`, {});

  /** Bauteile mit Befund aus dem Datenblattabgleich. */
  bauteilbefunde = (
    projektId: string, zustand?: string, familie?: string, typ?: string,
  ) => {
    const s = new URLSearchParams();
    if (zustand) s.set('zustand', zustand);
    // Nach Typ eingeschränkt: So kann die Zuordnungsseite die Befunde einer Zeile
    // zeigen, ohne die ganze Liste zu laden.
    if (familie) s.set('familie', familie);
    if (typ) s.set('typ', typ);

    return this.hole<Bauteilbefund>(
      `/api/projects/${projektId}/ventilation/fehler?${s}`);
  };

  befundGeprueft = (bauteilId: string) =>
    this.sende<{ id: string; fehlerZustand: string }>(
      `/api/ventilation/components/${bauteilId}/fehler-geprueft`, {});

  importVorschau = (
    id: string, projektId: string, familie: string, typ: string,
    auswahl: Importauswahl,
  ) => this.sende<Importvorschau>(`/api/datasheet-imports/${id}/vorschau`,
        { projectId: projektId, familie, typ, auswahl, ueberschreiben: false });

  importUebernehmen = (
    id: string, projektId: string, familie: string, typ: string,
    auswahl: Importauswahl, ueberschreiben: boolean,
  ) => this.sende<{
    datasheetId: string; uebernommen: number; offen: number;
    zustand: string; message: string;
  }>(`/api/datasheet-imports/${id}/uebernehmen`,
     { projectId: projektId, familie, typ, auswahl, ueberschreiben });

  datenblaetter = (projektId: string, zustand?: string) => {
    const suche = new URLSearchParams({ projectId: projektId });
    if (zustand) suche.set('zustand', zustand);

    return this.hole<DatenblattStand>(`/api/datasheets?${suche}`);
  };

  /**
   * Datensatz holen oder anlegen.
   *
   * Mit plantId beschreibt das Datenblatt ein Gerät dieses Projekts; ohne gilt
   * es für alle Bauteile derselben Familie und desselben Typs.
   */
  datenblattFinden = (
    familie: string, typ: string, projektId?: string, plantId?: string,
  ) => this.sende<Datenblatt>('/api/datasheets/finden',
        { familie, typ, projectId: projektId ?? null, plantId: plantId ?? null });

  /**
   * Ein Datenblatt als PDF ablegen.
   *
   * Über FormData, nicht als JSON: Ein PDF von mehreren Megabyte als Base64 zu
   * schicken wäre ein Drittel größer und läge schneller an der Grenze.
   */
  async datenblattDatei(id: string, datei: File, gewerk = 'lueftung') {
    const inhalt = new FormData();
    inhalt.append('datei', datei);

    const antwort = await fetch(
      `/api/datasheets/${id}/datei?gewerk=${encodeURIComponent(gewerk)}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${await this.token()}` },
        body: inhalt,
      });

    if (!antwort.ok) {
      throw new Error(await fehlertext(antwort));
    }

    return antwort.json() as Promise<Datenblattablage>;
  }

  /**
   * Die Datei auf alle Typen derselben Familie verteilen.
   *
   * Ein TROX-Datenblatt deckt die Baureihe ab — RN-D/80, RN-D/100 und RN-D/160
   * stehen in einem PDF. Geteilt wird nur die Datei, nicht die Auswertung: Die
   * Werte je Baugröße unterscheiden sich, und jeder Typ wird einzeln ausgewertet.
   */
  datenblattDateiVerteilen = (
    id: string, projektId: string,
    einstellungen: { ueberschreiben: boolean; beauftragen: boolean },
  ) => this.sende<{
    familie: string; typen: number; verteilt: number;
    angelegt: number; uebersprungen: number; beauftragt: number;
  }>(`/api/datasheets/${id}/datei-verteilen`,
     { projectId: projektId, ...einstellungen });

  /**
   * Vorschläge auf die Bauteile übernehmen.
   *
   * Feldweise und ausdrücklich: Nur die angehakten Schlüssel werden gesetzt. Mit
   * nurZaehlen=true nennt der Aufruf die Zahl der betroffenen Bauteile, ohne
   * etwas zu schreiben.
   */
  datenblattUebernehmen = (
    id: string,
    daten: {
      projectId: string; componentId?: string; felder: string[];
      /** Nur nötig, wo unter Alternativen gewählt wurde. */
      werte?: Record<string, string>;
      ueberschreiben: boolean; nurZaehlen: boolean;
    },
  ) => this.sende<{ betroffen: number; uebersprungen?: number; felder: number }>(
    `/api/datasheets/${id}/uebernehmen`, daten);

  /** In die Warteschlange stellen. art ist pdf oder netz. */
  datenblattBeauftragen = (id: string, art: 'pdf' | 'netz') =>
    this.sende<{ bereits: boolean; zustand: string; wartetSeit: string | null }>(
      `/api/datasheets/${id}/beauftragen`, { art });

  /**
   * Alle Typen des Projekts einstellen.
   *
   * Mit art=pdf werden nur die beauftragt, zu denen eine Datei vorliegt - die
   * übrigen bleiben offen und warten auf den Upload.
   */
  datenblattSammelauftrag = (
    projektId: string, art: 'pdf' | 'netz', wiederholen: boolean,
  ) => this.sende<{
    typen: number; angelegt: number; beauftragt: number;
    uebersprungen: number; dauerMinuten: number;
  }>('/api/datasheets/sammelauftrag',
     { projectId: projektId, art, wiederholen });

  lueftungVorlagen = () =>
    this.hole<LueftungVorlage[]>('/api/ventilation/templates');

  /**
   * Feldkatalog, eingeschränkt auf Bauart und Bereich.
   *
   * Für die Vorlagenpflege: Dort gibt es kein Bauteil, an dem die Bauart hängt —
   * die Vorlage nennt sie selbst.
   */
  lueftungFelder = (aksCode?: string, bereich?: string) => {
    const suche = new URLSearchParams();
    if (aksCode) suche.set('aksCode', aksCode);
    if (bereich) suche.set('bereich', bereich);

    return this.hole<LueftungFeld[]>(`/api/ventilation/fields?${suche}`);
  };

  lueftungVorlageAendern = (
    id: string,
    daten: { name?: string; beschreibung?: string; werte?: Record<string, string> },
  ) => this.schicke<void>(`/api/ventilation/templates/${id}`, 'PUT', daten);

  /**
   * Eine Vorlage anwenden.
   *
   * Mit nurZaehlen=true nennt der Aufruf nur die Zahl der betroffenen Bauteile -
   * die Vorschau vor dem Klick.
   */
  lueftungVorlageAnwenden = (
    vorlageId: string,
    daten: {
      projectId: string; plantId?: string; systemId?: string; ebene?: string;
      ueberschreiben: boolean; nurZaehlen: boolean;
    },
  ) => this.sende<{ vorlage: string; betroffen: number; felder: number; uebersprungen: number }>(
    `/api/ventilation/templates/${vorlageId}/apply`, daten);

  lueftungSpeichern = (id: string, werte: Record<string, string>) =>
    this.schicke<void>(`/api/ventilation/components/${id}/values`, 'PUT', werte);

  /**
   * Bestand als Tabelle, je Anlage, Verbundnetz oder Geschoss.
   *
   * Ohne Einschraenkung kaeme der Gesamtbestand - eine Datei mit 6622 Zeilen
   * wird aber nicht gepflegt, sondern ueberschrieben.
   */
  lueftungExcel = (
    projektId: string,
    dateiname: string,
    umfang?: Bauteilumfang,
  ) => {
    const suche = umfangSuche(umfang ?? {});

    const frage = suche.toString();

    return this.dateiLaden(
      `/api/projects/${projektId}/ventilation/excel${frage ? '?' + frage : ''}`,
      dateiname);
  };

  /** Tabelle einlesen. Mit uebernehmen=false nur die Vorschau. */
  async lueftungEinlesen(projektId: string, datei: File, uebernehmen: boolean) {
    const inhalt = new FormData();
    inhalt.append('datei', datei);
    inhalt.append('uebernehmen', uebernehmen ? 'true' : 'false');

    const antwort = await fetch(`/api/projects/${projektId}/ventilation/excel`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${await this.token()}` },
      body: inhalt,
    });

    if (!antwort.ok) {
      throw new Error(await fehlertext(antwort));
    }

    return antwort.json() as Promise<{ uebernommen: boolean; vorschau: ExcelVorschau }>;
  }

  /**
   * Setzt die Fachbereichsrollen eines Modells vollständig neu.
   *
   * Vollständig und nicht einzeln geschaltet: Bei einer Kreuztabelle geht der
   * Zustand einer Zeile als Ganzes hin, und ein verlorenes Ereignis hinterlässt
   * keinen halben Stand.
   */
  modellRollen = (projektId: string, modellId: string, rollen: string[]) =>
    this.schicke<void>(
      `/api/projects/${projektId}/models/${modellId}/roles`, 'PUT', { rollen });

  // --- Häuser (F60 H2b) ------------------------------------------------------

  /** Die Häuser des Projekts mit Modell- und Quellenzahl je Haus (F60 H1/H2). */
  haeuser = (projektId: string) =>
    this.hole<Haus[]>(`/api/projects/${projektId}/haeuser`);

  /** Haus anlegen (unbekannte Nummer) oder ändern. Das Standardhaus lässt der Server nicht stilllegen. */
  hausSetzen = (projektId: string, nummer: number, daten: HausEingabe) =>
    this.schicke<{ id: string; nummer: number }>(
      `/api/projects/${projektId}/haeuser/${nummer}`, 'PUT', daten);

  /** Haus eines Modells setzen; `null` = Standardhaus. */
  modellHausSetzen = (projektId: string, modellId: string, hausId: string | null) =>
    this.schicke<void>(
      `/api/projects/${projektId}/models/${modellId}/haus`, 'PUT', { hausId });

  projektAnlegen = (daten: { number: string; name: string; accRegion?: string }) =>
    this.sende<{ id: string }>('/api/projects', daten);

  projektAendern = (id: string, daten: { name?: string; accRegion?: string; isActive?: boolean }) =>
    this.schicke(`/api/projects/${id}`, 'PUT', daten);

  geoSpeichern = (id: string, daten: GeoEinstellungen) =>
    this.schicke(`/api/projects/${id}/geo`, 'PUT', daten);

  modellAnlegen = (projektId: string, daten: ModellEingabe) =>
    this.sende<{ id: string }>(`/api/projects/${projektId}/models`, daten);

  /** Aufträge und Dateien eines Modells, nach Exportart gruppiert. */
  modellExporte = (projektId: string, modellId: string) =>
    this.hole<Exportgruppe[]>(`/api/projects/${projektId}/models/${modellId}/exports`);

  modellAendern = (projektId: string, modellId: string, daten: Partial<ModellEingabe>) =>
    this.schicke(`/api/projects/${projektId}/models/${modellId}`, 'PUT', daten);

  modellStilllegen = (projektId: string, modellId: string) =>
    this.schicke(`/api/projects/${projektId}/models/${modellId}`, 'DELETE');

  /**
   * Die Sandbox-Kopien eines Projekts, je Quellmodell nach Stand absteigend.
   *
   * Zeigt, von welchem Modell eine Kopie stammt und ob sich das ACC-Modell
   * seither NACHWEISLICH weiterbewegt hat - `veraltetUnbekannt` bleibt in
   * jedem anderen Fall wahr, statt "aktuell" zu behaupten.
   */
  sandboxmodelle = (projektId: string) =>
    this.hole<Sandboxmodell[]>(`/api/projects/${projektId}/sandboxmodelle`);

  konfigDateien = (art?: string) =>
    this.hole<KonfigDatei[]>(`/api/config-files${art ? `?kind=${art}` : ''}`);

  /**
   * Laedt eine Konfigurationsdatei hoch.
   *
   * Ueber FormData, nicht als JSON - der Inhalt ist eine Datei. Der
   * Content-Type wird bewusst nicht gesetzt: Der Browser ergaenzt ihn samt
   * Trennzeichen, und ein selbst gesetzter Wert macht die Anfrage unlesbar.
   */
  // --- Maschinen ---------------------------------------------------------

  maschinenVerwalten = () => this.hole<Maschinenbestand>('/api/agents');

  arbeitsstationen = () =>
    this.hole<Arbeitsstationen>('/api/arbeitsplaetze');

  /**
   * Nicht gesperrte Arbeitsplaetze zur Auswahl beim Anlegen eines Auftrags.
   *
   * Eigener, schmalerer Aufruf als {@link arbeitsstationen}: jeder, der
   * einen Auftrag anlegen darf (Policies.Export), nicht nur die Verwaltung
   * (Policies.Administer) - Arbeitsplatz-Auswahl ist keine Verwaltungshandlung.
   */
  arbeitsplaetzeVerfuegbar = () =>
    this.hole<ArbeitsplatzAuswahl[]>('/api/arbeitsplaetze/verfuegbar');

  arbeitsstationAnlegen = (daten: {
    name: string; benutzer?: string; revitJahr?: string; bemerkung?: string;
  }) =>
    this.sende<Arbeitsstationsausstattung>('/api/arbeitsplaetze', daten);

  /**
   * Erzeugt einen neuen Schluessel UND die fertige Einstellungsdatei.
   *
   * Beides in einem Aufruf, weil der Schluessel danach nicht mehr
   * abrufbar ist -- gespeichert wird nur sein Hashwert.
   */
  arbeitsstationSchluessel = (id: string) =>
    this.sende<Arbeitsstationsausstattung>(
      `/api/arbeitsplaetze/${id}/schluessel`, {});

  /**
   * Laedt Helfer, fertige einstellungen.json und Anleitung als EIN Paket.
   *
   * Der Schluessel geht im Koerper mit und nicht in der Adresse: Adressen
   * landen in Zugriffsprotokollen. Aus der Datenbank ginge er ohnehin nicht --
   * dort steht nur sein Hashwert.
   */
  arbeitsstationPaket = async (
    id: string, name: string, schluessel: string,
  ) => {
    const antwort = await this.abrufen(`/api/arbeitsplaetze/${id}/paket`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await this.token()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ schluessel }),
    });

    if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`);

    const daten = await antwort.blob();
    const adresse = URL.createObjectURL(daten);
    const verweis = document.createElement('a');

    verweis.href = adresse;
    verweis.download = `TuvAddinAktualisierer-${name}.zip`;
    verweis.click();

    URL.revokeObjectURL(adresse);
  };

  arbeitsstationSchalten = (
    id: string, was: 'sperren' | 'freigeben' | 'loeschen',
  ) =>
    was === 'loeschen'
      ? this.schicke(`/api/arbeitsplaetze/${id}`, 'DELETE')
      : this.sende<void>(`/api/arbeitsplaetze/${id}/${was}`, {});

  maschineAnlegen = (daten: {
    name: string; revitVersions?: string; note?: string;
    /** MASCH-1 (F209 E1/E2): ohne Angabe REVIT und PRODUKTIV. */
    maschinenart?: Maschinenart; betriebsstufe?: Betriebsstufe;
  }) =>
    this.sende<NeueMaschine>('/api/agents', daten);

  schluesselErneuern = (id: string) =>
    this.sende<NeueMaschine>(`/api/agents/${id}/schluessel`, {});

  maschineAendern = (id: string, daten: {
    revitVersions?: string; note?: string; deaktiviert?: boolean; betriebsstufe?: Betriebsstufe;
  }) =>
    this.schicke(`/api/agents/${id}`, 'PUT', daten);

  maschineEntfernen = (id: string) => this.schicke(`/api/agents/${id}`, 'DELETE');

  // MASCH-1 (F208/F209 E9): Kontingente je Projekt (durchgesetzt) oder Mandant (vorbereitet).
  maschinenKontingente = () => this.hole<MaschinenKontingent[]>('/api/agents/kontingente');

  kontingentAnlegen = (daten: {
    projectId?: string | null; mandant?: string | null; maschinenart?: Maschinenart | null;
    hoechstGleichzeitig: number; notiz?: string | null;
  }) => this.sende<{ id: string }>('/api/agents/kontingente', daten);

  kontingentAendern = (id: string, daten: { hoechstGleichzeitig?: number; notiz?: string | null }) =>
    this.schicke(`/api/agents/kontingente/${id}`, 'PUT', daten);

  kontingentEntfernen = (id: string) => this.schicke(`/api/agents/kontingente/${id}`, 'DELETE');

  // --- Installationspakete -----------------------------------------------

  pakete = () => this.hole<PaketUebersicht>('/api/downloads');

  /**
   * Laedt das Agentenpaket mit eingetragenem Schluessel.
   *
   * Als POST, damit der Schluessel nicht in der Adresse steht - dort landete er
   * in Server- und Browserprotokollen.
   */
  async agentPaketLaden(maschinenId: string, schluessel: string | null, name: string) {
    const antwort = await this.abrufen('/api/downloads/agent', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await this.token()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ maschinenId, schluessel }),
    });

    if (!antwort.ok) {
      throw new Error(await fehlertext(antwort));
    }

    // Der Browser bekommt einen Blob; ein unsichtbarer Verweis loest das
    // Speichern aus.
    const daten = await antwort.blob();
    const adresse = URL.createObjectURL(daten);
    const verweis = document.createElement('a');
    verweis.href = adresse;
    verweis.download = `TuvExportAgent_${name}.zip`;
    verweis.click();
    URL.revokeObjectURL(adresse);
  }

  /**
   * Laedt eine Datei ueber einen GET-Endpunkt herunter.
   *
   * Ein einfacher Verweis im Browser sendet kein Token - die Anfrage wuerde mit
   * 401 abgewiesen. Deshalb der Umweg ueber fetch und einen Blob.
   */
  async dateiLaden(pfad: string, dateiname: string) {
    const antwort = await this.abrufen(pfad, {
      headers: { Authorization: `Bearer ${await this.token()}` },
    });

    if (!antwort.ok) {
      throw new Error(await fehlertext(antwort));
    }

    const daten = await antwort.blob();
    const adresse = URL.createObjectURL(daten);
    const verweis = document.createElement('a');
    verweis.href = adresse;
    // Der Name kommt vom Server, wenn er einen schickt: Die Berichte tragen
    // die Dokumentnummer vorn, und genau dieser Name steht in der
    // Registerzeile. Der uebergebene Name ist nur der Rueckfall.
    verweis.download = dateinameAusAntwort(antwort, dateiname);
    verweis.click();
    URL.revokeObjectURL(adresse);
  }

  // --- Raumbuch -----------------------------------------------------------

  raumfelder = () => this.hole<Raumfeld[]>('/api/room-fields');

  /** F60 H2b: mit `haus` nur die Räume dieses Hauses (`?haus=NN`); ohne wie bisher alle. */
  raeume = (projektId: string, haus?: number | null) =>
    this.hole<RaumbuchDaten>(`/api/projects/${projektId}/rooms${hausAbfrage(haus)}`);

  /**
   * Raumwerte speichern. Mit `vorher` (TR1) nur die geänderten Felder, je Feld
   * mit dem Wert, den die Seite gesehen hat — steht dort inzwischen etwas
   * anderes, antwortet der Server 409 und schreibt nichts.
   */
  raumSpeichern = (
    id: string,
    werte: Record<string, string | null>,
    vorher?: Record<string, string | null>,
  ) => this.schicke(`/api/rooms/${id}`, 'PUT', vorher ? { werte, vorher } : { werte });

  /**
   * Der Raumgrundriss als SVG-TEXT — nicht als Objekt-URL.
   *
   * Die MSR-Schemen holen ihr SVG als Blob und haengen es in ein `<img>`.
   * Hier geht das NICHT, und zwar aus zwei Gruenden: In einem `<img>` sind
   * die `<title>`-Elemente tot (kein Hover je Bauteil), und eine CSS-Regel
   * der Seite erreicht das Dokument im Bild nicht (kein Gewerkeumschalter).
   * Eingebettet erreicht beides sein Ziel — ohne zweiten Abruf je Schalter.
   */
  raumGrundrissSvg = async (projektId: string, roomId: string): Promise<string> => {
    const antwort = await this.abrufen(
      `/api/projects/${projektId}/raumbuch/raeume/${roomId}/grundriss.svg`,
      { headers: { Authorization: `Bearer ${await this.token()}` } },
    );

    if (!antwort.ok) throw new Error(await fehlertext(antwort));
    return antwort.text();
  };

  /** Die Zahlen hinter dem Bild: beide Flaechen, Massstab, Achsen, Gewerke. */
  raumGrundriss = (projektId: string, roomId: string) =>
    this.hole<RaumGrundriss>(
      `/api/projects/${projektId}/raumbuch/raeume/${roomId}/grundriss`);

  sammelaenderung = (projektId: string, daten: {
    raumIds: string[];
    templateId?: string | null;
    werte?: Record<string, string | null>;
    ueberschreiben: boolean;
  }) => this.sende<SammelErgebnis>(
    `/api/projects/${projektId}/rooms/sammelaenderung`, daten);

  werteUebernehmen = (zielId: string, quelleId: string) =>
    this.sende<void>(`/api/rooms/${zielId}/uebernehmen-von/${quelleId}`, {});

  /** Freigegebene Profile, passend zur Revit-Fassung des Modells. */
  /**
   * Alle Profile eines Projekts, auf Wunsch mit Entwürfen.
   *
   * Zum Ausführen zählen nur freigegebene Fassungen. Zum Bearbeiten müssen die
   * Entwürfe sichtbar sein — sonst legt man denselben zweimal an.
   */
  profileAlle = (projektId?: string) =>
    this.hole<Profil[]>(
      `/api/profiles?auchEntwuerfe=true${projektId ? `&projectId=${projektId}` : ''}`);

  /** Eine Fassung mit allen Einstellungen. */
  profilfassung = (id: string) =>
    this.hole<Profilfassung>(`/api/profiles/versions/${id}`);

  /**
   * Eine neue Fassung anlegen.
   *
   * Bestehende werden nie geändert — sonst wären alte Aufträge nicht mehr
   * nachvollziehbar. Der Server rechnet die Prüfsumme selbst.
   */
  profilfassungAnlegen = (profilId: string, definition: unknown) =>
    this.schicke<{ id: string; version: string }>(
      `/api/profiles/${profilId}/versions`, 'POST', definition);

  profilfassungFreigeben = (id: string) =>
    this.schicke<{ message?: string }>(
      `/api/profiles/versions/${id}/release`, 'POST', {});

  profile = (projektId: string, revitVersion: number) =>
    this.hole<ProfilEintrag[]>(
      `/api/profiles?projectId=${projektId}&revitVersion=${revitVersion}`);

  // --- Heiz-/Kühllast -------------------------------------------------------
  //
  // Die drei Schritte sind ausdrückliche Aufrufe und keine Nebeneffekte der
  // Hüllenaufnahme: Sie ersetzen einen Bestand, auf dem eine Berechnung
  // aufgebaut sein kann.

  heizlastUebersicht = (projektId: string) =>
    this.hole<HeizlastUebersicht>(
      `/api/projects/${projektId}/heizlast/uebersicht`);

  /**
   * Spielt die letzte Hüllenaufnahme erneut ein — ohne Revit.
   *
   * Der Grund, warum huelle.json als Artefakt liegen bleibt: Wird das
   * Parameter-Mapping korrigiert, entsteht der Bestand aus derselben Datei neu.
   */
  heizlastHuelleNeuUebernehmen = (projektId: string) =>
    this.sende<{
      quelle: string;
      gelesenAm: string;
      ergebnis: { hinweise: string[] };
    }>(`/api/projects/${projektId}/heizlast/huelle-neu-uebernehmen`, {});

  // --- Der Hüllenbaum ------------------------------------------------------
  //
  // Gruppe → Bauteilart → Typ. Der Typ ist die Zeile, weil der U-Wert eine
  // Eigenschaft der Bauart ist und nicht des einzelnen Wandstücks: 16.408
  // Bauteile stehen 635 Typen gegenüber.

  huellenbaum = (projektId: string) =>
    this.hole<Huellenbaum>(`/api/projects/${projektId}/huelle/baum`);

  /**
   * Legt einen Kennwert für einen ganzen Bauteiltyp fest.
   *
   * Ein leerer Wert löscht die Festlegung — Setzen und Löschen sind derselbe
   * Aufruf. Der Wert wird sofort auf die Bauteile ausgetragen; einer, der erst
   * beim nächsten Lauf wirkt, sieht aus wie einer, der nicht angekommen ist.
   */
  huelleTypwertSetzen = (
    projektId: string,
    eingabe: {
      bauteilart: string;
      typ: string;
      feldschluessel: string;
      zahlwert?: number | null;
      wert?: string | null;
      begruendung?: string | null;
      herkunft?: 'hand' | 'ersatzwert';
    },
  ) =>
    this.schicke<Typwertergebnis>(
      `/api/projects/${projektId}/huelle/typwert`, 'PUT', eingabe,
    ) as Promise<Typwertergebnis>;

  /**
   * Die einzelnen Bauteile eines Typs.
   *
   * Ein eigener Aufruf: 16.408 Bauteile in der Baumantwort wären mehrere
   * Megabyte, von denen immer nur die eines Typs angesehen werden.
   */
  huelleBauteileEinesTyps = (
    projektId: string, bauteilart: string, typ: string,
  ) =>
    this.hole<Huellenbauteil[]>(
      `/api/projects/${projektId}/huelle/bauteile`
      + `?bauteilart=${encodeURIComponent(bauteilart)}`
      + `&typ=${encodeURIComponent(typ)}`);

  /** Trägt alle Festlegungen neu aus — nach einem Import. */
  huelleTypwerteAnwenden = (projektId: string) =>
    this.sende<Typwertergebnis>(
      `/api/projects/${projektId}/huelle/typwerte-anwenden`, {});

  heizlastZuordnen = (projektId: string) =>
    this.sende<Zuordnungsergebnis>(
      `/api/projects/${projektId}/heizlast/zuordnung`, {});

  heizlastGegVerknuepfen = (projektId: string) =>
    this.sende<GegVerknuepfungsergebnis>(
      `/api/projects/${projektId}/heizlast/geg-verknuepfung`, {});

  heizlastPruefen = (projektId: string) =>
    this.sende<Pruefergebnis>(
      `/api/projects/${projektId}/heizlast/pruefung`, {});

  heizlastBefunde = (projektId: string, status?: string, schwere?: string) => {
    const q = new URLSearchParams();
    if (status) q.set('status', status);
    if (schwere) q.set('schwere', schwere);
    const suffix = q.toString() ? `?${q}` : '';

    return this.hole<{
      anzahl: number;
      fehler: number;
      warnungen: number;
      befunde: Heizlastbefund[];
    }>(`/api/projects/${projektId}/heizlast/befunde${suffix}`);
  };

  heizlastBefundAkzeptieren = (befundId: string, begruendung: string) =>
    this.sende<Heizlastbefund>(
      `/api/heizlast/befunde/${befundId}/akzeptieren`, { begruendung });

  /**
   * Rechnet einen Heizlastlauf.
   *
   * Ein Lauf ist unveränderlich und ersetzt keinen früheren - er bekommt eine
   * eigene Laufnummer und schreibt seine Eingangslage mit.
   */
  /**
   * Einen Lauf rechnen.
   *
   * `aussentemperaturC` leer lassen heißt: die Norm-Außentemperatur des
   * Leitdatensatzes (-13,5 °C nach DIN/TS 12831-1 für den Standort). Gesetzt
   * wird sie für Vergleichsläufe — die MBBM-Referenzwerte liegen bei -7,8
   * und -10 °C, und ohne denselben Außenwert vergleicht man zwei
   * verschiedene Lastfälle.
   */
  heizlastLaufRechnen = (
    projektId: string, bezeichnung?: string, aussentemperaturC?: number,
  ) =>
    this.sende<HeizlastLaufErgebnis>(
      `/api/projects/${projektId}/heizlast/lauf`,
      { bezeichnung, aussentemperaturC });

  /**
   * Die Bauteilbilanz einer Ergebniszeile — die Herleitung.
   *
   * Adressiert wird die Ergebniszeile und nicht der Raum: Eine Zone mit
   * Ersatzkörper hat keine RoomId.
   */
  heizlastLaufRaum = (laufId: string, ergebnisId: string) =>
    this.hole<HeizlastRaumBilanz>(
      `/api/heizlast/laeufe/${laufId}/raeume/${ergebnisId}`);

  /** Produktdaten einer Lüftungskomponente - dieselbe Form wie geraet(). */
  lueftungKomponente = (id: string) =>
    this.hole<Produktdaten>(`/api/lueftung/komponenten/${id}`);

  // --- Jahressimulation Heizwärmebedarf ------------------------------------

  heizlastJahressimulation = (laufId: string) =>
    this.sende<HeizlastSimulationsergebnis>(
      `/api/heizlast/laeufe/${laufId}/jahressimulation`, {});

  heizlastJahresgang = (laufId: string) =>
    this.hole<HeizlastJahresgang>(`/api/heizlast/laeufe/${laufId}/jahresgang`);

  /**
   * Den Bericht laden und im Browser speichern.
   *
   * Nicht als einfacher Link: Die API verlangt ein Bearer-Token, und ein
   * <a href> trägt keines. Deshalb wird geladen, als Blob abgelegt und der
   * Download ausgelöst.
   */
  heizlastBericht = (laufId: string, umfang: string, ebene?: string) =>
    this.berichtLaden(
      `/api/heizlast/laeufe/${laufId}/bericht.pdf`, umfang, ebene);

  kuehllastBericht = (laufId: string, umfang: string, ebene?: string) =>
    this.berichtLaden(
      `/api/kuehllast/laeufe/${laufId}/bericht.pdf`, umfang, ebene);

  // --- Die Jahresgang-Nachweise (Auftrag Fachplaner 16.09.2026) ------------
  //
  // Kein `umfang`: Ein Jahresgang hat keine Raumzeilen, die man weglassen
  // koennte — er ist die Gebaeudebilanz ueber die Zeit. Deshalb `pdfLaden`
  // und nicht `berichtLaden`; der Dateiname kommt in beiden Faellen aus der
  // Antwort und traegt die Dokumentnummer vorn.

  /** Die Jahressimulation des Heizwärmebedarfs eines Laufs als PDF. */
  heizjahresgangBericht = (laufId: string) =>
    this.pdfLaden(`/api/heizlast/laeufe/${laufId}/jahresgang/bericht.pdf`);

  /** Der Kühllast-Jahresgang eines Laufs als PDF. */
  kuehllastJahresgangBericht = (laufId: string) =>
    this.pdfLaden(`/api/kuehllast/laeufe/${laufId}/jahresgang/bericht.pdf`);

  // --- Küche: Aufnahme der Modelle -----------------------------------------

  /**
   * Der Zustand der Küchenaufnahme.
   *
   * Welches Hostmodell festgelegt ist, welche Verknüpfungen gefunden wurden,
   * welche als Küchenquelle freigegeben sind — und was der letzte Lauf
   * gelesen hat.
   */
  kuecheAufnahme = (projektId: string) =>
    this.hole<KuecheAufnahmestand>(
      `/api/projects/${projektId}/kueche/aufnahme`);

  /** Die PDF-Auswertung als Beobachtungen übernehmen. */
  kuechePdfUebernehmen = (projektId: string) =>
    this.sende<{ anschluesse: number; callouts: number; neu: number;
                 bekannt: number; ohnePlanbezug: number;
                 ohneKoordinate: number; hinweise: string[] }>(
      `/api/projects/${projektId}/kueche/pdf-uebernehmen`, {});

  /** Die zwei Achsschnittpunkte je Plan auswerten. */
  kuecheAchsenRegistrieren = (projektId: string) =>
    this.sende<{ plaene: number; registriert: number; ohneReferenz: number;
                 verworfen: number; suchpunkte: number; hinweise: string[] }>(
      `/api/projects/${projektId}/kueche/achsen-registrieren`, {});

  /** Kandidaten erzeugen, bewerten und die eindeutigen annehmen. */
  kuecheZuordnungRechnen = (projektId: string) =>
    this.sende<KuecheZuordnungslauf>(
      `/api/projects/${projektId}/kueche/zuordnung/rechnen`, {});

  /** Die offenen Zuordnungen mit ihren Kandidaten. */
  kuecheZuordnungOffen = (projektId: string, limit = 100) =>
    this.hole<KuecheOffeneZuordnungen>(
      `/api/projects/${projektId}/kueche/zuordnung/offen?limit=${limit}`);

  kuecheZuordnungAnnehmen = (
    projektId: string, id: string, komponenteId: string, begruendung?: string) =>
    this.sende<unknown>(
      `/api/projects/${projektId}/kueche/zuordnung/${id}/annehmen`,
      { komponenteId, begruendung });

  kuecheZuordnungAusschliessen = (
    projektId: string, id: string, begruendung?: string) =>
    this.sende<unknown>(
      `/api/projects/${projektId}/kueche/zuordnung/${id}/ausschliessen`,
      { begruendung });

  kuecheZuordnungOhneZiel = (
    projektId: string, id: string, begruendung?: string) =>
    this.sende<unknown>(
      `/api/projects/${projektId}/kueche/zuordnung/${id}/ohne-ziel`,
      { begruendung });

  /** Den Klärungsstand eines Bauteils setzen — das Gate fragt ihn ab. */
  kuecheBauteilstatus = (projektId: string, id: string, status: string) =>
    this.sende<{ id: string; bauteilstatus: string }>(
      `/api/projects/${projektId}/kueche/bauteile/${id}/status`, { status });

  /** Die Küchenleuchten gegen die Elektroplanung abgleichen. */
  kuecheLeuchtenAbgleichen = (projektId: string) =>
    this.sende<{ leuchten: number; zugeordnet: number; fehltInElektro: number;
                 mehrdeutig: number; ohneKoordinate: number;
                 inLueftungsdecke: number; hinweise: string[] }>(
      `/api/projects/${projektId}/kueche/leuchten/abgleichen`, {});

  kuecheLeuchten = (projektId: string) =>
    this.hole<KuecheLeuchtenstand>(
      `/api/projects/${projektId}/kueche/leuchten`);

  /** Das Vollständigkeits-Gate. */
  kuecheGate = (projektId: string) =>
    this.hole<KuecheGatebefund>(`/api/projects/${projektId}/kueche/gate`);

  /**
   * Den Probelauf der Markerprojektion an- oder abschalten.
   *
   * Voreinstellung ist AN. Abschalten heisst, dass der nächste Lauf 141 in
   * das Hostmodell schreibt — deshalb landet der Name des Absenders in der
   * Zeile.
   */
  kuecheMarkerProbelauf = (projektId: string, probelauf: boolean) =>
    this.sende<{ markerProbelauf: boolean;
                 markerProbelaufGeaendertVon: string | null;
                 markerProbelaufGeaendertAm: string | null;
                 hinweis: string }>(
      `/api/projects/${projektId}/kueche/markerprobelauf`, { probelauf });

  /** Ein gefundenes Modell als Küchenquelle freigeben oder zurücknehmen. */
  kuecheQuellmodellFreigabe = (
    projektId: string, id: string, freigegeben: boolean) =>
    this.sende<{ id: string; kuerzel: string | null; freigegeben: boolean }>(
      `/api/projects/${projektId}/kueche/quellmodelle/${id}/freigabe`,
      { freigegeben });

  // --- Druckverlust Lüftung ------------------------------------------------

  druckverlustUebersicht = (projektId: string) =>
    this.hole<DruckverlustUebersicht>(
      `/api/projekte/${projektId}/lueftung/druckverlust`);

  schallUebersicht = (projektId: string) =>
    this.hole<SchallUebersicht>(`/api/projekte/${projektId}/lueftung/schall`);

  druckverlustAnlage = (projektId: string, anlage: string, weg?: string) =>
    this.hole<DruckverlustStrang>(
      `/api/projekte/${projektId}/lueftung/druckverlust/`
      + encodeURIComponent(anlage)
      + (weg ? `?weg=${encodeURIComponent(weg)}` : ''));

  /**
   * Die Befunde der Grenzwertprüfung (Befund L-03).
   *
   * Eigener Zugriff und nicht Teil der Strangantwort: Ein Befund hängt am
   * Bauteil und nicht am maßgebenden Strang — 84 % der bewerteten Bauteile
   * liegen auf keinem, und gerade dort sitzen die Überschreitungen.
   */
  lueftungBefunde = (
    projektId: string,
    filter?: { anlage?: string; weg?: string; schwere?: string; regel?: string },
  ) => {
    const p = new URLSearchParams();

    if (filter?.anlage) p.set('anlage', filter.anlage);
    if (filter?.weg) p.set('weg', filter.weg);
    if (filter?.schwere) p.set('schwere', filter.schwere);
    if (filter?.regel) p.set('regel', filter.regel);

    const frage = p.toString();

    return this.hole<LueftungBefundliste>(
      `/api/projekte/${projektId}/lueftung/befunde`
      + (frage ? `?${frage}` : ''));
  };

  /**
   * Die Grundlagen der Lüftungsrechnung: Laufparameter, Medienwerte,
   * Zielpegel und Hygieneregeln.
   *
   * Der Zugriff liefert die Parameter MIT der Zahl, die die Rechnung daraus
   * bildet (`gerechnet`). Wer die Außenlufttemperatur auf −10 °C setzt, sieht
   * sofort, welche Dichte damit gilt — und muss sie nicht von Hand
   * nachschlagen.
   */
  lueftungGrundlagen = (projektId: string) =>
    this.hole<LueftungGrundlagen>(
      `/api/projekte/${projektId}/lueftung/grundlagen`);

  /**
   * Einen Laufparameter als PROJEKTwert setzen.
   *
   * Der hausweite Satz bleibt stehen; er ist der Stand, gegen den sich die
   * Projektfestlegung ausweist. Der Wert gilt ab dem nächsten Rechenlauf —
   * die Dichten entstehen dort und nicht beim Speichern.
   */
  lueftungLaufparameterSetzen = (
    projektId: string,
    schluessel: string,
    eingabe: {
      wert: string;
      quelle?: string;
      begruendung?: string;
      istBestaetigt?: boolean;
      bestaetigtVon?: string;
    },
  ) =>
    this.schicke<LueftungLaufparameterAntwort>(
      `/api/projekte/${projektId}/lueftung/grundlagen/laufparameter/`
      + encodeURIComponent(schluessel),
      'PUT', eingabe);

  /**
   * Der Soll-Ist-Vergleich der Luftmengen (Befund L-01, Stufe 1).
   *
   * Projektweit und nicht je Anlage: Die Luftmenge hängt am Raum, und die
   * Räume ohne jede gemessene Menge gehören zu keiner Anlage — in einer
   * anlagenbezogenen Antwort kämen sie nie vor.
   */
  lueftungLuftmengen = (
    projektId: string,
    filter?: { bewertung?: string; nutzungsart?: string; grenze?: number },
  ) => {
    const p = new URLSearchParams();

    if (filter?.bewertung) p.set('bewertung', filter.bewertung);
    if (filter?.nutzungsart) p.set('nutzungsart', filter.nutzungsart);
    if (filter?.grenze) p.set('grenze', String(filter.grenze));

    const frage = p.toString();

    return this.hole<LueftungLuftmengenliste>(
      `/api/projekte/${projektId}/lueftung/luftmengen`
      + (frage ? `?${frage}` : ''));
  };

  /**
   * Die Heizflächenauslegung nach DIN EN 442-2 (Befunde H-01 und H-13).
   *
   * Projektweit und nicht je Auslegungsgruppe: Die Heizfläche hängt am RAUM,
   * und ein Heizkörper ohne Weg zu einer Quelle gehört zu keiner Gruppe — in
   * einer gruppenbezogenen Antwort käme er nie vor.
   */
  heizungHeizflaechen = (
    projektId: string,
    filter?: { bewertung?: string; grenze?: number },
  ) => {
    const p = new URLSearchParams();

    if (filter?.bewertung) p.set('bewertung', filter.bewertung);
    if (filter?.grenze) p.set('grenze', String(filter.grenze));

    const frage = p.toString();

    return this.hole<HeizungHeizflaechenliste>(
      `/api/projects/${projektId}/heizung/heizflaechen`
      + (frage ? `?${frage}` : ''));
  };

  /**
   * Alle Teilstrecken eines Wegs — je Auslass eine, nach Δp absteigend
   * (Schritt 1c des Normprogramms).
   *
   * Aus `lueftung_teilstrecken` und nicht aus dem JSON-Klumpen der
   * Anlagenberechnung: Erst als Zeile ist ein Verbraucher filterbar,
   * sortierbar und je Raum zählbar.
   */
  lueftungTeilstrecken = (projektId: string, anlage: string, weg: string) =>
    this.hole<LueftungWegteilstrecken>(
      `/api/projekte/${projektId}/lueftung/anlagen/`
      + `${encodeURIComponent(anlage)}/wege/${encodeURIComponent(weg)}`
      + '/teilstrecken');

  /**
   * Der Strang EINER Teilstrecke, Posten für Posten.
   *
   * Eine leere Liste ist kein Fehler: Zugeordnet wird nur der maßgebende
   * Strang, und ein Bauteil gehört immer nur einer Teilstrecke. Die Antwort
   * trägt dann einen `grund`.
   */
  lueftungTeilstreckenposten = (projektId: string, teilstreckeId: string) =>
    this.hole<LueftungTeilstreckenposten>(
      `/api/projekte/${projektId}/lueftung/teilstrecken/`
      + `${encodeURIComponent(teilstreckeId)}/posten`);

  /**
   * Der Raumpegel je Raum (Befund L-02).
   *
   * Je RAUM und nicht je Auslass: Ein Raum hat selten einen Auslass, und die
   * energetische Summe hebt den Pegel im Mittel um 4,2 dB.
   */
  lueftungRaumschall = (
    projektId: string,
    filter?: { ebene?: string; nurUeberZiel?: boolean; grenze?: number },
  ) => {
    const p = new URLSearchParams();

    if (filter?.ebene) p.set('ebene', filter.ebene);
    if (filter?.nurUeberZiel) p.set('nurUeberZiel', 'true');
    if (filter?.grenze) p.set('grenze', String(filter.grenze));

    const frage = p.toString();

    return this.hole<LueftungRaumschallliste>(
      `/api/projekte/${projektId}/lueftung/raumschall`
      + (frage ? `?${frage}` : ''));
  };

  /** Ein Raum mit den Auslässen, die ihn versorgen. */
  lueftungRaumschallRaum = (projektId: string, raumnummer: string) =>
    this.hole<LueftungRaumschallRaum>(
      `/api/projekte/${projektId}/lueftung/raumschall/`
      + encodeURIComponent(raumnummer));

  /**
   * Die Schallberechnung auslösen.
   *
   * Es ist derselbe Lauf wie beim Druckverlust — die Schallkette hängt an
   * denselben Teilstrecken. Zurück kommt die fertige Schallübersicht, damit
   * die Seite nicht ein zweites Mal fragen muss.
   */
  schallRechnen = (projektId: string, anlage?: string) =>
    this.sende<SchallLauf>(
      `/api/projekte/${projektId}/lueftung/schall/rechnen`
      + (anlage ? `?anlage=${encodeURIComponent(anlage)}` : ''), {});

  druckverlustRechnen = (projektId: string, anlage?: string) =>
    this.sende<{ anzahlAnlagen: number; netze: number; ringnetze: number;
                 hinweise: string[] }>(
      `/api/projekte/${projektId}/lueftung/druckverlust/rechnen`
      + (anlage ? `?anlage=${encodeURIComponent(anlage)}` : ''), {});

  /** Der Nachweis als PDF — Übersicht oder eine einzelne Anlage. */
  /**
   * Das Anlagenschema des Lüftungsgeräts.
   *
   * Die Bauteile in Strömungsrichtung mit ihren Druckverlusten — was im Gerät
   * steht und was es leistet.
   */
  anlagenschemaPdf = (projektId: string, anlage: string) =>
    this.pdfLaden(
      `/api/projekte/${projektId}/lueftung/anlagenschema.pdf`
      + `?anlage=${encodeURIComponent(anlage)}`);

  /**
   * Das Strangschema der Anlage.
   *
   * Je Weg ein Blatt: alle Abgänge und Auslässe mit Teilstreckennummer,
   * Volumenstrom, Raum, Druckverlust, Abgleichdruck und Schallpegel.
   */
  strangschemaPdf = (projektId: string, anlage: string, weg?: string) =>
    this.pdfLaden(
      `/api/projekte/${projektId}/lueftung/strangschema.pdf`
      + `?anlage=${encodeURIComponent(anlage)}`
      + (weg ? `&weg=${encodeURIComponent(weg)}` : ''));

  /**
   * TV6d: Anlagen- oder Strangschema für die Vorschau auf der Seite (Anlagenliste) - dieselbe Route wie
   * `anlagenschemaPdf`/`strangschemaPdf` (bis TX2 ohne Register, also ohne Dokumentnummer), nur wird die Datei nicht
   * heruntergeladen, sondern zurückgegeben. Der Name kommt wie dort aus dem Kopf der Antwort.
   */
  lueftungSchemaVorschau = (projektId: string, art: 'anlagenschema' | 'strangschema', anlage: string) =>
    this.pdfHolen(`/api/projekte/${projektId}/lueftung/${art}.pdf?anlage=${encodeURIComponent(anlage)}`);

  druckverlustPdf = (projektId: string, anlage?: string) =>
    this.pdfLaden(
      `/api/projekte/${projektId}/lueftung/druckverlust/bericht.pdf`
      + (anlage ? `?anlage=${encodeURIComponent(anlage)}` : ''));

  /**
   * Die Anlagenliste Lüftung als PDF.
   *
   * Ohne `anlage` die Liste aller Anlagen mit Übersicht, den
   * Kreislaufverbunden und je Anlage einem Abschnitt; mit `anlage` nur dieser
   * eine Abschnitt.
   *
   * Eigener Aufruf neben `druckverlustPdf` und kein Merkmal daran: Es sind
   * zwei Dokumente mit verschiedenem Kürzel in der Dokumentnummer.
   */
  lueftungAnlagenlistePdf = (projektId: string, anlage?: string) =>
    this.pdfLaden(
      `/api/projekte/${projektId}/lueftung/anlagen/bericht.pdf`
      + (anlage ? `?anlage=${encodeURIComponent(anlage)}` : ''));

  /**
   * Der Schallnachweis des Kanalnetzes als PDF.
   *
   * Schallleistung AM AUSLASS je Weg - die Größe, gegen die ein
   * Schalldämpfer ausgeschrieben wird. Ohne `anlage` alle Wege.
   */
  lueftungSchallberichtPdf = (projektId: string, anlage?: string) =>
    this.pdfLaden(
      `/api/projekte/${projektId}/lueftung/schall/bericht.pdf`
      + (anlage ? `?anlage=${encodeURIComponent(anlage)}` : ''));

  /**
   * Der Raumschallnachweis als PDF.
   *
   * Schalldruckpegel IM RAUM - die Größe, gegen die Festlegung F36 die
   * 45 dB(A) stellt. Sein Kern ist die Arbeitsliste: alle Räume über dem
   * Ziel, nach Überschreitung sortiert. Ein eigener Aufruf neben
   * `lueftungSchallberichtPdf` und kein Merkmal daran - es sind zwei
   * Dokumente mit verschiedenem Kürzel in der Dokumentnummer.
   */
  /**
   * TV7a-2: Vorbehalte und Annahmen eines Lüftungsberichts für die Seite, im selben Umfang wie das PDF (`anlage` bei
   * Schall und Druckverlust, `ebene` beim Raumschall).
   */
  lueftungBerichtsvorbehalte = (
    projektId: string, bericht: 'schall' | 'raumschall' | 'druckverlust', umfang: { anlage?: string; ebene?: string } = {},
  ) => {
    const suche = new URLSearchParams();
    if (umfang.anlage) suche.set('anlage', umfang.anlage);
    if (umfang.ebene) suche.set('ebene', umfang.ebene);
    const frage = suche.toString();
    return this.hole<LueftungBerichtsvorbehalte>(
      `/api/projekte/${projektId}/lueftung/${bericht}/vorbehalte${frage ? `?${frage}` : ''}`);
  };

  lueftungRaumschallberichtPdf = (projektId: string, ebene?: string) =>
    this.pdfLaden(
      `/api/projekte/${projektId}/lueftung/raumschall/bericht.pdf`
      + (ebene ? `?ebene=${encodeURIComponent(ebene)}` : ''));

  /** TV8: Bestand der Brandschutzklappen mit AKS-Schlüssel, Anlage, Mengen und Lücken (nur lesend). */
  lueftungBrandschutzklappen = (projektId: string) =>
    this.hole<BrandschutzklappenAntwort>(`/api/projekte/${projektId}/lueftung/brandschutzklappen`);

  /**
   * TV8: die Brandschutzklappenliste LUE-BKL als PDF - ohne `anlage` gesamt, mit `anlage` je Anlage, mit `anlage`
   * und `ebene` je Knoten des Baums. `-` wählt „ohne Anlage“ bzw. „ohne Ebene“. Bei gleichem Stand liefert der
   * Server die abgelegte Fassung (F11).
   */
  lueftungBrandschutzklappenPdf = (projektId: string, anlage?: string, ebene?: string) =>
    this.pdfLaden(
      `/api/projekte/${projektId}/lueftung/brandschutzklappen/bericht.pdf`
      + (anlage ? `?anlage=${encodeURIComponent(anlage)}` : '')
      + (anlage && ebene ? `&ebene=${encodeURIComponent(ebene)}` : ''));

  /** Eine PDF-Datei holen, ohne sie herunterzuladen (Vorschau); Name wie bei `pdfLaden` aus dem Kopf. */
  private async pdfHolen(pfad: string): Promise<{ datei: Blob; name: string }> {
    const antwort = await this.abrufen(pfad, {
      headers: { Authorization: `Bearer ${await this.token()}` },
    });

    if (!antwort.ok) {
      throw new Error(await fehlertext(antwort));
    }

    return { datei: await antwort.blob(), name: dateinameAusAntwort(antwort, 'Schema.pdf') };
  }

  /**
   * Eine Datei herunterladen, deren Namen die API bestimmt.
   *
   * Getrennt von dateiLaden, das den Namen als Aufrufparameter erwartet: Bei
   * der Druckverlustrechnung kennt nur die API Projektnummer und
   * Anlagenkennung, und beides in der Oberfläche noch einmal zusammenzusetzen
   * wären zwei Stellen, an denen derselbe Name entsteht.
   */
  private async pdfLaden(pfad: string): Promise<void> {
    const antwort = await this.abrufen(pfad, {
      headers: { Authorization: `Bearer ${await this.token()}` },
    });

    if (!antwort.ok) {
      throw new Error(await fehlertext(antwort));
    }

    const name = dateinameAusAntwort(antwort, 'Bericht.pdf');

    const blob = await antwort.blob();
    const ziel = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = ziel;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(ziel), 2000);
  }

  private async berichtLaden(
    pfad: string, umfang: string, ebene?: string,
  ): Promise<void> {
    const url = `${pfad}?umfang=${encodeURIComponent(umfang)}`
      + (ebene ? `&ebene=${encodeURIComponent(ebene)}` : '');

    const antwort = await this.abrufen(url, {
      headers: { Authorization: `Bearer ${await this.token()}` },
    });

    if (!antwort.ok) {
      throw new Error(await fehlertext(antwort));
    }

    // Den Dateinamen aus dem Kopf übernehmen — die API kennt Projektnummer,
    // Laufnummer und Geschoss, die Oberfläche müsste sie zusammensetzen.
    const name = dateinameAusAntwort(antwort, 'Bericht.pdf');

    const blob = await antwort.blob();
    const ziel = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = ziel;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();

    // Ohne das Freigeben bleibt der Blob bis zum Neuladen im Speicher.
    setTimeout(() => URL.revokeObjectURL(ziel), 2000);
  }

  // --- Kühllast ------------------------------------------------------------

  kuehllastRechnen = (projektId: string, bezeichnung?: string,
                      mitJahresgang = true) =>
    this.sende<KuehllastLaufErgebnis>(
      `/api/projects/${projektId}/kuehllast/lauf`,
      { bezeichnung, mitJahresgang });

  kuehllastLaeufe = (projektId: string) =>
    this.hole<KuehllastLaufKopf[]>(
      `/api/projects/${projektId}/kuehllast/laeufe`);

  kuehllastLauf = (laufId: string) =>
    this.hole<KuehllastLaufDetail>(`/api/kuehllast/laeufe/${laufId}`);

  kuehllastRaum = (laufId: string, ergebnisId: string) =>
    this.hole<KuehllastRaumDetail>(
      `/api/kuehllast/laeufe/${laufId}/raeume/${ergebnisId}`);

  /** Je Lauf die Zahl seiner Jahresgangstunden — ohne die Zeilen zu laden. */
  kuehllastJahresgangBestand = (projektId: string) =>
    this.hole<KuehllastJahresgangBestand>(
      `/api/projects/${projektId}/kuehllast/jahresgang-bestand`);

  kuehllastJahresgang = (laufId: string) =>
    this.hole<KuehllastJahresgang>(
      `/api/kuehllast/laeufe/${laufId}/jahresgang`);

  /**
   * Den Jahresgang eines BESTEHENDEN Laufs nachrechnen.
   *
   * Der Lauf behält seine Nummer und seine Raumergebnisse; nur die
   * Jahresgangzeilen werden ersetzt. 8.760 Stunden über rund 670 Räume —
   * gemessen 10,4 s, unter Last deutlich mehr. Der Aufrufer muss den Knopf
   * sperren; ein zweiter Start würde denselben Lauf zweimal ersetzen.
   */
  kuehllastJahresgangRechnen = (laufId: string) =>
    this.sende<KuehllastJahresgangErgebnis>(
      `/api/kuehllast/laeufe/${laufId}/jahresgang`, {});

  /** Ein Lauf mit seinen Raum- und Zonenergebnissen. */
  heizlastLauf = (laufId: string) =>
    this.hole<HeizlastLaufDetail>(`/api/heizlast/laeufe/${laufId}`);

  heizlastLaeufe = (projektId: string) =>
    this.hole<HeizlastLaufKopf[]>(
      `/api/projects/${projektId}/heizlast/laeufe`);

  /** Die Kreislaufverbunde eines Projekts samt Geräten und Verweisen. */
  lueftungKvs = (projektId: string) =>
    this.hole<KvsUebersicht>(`/api/projects/${projektId}/lueftung/kvs`);

  /** Standort, führender Klimadatensatz und die Pflichtangaben. */
  heizlastStandort = (projektId: string) =>
    this.hole<HeizlastStandortstand>(
      `/api/projects/${projektId}/heizlast/standort`);

  /**
   * Die Raumtemperaturanforderungen (Paket RT1): Vorlage, Regeln,
   * Zuordnungsquote und die Raumliste mit Herkunft und Abgleich.
   */
  raumtemperaturen = (projektId: string) =>
    this.hole<RaumtempStand>(
      `/api/projects/${projektId}/raumtemperaturen`);

  /**
   * Die Zuordnung neu bilden. Ohne `schreiben` ist es ein Probelauf — er
   * zählt und meldet, ändert aber nichts.
   */
  raumtemperaturenZuordnen = (projektId: string, schreiben: boolean) =>
    this.sende<RaumtempLaufErgebnis>(
      `/api/projects/${projektId}/raumtemperaturen/zuordnen`
      + `?schreiben=${schreiben ? 'true' : 'false'}`, {});

  /**
   * Die aus einer Fremdsimulation übernommenen Randbedingungen.
   *
   * Getrennt von der Übersicht, weil es eine andere Frage beantwortet: nicht
   * "wie weit ist der eigene Bestand", sondern "was hat ein Dritter
   * vorgegeben, und hängt es überhaupt an einem Raum".
   */
  heizlastExternwerte = (projektId: string, kategorie?: string) =>
    this.hole<HeizlastExternwerte>(
      `/api/projects/${projektId}/heizlast/externwerte`
      + (kategorie ? `?kategorie=${encodeURIComponent(kategorie)}` : ''));

  heizlastBereiche = (projektId: string) =>
    this.hole<HeizlastBereich[]>(
      `/api/projects/${projektId}/heizlast/bereiche`);

  /**
   * Externe Vergleichswerte — KEIN Berechnungseingang.
   *
   * Eigener Aufruf und eigene Karte in der Oberfläche. Wer sie neben den
   * Eingangsgrößen ausliefert, lädt dazu ein, sie dafür zu halten.
   */
  heizlastReferenzwerte = (projektId: string) =>
    this.hole<HeizlastReferenzwerte>(
      `/api/projects/${projektId}/heizlast/referenzwerte`);

  heizlastAliasSetzen = (daten: {
    alias: string;
    typCode: string;
    quelle?: string;
    sourceStatus?: string;
    bemerkung?: string;
  }) => this.sende<{ message: string }>('/api/heizlast/geg/alias', daten);

  /**
   * Legt einen Auftrag an.
   *
   * Die Arten entsprechen ExportKind: 1 IFC, 2 PDF, 3 DWG, 10 Raumaufnahme,
   * 11 Rueckschreiben.
   */
  auftragAnlegen = (daten: {
    projectId: string;
    exportProfileVersionId: string;
    modelIds: string[];
    kinds: number[];
    /** Zusätzliche Empfänger. Der Auftraggeber bekommt die Mail ohnehin. */
    notifyEmails?: string[];
    /**
     * Laesst den Auftrag an einem bestimmten Arbeitsplatz laufen statt auf
     * einer Verarbeitungsmaschine (Dokument 34, Abschnitt C). Leer heisst:
     * wie bisher eine Verarbeitungsmaschine.
     */
    arbeitsplatzId?: string;
  }) => this.sende<{ id: string; jobNumber: string }>('/api/jobs', {
    ...daten,
    versionSelection: 1,
  });

  // --- Produktseite ---------------------------------------------------------

  /** Alle Geräte eines Projekts mit ihrem öffentlichen Verweis. */
  geraete = (projektId: string) =>
    this.hole<Geraetezeile[]>(`/api/projects/${projektId}/geraete`);

  /**
   * Alle Daten eines Geräts in einer Antwort.
   *
   * Eine Abfrage statt vier: Die Seite zeigt Kopf, Kreise, Betriebspunkte und
   * Werte gemeinsam, und vier Aufrufe wären vier Ladezustände, die nacheinander
   * erscheinen.
   */
  geraet = (bauteilId: string) =>
    this.hole<Produktdaten>(`/api/geraete/${bauteilId}`);


  // --- Anlagenseite ---------------------------------------------------------

  /** Die Lüftungsanlagen eines Projekts, mit öffentlichem Verweis. */
  anlagenseiten = (projektId: string) =>
    this.hole<Anlagenzeile[]>(`/api/projects/${projektId}/anlagen`);

  /** Eine Anlage mit Geräteaufbau, Betriebspunkten und Unterlagen. */
  anlagenseite = (plantId: string) =>
    this.hole<Anlagendaten>(`/api/anlagen/${plantId}`);

  /**
   * Bildet die öffentlichen Verweise für alle Anlagen und trägt sie ein.
   *
   * Wiederholbar: Der Verweis ist ein HMAC über die Anlagenkennung und ändert
   * sich nicht. Ein zweiter Lauf ergänzt nur, was fehlt.
   */
  anlagenVerweise = (projektId: string) =>
    this.sende<{
      anlagen: number; neu: number; geaendert: number;
      unveraendert: number; hinweis: string;
    }>(`/api/projects/${projektId}/anlagen/verweise`, {});

  /**
   * Bildet die öffentlichen Verweise für alle Geräte und trägt sie ein.
   *
   * Wiederholbar: Der Verweis ist ein HMAC über die Bauteilkennung und ändert
   * sich nicht. Ein zweiter Lauf ergänzt nur, was fehlt.
   */
  geraeteVerweise = (projektId: string) =>
    this.sende<{
      geraete: number; neu: number; geaendert: number;
      unveraendert: number; hinweis: string;
    }>(`/api/projects/${projektId}/geraete/verweise`, {});

  // --- Türbuch --------------------------------------------------------------

  /** F60 H2b: mit `haus` nur die Türen dieses Hauses (`?haus=NN`); ohne wie bisher alle. */
  tueren = (projektId: string, haus?: number | null) =>
    this.hole<TuerbuchDaten>(`/api/projects/${projektId}/doors${hausAbfrage(haus)}`);

  /** Eine einzelne Tür mit allen Werten. */
  tuer = (tuerId: string) => this.hole<TuerDetail>(`/api/doors/${tuerId}`);

  /**
   * Die Türanforderungen aus dem Sicherheitskonzept ableiten.
   *
   * SCHREIBT: in `tuer_sicherheitsanforderung` und in die abgeleiteten Spalten
   * von `doors`. Gepflegte Werte (Fassadentyp, Brandschutzanforderung,
   * Zonenflächentyp) bleiben unberührt; zweimal ausgeführt ergibt dasselbe.
   */
  tuerSicherheitAbleiten = (projektId: string) =>
    this.sende<TuerAbleitungsergebnis>(
      `/api/projects/${projektId}/doors/security/derive`, {});

  /** Die Anforderungen einer Tür — mit dem Regeltext, der sie erzeugt hat. */
  tuerSicherheit = (tuerId: string) =>
    this.hole<TuerSicherheitDetail>(`/api/doors/${tuerId}/security`);

  /**
   * Die Prüfliste: nicht „was gilt an dieser Tür", sondern „wo ist etwas zu
   * tun". Mit Raum- und Türnummern statt Summen — eine Zeile „261 Räume ohne
   * Schutzzone" ist eine Zahl, eine Liste ist Arbeit, die jemand erledigen kann.
   */
  tuerSicherheitPruefliste = (projektId: string) =>
    this.hole<TuerSicherheitPruefliste>(
      `/api/projects/${projektId}/doors/security/checklist`);

  /**
   * Die Sicherheitszonen der Türen und die Zonenübergänge.
   *
   * Gruppiert richtungsneutral: In der Datenbank stehen 76 Türen SZ4→SZ2 und
   * 69 Türen SZ2→SZ4 — das sind 145 Türen zwischen BOH und GA und nicht zwei
   * verschiedene Sachverhalte.
   */
  tuerSicherheitszonen = (projektId: string) =>
    this.hole<TuerSicherheitszonen>(
      `/api/projects/${projektId}/doors/security-zones`);

  tueranforderungsbericht = (projektId: string) =>
    this.hole<Tueranforderungsbericht>(
      `/api/projects/${projektId}/doors/requirements/report`);

  tuerfelder = () => this.hole<Tuerfeld[]>('/api/doorfields');

  tuerSpeichern = (tuerId: string, werte: Record<string, string | null>) =>
    this.schicke(`/api/doors/${tuerId}`, 'PUT', werte);

  /**
   * Weist einer Tür einen Türtyp zu.
   *
   * Eigener Weg, weil `tuerSpeichern` den Türtyp verwirft: Er ist ein
   * geschützter Feldschlüssel, damit ihn niemand beim Pflegen anderer Felder
   * nebenbei verschiebt. Zuweisen ist eine eigene Handlung, und der Schlüssel
   * wird dabei gegen die Türtypen geprüft.
   *
   * Leerer Schlüssel heisst „nicht festgelegt".
   */
  tuerTuertypZuweisen = (tuerId: string, templateKey: string | null) =>
    this.schicke<{ tuertyp: string | null; vorher: string | null }>(
      `/api/doors/${tuerId}/tuertyp`, 'PUT', { templateKey: templateKey ?? '' });

  /** Wendet einen Türtyp auf mehrere Türen an. */
  tuerVorlageZuweisen = (
    projektId: string,
    daten: { vorlageId: string; tuerIds: string[]; ueberschreiben?: boolean },
  ) => this.sende<{ vorlage: string; betroffen: number; uebersprungen: number }>(
    `/api/projects/${projektId}/doors/vorlage`, daten);

  tuerbuchExcel = (projektId: string, dateiname: string) =>
    this.dateiLaden(`/api/projects/${projektId}/doors/excel`, dateiname);

  tuerbuchPdf = (projektId: string, dateiname: string) =>
    this.dateiLaden(`/api/projects/${projektId}/doors/pdf`, dateiname);

  async tuerbuchEinlesen(projektId: string, datei: File, uebernehmen: boolean) {
    const inhalt = new FormData();
    inhalt.append('datei', datei);
    inhalt.append('uebernehmen', uebernehmen ? 'true' : 'false');

    const antwort = await fetch(`/api/projects/${projektId}/doors/excel`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${await this.token()}` },
      body: inhalt,
    });

    if (!antwort.ok) {
      throw new Error(await fehlertext(antwort));
    }

    return antwort.json() as Promise<{ uebernommen: boolean; vorschau: ExcelVorschau }>;
  }

  tuertypen = (projektId?: string) =>
    this.hole<Tuertyp[]>('/api/doortemplates' + (projektId ? `?projectId=${projektId}` : ''));

  tuertypAendern = (id: string, daten: {
    name?: string;
    beschreibung?: string | null;
    werte?: Record<string, string | null>;
    isActive?: boolean;
  }) => this.schicke(`/api/doortemplates/${id}`, 'PUT', daten);

  tuertypAnlegen = (daten: {
    templateKey: string;
    name: string;
    beschreibung?: string | null;
    projectId?: string | null;
  }) => this.sende<{ id: string; templateKey: string; name: string }>(
    '/api/doortemplates', daten);

  // --- Zeitplaene ---------------------------------------------------------

  zeitplaene = (projektId: string) =>
    this.hole<Zeitplan[]>(`/api/projects/${projektId}/schedules`);

  zeitplanAnlegen = (projektId: string, daten: ZeitplanEingabe) =>
    this.sende<{ id: string; nextRunAt: string }>(
      `/api/projects/${projektId}/schedules`, daten);

  zeitplanAendern = (id: string, daten: ZeitplanEingabe) =>
    this.schicke(`/api/schedules/${id}`, 'PUT', daten);

  zeitplanEntfernen = (id: string) => this.schicke(`/api/schedules/${id}`, 'DELETE');

  // --- Regelaufgaben (WE1: Einstellungen → Wiederkehrende Exporte → Ketten) --

  /** Was eine Kette enthalten darf - Rückschreiben steht als „noch nicht freigegeben“ dabei, ist aber nicht wählbar. */
  regelaufgabenKatalog = () => this.hole<RegelKatalog>('/api/regelaufgaben/katalog');

  regelaufgaben = (projektId: string) =>
    this.hole<RegelZeitplan[]>(`/api/projects/${projektId}/regelaufgaben`);

  regelaufgabeAnlegen = (projektId: string, daten: RegelZeitplanEingabe) =>
    this.sende<RegelZeitplan>(`/api/projects/${projektId}/regelaufgaben`, daten);

  /** Die beiden Startketten des Eigentümers (Mo–Fr 22:00, Mo 23:00) - stillgelegt angelegt, bestehende bleiben. */
  regelaufgabenStartwerte = (projektId: string) =>
    this.sende<{ angelegt: number; hinweise: string[] }>(`/api/projects/${projektId}/regelaufgaben/startwerte`, {});

  /** `version` aus dem gelesenen Stand mitgeben - ein veralteter Stand gibt 409. */
  regelaufgabeAendern = (id: string, daten: RegelZeitplanEingabe) =>
    this.schicke<RegelZeitplan>(`/api/regelaufgaben/${id}`, 'PUT', daten);

  regelaufgabeEntfernen = (id: string) => this.schicke(`/api/regelaufgaben/${id}`, 'DELETE');

  /** Probe (läuft echt, sofort) oder Trockenlauf (nur Plan und Bereitschaft). Bestätigung und Grund sind Pflicht. */
  regelaufgabeAusloesen = (id: string, daten: { art: 'probe' | 'trocken'; grund: string; bestaetigt: boolean }) =>
    this.sende<{ id: string; status: string }>(`/api/regelaufgaben/${id}/laeufe`, daten);

  regelaufgabenLaeufe = (id: string, anzahl = 10) =>
    this.hole<RegelLauf[]>(`/api/regelaufgaben/${id}/laeufe?take=${anzahl}`);

  regelLaufAnhalten = (laufId: string, grund?: string) =>
    this.schicke(`/api/regelaufgaben/laeufe/${laufId}/abbrechen`, 'POST', { grund: grund ?? null });

  // --- Betrieb -------------------------------------------------------------

  /**
   * Der Regelbetrieb der letzten Tage.
   *
   * Reines Lesen: Die Karte fragt bei jedem Seitenaufruf ab. Der PDF-Weg
   * daneben ist bewusst getrennt — er zieht eine Dokumentnummer aus dem
   * Berichtsregister, und das darf nicht bei jedem Blick geschehen.
   */
  regelbetrieb = (projektId: string, tage = 7) =>
    this.hole<Regelbetrieb>(
      `/api/projects/${projektId}/regelbetrieb?tage=${tage}`);

  regelbetriebPdf = (projektId: string, tage = 7) =>
    this.pdfLaden(`/api/projects/${projektId}/regelbetrieb/pdf?tage=${tage}`);

  /**
   * Wie die Rechnungen zum aktiven Raumbuch stehen - beim Lesen ermittelt,
   * die Route schreibt nichts.
   */
  raumstand = (projektId: string) =>
    this.hole<Raumstand[]>(`/api/projects/${projektId}/raumstand`);

  // --- Parameterfuellgrad (Vertiefungsplan V-P1) ---------------------------

  /**
   * Wie weit die Feldkataloge aller Gewerke gefüllt sind.
   *
   * `mitFeldern` holt zusätzlich jede einzelne Feldzeile — über alle Gewerke
   * sind das weit über neunhundert. Die Karte braucht sie nicht; erst wer ein
   * Gewerk aufklappt, holt sie für DIESES Gewerk nach.
   */
  parameterFuellgrad = (projektId: string, gewerk?: string, mitFelder = false) =>
    this.hole<Parameterfuellgrad>(
      `/api/projects/${projektId}/parameter/fuellgrad`
      + `?felder=${mitFelder}`
      + (gewerk ? `&gewerk=${encodeURIComponent(gewerk)}` : ''));

  /**
   * Der Füllgradbericht als PDF (PAR-FGB).
   *
   * Getrennt vom Lesen, wie beim Regelbetrieb: Diese Route zieht eine
   * Dokumentnummer aus dem Berichtsregister.
   */
  parameterFuellgradPdf = (projektId: string, gewerk?: string) =>
    this.pdfLaden(
      `/api/projects/${projektId}/parameter/fuellgrad/pdf`
      + (gewerk ? `?gewerk=${encodeURIComponent(gewerk)}` : ''));

  // --- Berichtsregister und Berichtsablage (F1; Seite „Berichte“, TX2) -----

  /**
   * Die Fassungen des Berichtsregisters eines Projekts, jüngste zuerst, mit
   * Ablagestand. Schreibt nichts und vergibt keine Nummer.
   */
  berichtsfassungen = (
    projektId: string,
    filter: { gewerk?: string; art?: string; abgelegt?: boolean; seite?: number; groesse?: number } = {},
  ) => {
    const q = new URLSearchParams();
    if (filter.gewerk) q.set('gewerk', filter.gewerk);
    if (filter.art) q.set('art', filter.art);
    if (filter.abgelegt !== undefined) q.set('abgelegt', String(filter.abgelegt));
    if (filter.seite) q.set('seite', String(filter.seite));
    if (filter.groesse) q.set('groesse', String(filter.groesse));
    const s = q.toString();
    return this.hole<BerichtsfassungenSeite>(
      `/api/projects/${projektId}/berichte${s ? `?${s}` : ''}`);
  };

  /**
   * Eine ABGELEGTE Fassung herunterladen - genau die Bytes, die beim Erzeugen
   * ausgeliefert wurden, sha-geprüft. Zieht keine neue Nummer.
   */
  berichtsfassungLaden = (projektId: string, dokumentnummer: string) =>
    this.pdfLaden(
      `/api/projects/${projektId}/berichte/${encodeURIComponent(dokumentnummer)}.pdf`);

  /**
   * N7b-2 (QUE-13): der Freigabestand der Berichte - je jüngster Fassung und je Fassung mit Schritten, mit den
   * Schritten, die die angemeldete Person jetzt darf. `null`, wenn der Freigabeweg aus ist (Schalter, 404).
   */
  berichtsfreigabestand = async (projektId: string): Promise<BerichtsfreigabeStand[] | null> => {
    const antwort = await this.abrufen(`/api/projects/${projektId}/berichtsfreigaben`, {
      headers: { Authorization: `Bearer ${await this.token()}` },
    });
    if (antwort.status === 404) return null;
    if (!antwort.ok) throw new Error(await fehlertext(antwort));
    return antwort.json() as Promise<BerichtsfreigabeStand[]>;
  };

  /** N7a/N7b-2: einen Schritt des Freigabewegs gehen (Vier-Augen; Begründung bei Vorbehalt und beim Zurückziehen). */
  berichtsfreigabeSchritt = (
    projektId: string, dokumentnummer: string, schritt: Freigabeschritt, begruendung?: string,
  ) => this.sende<unknown>(
    `/api/projects/${projektId}/berichte/${encodeURIComponent(dokumentnummer)}/${schritt}`,
    { begruendung: begruendung ?? null });

  /**
   * Raumbuch als PDF, ein Blatt je Raum.
   *
   * Ohne Geschoss alle Räume - bei tausend Räumen ist das ein Buch von tausend
   * Seiten, deshalb lässt sich eines auswählen.
   */
  raumbuchPdf = (projektId: string, dateiname: string, geschoss?: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/rooms/pdf`
      + (geschoss ? `?geschoss=${encodeURIComponent(geschoss)}` : ''),
      dateiname);

  /**
   * Dasselbe als AUFTRAG: anstoßen, nachsehen, abholen.
   *
   * Der Abruf oben hängt in EINER Anfrage, und die endet am nginx nach 300 s.
   * Gemessen am 23.09.2026: ein Geschoss (259 Räume) 18 s, alle 930 Räume
   * 109 s. Für das ganze Buch ist der Auftrag der Weg — er sagt außerdem, wo
   * er steht, statt einen Kreisel zu drehen.
   */
  raumbuchPdfAnstossen = (projektId: string, geschoss?: string) =>
    this.sende<{ kennung: string }>(
      `/api/projects/${projektId}/rooms/pdf/auftraege`
      + (geschoss ? `?geschoss=${encodeURIComponent(geschoss)}` : ''),
      {});

  raumbuchPdfStand = (projektId: string, kennung: string) =>
    this.hole<RaumbuchAuftragStand>(
      `/api/projects/${projektId}/rooms/pdf/auftraege/${kennung}`);

  raumbuchPdfAbholen = (projektId: string, kennung: string, dateiname: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/rooms/pdf/auftraege/${kennung}/datei`,
      dateiname);

  /**
   * Die zuletzt GEMESSENEN Dauern — für den Hinweis im Dialog.
   *
   * Leer heißt: in diesem Prozess ist noch keiner gelaufen. Dann steht dort
   * auch keine Zahl, statt einer geschätzten.
   */
  raumbuchPdfDauern = (projektId: string) =>
    this.hole<RaumbuchDauer[]>(`/api/projects/${projektId}/rooms/pdf/dauern`);

  raumbuchExcel = (projektId: string, dateiname: string) =>
    this.dateiLaden(`/api/projects/${projektId}/rooms/excel`, dateiname);

  /**
   * Liest eine Tabelle ein.
   *
   * Ohne 'uebernehmen' wird nur gezeigt, was sich aendern wuerde - die Datei
   * war zwischenzeitlich in fremden Haenden.
   */
  async raumbuchEinlesen(projektId: string, datei: File, uebernehmen: boolean) {
    const inhalt = new FormData();
    inhalt.append('datei', datei);
    inhalt.append('uebernehmen', uebernehmen ? 'true' : 'false');

    const antwort = await fetch(`/api/projects/${projektId}/rooms/excel`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${await this.token()}` },
      body: inhalt,
    });

    if (!antwort.ok) {
      throw new Error(await fehlertext(antwort));
    }

    return antwort.json() as Promise<{ uebernommen: boolean; vorschau: ExcelVorschau }>;
  }

  vorlageAnlegen = (daten: {
    projectId?: string | null;
    templateKey: string;
    name: string;
    werte: Record<string, string | null>;
    note?: string;
  }) => this.sende<{ id: string }>('/api/room-templates', daten);

  vorlageAendern = (id: string, daten: {
    name?: string;
    werte?: Record<string, string | null>;
    note?: string;
  }) => this.schicke(`/api/room-templates/${id}`, 'PUT', daten);

  vorlageEntfernen = (id: string) => this.schicke(`/api/room-templates/${id}`, 'DELETE');

  async konfigHochladen(daten: {
    datei: File;
    key: string;
    kind: string;
    version: string;
    projektId?: string;
  }): Promise<KonfigDatei> {
    const inhalt = new FormData();
    inhalt.append('file', daten.datei);
    inhalt.append('key', daten.key);
    inhalt.append('kind', daten.kind);
    inhalt.append('version', daten.version);
    if (daten.projektId) inhalt.append('projectId', daten.projektId);

    const antwort = await fetch('/api/config-files', {
      method: 'POST',
      headers: { Authorization: `Bearer ${await this.token()}` },
      body: inhalt,
    });

    if (!antwort.ok) {
      throw new Error(await fehlertext(antwort));
    }

    return antwort.json() as Promise<KonfigDatei>;
  }

  // --- MSR / Gebäudeautomation ----------------------------------------------
  //
  // Basis `/api/projects/{projektId}/msr`, siehe docs/msr-api-vertrag.md. Der
  // Umfang von MSR wird nicht gezeichnet, er FOLGT aus den Gewerken — diese
  // Seite liest nur, mit Ausnahme der Ableitung und der Pflegeformulare unter
  // "Grundlagen"/"Kapazität" und der AKS-Vergabe.

  msrUebersicht = (projektId: string) =>
    this.hole<MsrUebersicht>(`/api/projects/${projektId}/msr/uebersicht`);

  msrBaum = (projektId: string, nach: 'gewerk' | 'asp' = 'gewerk') =>
    this.hole<MsrBaum>(`/api/projects/${projektId}/msr/baum?nach=${nach}`);

  msrAsps = (projektId: string) =>
    this.hole<MsrAspZeile[]>(`/api/projects/${projektId}/msr/asps`);

  msrAsp = (projektId: string, id: string) =>
    this.hole<MsrAspDetail>(`/api/projects/${projektId}/msr/asps/${id}`);

  msrAspKapazitaetSetzen = (
    projektId: string, id: string,
    w: {
      be: number; ba: number; ae: number; aa: number; ze: number; kom: number;
      reserveProzent: number; fabrikat?: string; baureihe?: string;
      begruendung: string;
    },
  ) => this.schicke<MsrAspZeile>(
    `/api/projects/${projektId}/msr/asps/${id}/kapazitaet`, 'PUT', w) as Promise<MsrAspZeile>;

  msrAnlagen = (
    projektId: string,
    f?: { gewerk?: string; anlagenart?: string; asp?: string },
  ) => {
    const p = new URLSearchParams();
    if (f?.gewerk) p.set('gewerk', f.gewerk);
    if (f?.anlagenart) p.set('anlagenart', f.anlagenart);
    if (f?.asp) p.set('asp', f.asp);

    return this.hole<MsrAnlageZeile[]>(
      `/api/projects/${projektId}/msr/anlagen?${p}`);
  };

  msrAnlage = (projektId: string, id: string) =>
    this.hole<MsrAnlageDetail>(`/api/projects/${projektId}/msr/anlagen/${id}`);

  /** KI-Beschreibung einer Anlage erzeugen oder erneuern (`force=true`). */
  msrBeschreibungErzeugen = (projektId: string, id: string, force = false) =>
    this.sende<MsrBeschreibung>(
      `/api/projects/${projektId}/msr/anlagen/${id}/beschreibung`
        + (force ? '?force=true' : ''), {});

  /** Beschreibungen für alle Anlagen ohne Beschreibung erzeugen (Sammelaktion). */
  msrBeschreibungenErzeugen = (projektId: string, nurFehlende = true) =>
    this.sende<{
      erzeugt: number; uebersprungen: number; fehler: number;
      eingabeTokens: number; ausgabeTokens: number;
    }>(
      `/api/projects/${projektId}/msr/beschreibungen`
        + `?nur_fehlende=${nurFehlende ? 'true' : 'false'}`, {});

  msrFeldgeraete = (
    projektId: string,
    f?: {
      gewerk?: string; bauteilart?: string; asp?: string; anlage?: string;
      suche?: string; seite?: number; groesse?: number;
    },
  ) => {
    const p = new URLSearchParams();
    if (f?.gewerk) p.set('gewerk', f.gewerk);
    if (f?.bauteilart) p.set('bauteilart', f.bauteilart);
    if (f?.asp) p.set('asp', f.asp);
    if (f?.anlage) p.set('anlage', f.anlage);
    if (f?.suche) p.set('suche', f.suche);
    if (f?.seite !== undefined) p.set('seite', String(f.seite));
    if (f?.groesse !== undefined) p.set('groesse', String(f.groesse));

    return this.hole<MsrSeite<MsrFeldgeraetZeile>>(
      `/api/projects/${projektId}/msr/feldgeraete?${p}`);
  };

  msrFeldgeraet = (projektId: string, id: string) =>
    this.hole<MsrFeldgeraetDetail>(
      `/api/projects/${projektId}/msr/feldgeraete/${id}`);

  msrDatenpunkte = (
    projektId: string,
    f?: {
      asp?: string; gewerk?: string; anlage?: string; art?: string;
      herkunft?: string; anbindung?: string; suche?: string;
      seite?: number; groesse?: number;
    },
  ) => {
    const p = new URLSearchParams();
    if (f?.asp) p.set('asp', f.asp);
    if (f?.gewerk) p.set('gewerk', f.gewerk);
    if (f?.anlage) p.set('anlage', f.anlage);
    if (f?.art) p.set('art', f.art);
    if (f?.herkunft) p.set('herkunft', f.herkunft);
    if (f?.anbindung) p.set('anbindung', f.anbindung);
    if (f?.suche) p.set('suche', f.suche);
    if (f?.seite !== undefined) p.set('seite', String(f.seite));
    if (f?.groesse !== undefined) p.set('groesse', String(f.groesse));

    return this.hole<MsrSeite<MsrDatenpunktZeile>>(
      `/api/projects/${projektId}/msr/datenpunkte?${p}`);
  };

  msrFunktionen = (projektId: string, f?: { anlage?: string; kategorie?: string }) => {
    const p = new URLSearchParams();
    if (f?.anlage) p.set('anlage', f.anlage);
    if (f?.kategorie) p.set('kategorie', f.kategorie);

    return this.hole<MsrFunktionZeile[]>(
      `/api/projects/${projektId}/msr/funktionen?${p}`);
  };

  msrRaumautomation = (projektId: string, f?: { geschoss?: string; quadrant?: string }) => {
    const p = new URLSearchParams();
    if (f?.geschoss) p.set('geschoss', f.geschoss);
    if (f?.quadrant) p.set('quadrant', f.quadrant);

    return this.hole<MsrRaumZeile[]>(
      `/api/projects/${projektId}/msr/raumautomation?${p}`);
  };

  msrAksAufbau = (projektId: string) =>
    this.hole<MsrAksAufbauZeile[]>(`/api/projects/${projektId}/msr/aks/aufbau`);

  /** Die vollständige Liste — sie ersetzt die bisherige Reihenfolge komplett. */
  msrAksAufbauSetzen = (projektId: string, zeilen: MsrAksAufbauZeile[]) =>
    this.schicke<MsrAksAufbauZeile[]>(
      `/api/projects/${projektId}/msr/aks/aufbau`, 'PUT', zeilen) as Promise<MsrAksAufbauZeile[]>;

  msrAksProtokoll = (projektId: string, f?: { seite?: number; groesse?: number }) => {
    const p = new URLSearchParams();
    if (f?.seite !== undefined) p.set('seite', String(f.seite));
    if (f?.groesse !== undefined) p.set('groesse', String(f.groesse));

    return this.hole<MsrSeite<MsrAksProtokollZeile>>(
      `/api/projects/${projektId}/msr/aks/protokoll?${p}`);
  };

  msrGrundlagen = (projektId: string) =>
    this.hole<MsrGrundlagen>(`/api/projects/${projektId}/msr/grundlagen`);

  /**
   * Die Festlegungen des Fachplaners vom 16.09.2026 (Fragen 64 bis 79) mit
   * ihrer Wirkung: GA-Effizienzklasse und Stand der Klemmenfreigabe.
   */
  msrFestlegungen = (projektId: string) =>
    this.hole<MsrFestlegungen>(`/api/projects/${projektId}/msr/festlegungen`);

  /** Der M-Bus-Adressplan (Frage 64). */
  msrMbus = (projektId: string) =>
    this.hole<MsrMbus>(`/api/projects/${projektId}/msr/mbus`);

  /** Klemmenzuteilung freigeben und einfrieren (Frage 76). */
  msrKlemmenFreigeben = (projektId: string, bemerkung?: string) =>
    this.schicke<MsrKlemmenfreigabe>(
      `/api/projects/${projektId}/msr/klemmen/freigeben`, 'POST',
      { bemerkung }) as Promise<MsrKlemmenfreigabe>;

  /** Die Freigabe aufheben — nur mit Begruendung (Frage 76). */
  msrKlemmenFreigabeAufheben = (projektId: string, bemerkung: string) =>
    this.schicke<MsrKlemmenfreigabe>(
      `/api/projects/${projektId}/msr/klemmen/freigabe-aufheben`, 'POST',
      { bemerkung }) as Promise<MsrKlemmenfreigabe>;

  msrVorlageSetzen = (projektId: string, id: string, zeile: Omit<MsrVorlage, 'id'>) =>
    this.schicke<MsrVorlage>(
      `/api/projects/${projektId}/msr/grundlagen/vorlagen/${id}`, 'PUT', zeile) as Promise<MsrVorlage>;

  msrFunktionsvorlageSetzen = (
    projektId: string, id: string, zeile: Omit<MsrFunktionsvorlage, 'id'>,
  ) => this.schicke<MsrFunktionsvorlage>(
    `/api/projects/${projektId}/msr/grundlagen/funktionsvorlagen/${id}`, 'PUT', zeile) as Promise<MsrFunktionsvorlage>;

  msrAspregelSetzen = (projektId: string, id: string, zeile: Omit<MsrAspregel, 'id'>) =>
    this.schicke<MsrAspregel>(
      `/api/projects/${projektId}/msr/grundlagen/aspregeln/${id}`, 'PUT', zeile) as Promise<MsrAspregel>;

  /** Grundlagen der VDI-3814-Liste (Spaltenkatalog, Zeilenvorlagen, Aggregatkürzel-Katalog). */
  msrVdiGrundlagen = (projektId: string) =>
    this.hole<MsrVdiGrundlagen>(`/api/projects/${projektId}/msr/grundlagen/vdi`);

  msrVdiZeilenvorlageSetzen = (
    projektId: string, id: string, zeile: Omit<MsrVdiZeilenvorlage, 'id'>,
  ) => this.schicke<MsrVdiZeilenvorlage>(
    `/api/projects/${projektId}/msr/grundlagen/vdi/zeilenvorlagen/${id}`,
    'PUT', zeile) as Promise<MsrVdiZeilenvorlage>;

  msrEinstellungSetzen = (
    projektId: string, schluessel: string,
    w: { wert: string; zahlwert?: number; begruendung?: string },
  ) => this.schicke<MsrEinstellung>(
    `/api/projects/${projektId}/msr/grundlagen/einstellungen/${schluessel}`,
    'PUT', w) as Promise<MsrEinstellung>;

  /** Führt den Ableitungslauf synchron aus — Sekunden, keine Warteschlange. */
  msrAbleiten = (projektId: string, schreiben = false, ki = false) =>
    this.sende<MsrLauf>(
      `/api/projects/${projektId}/msr/ableiten`
        + `?schreiben=${schreiben ? 'true' : 'false'}`
        + `&ki=${ki ? 'true' : 'false'}`, {});

  /**
   * Ein GA-Schema (Funktionsschema, Topologie oder ASP-Blatt) als Objekt-URL —
   * dasselbe Muster wie `koordinationVorschauSvg`: Ein <img src="/api/..">
   * sendet kein Bearer-Token und würde mit 401 abgewiesen.
   */
  private async msrSchemaBlob(pfad: string): Promise<string> {
    const antwort = await this.abrufen(pfad, {
      headers: { Authorization: `Bearer ${await this.token()}` },
    });

    if (!antwort.ok) throw new Error(await fehlertext(antwort));

    const blob = await antwort.blob();
    return URL.createObjectURL(blob);
  }

  msrSchemaAnlageSvg = (projektId: string, anlageId: string) =>
    this.msrSchemaBlob(
      `/api/projects/${projektId}/msr/schema/anlage/${anlageId}.svg`);

  msrSchemaTopologieSvg = (projektId: string) =>
    this.msrSchemaBlob(`/api/projects/${projektId}/msr/schema/topologie.svg`);

  msrSchemaAspSvg = (projektId: string, aspId: string) =>
    this.msrSchemaBlob(`/api/projects/${projektId}/msr/schema/asp/${aspId}.svg`);

  /** Übersichtsschaltplan des ASP (Spezifikation 12.3) — dasselbe Blob-Muster. */
  msrSchemaAspSchaltplanSvg = (projektId: string, aspId: string) =>
    this.msrSchemaBlob(
      `/api/projects/${projektId}/msr/schema/asp/${aspId}/schaltplan.svg`);

  /**
   * Das bisherige Funktionsschema (VDI 3814 Blatt 1) — seit der Vorgabe
   * Teuber + Viel liefert `schema/anlage/{id}.svg` das MSR-Schema im Stil
   * 13.6; das alte Schema bleibt unter diesem Pfad zusätzlich erreichbar.
   */
  msrSchemaAnlageFunktionsschemaSvg = (projektId: string, anlageId: string) =>
    this.msrSchemaBlob(
      `/api/projects/${projektId}/msr/schema/anlage/${anlageId}/funktionsschema.svg`);

  /** GA-Funktionsliste einer Anlage nach VDI 3814 Blatt 4.3 (Vorgabe Teuber + Viel). */
  msrVdiFunktionsliste = (projektId: string, anlageId: string) =>
    this.hole<MsrVdiFunktionsliste>(
      `/api/projects/${projektId}/msr/vdi/funktionsliste?anlage=${anlageId}`);

  /** Klemmenbelegung eines ASP als JSON (dieselben Daten wie im Excel-Export). */
  msrKlemmen = (projektId: string, aspId: string) =>
    this.hole<MsrKlemmenbelegung>(
      `/api/projects/${projektId}/msr/asps/${aspId}/klemmen`);

  /**
   * Befunde der Gebäudeautomation (Migration 0500) — gefiltert nach Ebene,
   * Schwere, Regel oder Ziel. Vor dem 16.09.2026 standen sie nur im
   * Hinweistext eines Laufs und verschwanden mit dem nächsten.
   */
  msrBefunde = (
    projektId: string,
    filter: { ebene?: string; schwere?: string; regel?: string; ziel?: string } = {},
  ) => {
    const p = new URLSearchParams();
    if (filter.ebene) p.set('ebene', filter.ebene);
    if (filter.schwere) p.set('schwere', filter.schwere);
    if (filter.regel) p.set('regel', filter.regel);
    if (filter.ziel) p.set('ziel', filter.ziel);
    const q = p.toString();
    return this.hole<MsrBefundliste>(
      `/api/projects/${projektId}/msr/befunde${q ? `?${q}` : ''}`);
  };

  /** Kabelliste eines ASP — je Feldgerät und Kabeltyp, mit Länge und Schirmung. */
  msrKabel = (projektId: string, aspId: string) =>
    this.hole<MsrKabelliste>(
      `/api/projects/${projektId}/msr/asps/${aspId}/kabel`);

  /** Leistungs- und Wärmebilanz eines Schranks nach DIN EN 61439-1. */
  msrSchrankbilanz = (projektId: string, aspId: string) =>
    this.hole<MsrSchrankbilanz>(
      `/api/projects/${projektId}/msr/asps/${aspId}/schrankbilanz`);

  /** Einstellwerte einer Anlage (Anlagenwerte und je Aggregat), Spezifikation Abschnitt 14. */
  msrEinstellwerte = (projektId: string, anlageId: string) =>
    this.hole<MsrEinstellwerteAntwort>(
      `/api/projects/${projektId}/msr/anlagen/${anlageId}/einstellwerte`);

  /** Einen Einstellwert von Hand setzen — Begründung ist Pflicht, Herkunft wird 'gepflegt'. */
  msrEinstellwertSetzen = (
    projektId: string, id: string, w: { wert: string; begruendung: string },
  ) => this.schicke<MsrEinstellwert>(
    `/api/projects/${projektId}/msr/einstellwerte/${id}`, 'PUT', w) as Promise<MsrEinstellwert>;

  /** KI-Vorschlag für die Einstellwerte einer Anlage erzeugen oder erneuern (`force=true`). */
  msrEinstellwerteVorschlagen = (projektId: string, anlageId: string, force = false) =>
    this.sende<MsrEinstellwerteVorschlagErgebnis>(
      `/api/projects/${projektId}/msr/anlagen/${anlageId}/einstellwerte/vorschlagen`
        + (force ? '?force=true' : ''), {});

  /** KI-Vorschläge für alle Anlagen ohne Vorschlag erzeugen (Sammelaktion). */
  msrEinstellwerteVorschlagenAlle = (projektId: string, nurFehlende = true) =>
    this.sende<MsrEinstellwerteVorschlagErgebnis>(
      `/api/projects/${projektId}/msr/einstellwerte/vorschlagen`
        + `?nur_fehlende=${nurFehlende ? 'true' : 'false'}`, {});

  /** Der Vorlagenkatalog der Einstellwerte (Spezifikation 14.1), gepflegt in "Grundlagen". */
  msrEinstellwertvorlagen = (projektId: string) =>
    this.hole<MsrEinstellwertvorlage[]>(
      `/api/projects/${projektId}/msr/grundlagen/einstellwertvorlagen`);

  msrEinstellwertvorlageSetzen = (
    projektId: string, id: string, zeile: Omit<MsrEinstellwertvorlage, 'id'>,
  ) => this.schicke<MsrEinstellwertvorlage>(
    `/api/projects/${projektId}/msr/grundlagen/einstellwertvorlagen/${id}`,
    'PUT', zeile) as Promise<MsrEinstellwertvorlage>;

  /** Excel der Einstellwerte — genau einer der beiden Filter (`anlage` oder `asp`) ist gesetzt. */
  msrEinstellwerteExcel = (
    projektId: string, dateiname: string, f: { anlage?: string; asp?: string },
  ) => {
    const p = new URLSearchParams();
    if (f.anlage) p.set('anlage', f.anlage);
    if (f.asp) p.set('asp', f.asp);

    return this.dateiLaden(
      `/api/projects/${projektId}/msr/export/einstellwerte.xlsx?${p}`, dateiname);
  };

  // --- PDF-Dokumente (Ergänzung 08.09.2026, Spezifikation Abschnitt 15) -----

  /** Liste der abgelegten PDF-Dokumente, mit Filtern Art/ASP/Anlage/Ziel. */
  msrDokumente = (
    projektId: string,
    f?: { art?: MsrDokumentart; asp?: string; anlage?: string; ziel?: 'projekt' | 'asp' | 'anlage' },
  ) => {
    const p = new URLSearchParams();
    if (f?.art) p.set('art', f.art);
    if (f?.asp) p.set('asp', f.asp);
    if (f?.anlage) p.set('anlage', f.anlage);
    if (f?.ziel) p.set('ziel', f.ziel);
    const frage = p.toString();
    return this.hole<MsrDokument[]>(
      `/api/projects/${projektId}/msr/dokumente${frage ? `?${frage}` : ''}`);
  };

  /** Läufe von `msr-pdf`, mit Zahlen und Gegenproben. */
  msrDokumentlaeufe = (projektId: string, seite = 1, groesse = 20) =>
    this.hole<MsrSeite<MsrDokumentlauf>>(
      `/api/projects/${projektId}/msr/dokumentlaeufe?seite=${seite}&groesse=${groesse}`);

  /** Ein Dokument herunterladen (Content-Disposition: attachment). */
  msrDokumentHerunterladen = (projektId: string, dokumentId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/msr/dokumente/${dokumentId}`);

  /**
   * Dasselbe Dokument als Objekt-URL zur Anzeige — dasselbe Blob-Muster wie
   * `msrSchemaBlob`, nur gegen den 'inline'-Pfad statt den Download-Pfad.
   */
  msrDokumentAnzeigen = (projektId: string, dokumentId: string) =>
    this.msrSchemaBlob(`/api/projects/${projektId}/msr/dokumente/${dokumentId}/inline`);

  /**
   * Alle gefilterten MSR-Dokumente als ein ZIP (Auftrag Fachplaner
   * 16.09.2026).
   *
   * Derselbe Filter wie `msrDokumente` — der Knopf packt, was die Tabelle
   * zeigt. Der Dateiname kommt aus `Content-Disposition`; der hier
   * übergebene ist nur der Rückfall.
   */
  msrDokumenteZip = (
    projektId: string,
    f?: { art?: MsrDokumentart; asp?: string; anlage?: string; ziel?: 'projekt' | 'asp' | 'anlage' },
  ) => {
    const p = new URLSearchParams();
    if (f?.art) p.set('art', f.art);
    if (f?.asp) p.set('asp', f.asp);
    if (f?.anlage) p.set('anlage', f.anlage);
    if (f?.ziel) p.set('ziel', f.ziel);
    const frage = p.toString();

    return this.dateiLaden(
      `/api/projects/${projektId}/msr/dokumente/zip${frage ? `?${frage}` : ''}`,
      'MSR_Unterlagen.zip');
  };

  // --- Unterlagenpaket je Gewerk ------------------------------------------
  //
  // Die uebrigen Gewerke haben keine Dokumentenablage: Ihre Berichte
  // entstehen beim Abruf. Das Paket erzeugt sie nacheinander frisch, jeder
  // mit eigener Dokumentnummer - deshalb dauert es und deshalb gibt es
  // '?art=' als Ausweg.

  /** Was ein Paket je Gewerk enthalten würde — ohne etwas zu erzeugen. */
  unterlagenUebersicht = (projektId: string) =>
    this.hole<UnterlagenGewerk[]>(`/api/projects/${projektId}/unterlagen`);

  // --- Funktionale Leistungsbeschreibung (Plan Abschnitt 7) --------------

  /** Der Kostengruppenbaum mit dem Stand des letzten Laufs. */
  flbBaum = (projektId: string) =>
    this.hole<FlbBaum>(`/api/projects/${projektId}/funktionalbeschreibung`);

  /** Ein Gewerk mit allen Kapiteln, Anlagenlisten und Texten. */
  flbGewerk = (projektId: string, gewerk: string) =>
    this.hole<FlbGewerk>(
      `/api/projects/${projektId}/funktionalbeschreibung/${encodeURIComponent(gewerk)}`);

  /**
   * Einen Abschnittstext pflegen. Die HERKUNFT wird nicht mitgeschickt: Wer
   * ueber diesen Weg schreibt, ist der Fachplaner - die API setzt sie.
   */
  flbAbschnittPflegen = (
    projektId: string, gewerk: string, kostengruppe: string, abschnitt: number,
    eingabe: { titel?: string; text: string; normen?: string; quelle?: string;
               istBestaetigt: boolean },
  ) =>
    this.schicke<{ kostengruppe: string; abschnitt: number; herkunft: string }>(
      `/api/projects/${projektId}/funktionalbeschreibung/${encodeURIComponent(gewerk)}`
      + `/${encodeURIComponent(kostengruppe)}/${abschnitt}`,
      'PUT', eingabe);

  /** Das Heft eines Gewerks als PDF. */
  flbBericht = (projektId: string, gewerk: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/funktionalbeschreibung/${encodeURIComponent(gewerk)}/bericht.pdf`,
      `FLB_${gewerk}.pdf`);

  /** Der Gesamtband ueber alle Kostengruppen. */
  flbGesamt = (projektId: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/funktionalbeschreibung/gesamt.pdf`,
      'FLB_Gesamtband.pdf');

  /** Das Paket eines Gewerks laden. `art` schränkt auf eine Art ein. */
  unterlagenZip = (projektId: string, gewerk: string, art?: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/unterlagen/${gewerk}.zip`
      + (art ? `?art=${encodeURIComponent(art)}` : ''),
      `${gewerk}_Unterlagen.zip`);

  /**
   * Dokumente erzeugen (der `msr-pdf`-Lauf, synchron) — `arten` leer heißt
   * "alle Einzelblätter" (Spezifikation 15.4).
   */
  msrDokumenteErzeugen = (
    projektId: string,
    f?: { arten?: MsrDokumentart[]; asp?: string; anlage?: string; hefte?: boolean; projektheft?: boolean },
  ) => {
    const p = new URLSearchParams();
    if (f?.arten?.length) p.set('arten', f.arten.join(','));
    if (f?.asp) p.set('asp', f.asp);
    if (f?.anlage) p.set('anlage', f.anlage);
    if (f?.hefte) p.set('hefte', 'true');
    if (f?.projektheft) p.set('projektheft', 'true');
    const frage = p.toString();
    return this.sende<MsrDokumentlauf>(
      `/api/projects/${projektId}/msr/dokumente/erzeugen${frage ? `?${frage}` : ''}`, {});
  };

  // --- MSR Excel — dasselbe Muster wie `lueftungExcel`: ein Download über
  // `dateiLaden`, der Umfang als Filter in der Adresse.

  msrDatenpunktlisteExcel = (
    projektId: string, dateiname: string, f?: { asp?: string; gewerk?: string },
  ) => {
    const p = new URLSearchParams();
    if (f?.asp) p.set('asp', f.asp);
    if (f?.gewerk) p.set('gewerk', f.gewerk);
    const frage = p.toString();

    return this.dateiLaden(
      `/api/projects/${projektId}/msr/export/datenpunktliste.xlsx`
        + (frage ? `?${frage}` : ''),
      dateiname);
  };

  msrFunktionslisteExcel = (projektId: string, dateiname: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/msr/export/funktionsliste.xlsx`, dateiname);

  /**
   * Datei nach 13.5 — eine Anlage (`anlage`) oder die Sammeldatei eines ASP
   * (`asp`); genau einer der beiden Filter ist gesetzt.
   */
  msrVdiFunktionslisteExcel = (
    projektId: string, dateiname: string, f?: { anlage?: string; asp?: string },
  ) => {
    const p = new URLSearchParams();
    if (f?.anlage) p.set('anlage', f.anlage);
    if (f?.asp) p.set('asp', f.asp);
    const frage = p.toString();

    return this.dateiLaden(
      `/api/projects/${projektId}/msr/export/vdi-funktionsliste.xlsx`
        + (frage ? `?${frage}` : ''),
      dateiname);
  };

  msrKlemmenbelegungExcel = (projektId: string, aspId: string, dateiname: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/msr/export/klemmenbelegung.xlsx?asp=${aspId}`, dateiname);

  msrAspListeExcel = (projektId: string, dateiname: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/msr/export/asp-liste.xlsx`, dateiname);

  msrFeldgeraeteExcel = (projektId: string, dateiname: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/msr/export/feldgeraete.xlsx`, dateiname);

  msrRaumautomationExcel = (projektId: string, dateiname: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/msr/export/raumautomation.xlsx`, dateiname);

  // --- TM-2b/TM-8a: die MSR-Themenmappen über den Excel-Kern (…/msr/<thema>/listen.xlsx) und der Rechenstand.
  // Der Dateiname kommt vom Server; der übergebene ist nur der Rückfall.

  /** Kabel und Klemmen des Projekts oder eines ASP (`asp`). */
  msrKabelExcel = (projektId: string, asp?: string) =>
    this.dateiLaden(
      `/api/projects/${projektId}/msr/kabel/listen.xlsx` + (asp ? `?asp=${encodeURIComponent(asp)}` : ''),
      'MSR_Kabel-und-Klemmen.xlsx');

  msrBefundeExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/msr/befunde/listen.xlsx`, 'MSR_Befunde.xlsx');

  msrEffizienzExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/msr/effizienz/listen.xlsx`, 'MSR_Effizienzklasse.xlsx');

  msrMbusExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/msr/mbus/listen.xlsx`, 'MSR_M-Bus.xlsx');

  msrSchrankbilanzExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/msr/schrankbilanz/listen.xlsx`, 'MSR_Schrankbilanz.xlsx');

  msrIspExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/msr/isp/listen.xlsx`, 'MSR_ISP-Listen.xlsx');

  /** Jüngster Lauf von msr-ableiten, Modellstand der Eingänge, veraltet (TM-8a). */
  msrRechenstand = (projektId: string) =>
    this.hole<MsrRechenstand>(`/api/projects/${projektId}/msr/rechenstand`);

  /** Annahmen und Vorgaben (TM-8b, S1.6) - dieselbe Liste wie die Blätter „Annahmen“ der Mappen. */
  msrAnnahmen = (projektId: string) =>
    this.hole<MsrAnnahmenliste>(`/api/projects/${projektId}/msr/annahmen`);

  // ═══ TL1: Der maßgebende Auslegungslauf ════════════════════════════════
  //
  // Welcher Heiz- bzw. Kühllastlauf gilt, sagt der Server an EINER Stelle
  // (Auslegungslauf.cs). Die Laufliste bleibt nach Laufnummer sortiert; ihr
  // erster Eintrag ist nicht mehr automatisch „der“ Lauf.

  heizlastAuslegungslauf = (projektId: string) =>
    this.hole<{ wahl: AuslegungslaufWahl | null; auslegungstemperaturC: number | null }>(
      `/api/projects/${projektId}/heizlast/auslegungslauf`);

  kuehllastAuslegungslauf = (projektId: string) =>
    this.hole<{ wahl: AuslegungslaufWahl | null }>(
      `/api/projects/${projektId}/kuehllast/auslegungslauf`);

  /** Kennzeichnet einen Heizlastlauf als maßgebend (oder setzt zurück). Begründung ist Pflicht. */
  heizlastMassgebend = (laufId: string, begruendung: string, zuruecksetzen = false) =>
    this.sende<KennzeichnungAntwort>(
      `/api/heizlast/laeufe/${laufId}/massgebend`, { begruendung, zuruecksetzen });

  kuehllastMassgebend = (laufId: string, begruendung: string, zuruecksetzen = false) =>
    this.sende<KennzeichnungAntwort>(
      `/api/kuehllast/laeufe/${laufId}/massgebend`, { begruendung, zuruecksetzen });

  // ═══ AK1: Das Brandschottkataster als Excel ════════════════════════════
  //
  // Dieselben Zeilen und derselbe Datenhash wie `katasterPdf`, als Arbeitsliste
  // ohne eigene Dokumentnummer (TX-STANDARD S2.5). Dieselben Filter. Den
  // Dateinamen setzt der Server; der übergebene ist nur der Rückfall.

  katasterExcel = (projektId: string, o?: {
    planungsbereich?: string; geschoss?: string;
  }) => {
    const p = new URLSearchParams();
    if (o?.planungsbereich) p.set('planungsbereich', o.planungsbereich);
    if (o?.geschoss) p.set('geschoss', o.geschoss);

    const frage = p.toString();

    return this.dateiLaden(
      `/api/projects/${projektId}/aussparungen/kataster.xlsx`
        + (frage ? `?${frage}` : ''),
      'Brandschottkataster.xlsx');
  };
  // ═══ TL2: Annahmen und Vorgaben ═══════════════════════════════════════
  //
  // Welche Vorgaben die Auslegungsläufe gelesen haben (Momentaufnahme je
  // Lauf) und ihr Bestätigungsstand. Bestätigen geht nur mit der Rolle aus
  // der Einstellung (vorläufig ProjectAdmin) und mit Begründung und Quelle.

  heizlastVorgaben = (projektId: string) =>
    this.hole<{ vorgaben: VorgabeZeile[]; bestaetigenRollen: string[] }>(
      `/api/projects/${projektId}/heizlast/vorgaben`);

  heizlastAnnahmenStand = (projektId: string) =>
    this.hole<AnnahmenStandProjekt>(`/api/projects/${projektId}/heizlast/annahmen-stand`);

  heizlastLaufAnnahmen = (laufId: string) =>
    this.hole<Annahmenstand>(`/api/heizlast/laeufe/${laufId}/annahmen`);

  kuehllastLaufAnnahmen = (laufId: string) =>
    this.hole<Annahmenstand>(`/api/kuehllast/laeufe/${laufId}/annahmen`);

  /** Bestätigt eine Vorgabe oder nimmt die Bestätigung zurück. Begründung und Quelle sind Pflicht. */
  vorgabeBestaetigen = (projektId: string, anfrage: VorgabeBestaetigung) =>
    this.sende<{ ausgang: string; message: string; zeilen: number }>(
      `/api/projects/${projektId}/heizlast/vorgaben/bestaetigen`, anfrage);

  // ═══ TL3: Heiz- und Kühllastausweis als Excel (TX-STANDARD S2.6) ═══════
  //
  // Die Mappe eines bestimmten Laufs; den Dateinamen setzt der Server.

  heizlastListe = (projektId: string, laufId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/heizlast/laeufe/${laufId}/listen.xlsx`, 'Heizlast.xlsx');

  kuehllastListe = (projektId: string, laufId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/kuehllast/laeufe/${laufId}/listen.xlsx`, 'Kuehllast.xlsx');

  // ═══ TV5 Lüftung „Prüfungen sichtbar“ (Anfang) ══════════════════════════
  //
  // Die Ergebnisse von lueftung-pruefen (J9), luftmengen-pruefen und die
  // Befunde von druckverlust-lueftung. Seite, PDF und Excel lesen dasselbe
  // Datenmodell (LueftungPruefungListen.cs); nur lesend.

  lueftungGeraetepruefung = (projektId: string) =>
    this.hole<LueftungPruefantwort<LueftungGeraetemodell, LueftungGeraetezeile>>(
      `/api/projects/${projektId}/lueftung/pruefung/geraete`);

  lueftungBrandschutz = (projektId: string) =>
    this.hole<LueftungBrandschutzantwort>(`/api/projects/${projektId}/lueftung/pruefung/brandschutz`);

  lueftungLuftmengenliste = (projektId: string) =>
    this.hole<LueftungPruefantwort<LueftungLuftmengenmodell, LueftungLuftmengenzeile>>(
      `/api/projects/${projektId}/lueftung/pruefung/luftmengen`);

  lueftungPruefbefunde = (projektId: string) =>
    this.hole<LueftungPruefantwort<LueftungNetzbefundmodell, LueftungNetzbefundzeile>>(
      `/api/projects/${projektId}/lueftung/pruefung/befunde`);

  /** PDF über das Register (LUE-RLT, -BSK, -ENR, -LMG); der Dateiname kommt vom Server. */
  lueftungPruefberichtPdf = (projektId: string, art: LueftungPruefbericht) =>
    this.pdfLaden(`/api/projects/${projektId}/lueftung/pruefung/${art}/bericht.pdf`);

  /** Excel über den Kern (TX1); `blatt` nur bei der J9-Mappe. Der Dateiname kommt vom Server. */
  lueftungPruefListen = (projektId: string, thema: LueftungPruefliste, blatt?: 'geraete' | 'bsk' | 'entrauchung') =>
    this.dateiLaden(
      `/api/projects/${projektId}/lueftung/pruefung/`
      + (thema === 'pruefung' ? 'listen.xlsx' : `${thema}/listen.xlsx`)
      + (blatt ? `?blatt=${blatt}` : ''),
      'Lueftung.xlsx');
  // ═══ TV5 (Ende) ════════════════════════════════════════════════════════
  // ═══ TH-2 (Anfang): Heizung – Erzeugung und Absicherung ════════════════

  /**
   * Erzeugung und Absicherung (TH-2): Erzeugerbilanz, Kaskade, Wärmeerzeuger, Absicherung je Erzeuger,
   * Ausdehnung und Absicherungsprüfung (J15) – gelesen, nicht gerechnet; mit Vorbehalten (HZK-29, EN 378).
   */
  heizungErzeugung = (projektId: string) =>
    this.hole<HeizungErzeugungAntwort>(`/api/projects/${projektId}/heizung/erzeugung`);

  /** Die Stundenwerte des maßgebenden Heizlastlaufs (Stundenbilanz, kein Auslegungsfall); null ohne Werte. */
  heizungErzeugungJahresgang = (projektId: string) =>
    this.hole<HeizungJahresgangwerte | null>(`/api/projects/${projektId}/heizung/erzeugung/jahresgang`);

  /** Die Excel-Mappe „Erzeugung und Absicherung“ (Dateiname vom Server). */
  heizungErzeugungExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/heizung/erzeugung/listen.xlsx`, 'Heizung_Erzeugung.xlsx');

  /** Die Excel-Mappe „Rohrnetz“: Gruppen, Verbraucherklassen, alle Wege flach (Dateiname vom Server). */
  heizungRohrnetzExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/heizung/rohrnetz/listen.xlsx`, 'Heizung_Rohrnetz.xlsx');

  /** Bericht HZG-ABG (TH-4) über die Berichtserzeugung; gleicher Stand liefert die abgelegte Fassung (F11). */
  heizungAbgleichPdf = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/heizung/abgleich/bericht.pdf`);

  // ═══ TH-2b (Anfang): Excel und PDF der Kälte, Einstellwerteliste des Abgleichs ═══

  /** Die Excel-Mappe „Abgleich - Einstellwerte“ (Monteurliste, TH-2b; Dateiname vom Server). */
  heizungAbgleichExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/heizung/abgleich/listen.xlsx`, 'Heizung_Abgleich.xlsx');

  /** Die Excel-Mappe „Kältebilanz“: Lesarten, Befunde, Anlagen, Kennwerte (TH-2b; Dateiname vom Server). */
  kaelteExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/kaelte/listen.xlsx`, 'Kaelte_Bilanz.xlsx');

  /** Die Excel-Mappe „Kältenachweis“: Ergebnisse je Bereich, Eisspeicherprofil (TH-2b; Dateiname vom Server). */
  kaelteNachweisExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/kaelte/nachweis/listen.xlsx`, 'Kaelte_Nachweis.xlsx');

  /** Bericht KAE-KLB (Kältebilanz) über die Berichtserzeugung; gleicher Stand liefert die abgelegte Fassung (F11). */
  kaeltePdf = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/kaelte/bericht.pdf`);

  /** Bericht KAE-KMN (Kältenachweis, DIN EN 378) über die Berichtserzeugung; gleicher Stand: abgelegte Fassung (F11). */
  kaelteNachweisPdf = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/kaelte/nachweis/bericht.pdf`);

  // ═══ TH-2b (Ende) ═══

  // ═══ TL10 (Anfang): GEG-Abgleich Modell ↔ Nachweis ═══

  /** Der GEG-Abgleich je Bauteiltyp (TL10); ohne Nachweis `modell = null` und `hinweis`. */
  gegAbgleich = (projektId: string) =>
    this.hole<GegAbgleichAntwort>(`/api/projects/${projektId}/heizlast/geg-abgleich`);

  /** Die Excel-Mappe „GEG-Abgleich“ (Dateiname vom Server). */
  gegAbgleichExcel = (projektId: string) =>
    this.dateiLaden(`/api/projects/${projektId}/heizlast/geg-abgleich/listen.xlsx`, 'Huelle_GEG-Abgleich.xlsx');

  // ═══ TL10 (Ende) ═══

  /** Bericht HZG-HFL (Heizflächennachweis) über die Berichtserzeugung. */
  heizungHeizflaechenPdf = (projektId: string) =>
    this.pdfLaden(`/api/projects/${projektId}/heizung/heizflaechen/bericht.pdf`);

  /** Kühllast-Jahresgang des maßgebenden Laufs (Stundenbilanz, kein Auslegungsfall) mit Q_inst und n−1. */
  kaelteJahresgang = (projektId: string) =>
    this.hole<KaelteJahresgangwerte | null>(`/api/projects/${projektId}/kaelte/jahresgang`);

  // ═══ TH-2 (Ende) ════════════════════════════════════════════════════════

  // ═══ AC1a (Anfang): ACC-Anbindung – Verbindung zu Autodesk ═══

  /** Stand der Verbindung (jeder Angemeldete); `rechte.verbinden` nur für den SystemAdmin. Nie Token. */
  accVerbindung = () => this.hole<AccVerbindung>('/api/acc/verbindung');

  /** Beginnt die Zustimmung (SystemAdmin): setzt das Bindungs-Cookie und liefert die Adresse bei Autodesk. */
  accVerbindungStarten = (projectId: string) =>
    this.sende<{ url: string; gueltigBis: string }>('/api/acc/verbindung/start', { projectId });

  /** Sofort erneuern (SystemAdmin); der Stand kommt auch bei einem Fehler zurück. */
  accVerbindungErneuern = () =>
    this.sende<{ erneuert: boolean; stand: AccVerbindung }>('/api/acc/verbindung/erneuern', {});

  /** Trennen mit Grund (SystemAdmin): Widerruf bei Autodesk, Token gelöscht. */
  accVerbindungTrennen = (grund: string) =>
    this.schicke<AccVerbindung>(`/api/acc/verbindung?grund=${encodeURIComponent(grund)}`, 'DELETE') as Promise<AccVerbindung>;

  // ═══ AC1a (Ende) ═══
  // ═══ AC1b (Anfang): ACC durchblättern (Data Management, nur lesend; ProjectAdmin/SystemAdmin) ═══

  accHubs = () => this.hole<{ hubs: AccHub[]; warnungen: AccHinweis[] }>('/api/acc/hubs');
  accProjekte = (hub: string) =>
    this.hole<AccProjektEintrag[]>(`/api/acc/projekte?hub=${encodeURIComponent(hub)}`);
  accOberordner = (hub: string, projekt: string) =>
    this.hole<AccOrdner[]>(`/api/acc/ordner?hub=${encodeURIComponent(hub)}&projekt=${encodeURIComponent(projekt)}`);
  accInhalt = (projekt: string, ordner: string) =>
    this.hole<AccInhalt>(`/api/acc/inhalt?projekt=${encodeURIComponent(projekt)}&ordner=${encodeURIComponent(ordner)}`);
  accVersionen = (projekt: string, item: string) =>
    this.hole<AccVersion[]>(`/api/acc/versionen?projekt=${encodeURIComponent(projekt)}&item=${encodeURIComponent(item)}`);

  // ═══ AC1b (Ende) ═══
  // ═══ RS1a/RS1b (Anfang): Revit Server (nur lesend gegen den Server; Übernahme mit Grund, ProjectAdmin/SystemAdmin) ═══

  /** Register der Revit Server; `null`, wenn die Anbindung aus ist (404: Schalter `RevitServer:Aktiv`). */
  revitServerListe = async (): Promise<RsServer[] | null> => {
    const antwort = await this.abrufen('/api/revitserver/server', {
      headers: { Authorization: `Bearer ${await this.token()}` },
    });
    if (antwort.status === 404) return null;
    if (!antwort.ok) throw new Error(await fehlertext(antwort));
    return ((await antwort.json()) as { server: RsServer[] }).server;
  };
  revitServerInhalt = (kennung: string, pfad: string) =>
    this.hole<RsInhalt>(`/api/revitserver/${encodeURIComponent(kennung)}/inhalt?pfad=${encodeURIComponent(pfad)}`);
  revitServerModell = (kennung: string, pfad: string) =>
    this.hole<RsModellInfo>(`/api/revitserver/${encodeURIComponent(kennung)}/modell?pfad=${encodeURIComponent(pfad)}`);
  revitServerUebernehmen = (projektId: string,
    daten: { server: string; pfad: string; name?: string | null; discipline?: string | null; grund: string }) =>
    this.sende<RsUebernommen>(`/api/projects/${projektId}/revitserver/uebernehmen`, daten);

  // ═══ RS1a/RS1b (Ende) ═══
  // ═══ AC1c (Anfang): ACC-Zuordnung Projekt/Modell ↔ Datei (schreibend nur mit Grund, ProjectAdmin/SystemAdmin) ═══

  /** Stand der Zuordnung aus der Datenbank (jeder Angemeldete; ruft ACC nicht auf). */
  accStand = (projektId: string) => this.hole<AccStand>(`/api/projects/${projektId}/acc`);
  /** Vorschläge mit Begründung (liest ACC, schreibt nichts). */
  accVorschlaege = (projektId: string) => this.hole<AccVorschlaege>(`/api/projects/${projektId}/acc/vorschlaege`);
  accProjektZuordnen = (projektId: string, daten: { hubId: string; projektId: string; grund: string }) =>
    this.schicke<{ ok: boolean }>(`/api/projects/${projektId}/acc/projekt`, 'PUT', daten);
  accProjektLoesen = (projektId: string, grund: string) =>
    this.schicke<{ ok: boolean }>(`/api/projects/${projektId}/acc/projekt?grund=${encodeURIComponent(grund)}`, 'DELETE');
  accZuordnen = (projektId: string, zuordnungen: { modelId: string; itemId: string }[], grund: string) =>
    this.sende<{ zugeordnet: number }>(`/api/projects/${projektId}/acc/zuordnen`, { zuordnungen, grund });
  accUebernehmen = (projektId: string,
    dateien: { itemId: string; name?: string | null; revitVersion: number; discipline?: string | null }[], grund: string) =>
    this.sende<{ angelegt: { id: string; name: string }[] }>(`/api/projects/${projektId}/acc/uebernehmen`, { dateien, grund });
  accModellLoesen = (projektId: string, modelId: string, grund: string) =>
    this.schicke<{ ok: boolean }>(`/api/projects/${projektId}/acc/modelle/${modelId}?grund=${encodeURIComponent(grund)}`, 'DELETE');

  // ═══ AC1c (Ende) ═══
  // ═══ AC1d (Anfang): neue ACC-Versionen - Hinweise, Aufnahme auf Klick, Einstellung je Projekt ═══

  accVersionsstand = (projektId: string) => this.hole<AccVersionsstand>(`/api/projects/${projektId}/acc/versionen`);
  accVersionenPruefen = (projektId: string) =>
    this.sende<AccPruefung>(`/api/projects/${projektId}/acc/versionen/pruefen`, {});
  accHinweisErledigen = (projektId: string, hinweisId: string, grund: string) =>
    this.sende<{ id: string; status: string }>(`/api/projects/${projektId}/acc/hinweise/${hinweisId}/erledigen`, { grund });
  accHinweisAufnehmen = (projektId: string, hinweisId: string, daten: { exportProfileVersionId: string; arten: number[]; grund: string }) =>
    this.sende<{ id: string; status: string; auftrag: string; auftragId: string }>(
      `/api/projects/${projektId}/acc/hinweise/${hinweisId}/aufnehmen`, daten);
  accEinstellungen = (projektId: string, daten: {
    versionsabfrage: boolean; autoAufnahme: boolean; exportProfileVersionId: string | null; arten: number[]; grund: string;
  }) => this.schicke<unknown>(`/api/projects/${projektId}/acc/einstellungen`, 'PUT', daten);

  // ═══ AC1d (Ende) ═══
}


/** MASCH-1 (F206, F209 E1): Art der Verarbeitungsmaschine. Nur REVIT erhält Revit-Teilaufträge. */
export type Maschinenart = 'REVIT' | 'BIM' | 'IFC' | 'VISUALISIERUNG';

/** MASCH-1 (F209 E2): In Erprobung nur ausdrücklich an die Maschine gerichtete Teilaufträge. */
export type Betriebsstufe = 'PRODUKTIV' | 'ERPROBUNG';

export const MASCHINENARTEN: { wert: Maschinenart; text: string }[] = [
  { wert: 'REVIT', text: 'Revit' },
  { wert: 'BIM', text: 'BIM-Worker' },
  { wert: 'IFC', text: 'IFC-Worker' },
  { wert: 'VISUALISIERUNG', text: 'Visualisierung' },
];

export function maschinenartText(art: string | null | undefined): string {
  return MASCHINENARTEN.find((a) => a.wert === art)?.text ?? (art || 'Revit');
}

/** MASCH-3 (F248 E-4a): Anzeigetexte der Maschinenfähigkeiten (`MaschineVerwaltung.faehigkeiten`). */
export const MASCHINENFAEHIGKEITEN: Record<string, string> = {
  IFC_EXPORT: 'IFC-Export',
};

export function maschinenfaehigkeitText(f: string): string {
  return MASCHINENFAEHIGKEITEN[f] ?? f;
}

/**
 * MASCH-1 (F208, F209 E9): Höchstzahl gleichzeitig laufender Teilaufträge je Projekt oder Mandant
 * (`GET /api/agents/kontingente`). `durchgesetzt` ist nur bei Projekt-Zeilen wahr; Mandanten-Zeilen sind
 * vorbereitet und greifen erst mit der Mehrmandantenfähigkeit.
 */
export interface MaschinenKontingent {
  id: string;
  projectId: string | null;
  projektNummer: string | null;
  projektName: string | null;
  mandant: string | null;
  /** Leer heißt: alle Arten. */
  maschinenart: Maschinenart | null;
  hoechstGleichzeitig: number;
  notiz: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
  durchgesetzt: boolean;
}
export interface MaschineVerwaltung {
  id: string;
  name: string;
  revitVersions: string;
  agentVersion: string | null;

  /** MASCH-1 (F209 E1/E2): Art und Stufe. Ältere Server liefern sie nicht - dann gilt Revit/Produktiv. */
  maschinenart?: Maschinenart;
  betriebsstufe?: Betriebsstufe;
  /** MASCH-3 (F248 E-4a): Fähigkeiten aus der Art, z. B. `IFC_EXPORT` bei Revit. Ältere Server liefern sie nicht. */
  faehigkeiten?: string[];

  /** Logische Laufzeitidentitaet. Golden und Production teilen sich die VM,
   *  aber weder Identitaet noch Auftragsrouting. */
  environment: string | null;
  machineName: string | null;
  goldenRole: string | null;
  goldenReady: boolean;
  goldenAttestationJson: string | null;
  goldenReadinessReasonsJson: string | null;

  /** Die Fassung der Revit-Erweiterung -- die wichtigere von beiden. */
  appBundleVersion: string | null;

  agentAktuell: boolean;
  appBundleAktuell: boolean;

  lastSeenIp: string | null;
  lastHeartbeat: string | null;
  note: string | null;
  completedCount: number;
  failedCount: number;
  createdAt: string;
  zustand: number;
  erreichbar: boolean;
}

/**
 * Die Maschinen MIT den freigegebenen Fassungen.
 *
 * Eine Fassungsnummer ohne Vergleich sagt nichts: '1.6.89' ist nur dann eine
 * Auskunft, wenn danebensteht, dass 1.6.90 freigegeben ist.
 */
export interface Maschinenbestand {
  agentFassung: string | null;
  appBundleFassung: string | null;
  maschinen: MaschineVerwaltung[];
}

export interface Arbeitsstationen {
  aktuelleFassung: string | null;
  arbeitsplaetze: {
    id: string;
    name: string;
    benutzer: string | null;
    addinFassung: string | null;
    revitJahr: string | null;
    helferFassung: string | null;
    letzteMeldung: string | null;
    letzteInstallation: string | null;
    status: number;
    bemerkung: string | null;
    createdAt: string;
    gesperrt: boolean;
    erreichbar: boolean;
    istAktuell: boolean;
  }[];
  hinweise: string[];
}

/**
 * Ein Arbeitsplatz zur Auswahl beim Anlegen eines Auftrags.
 *
 * Bewusst schmal -- anders als {@link Arbeitsstationen}: kein Schluessel,
 * keine Verwaltungs-Hinweise, nur was die Auswahl braucht. Nur nicht
 * gesperrte Arbeitsplaetze kommen ueberhaupt in der Liste vor
 * (GET /api/arbeitsplaetze/verfuegbar).
 */
export interface ArbeitsplatzAuswahl {
  id: string;
  name: string;
  benutzer: string | null;
  letzteMeldung: string | null;
  erreichbar: boolean;
}

/**
 * Alles, was der Einrichter braucht -- in EINER Antwort.
 *
 * Der Schluessel erscheint genau einmal. Wer nur ihn bekaeme, muesste die
 * Datei von Hand bauen, und der haeufigste Fehler dabei ist ein Tippfehler
 * im Schluessel -- der sich als 'Anmeldung abgewiesen' zeigt.
 */
export interface Arbeitsstationsausstattung {
  id: string;
  name: string;
  schluessel: string;
  dateiname: string;
  zielpfad: string;
  inhalt: string;
  anweisung: string[];
  /** Der Add-in-Ordner heisst nach dem Jahr — die Anleitung nennt ihn. */
  revitJahr: string;
}

export interface NeueMaschine {
  id: string;
  name: string;
  /** Wird genau einmal ausgegeben und ist danach nicht mehr abrufbar. */
  schluessel: string;
  hinweis: string;
}

export interface PaketUebersicht {
  pakete: {
    kennung: string;
    titel: string;
    beschreibung: string;
    vorhanden: boolean;
    /** Die freigegebene Fassung. Leer, solange nichts hinterlegt ist. */
    fassung?: string | null;
    /** Wann sie freigegeben wurde — ohne das Datum sieht jede Fassung
     *  aus wie die aktuelle. */
    stand?: string | null;
    /** DL27 (F439): noch nicht im Zieljahrgang praxisgeprüft (Revit-2027-Add-in). */
    vorabfassung?: boolean;
  }[];
  serveradresse: string;
}

export interface Raumfeld {
  id: string;
  fieldKey: string;
  label: string;
  gruppe: string | null;
  unit: string | null;
  /** zahl | text | auswahl | ja_nein */
  dataType: string;
  options: string | null;
  /** Der NAME des Zielparameters — für den Tooltip, nicht für die Farbe. */
  revitParameter: string | null;
  /**
   * Geht dieses Feld ins Modell zurück? Die FERTIGE Antwort aus dem
   * Rechenkern (`Rueckschreibweg.GehtInsModell`), nicht die Zutaten.
   *
   * Sie ist NICHT dasselbe wie `revitParameter !== null`: Ein Feld kann
   * einen Parameter tragen, aus dem nur GELESEN wird. Wer hier wieder
   * `revitParameter` befragt, baut die elfte Fassung derselben Regel — und
   * genau die war seit den Migrationen 0729–0731 falsch.
   *
   * Zum Färben `farbklasse(feld.gehtInsModell)` aus `Farbregel.tsx`.
   */
  gehtInsModell: boolean;
  sortOrder: number;
}

export interface Raum {
  id: string;
  spaceGuid: string;
  arcRoomGuid: string | null;
  number: string;
  name: string;
  level: string | null;
  building: string | null;
  area: number | null;
  volume: number | null;
  templateId: string | null;
  /** Die gepflegten Werte als JSON-Zeichenkette. */
  dataJson: string | null;
  /**
   * Je FieldKey aus dataJson eine Herkunft: hand | modell | abgeleitet |
   * standard. Fehlt ein Schluessel, gilt er als 'modell' (Konvention seit
   * der Raumbuchableitung, Migration 0464).
   */
  herkunftJson: string | null;
  /** neu | bekannt | entfernt */
  state: string;
  firstSeenAt: string;
  lastSeenAt: string;
  removedAt: string | null;
  /** F60 H2: Nummer des Hauses (NULL in der DB = Standardhaus). Optional, ältere Antworten tragen sie nicht. */
  hausNummer?: number | null;
}

/** Eine Gewerkgruppe im Grundriss — Reihenfolge und Farbe kommen vom Server. */
export interface RaumGrundrissGewerk {
  gewerk: string;
  bezeichnung: string;
  imPolygon: number;
  /** sinnbild | punkt | nurgezaehlt */
  darstellung: string;
  /** Traegt diese Raumnummer, liegt aber ausserhalb des Umrisses. */
  mitRaumnummerFremd: number;
  farbe: string;
}

/**
 * Die Auskunft zum Raumgrundriss.
 *
 * `flaecheModellM2` und `flaechePolygonM2` stehen BEIDE hier, unabhaengig
 * davon, ob sie streiten — auf dem Blatt erscheinen beide erst ab
 * `streitgrenze` Prozent Abweichung.
 */
export interface RaumGrundriss {
  raumnummer: string;
  raumname: string | null;
  geschoss: string | null;
  gebaeude: string | null;
  /** gezeichnet | ohne_segmente | zu_wenige_punkte | entartetes_polygon */
  befund: string;
  befundText: string | null;
  hatUmriss: boolean;
  flaecheModellM2: number | null;
  flaechePolygonM2: number | null;
  abweichungProzent: number | null;
  flaechenStreit: boolean;
  streitgrenze: number;
  massstab: number;
  massstabIstNorm: boolean;
  blatthoeheMm: number;
  achsen: string[];
  achsenUnterdrueckt: number;
  oeffnungen: number;
  tueren: number;
  fenster: number;
  sehnenSegmente: number;
  sehnenUeberlaengeM: number;
  gewerke: RaumGrundrissGewerk[];
  stand: string;
}

export interface Raumvorlage {
  id: string;
  templateKey: string;
  name: string;
  dataJson: string;
  note: string | null;
  hausweit: boolean;
}

export interface RaumbuchDaten {
  raeume: Raum[];
  vorlagen: Raumvorlage[];
  neu: number;
  entfernt: number;
  /** F60-2: ab zwei aktiven Häusern - dann Haus in der Gliederung. */
  mehrhaus?: boolean;
}

export interface SammelErgebnis {
  geaendert: number;
  mitEigenenWerten: string[];
  hinweis: string | null;
}

export interface ExcelVorschau {
  zeilenGelesen: number;
  zugeordnet: number;
  unveraendert: number;
  aenderungen: { nummer: string; feld: string; alt: string | null; neu: string | null }[];
  nichtZuordenbar: string[];
  auffaelligkeiten: string[];
}

/**
 * Ein Fachbereich aus dem Katalog.
 *
 * Treibt die Reiterleiste der Projektansicht und die Kreuztabelle in den
 * Einstellungen. Ein neuer Fachbereich ist damit eine Zeile in der Datenbank
 * und keine Verteilung.
 */
export interface Fachbereich {
  roleKey: string;
  bezeichnung: string;
  beschreibung: string | null;
  /** Routenteil unter /projekte/:id/ */
  pfad: string;
  /** Ob der Bereich schon eine Aufnahme hat oder nur vorbereitet ist. */
  istUmgesetzt: boolean;
  /** Ob der Bereich ein Quellmodell braucht. */
  istQuellRolle: boolean;
  reihenfolge: number;
}

// --- Lüftung ---------------------------------------------------------------

// --- Das LV-Modul (Ausschreibung L1) ---------------------------------------

/** Kennzahlen eines abgeleiteten Leistungsverzeichnisses. */
export interface LvKopf {
  id: string;
  gewerk: string;
  kuerzel: string;
  bezeichnung: string;
  /** Zaehlt nur hoch, wenn sich am Dokument etwas geaendert hat. */
  fassung: number;
  stand: string;
  losNr: number;
  titel: number;
  positionen: number;
  modellelemente: number;
  /** Die Abnahmeprobe: Bauteile, die in keiner Bauteilposition stehen. */
  elementeOhnePosition: number;
  datenhash?: string | null;
  datenstand: string | null;
  erzeugtVon: string | null;
  erzeugtAm: string | null;
}

export interface LvPosition {
  id: string;
  oz: string;
  positionsart: string;
  kurztext: string;
  langtext: string;
  menge: number;
  einheit: string;
  dezimalstellen: number;
  /** modell | kataster | berechnung | festlegung | pauschal. */
  mengenherkunft: string;
  modellelemente: number;
  nachweis: string | null;
  bieterangaben: string | null;
  hinweis: string | null;
  regel: string | null;
  textbaustein: string | null;
}

export interface LvTitel {
  id: string;
  oz: string;
  ebene: string;
  bezeichnung: string;
  atv: string | null;
  leistungsbereich: string | null;
  positionen: LvPosition[];
}

export interface LvVorbemerkung {
  id: string;
  abschnitt: string;
  titel: string;
  text: string;
  norm: string | null;
  herkunft: string;
  istBestaetigt: boolean;
  /** Wann bestätigt wurde — und damit gegen welchen Stand des Textes. */
  bestaetigtAm?: string | null;
  /** Der Wortlaut, mit dem der Fachplaner entschieden hat. */
  begruendung?: string | null;
  projectId?: string | null;
  quelle?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface LvDokument {
  kopf: LvKopf;
  projekt: { nummer: string; name: string };
  vorbemerkungen: LvVorbemerkung[];
  titel: LvTitel[];
  ohnePosition: { bauteilart: string; anzahl: number }[];
  hinweise: string[];
}

export interface LvPositionselement {
  quelltabelle: string;
  quellId: string;
  leistungsart: string;
  mengeAnteil: number;
}

export interface LvTextbaustein {
  id: string;
  projectId: string | null;
  schluessel: string;
  bauteilart: string | null;
  kurztext: string;
  langtext: string;
  normverweise: string | null;
  nachweis: string | null;
  bieterangaben: string | null;
  positionsart: string;
  einheitKuerzel: string | null;
  herkunft: string;
  istBestaetigt: boolean;
  quelle: string | null;
  bestaetigtAm?: string | null;
  begruendung?: string | null;
  istAktiv?: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
  /** Die Platzhalternamen der Vorlage. */
  felder: string[];
}

export interface LvMengenregel {
  id: string;
  projectId: string | null;
  schluessel: string;
  bauteilart: string;
  quelle: string;
  filterJson: string | null;
  gruppierungJson: string | null;
  aggregation: string;
  einheitKuerzel: string;
  rundung: number;
  mindestmenge: number | null;
  festmenge: number | null;
  sammelKurztext: string | null;
  sammelBaustein: string | null;
  textbausteinSchluessel: string;
  titelOz: string;
  titelBezeichnung: string;
  mengenherkunft: string;
  positionsart: string;
  leistungsart: string;
  herkunft: string;
  istBestaetigt: boolean;
  bestaetigtAm?: string | null;
  begruendung: string | null;
  istAktiv?: boolean;
  reihenfolge?: number;
}

/**
 * Eine Merkmalregel: aus einem Produktnamen des Modells wird eine Leistung.
 *
 * Sie beantwortet die zwei Fragen, an denen das Referenz-LV gescheitert ist:
 * Was IST "TROX RN-D 80" (Mangel M-4 — 577 Kurztexte waren Revit-Typnamen),
 * und wozu dient "Antidröhnmatten 50mm" (Mangel M-6 — 7.406 m² Körperschall
 * standen als Wärmedämmung im LV).
 */
export interface LvMerkmalregel {
  id: string;
  projectId: string | null;
  /** bauart | daemmzweck */
  merkmal: string;
  quellfeld: string;
  /** gleich | beginnt | enthaelt */
  vergleich: string;
  muster: string;
  wert: string;
  norm: string | null;
  reihenfolge: number;
  herkunft: string;
  istBestaetigt: boolean;
  bestaetigtAm: string | null;
  begruendung: string | null;
  istAktiv: boolean;
}

export interface LvEinheit {
  kuerzel: string;
  bezeichnung: string;
  art: string;
  dezimalstellen: number;
  /** Ob das Kuerzel am GAEB-Katalog belegt ist - oder nur ueblich. */
  istGaebKatalog: boolean;
  quelle: string | null;
}

export interface LvGrundlagen {
  textbausteine: LvTextbaustein[];
  vorbemerkungen: LvVorbemerkung[];
  mengenregeln: LvMengenregel[];
  merkmalregeln: LvMerkmalregel[];
  einheiten: LvEinheit[];
}

/** Eine Zeile des Zahlenvergleichs eines Probelaufs. */
export interface LvVergleichszeile {
  titelOz: string;
  bezeichnung: string;
  einheit: string;
  positionenVorher: number;
  positionenNachher: number;
  mengeVorher: number;
  mengeNachher: number;
  aendert: boolean;
}

/**
 * Das Ergebnis eines Laufs — Probelauf oder schreibend.
 *
 * `geschrieben` sagt, welcher von beiden es war. `hatGeaendert` ist im
 * Probelauf die Vorhersage und im schreibenden Lauf die Tatsache.
 */
export interface LvLauf {
  gewerk: string;
  geschrieben: boolean;
  hatGeaendert: boolean;
  fassung: number;
  zahlen: Record<string, number>;
  gegenproben: {
    schluessel: string; ergebnis: string; aussage: string; zahl: number | null;
  }[];
  hinweise: string[];
  ohnePosition: { bauteilart: string; anzahl: number }[];
  vergleich: LvVergleichszeile[];
}

/** Was der Fachplaner an einem Textbaustein ändern darf. */
export interface LvTextbausteinpflege {
  kurztext: string;
  langtext: string;
  normverweise: string | null;
  nachweis: string | null;
  bieterangaben: string | null;
  istBestaetigt: boolean;
  begruendung: string | null;
}

/** Was der Fachplaner an einer Vorbemerkung ändern darf. */
export interface LvVorbemerkungspflege {
  titel: string;
  text: string;
  norm: string | null;
  istBestaetigt: boolean;
  begruendung: string | null;
}

export interface LvGaebPruefung {
  bytes: number;
  rot: number;
  zeilen: { pruefung: string; ergebnis: string; aussage: string }[];
}

// --- Das Gesamt-LV (Schritt L10) --------------------------------------------
//
// Fasst die je Gewerk abgeleiteten Lose zu EINEM Dokument zusammen. Es
// kopiert keine Titel/Positionen — die stehen bereits je Los —, sondern
// zeigt die Zahlen je Los und die Gegenproben, die NUR über die Losgrenze
// hinweg prüfbar sind (Doppelzuordnung, Summenkontrolle).

export interface LvGesamtLos {
  gewerk: string;
  kuerzel: string;
  bezeichnung: string;
  losNr: number;
  fassung: number;
  titel: number;
  positionen: number;
  modellelemente: number;
  elementeOhnePosition: number;
  datenstand: string | null;
}

export interface LvGesamt {
  projektId: string;
  projektnummer: string;
  projektname: string;
  fassung: number;
  datenstand: string | null;
  loseGesamt: number;
  lose: LvGesamtLos[];
  fehlendeLose: string[];
  zahlen: {
    lose: number;
    titel: number;
    positionen: number;
    modellelemente: number;
    elementeOhnePosition: number;
  };
  gegenproben: {
    schluessel: string; ergebnis: string; aussage: string; zahl: number | null;
  }[];
}

/**
 * Die Zusammenfassung des Lüftungs-LV am alten Pfad.
 *
 * Seit Schritt L2 kommt sie aus dem LV-Modul und nicht mehr aus einem Dienst,
 * der bei jedem Abruf neu rechnet. Weggefallen sind die Felder, die es nur im
 * alten Dienst gab: Modellname, Revit-Fassung, offene Anschlüsse und die
 * beiden Gesamtlängen. Sie standen dort im Kopf des Dokuments; im LV-Modul
 * stehen die Längen je Titel, und die offenen Anschlüsse gehören in die
 * Lüftungsberechnung und nicht in ein Vergabedokument.
 *
 * Dazugekommen sind Fassung, Stand und die Zahl der Elemente ohne Position -
 * die drei Angaben, an denen sich ablesen lässt, WELCHEN Stand man vor sich
 * hat und was daran offen ist.
 */
export interface AusschreibungsUebersicht {
  gewerk: string;
  projekt: { nummer: string; name: string };
  fassung: number;
  stand: string;
  datenstand: string | null;
  modellelemente: number;
  elementeOhnePosition: number;
  positionsanzahl: number;
  kapitel: {
    nummer: string;
    bezeichnung: string;
    positionen: number;
    modellelemente: number;
    /** Je Mengeneinheit eine Summe - im Dämmtitel stehen m² neben St. */
    mengen: Record<string, number>;
  }[];
  hinweise: string[];
}

export interface LueftungAnlage {
  id: string;
  anlage: string;
  ebene: string | null;
  ebeneIndex: number | null;
  aksSchluessel: string | null;
  aksGeschoss: string | null;
  aksGeschossModell: string | null;
  raumnummer: string | null;
  raumname: string | null;
  state: string;
  bauteile: number;
  ohneAks: number;
  /** Geschoss im Namen weicht von der Revit-Ebene ab. Hinweis, kein Fehler. */
  ebenenkonflikt: boolean;
}

export interface LueftungNetz {
  id: string;
  systemname: string;
  aksSchluessel: string | null;
  aksArt: string | null;
  luftart: string | null;
  anlagenAnzahl: number;
  anzahl: number;
  /** Die versorgten Anlagen. Ein Verbundnetz hat keine Primäranlage. */
  anlagen: string[];
}

export interface LueftungOffen {
  ebene: string | null;
  ebeneIndex: number | null;
  zuordnung: string;
  anzahl: number;
}

export interface Lueftungsfeld {
  fieldKey: string;
  label: string;
  gruppe: string | null;
  unit: string | null;
  dataType: string;
  options: string | null;
  /** anlage | bauteil */
  bereich: string;
  /** Der NAME des Zielparameters — für den Tooltip, nicht für die Farbe. */
  revitParameter: string | null;
  /**
   * Geht dieses Feld ins Modell zurück? Die FERTIGE Antwort aus dem
   * Rechenkern (`Rueckschreibweg.GehtInsModell`), nicht die Zutaten.
   *
   * Sie ist NICHT dasselbe wie `revitParameter !== null`: Ein Feld kann
   * einen Parameter tragen, aus dem nur GELESEN wird. Wer hier wieder
   * `revitParameter` befragt, baut die elfte Fassung derselben Regel — und
   * genau die war seit den Migrationen 0729–0731 falsch.
   *
   * Zum Färben `farbklasse(feld.gehtInsModell)` aus `Farbregel.tsx`.
   */
  gehtInsModell: boolean;
}

export interface LueftungBestand {
  anlagen: LueftungAnlage[];
  verbundnetze: LueftungNetz[];
  nichtZugeordnet: LueftungOffen[];
  felder: Lueftungsfeld[];
}

/** Ein Feld im Bearbeitungsformular, eingeschränkt auf die Bauart. */
export interface LueftungFeld {
  fieldKey: string;
  label: string;
  gruppe: string | null;
  unit: string | null;
  dataType: string;
  options: string | null;
  istPflicht: boolean;
  /** Der NAME des Zielparameters — für den Hinweis unter dem Feld. */
  revitParameter: string | null;
  /**
   * Geht dieses Feld ins Modell zurück? Die FERTIGE Antwort aus dem
   * Rechenkern (`Rueckschreibweg.GehtInsModell`), nicht die Zutaten.
   *
   * Sie ist NICHT dasselbe wie `revitParameter !== null`: Ein Feld kann
   * einen Parameter tragen, aus dem nur GELESEN wird. Wer hier wieder
   * `revitParameter` befragt, baut die elfte Fassung derselben Regel — und
   * genau die war seit den Migrationen 0729–0731 falsch.
   *
   * Zum Färben `farbklasse(feld.gehtInsModell)` aus `Farbregel.tsx`.
   */
  gehtInsModell: boolean;
}

export interface LueftungBauteilDetail {
  id: string;
  elementGuid: string;
  aksSchluessel: string | null;
  aksCode: string | null;
  luftart: string | null;
  revitKategorie: string;
  familie: string | null;
  typ: string | null;
  groesse: string | null;
  volumenstrom: number | null;
  hersteller: string | null;
  produktcode: string | null;
  ebene: string | null;
  raumnummer: string | null;
  raumname: string | null;
  raumQuelle: string | null;
  posX: number | null;
  posY: number | null;
  posZ: number | null;
  modellpruefung: string | null;
  zuordnung: string;
  aksTraeger: string;
  anlage: string | null;
  systemname: string | null;
  bauart: string | null;
  verbundanlagen: string[];
  dataJson: string | null;
  herkunft: string | null;
  felder: LueftungFeld[];
  state: string;
  updatedAt: string | null;
  updatedBy: string | null;
  // Befund aus dem Datenblattabgleich — die Luftmenge liegt außerhalb des
  // Bereichs der Herstellertabelle.
  hatFehler: boolean;
  fehlerHinweis: string | null;
  /** offen | geprueft | behoben */
  fehlerZustand: string | null;
  fehlerAm: string | null;
  fehlerVon: string | null;
  datenblattId: string | null;
  abgleichMenge: number | null;
  abgleichArt: string | null;
  abgleichAm: string | null;
  abgleichVon: string | null;
}

/** Eine Funktionseinheit im Lüftungsgerät. */
export interface Funktionseinheit {
  id: string;
  /** ZU | AB | AU | FO */
  luftstrom: string;
  reihenfolge: number;
  /** RAHMEN | KLAPPE | FILTER | SCHALL | ERH | KUEHL | CHANGE | TROPF | VENT | WRG | BEF | UNIV | ABSTAND */
  art: string;
  bezeichnung: string;
  druckverlustPa: number | null;
  dataJson: string | null;
  /** datenblatt | modell | hand */
  herkunft: string;
  updatedBy: string | null;
  betriebspunkte: Anlagenpunkt[];
}

/**
 * Die Druckverlustprobe je Luftstrom.
 *
 * Die Summe ohne den Ventilator ist der interne Druckverlust. Der Ventilator
 * liefert Druck, er verbraucht keinen.
 */
export interface Druckprobe {
  luftstrom: string;
  einheiten: number;
  internPa: number;
  ventilatorPa: number;
}

export interface LueftungAnlagenDetail {
  id: string;
  anlage: string;
  anlageQuelle: string;
  ebene: string | null;
  ebeneIndex: number | null;
  raumnummer: string | null;
  raumname: string | null;
  aksTeilprojekt: string | null;
  aksGewerk: string | null;
  aksGruppe: string | null;
  aksGeschoss: string | null;
  aksArt: string | null;
  aksNummer: number | null;
  aksSchluessel: string | null;
  aksGeschossModell: string | null;
  /** Geschoss im Namen weicht von der Revit-Ebene ab. Hinweis, kein Fehler. */
  ebenenkonflikt: boolean;
  dataJson: string | null;
  herkunft: string | null;
  state: string;
  updatedAt: string | null;
  updatedBy: string | null;
  elemente: {
    id: string; elementGuid: string; ifcGuid: string | null;
    familie: string | null; typ: string | null; rolle: string;
    ebene: string | null; bearbeitungsbereich: string | null;
    posX: number | null; posY: number | null; posZ: number | null;
  }[];
  systeme: {
    id: string; systemname: string; abkuerzung: string | null;
    luftart: string | null; istVerbundnetz: boolean;
    aksSchluessel: string | null; anlagenAnzahl: number; bauteile: number;
  }[];
  felder: LueftungFeld[];
  einheiten: Funktionseinheit[];
  druck: Druckprobe[];
  einheitsfelder: (LueftungFeld & { aksCodes: string | null })[];
  datenblatt: {
    id: string;
    hersteller: string | null;
    geraetebezeichnung: string | null;
    angebotsnummer: string | null;
    datenblattDatum: string | null;
    dateiname: string | null;
    dateiGroesse: number | null;
    zustand: string;
    zustandHinweis: string | null;
    herstellerUrl: string | null;
    vorschlaegeJson: string | null;
    hatDatei: boolean;
    produktreihe: string | null;

    // Die Geräteskizze — ein zweites Dokument, das anderes zeigt: Abmessungen und
    // Anordnung statt der Auslegung.
    skizzeDateiname: string | null;
    skizzeGroesse: number | null;
    hatSkizze: boolean;

    // Die Fassung und wann sie kam. Ohne das ist nicht erkennbar, ob die Werte von
    // heute oder von vor drei Monaten sind.
    aktuelleDatenversion: string | null;
    aktuelleRevisionId: string | null;
    importiertAm: string | null;
    importiertVon: string | null;
    fassungen: number;
  } | null;
  /** Der gleichbleibende Verweis auf das technische Datenblatt. */
  datenblattUrl: string | null;
  /** Der gleichbleibende Verweis auf die Geräteskizze. */
  skizzeUrl: string | null;
  /** Die Seite des Herstellers, falls im Dokument genannt. */
  herstellerSeite: string | null;
}

// --- Anlagenimporte --------------------------------------------------------

/**
 * Ein Feld der Projektgrunddaten, mit Wert und dem Stand im Modell.
 *
 * Alle Felder kommen mit, auch die ohne Wert — sonst wäre nicht erkennbar, was noch
 * fehlt, und genau das ist beim Einrichten die Auskunft, die gebraucht wird.
 */
export interface Grunddatenfeld {
  schluessel: string;
  bezeichnung: string;
  /** Der Parameter im Modell. Leer heißt: wird nicht geschrieben. */
  revitParameter: string | null;
  /** beteiligte | bauherr | bearbeiter | projekt | vorlage */
  bereich: string;
  /** text | mehrzeilig | telefon | email | url | zahl | auswahl */
  art: string;
  /** Bei art = auswahl: die zulässigen Werte, durch Senkrechtstrich getrennt. */
  auswahl: string | null;
  hinweis: string | null;
  istAktiv: boolean;
  wert: string | null;
  /** Was im Modell stand, als zuletzt gelesen wurde. */
  wertImModell: string | null;
  herkunft: string | null;
  gelesenAm: string | null;
  uebertragenAm: string | null;
  hatKonflikt: boolean;
  /**
   * Fixiert — die Plattform gewinnt.
   *
   * Ein Auslesen vermerkt den Wert aus dem Modell, übernimmt ihn aber nicht.
   */
  istFixiert: boolean;
  fixiertAm: string | null;
  fixiertVon: string | null;
  /** Weicht das Modell vom gepflegten Wert ab? */
  weichtAb: boolean;
  /** Ohne Revit-Parameter wird nichts geschrieben. */
  schreibbar: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Eine eingelesene Produktreihe. */
export interface Heizungproduktreihe {
  id: string;
  bezeichnung: string;
  hersteller: string | null;
  produktreihe: string | null;
  bauteilart: string | null;
  kennfeld: string | null;
  darfInterpolieren: boolean;
  darfExtrapolieren: boolean;
  auslegung: string | null;
  quelle: string | null;
  stand: string | null;
  baugroessen: number;
  von: number | null;
  bis: number | null;
  /** Wie viele Bauteile diese Reihe tragen. */
  verwendet: number;
}

/** Ein Bauteiltyp für die Datenblattpflege. */
export interface Heizungtyp {
  bauteilart: string;
  artbezeichnung: string;
  brauchtDatenblatt: boolean;
  familie: string | null;
  typ: string | null;
  anzahl: number;
  hersteller: string | null;
  modell: string | null;
  artikelnummer: string | null;
  herstellerUrl: string | null;
  datasheetId: string | null;
  mitDatenblatt: number;
  dateiname: string | null;
  zustand: string | null;
  /** Der Datenzustand der Gruppe. */
  offen: number;
  zugewiesen: number;
  zuPruefen: number;
  /** Anonymer, signierter Verweis auf das Datenblatt. */
  verweis: string | null;
}

/** Ein Knoten im Bauteilbaum. */
export interface Heizungknoten {
  /** geschoss | raum | kreis | netz | art */
  ebene: string;
  name: string;
  zusatz?: string | null;
  anzahl: number;
  /** Nur auf Raumebene: die Aufteilung nach Kreis. */
  heizung?: number;
  kaelte?: number;
  rueckkuehlung?: number;
  ohneKreis?: number;
  ausLage?: number;
  /** Nur auf Blattebene. */
  schluessel?: string;
  kreis?: string | null;
  kinder?: Heizungknoten[];
}

export interface Heizungbaum {
  nach: string;
  baum: Heizungknoten[];
}

/** Eine Bauteilart des Gewerks Heizung und Kälte. */
export interface Heizungbauteilart {
  schluessel: string;
  bezeichnung: string;
  /** geraet | armatur | anschluss | netz */
  gruppe: string;
  kostengruppe: string | null;
  wirdGefuehrt: boolean;
  brauchtDatenblatt: boolean;
  hinweis: string | null;
  anzahl?: number;
  ohneRaum?: number;
  raumAusLage?: number;
  mitDatenblatt?: number;
}

/** Ein Feld des Heizungskatalogs. */
export interface Heizungfeld {
  feldschluessel: string;
  bezeichnung: string;
  gruppe: string | null;
  einheit: string | null;
  art: string;
  auswahl: string | null;
  bauteilarten: string | null;
  /** Der NAME des Zielparameters — für den Hinweis, nicht für die Farbe. */
  revitParameter: string | null;
  /** Die FESTLEGUNG allein. Zum Färben ist `gehtInsModell` gemeint. */
  istSchreibbar: boolean;
  hinweis: string | null;
  /**
   * Geht dieses Feld ins Modell zurück? Die FERTIGE Antwort aus dem
   * Rechenkern (`Rueckschreibweg.GehtInsModell`), nicht die Zutaten.
   *
   * Sie ist NICHT dasselbe wie `revitParameter !== null`: Ein Feld kann
   * einen Parameter tragen, aus dem nur GELESEN wird. Wer hier wieder
   * `revitParameter` befragt, baut die nächste Fassung derselben Regel —
   * das Projekt hatte davon neunzehn.
   *
   * Zum Färben `farbklasse(feld.gehtInsModell)` aus `Farbregel.tsx`.
   */
  gehtInsModell: boolean;
}

/** Das Ergebnis eines Rechenlaufs — was die Schaltfläche zurückgibt. */
export interface Heizungberechnungslauf {
  erfolg: boolean;
  knoten: number;
  wege: number;
  wegeMitErgebnis: number;
  verbraucher: number;
  verbraucherOhneAnschluss: number;
  teilstrecken: number;
  gruppen: number;
  seiten: {
    seite: string; knoten: number; kanten: number; quellen: number;
    erreichteKnoten: number; maschen: number;
    anschluesse: number; anschluesseErreicht: number;
  }[];
  ergebnisse: {
    systemgruppe: string; kreis: string | null; wege: number;
    mitErgebnis: number; foerderhoehePa: number | null;
    volumenstromM3h: number | null; guete: string | null;
    massgebenderWeg: string | null;

    /** F72 — siehe `Heizungberechnung.netze[].vollstaendigkeit`. */
    vollstaendigkeit?: 'vollstaendig' | 'untere_schranke' | null;
  }[];
  hinweise: string[];

  /**
   * Die Verbraucher OHNE Weg, je Systemgruppe und Grund — die Arbeitsliste
   * der Modellpflege. Die Gründe schließen einander aus: Jeder wegelose
   * Verbraucher trägt genau einen, den ersten, der greift.
   */
  gruende?: {
    systemgruppe: string; grund: string;
    verbraucher: number; leistungW: number;
  }[];
}

/** Je Auslegungsgruppe eine Zeile, darunter die Wege. */
export interface Heizungberechnung {
  netze: {
    id: string; kreis: string | null; systemgruppe: string;
    bezeichnung: string | null;
    vorlaufC: number | null; ruecklaufC: number | null;
    dichteKgM3: number | null; viskositaetMm2S: number | null;
    waermekapazitaetWsKgK: number | null; glykolProzent: number | null;
    wege: number | null; wegeMitErgebnis: number | null;

    /**
     * Festlegung F72: die Kennzeichnung der Förderhöhe DIESER Zeile.
     *
     * `'untere_schranke'` heißt: `wegeMitErgebnis` ist kleiner als `wege`.
     * Der maßgebende Weg wurde unter den GERECHNETEN gesucht; der wirklich
     * ungünstigste Verbraucher kann unter den ungerechneten liegen. Die
     * Förderhöhe kann deshalb zu klein sein und nie zu groß.
     *
     * Sie gehört an die Zahl und nicht in eine Fußnote — wer
     * `foerderhoehePa` ausgibt, gibt das hier daneben aus.
     */
    vollstaendigkeit: 'vollstaendig' | 'untere_schranke' | null;

    verbraucher: number | null;
    leistungW: number | null; volumenstromM3h: number | null;
    massgebenderWegId: string | null;
    foerderhoehePa: number | null; foerderhoeheMWS: number | null;
    guete: string | null; hinweise: string | null; berechnetAm: string;

    /**
     * NULL = die Gesamtauswertung der Gruppe. Das ist das Ergebnis der
     * Anlage.
     *
     * Gefüllt = eine VERGLEICHSZEILE für eine Verbraucherklasse (H-14):
     * dieselbe Rechnung, so als hinge nur diese Klasse an einer eigenen
     * Pumpe. Wer die Zeilen ungefiltert anzeigt, zeigt dieselbe Gruppe
     * mehrfach.
     */
    verbraucherklasse: string | null;
    verbraucherklassen: number | null;
    abgleichMittelPa: number | null;
    abgleichMaxPa: number | null;
    ventileGesamt: number | null;
    ventileUnterAnschlag: number | null;
  }[];
  wege: Heizungwegzeile[];

  /**
   * Die Grundgesamtheit über das PROJEKT — vom Server gerechnet und nur über
   * die Gesamtzeilen (`verbraucherklasse === null`).
   *
   * Sie steht hier, damit niemand `netze` summiert: Die Liste trägt neben
   * jeder Gruppe auch deren Vergleichsrechnung je Verbraucherklasse (H-14).
   * Eine Summe über alle Zeilen zählt die gemischten Gruppen doppelt — am
   * Bestand 1.113 statt 880 Wege.
   */
  grundgesamtheit?: {
    gruppen: number;
    vergleichszeilen: number;
    wege: number;
    wegeMitErgebnis: number;
    gruppenUntereSchranke: number;
    vollstaendigkeit: 'vollstaendig' | 'untere_schranke' | null;
  };
}

/**
 * Ein Ende einer Netzbrücke. Die ElementId kann ein Rohr ODER ein Bauteil
 * bezeichnen; `herkunft` sagt welches. `'unbekannt'` ist kein Darstellungs-
 * fehler, sondern ein Befund: Die Brücke hängt an einem Element, das die
 * Aufnahme nicht (mehr) kennt.
 */
export interface HeizungNetzbrueckenende {
  elementId: number;
  herkunft: 'rohr' | 'bauteil' | 'unbekannt';
  art: string | null;
  bezeichnung: string | null;
  systemname: string | null;
  systemabkuerzung: string | null;
  systemgruppe: string | null;
  kreis: string | null;
  nennweite: number | null;
  netzrolle: string | null;
  geschoss: string | null;
  lageX: number | null;
  lageY: number | null;
  lageZ: number | null;
}

export interface HeizungNetzbrueckenzeile {
  id: string;
  systemgruppe: string | null;
  abstandMm: number | null;
  begruendung: string;
  quelle: string | null;

  /**
   * `aktiv` und `bestaetigt` sind DIESELBE Spalte. Eine Brücke zu bestätigen
   * heißt, sie zu aktivieren — und das ist eine Planungsentscheidung: Sie
   * behauptet, dass zwei Rohre in Wirklichkeit verbunden sind.
   */
  aktiv: boolean;
  bestaetigt: boolean;

  createdAt: string;
  updatedAt: string | null;
  von: HeizungNetzbrueckenende;
  nach: HeizungNetzbrueckenende;
}

// ── Kälte (Paket HZ5, Migrationen 0775-0778) ───────────────────────────────
//
// `randbedingung` ist KEIN Beiwerk. Ein Datenblatt ist eine Auslegung, keine
// Messung: „600 kW" gilt bei 32 °C Lufteintritt und 45/40 °C. Ohne diese
// Angabe ist die Zahl in einem Bericht von einer gemessenen nicht mehr zu
// unterscheiden — deshalb reicht der Typ sie durch und die Seite zeigt sie.

export interface Kaeltekennwert {
  schluessel: string;
  bezeichnung: string;
  wert: number | null;
  textwert: string | null;
  einheit: string | null;
  betriebsfall: string | null;
  randbedingung: string | null;
  herkunft: string;
  fundstelle: string | null;
  hinweis: string | null;
}

export interface Kaelteanlage {
  id: string;
  kennung: string;
  bezeichnung: string;
  anlagenart: string;
  hersteller: string | null;
  typ: string | null;
  kaeltemittel: string | null;
  standort: string | null;
  datenblatt: string | null;
  herkunft: string;
  hinweis: string | null;
  /** F80: aus dem Modell GEZAEHLT, nicht aus der Geraeteliste uebernommen. */
  geraete: number;
  kennwerte: Kaeltekennwert[];
}

export interface Kaeltebilanzzeile {
  bilanzart: string;
  betriebsfall: string;
  bezeichnung: string;
  erforderlichKw: number | null;
  installiertKw: number | null;
  reserveKw: number | null;
  deckungProzent: number | null;
  /** Beim Speicher GETRENNT von der Leistung: kWh gehoeren in keine kW-Bilanz. */
  kapazitaetKwh: number | null;
  dauerH: number | null;
  aussentemperaturC: number | null;
  temperaturQuelle: string | null;
  rechenweg: string | null;
  herkunft: string;
  istVorlaeufig: boolean;
  vorbehalt: string | null;
  stand: string | null;
}

export interface Kaeltestoffwert {
  fluid: string;
  konzentrationVolProz: number | null;
  temperaturC: number;
  dichteKgM3: number | null;
  waermekapazitaetWsKgK: number | null;
  waermeleitfWMK: number | null;
  viskositaetDynKgMS: number | null;
  /** Darf LEER sein - und ist es fuer 34 Vol. % bei 42,5 °C auch. */
  viskositaetKinMm2S: number | null;
  herkunft: string;
  quelle: string | null;
  randbedingung: string | null;
  hinweis: string | null;
}

export interface Kaeltebefund {
  schluessel: string;
  schwere: string;
  titel: string;
  text: string;
  vorschlag: string | null;
  status: string;
  erkanntAm: string;
}

export interface Kaelterueckkuehlkreis {
  kreisart: string;
  medium: string | null;
  glykolProzent: number | null;
  waermekapazitaetWsKgK: number | null;
  dichteKgM3: number | null;
  viskositaetMm2S: number | null;
  stoffwertBei: number | null;
  anzahl: number;
}

export interface Kaelteseite {
  anlagen: Kaelteanlage[];
  bilanz: Kaeltebilanzzeile[];
  stoffwerte: Kaeltestoffwert[];
  befunde: Kaeltebefund[];
  rueckkuehlkreis: Kaelterueckkuehlkreis[];
}

export interface HeizungNetzbrueckenliste {
  summe: {
    bruecken: number;
    aktiv: number;
    unbestaetigt: number;
    grenzeAbstandMm: number;
    abstandMaxMm: number | null;
    jeGruppe: {
      systemgruppe: string;
      bruecken: number;
      aktiv: number;
      unbestaetigt: number;
    }[];
  };
  zeilen: HeizungNetzbrueckenzeile[];
}

export interface Heizungwegzeile {
  id: string;
  bezeichnung: string | null;
  verbraucherart: string | null;
  geraet: string | null;
  kreis: string | null;
  systemgruppe: string | null;
  raumnummer: string | null;
  raumname: string | null;
  geschossgruppe: string | null;
  leistungW: number | null;
  massenstromKgH: number | null;
  volumenstromM3h: number | null;
  spreizungK: number | null;
  laengeVorlaufM: number | null;
  laengeRuecklaufM: number | null;
  druckverlustVorlaufPa: number | null;
  druckverlustVerbraucherPa: number | null;
  druckverlustRuecklaufPa: number | null;
  druckverlustGesamtPa: number | null;
  istMassgebend: boolean;
  abgleichPa: number | null;
  guete: string | null;
  gueteJson: string | null;
  verbraucherHerkunft: string | null;
  hinweis: string | null;
  berechnetAm: string;
  ventilBaureihe: string | null;
  ventilDruckPa: number | null;
  ventilKvErforderlich: number | null;
  ventilVoreinstellung: number | null;
  ventilHinweis: string | null;

  /** H-11: woher der Verbraucher erkannt wurde, wenn nicht aus der Netzrolle. */
  erkanntUeber: string | null;

  /** H-18: der gerechnete Bedarf einer Frischwasserstation. */
  leistungBedarfW: number | null;

  /** typenschild | bedarf — worauf dieser Weg bemessen ist. */
  leistungHerkunft: string | null;
}

/** Ein Weg mit den Einzelstücken in TeilstreckenJson. */
export interface Heizungweg extends Heizungwegzeile {
  teilstreckenJson: string | null;
}

/** Ein Einzelstück des Rechenwegs — eine Zeile des Nachweises. */
export interface Heizungwegstueck {
  eid: number;
  art: string;
  bauform: string | null;
  gruppe: string | null;
  dn: number | null;
  diMm: number | null;
  laengeMm: number | null;
  volumenstromM3h: number;
  geschwindigkeitMs: number | null;
  reynolds: number | null;
  lambda: number | null;
  zeta: number | null;
  druckverlustPa: number | null;
  druckgefaellePaM: number | null;
  hinweis: string | null;
}

/** Eine Hälfte des Wegs — Vorlauf oder Rücklauf. */
export interface Heizungweghaelfte {
  seite: string;
  strecken: number;
  laengeM: number;
  reibungPa: number;
  einzelPa: number;
  druckverlustPa: number;
  ohneBeiwert: number;
  maxGeschwindigkeitMs: number;
  maxDruckgefaellePaM: number;
  elemente: Heizungwegstueck[];
}


// ═══════════════════════════════════════════════════════════════════════════
// SCHRITT 2c - DIE DARSTELLUNG NACH TEILSTRECKEN UND VERBRAUCHERN
// ═══════════════════════════════════════════════════════════════════════════

/** Eine Wegzeile der Gliederung — mit dem Vermerk, ob ein Rechenweg vorliegt. */
export interface HeizungWegzeile2c extends Heizungwegzeile {
  druckverlustKorrigiertPa: number | null;
  korrigierteRohre: number;

  /**
   * Ob der Weg ein TeilstreckenJson trägt.
   *
   * Ohne diese Angabe sähe eine leere Postenliste wie ein Fehler aus. Im
   * Bestand sind 410 von 635 Wegen ohne Rechnung.
   */
  hatRechenweg: boolean;

  /**
   * Warum dieser Verbraucher KEINEN Weg hat — `null` bei jedem gerechneten.
   *
   * Bis zum 16.09.2026 stand ein Verbraucher ohne Weg in keiner Zeile: Die
   * Rechnung meldete „221 von 834 Verbrauchern haben auf keiner Seite einen
   * Weg zu einer Quelle" und war damit fertig. Jetzt steht er in der Liste,
   * ohne Druckverlust und mit dem Grund — er geht in keine Summe ein.
   *
   * ohne_anschluss | ohne_seite | ohne_systemgruppe | gruppe_ohne_auslegung
   * | netzluecke | ohne_massenstrom
   */
  grund: string | null;

  /**
   * Die Gruppe des ROHRS, wenn der Weg in einer anderen ausgewertet wird
   * (Festlegung 16.09.2026, Frage 10). `null` ist der Regelfall.
   */
  systemgruppeQuelle: string | null;

  /** heizleistung/kuehlleistung am Bauteil — Vergleich, kein Eingang. */
  leistungModellW: number | null;

  /** Der Massenstrom am Bauteil — Vergleich (Frage 9). */
  massenstromModellKgH: number | null;

  /**
   * Gerechneter Massenstrom geteilt durch den Modellwert. 1,00 heißt:
   * Modell und Rechnung sagen dasselbe.
   */
  massenstromAbweichung: number | null;
}

export interface HeizungWegliste {
  gruppe: string | null;
  klasse: string | null;
  zeilen: HeizungWegzeile2c[];
  gekuerzt: boolean;
  summe: {
    wege: number; mitErgebnis: number; mitRechenweg: number;
    hoechsterPa: number | null; groessterAbgleichPa: number | null;
    leistungW: number; volumenstromM3h: number;
    ventileGesamt: number; ventileUnterAnschlag: number;
  };
  jeKlasse: {
    klasse: string; wege: number; mitErgebnis: number;
    hoechsterPa: number | null; leistungW: number;
  }[];
}

/**
 * Eine Teilstrecke AUF EINEM WEG.
 *
 * `druckverlustPa` ist die Summe der Stücke auf diesem Weg;
 * `teilstreckeDruckverlustPa` die ganze Strecke aus der Tabelle. Beide sind
 * richtig, und sie dürfen auseinandergehen.
 *
 * Es ist NICHT der Volumenstrom: Beide rechnen mit dem Strom des Knotens,
 * also mit allem, was durch das Element fließt. Wo der Weg eine Strecke
 * geradeaus durchläuft, stimmen sie auf die Rundung überein. Auseinander
 * gehen sie, wenn der Weg abzweigt (dann trägt er weniger als die ganze
 * Strecke) oder wenn der Beiwert wegtreu ist: Ein T-Stück hat 0,3 für den
 * Durchgang und 1,3 für den Abzweig, und welcher gilt, entscheidet der Weg.
 *
 * `unterschied` sagt es in Worten — damit niemand die Differenz für einen
 * Rechenfehler hält. `zetaAufWeg` gegen `teilstreckeZeta` ist der Beleg.
 */
export interface HeizungTeilstreckenzeile {
  id: string | null;
  kennzeichen: string | null;
  bezeichnung: string | null;
  seite: string;
  nummer: number;
  posten: number;
  rohre: number;
  laengeM: number;
  druckverlustPa: number;
  reibungPa: number;
  einzelPa: number;
  summePa: number;
  postenOhneBeiwert: number;
  maxGeschwindigkeitMs: number | null;
  kleinsteNennweite: number | null;
  postenMitKorrektur: number;
  teilstreckeLaengeM: number | null;
  teilstreckeDruckverlustPa: number | null;
  teilstreckeMassenstromKgH: number | null;
  teilstreckeReynolds: number | null;
  teilstreckeLambda: number | null;
  teilstreckeDruckgefaellePaM: number | null;
  volumenstromHerkunft: string | null;
  teilstreckeIstMassgebend: boolean;
  teilstreckeNennweite: number | null;
  teilstreckeInnendurchmesser: number | null;
  teilstreckeHinweis: string | null;
  zetaAufWeg: number;
  teilstreckeZeta: number | null;
  unterschied: string | null;
}

export interface HeizungWegaufbau {
  id: string;
  bezeichnung: string | null;
  verbraucherart: string | null;
  geraet: string | null;
  kreis: string | null;
  systemgruppe: string | null;
  raumnummer: string | null;
  raumname: string | null;
  geschossgruppe: string | null;
  leistungW: number | null;
  massenstromKgH: number | null;
  volumenstromM3h: number | null;
  spreizungK: number | null;
  druckverlustVorlaufPa: number | null;
  druckverlustVerbraucherPa: number | null;
  druckverlustRuecklaufPa: number | null;
  druckverlustGesamtPa: number | null;
  druckverlustKorrigiertPa: number | null;
  korrigierteRohre: number;
  istMassgebend: boolean;
  abgleichPa: number | null;
  guete: string | null;
  gueteJson: string | null;
  verbraucherHerkunft: string | null;
  erkanntUeber: string | null;
  ventilBaureihe: string | null;
  ventilDruckPa: number | null;
  ventilKvErforderlich: number | null;
  ventilVoreinstellung: number | null;
  ventilHinweis: string | null;
  hinweis: string | null;

  /** rohre | keine — woher die Gliederung stammt. */
  quelle: string;

  /** Warum nichts da ist. Ohne diesen Satz sieht leer wie kaputt aus. */
  grund: string | null;

  summe: {
    teilstrecken: number; ohneTeilstrecke: number; posten: number;
    postenOhneBeiwert: number; druckverlustPa: number;
    hoechsterPa: number | null; postenMitKorrektur: number;
  };

  teilstrecken: HeizungTeilstreckenzeile[];
}

/** Ein Posten — Rohr, Formstück oder Einbauteil — mit seinen Herkünften. */
export interface HeizungPostenzeile {
  nummer: number;
  elementId: number;
  art: string;
  bauform: string | null;
  seite: string;
  teilstreckeId: string | null;
  kennzeichen: string | null;
  positionInTeilstrecke: number | null;
  dn: number | null;
  innendurchmesserMm: number | null;
  laengeM: number | null;
  massenstromKgH: number | null;
  volumenstromM3h: number;
  geschwindigkeitMs: number | null;
  reynolds: number | null;
  lambda: number | null;
  zeta: number | null;
  druckgefaellePaM: number | null;
  druckverlustPa: number | null;
  summePa: number;

  /** modell | werkstoff | annahme — am Rohr. */
  rauigkeitHerkunft: string | null;

  /** Die Quelle der Zetaregel; „keine" heißt: der Beiwert FEHLT. */
  beiwertHerkunft: string | null;
  beiwertBegruendung: string | null;

  /** Immer „weg": der Strom entsteht rückwärts aus dem Verbraucher. */
  volumenstromHerkunftWeg: string;
  volumenstromHerkunftTeilstrecke: string | null;

  korrekturDn: number | null;
  korrekturPaM: number | null;
  raumnummer: string | null;
  hinweis: string | null;
}

export interface HeizungPostenliste {
  wegId: string;
  weg: string | null;
  systemgruppe: string | null;
  verbraucherart: string | null;
  verbraucherHerkunft: string | null;
  verbraucherDruckverlustPa: number | null;
  teilstreckeId: string | null;
  kennzeichen: string | null;
  bezeichnung: string | null;
  teilstreckeLaengeM: number | null;
  teilstreckeDruckverlustPa: number | null;
  teilstreckeReynolds: number | null;
  teilstreckeLambda: number | null;
  teilstreckeDruckgefaellePaM: number | null;
  volumenstromHerkunft: string | null;
  teilstreckeOhneBeiwert: number | null;
  teilstreckeHinweis: string | null;
  quelle: string;
  grund: string | null;
  summe: {
    posten: number; rohre: number; ohneBeiwert: number;
    ohneDruckverlust: number; druckverlustPa: number; reibungPa: number;
    einzelPa: number; laengeM: number; maxGeschwindigkeitMs: number | null;
    mitKorrektur: number;
  };
  posten: HeizungPostenzeile[];
}

/** Ein Verbraucher mit den drei Leistungszahlen desselben Geräts (H-01). */
export interface HeizungVerbraucherzeile {
  wegId: string;
  elementId: number | null;
  bezeichnung: string | null;
  verbraucherart: string | null;
  geraet: string | null;
  systemgruppe: string | null;
  kreis: string | null;
  raumnummer: string | null;
  raumname: string | null;
  geschossgruppe: string | null;
  leistungW: number | null;
  leistungBedarfW: number | null;
  leistungHerkunft: string | null;
  massenstromKgH: number | null;
  volumenstromM3h: number | null;
  spreizungK: number | null;
  druckverlustVerbraucherPa: number | null;
  druckverlustGesamtPa: number | null;
  abgleichPa: number | null;
  istMassgebend: boolean;
  guete: string | null;
  verbraucherHerkunft: string | null;
  ventilBaureihe: string | null;
  ventilVoreinstellung: number | null;
  ventilHinweis: string | null;
  /** Der kv-Wert der gewählten Stufe (V-L2). */
  ventilKvGewaehlt: number | null;
  /**
   * a = Δp_Ventil / (Δp_Ventil + Δp_Kreisrest). Unter 0,3 regelt das Ventil
   * nicht mehr — Abhilfe ist weniger Differenzdruck, kein anderes Ventil.
   */
  ventilAutoritaet: number | null;
  /** gruen | gelb | rot | ohne. */
  ventilAutoritaetBewertung: string | null;
  bauart: string | null;
  typbezeichnung: string | null;
  normleistungW: number | null;
  betriebsleistungW: number | null;
  leistungAusMassenstromW: number | null;
  modellleistungW: number | null;
  heizlastRaumW: number | null;
  deckungProzent: number | null;
  verhaeltnis: number | null;
  bewertung: string | null;
  exponent: number | null;
  exponentHerkunft: string | null;
  heizflaechenhinweis: string | null;
}

export interface HeizungVerbraucherliste {
  gruppe: string | null;
  klasse: string | null;
  zeilen: HeizungVerbraucherzeile[];
  gekuerzt: boolean;
  summe: {
    verbraucher: number; mitHeizflaeche: number; leistungW: number;
    betriebsleistungW: number; leistungAusMassenstromW: number;
    modellleistungW: number; volumenstromM3h: number;
  };
  jeBewertung: { stufe: string; anzahl: number }[];
}


/**
 * Anlageninhalt und Absicherung je Systemgruppe nach DIN EN 12828 — Befunde
 * H-06 und H-07.
 *
 * Die Ansätze stehen daneben, weil die Zahl ohne sie nicht zu lesen ist:
 * Vier von ihnen sind unbestätigte übliche Werte.
 */
export interface HeizungAusdehnungsliste {
  zeilen: {
    id: string; systemgruppe: string; bezeichnung: string | null;
    kreis: string | null; vorlaufC: number | null;
    glykolProzent: number | null;
    rohre: number; rohrLiter: number; zuschlagLiter: number;
    verbraucher: number; verbraucherLiter: number;
    verbraucherOhneInhalt: number;
    anlageninhaltL: number; leistungKw: number;
    ausdehnungProzent: number | null; ausdehnungL: number | null;
    wasservorlageL: number;
    statischeHoeheM: number; statischerDruckBar: number;
    vordruckBar: number; ansprechdruckBar: number; enddruckBar: number;
    ansprechdruckStufeBar: number | null;
    nennvolumenBeiStufeL: number | null;
    nennvolumenErfL: number | null; nennvolumenModellL: number | null;
    gefaesseModell: number; bewertung: string;
    spezVolumenLKw: number | null;
    vdi2035Stufe: string | null; vdi2035Anforderung: string | null;
    hinweis: string | null; gerechnetAm: string;
  }[];
  ansaetze: {
    schluessel: string; bezeichnung: string; wert: number | null;
    einheit: string | null; herkunft: string; quelle: string | null;
    istBestaetigt: boolean; hinweis: string | null;
  }[];

  /**
   * Die Absicherung je ERZEUGER nach DIN EN 12828 — Festlegung des
   * Fachplaners vom 16.09.2026, Frage 20.
   *
   * Das Ausdehnungsgefäß gehört zum Kreis (zeilen), das Sicherheitsventil
   * zum Erzeuger. Zwei Listen, weil es zwei Bezugsgrößen sind.
   */
  absicherungen: {
    bezeichnung: string | null; bauteilart: string;
    systemgruppe: string | null; raumnummer: string | null;
    leistungKw: number | null; leistungHerkunft: string | null;
    ansprechdruckBar: number | null; zulaessigerDruckBar: number | null;
    zulaessigeTemperaturC: number | null; vorlaufC: number | null;
    svEintrittDn: number | null; svAustrittDn: number | null;
    bewertung: string; hinweis: string | null; gerechnetAm: string;
  }[];
}

/**
 * Der Dämmnachweis nach GEG Anlage 8 und gegen den Taupunkt.
 *
 * `nichtBeurteilbarAnzahl` steht in DERSELBEN Zeile wie `anzahl`,
 * `erfuelltAnzahl` und `unterschrittenAnzahl` — und `beurteilbarProzent` ist
 * KEINE Erfüllungsquote, sondern sagt, über welchen Teil des Bestands
 * überhaupt geurteilt wurde.
 */
export interface HeizungDaemmnachweisliste {
  nachweis: {
    id: string; gattung: string; schluessel: string; bezeichnung: string;
    anzahl: number; laengeM: number;
    erfuelltAnzahl: number; unterschrittenAnzahl: number;
    nichtBeurteilbarAnzahl: number;
    erfuelltM: number; unterschrittenM: number; nichtBeurteilbarM: number;
    beurteilbarProzent: number | null;
    mitDaemmstoff: number; mitDicke: number;
    fehlmengeMittelMm: number | null; fehlmengeMaxMm: number | null;
    taupunktC: number | null; norm: string | null; befund: string | null;
    parameterJson: string | null; gerechnetAm: string;
  }[];
  ansaetze: {
    schluessel: string; bezeichnung: string; wert: number | null;
    einheit: string | null; herkunft: string; quelle: string | null;
    istBestaetigt: boolean; hinweis: string | null;
  }[];
  klassen: {
    muster: string; bezeichnung: string; lambdaWmK: number;
    bezugstemperaturC: number | null; norm: string | null; herkunft: string;
    geprueft: boolean; begruendung: string | null;
  }[];
  regeln: {
    schluessel: string; zeile: string; medium: string; verlegeort: string;
    dnVonMm: number | null; dnBisMm: number | null; dickeMm: number | null;
    dickeWieNennmass: boolean; anteilProzent: number | null;
    bezugsLambda: number; norm: string; zitat: string; herkunft: string;
    istGeprueft: boolean;
  }[];
  liste: {
    id: string; revitElementId: number; art: string; kreis: string | null;
    geschoss: string | null; systemtyp: string | null;
    laengeMm: number | null; nennweite: number | null;
    daemmungstyp: string | null; vorhandenMm: number | null;
    urteil: string | null; grund: string | null; zeile: string | null;
    sollTabelleMm: number | null; sollGegMm: number | null;
    sollTaupunktMm: number | null; sollMm: number | null;
    verhaeltnis: number | null; lambdaWmK: number | null;
    massHerkunft: string | null; oberflaecheC: number | null;
    taupunktC: number | null; betriebstemperaturC: number | null;
  }[];
}

export interface Heizungzetaregel {
  id: string; rang: number; muster: string; bauform: string;
  zetaDurchgang: number | null; zetaAbzweig: number | null;
  festdruckPa: number | null; winkelSkaliert: boolean;
  bezugsdurchmesser: string | null; istDurchstroemt: boolean;
  liegtImWeg: boolean; quelle: string | null; begruendung: string | null;
}

/**
 * Eine Zeile der Geräteliste Heizung + Kälte des Fachplaners.
 *
 * `groesseText` ist der Rohtext der Vorlage („1.000kW"), `groesseWert` die
 * daraus gelesene Zahl (1000). Beide stehen da, und zwar mit Absicht: Der
 * Rohtext ist der Beleg, die Zahl die Rechengröße.
 *
 * `aksNummer` ist die Adresse AUS DER VORLAGE. Sie wird an kein Bauteil
 * geschrieben — die AKS ist eingefroren (F29).
 */
export interface Sollgeraetezeile {
  id: string;
  quelle: 'HK' | 'FWS' | 'RLT';
  seite: number;
  kennung: string;
  aksNummer: string | null;
  abschnitt: string | null;
  system: string | null;
  kategorie: string | null;
  geraetFunktion: string | null;
  standort: string | null;
  geschoss: string | null;
  schacht: string | null;
  menge: number | null;
  einheit: string | null;
  groesseText: string | null;
  groesseWert: number | null;
  groesseEinheit: string | null;
  groesseVergleich: string | null;
  leistungKw: number | null;
  volumenM3: number | null;
  nennweiteDn: number | null;
  luftmengeM3H: number | null;
  heizleistungKw: number | null;
  kuehlleistungKw: number | null;
  zapfmengeLMin: number | null;
  bemerkung: string | null;
  /** aks | standort | name — oder null. Eine Zuordnung ohne Weg ist keine. */
  zuordnungWeg: string | null;
  zuordnungZielTabelle: string | null;
  zuordnungZielId: string | null;
  zuordnungZielText: string | null;
  zuordnungKandidaten: number;
  zuordnungHinweis: string | null;
}

/** Der Einlesevorgang — welche Datei, welche Fassung, welches Ergebnis. */
export interface Sollgeraetelauf {
  id: string;
  dateiname: string;
  dateiSha256: string | null;
  dateiStand: string | null;
  seiten: number;
  zeilenHk: number;
  zeilenFws: number;
  zeilenRlt: number;
  zuordnungAks: number;
  zuordnungStandort: number;
  zuordnungName: number;
  ohneZuordnung: number;
  gelesenAm: string;
  hinweis: string | null;
}

export interface Sollgeraeteliste {
  lauf: Sollgeraetelauf | null;
  zeilen: Sollgeraetezeile[];
}

/** Die Sollseite als Zahl — die Gegenprobe jeder Lastrechnung. */
export interface Sollseite {
  gruppen: {
    system: string | null; kategorie: string | null;
    zeilen: number; stueck: number; leistungKw: number;
    /** Was die Vorlage als GRENZE nennt („max. 3MW") — NEBEN der Summe. */
    leistungGrenzeKw: number;
    volumenM3: number; mitLeistung: number;
  }[];
  waermeerzeugungKw: number;
  kaelteerzeugungKw: number;
  rueckkuehlungKw: number;
  speicherM3: number;
  pumpenHeizung: number;
  pumpenKaelte: number;
  rltGeraete: number;
  rltLuftmengeM3H: number;
  rltHeizleistungKw: number;
  rltKuehlleistungKw: number;
}

/** Liste gegen Modell — in BEIDE Richtungen. */
export interface Sollabweichung {
  offen: {
    quelle: string; kennung: string; kategorie: string | null;
    standort: string | null; geraetFunktion: string | null;
    zuordnungWeg: string | null;
  }[];
  nichtInListe: { id: string; anlage: string }[];
  stueckzahlen: {
    kategorie: string; inListe: number;
    bauteilart: string; imModell: number | null;
  }[];
  anlagenImModell: number;
}

export interface Heizunguebersicht {
  arten: Heizungbauteilart[];
  netze: { kreis: string; anzahl: number }[];
  entfernt: number;
  gesamt: number;
}

// --- Sanitär ---------------------------------------------------------------

export interface Sanitaeruebersicht {
  gesamt: number;
  verbindungen: number;
  gelesen: string | null;
  jeArt: { art: string; anzahl: number }[];
  jeKreis: {
    kreis: string | null;
    fuehrungsart: string | null;
    rohre: number;
    laengeM: number;
    /** Fallleitungen haben KEIN Gefälle — das ist richtig, kein Befund. */
    fallleitungen: number;
    ohneGefaelle: number;
    gegengefaelle: number;
  }[];
  jeSystemtypOhneKreis: { systemtyp: string | null; anzahl: number }[];
  gegenstaende: {
    herkunftsmodell: string | null;
    anzahl: number;
    /** Nach der Einstufung: echte Sanitärgegenstände. */
    sanitaer: number;
    /** Aus einem eigenen Fachmodell oder ohne Anschluss — mit Grund. */
    ausgeschlossen: number;
    uneingestuft: number;
    mitRaum: number;
    mitAnschlusswert: number;
  }[];
  gegenstandsarten: {
    art: string | null;
    anzahl: number;
    mitAnschlusswert: number;
  }[];
  offene: { einstufung: string; anzahl: number }[];
  annahmen: {
    schluessel: string;
    bezeichnung: string;
    wert: number | null;
    einheit: string | null;
    herkunft: string;
    norm: string | null;
    begruendung: string | null;
    geprueft: boolean;
  }[];
}

export interface SanitaerOffenesEnde {
  revitElementId: number;
  connectorId: number;
  x: number | null;
  y: number | null;
  z: number | null;
  durchmesserMm: number | null;
  systemtyp: string | null;
  kreis: string | null;
  geschoss: string | null;
  raumnummer: string | null;
  raumname: string | null;
  einstufung: string;
  begruendung: string | null;
  abstandMm: number | null;
  grenzabstandMm: number | null;
}

/**
 * Ein Knoten des Sanitärbaums.
 *
 * Drei Ebenen mit derselben Form: `geschoss`, `raum`, `art`. Eine eigene
 * Schnittstelle je Ebene wäre dreimal fast dasselbe — und die Anzeige müsste
 * dreimal unterscheiden, was sie gerade in der Hand hat.
 */
export interface SanitaerBaumknoten {
  ebene: string;
  name: string;
  zusatz: string | null;
  anzahl: number;
  laengeM: number;
  summeDu: number;
  raeume: number;
  gegenstaende: number;
  geraete: number;
  gattung: string;
  mitProdukt: number;
  kinder?: SanitaerBaumknoten[];
}

export interface SanitaerRaum {
  bauteile: {
    id: string;
    revitElementId: number;
    art: string;
    familie: string | null;
    typ: string | null;
    kreis: string | null;
    nennweite: number | null;
    werkstoff: string | null;
    laengeMm: number | null;
    gefaelle: number | null;
    leitungsart: string | null;
    hatProdukt: boolean;
  }[];
  gegenstaende: {
    id: string;
    revitElementId: number;
    art: string | null;
    familie: string | null;
    typ: string | null;
    anschlusswertDU: number | null;
    herkunftsmodell: string | null;
  }[];
}

// --- Die Sanitärberechnungen ------------------------------------------------

export interface SanitaerBerechnungslauf {
  id: string;
  bereich: string;
  norm: string | null;
  zeilen: number;
  beanstandet: number;
  gerechnetAm: string;

  /**
   * Der Stand des Modells, gegen das gerechnet wurde — das jüngste
   * `GelesenAm` der Sanitärrohre.
   *
   * Ohne ihn ist einer Zahl nicht anzusehen, ob sie noch gilt: Der Lauf vom
   * 05.09.2026 stand nach der Aufnahme vom 15.09.2026 zehn Tage lang
   * unverändert auf der Seite und wies sich als aktuell aus.
   */
  modellstand?: string | null;
  standVeraltet?: boolean;
  veraltetTage?: number | null;

  /**
   * Der Lauf ist ein ZWISCHENSTAND und keine Bemessung (PB44): Seine
   * Grundlage ist doppelt verknüpft.
   */
  bemessungGesperrt?: boolean;
  sperrgrund?: string | null;
}

export interface SanitaerBerechnungsuebersicht {
  bereiche: SanitaerBerechnungslauf[];
}

/**
 * Eine Zeile einer Berechnung.
 *
 * `werte` und `rechenweg` sind bewusst untypisiert: Jede Gattung trägt andere
 * Größen — der Trinkwasserstrang Fließdruck und Geschwindigkeit, das
 * Abwassernetzteil Füllungsgrad und Gefälle, der Fettabscheider Nenngröße und
 * Schlammfang.
 *
 * Acht Schnittstellen dafür wären acht Stellen, die bei der nächsten Kennzahl
 * auseinanderlaufen. Die Tabelle baut ihre Spalten aus den Daten.
 */
export interface SanitaerBerechnungszeile {
  id: string;
  gattung: string;
  ordnung: number;
  bezeichnung: string;
  kreis: string | null;
  raumnummer: string | null;
  geschoss: string | null;
  revitElementId: number | null;
  kennwert: number | null;
  istMassgebend: boolean;
  bewertung: string;
  befund: string | null;
  werte: Record<string, unknown> | null;
  rechenweg: string[] | null;

  /**
   * Die Teilstrecken — die Auffaltung der Zeile in ihre Bauteile.
   *
   * Beim Trinkwasserweg 810 Bauteile vom Eintritt zur ungünstigsten
   * Zapfstelle, bei einem Netzteil dessen Rohre. Untypisiert, weil die Felder
   * je Bereich andere sind: Der Trinkwasserweg trägt Druckverlust und
   * Geschwindigkeit, das Abwasserrohr Gefälle und Füllungsgrad.
   */
  teilstrecken: Record<string, unknown>[] | null;
}

export interface SanitaerBerechnungsbereich {
  lauf: SanitaerBerechnungslauf & {
    kennzahlen: Record<string, unknown> | null;
    annahmen: {
      schluessel: string;
      wert: number | null;
      einheit: string | null;
      herkunft: string;
      geprueft: boolean;
    }[] | null;
    hinweise: string[] | null;
  };
  gattungen: { gattung: string; anzahl: number }[];
  zeilen: SanitaerBerechnungszeile[];
}

/**
 * Ein Strang der Sanitärrechnung — der Wurzelknoten des Baums.
 *
 * Ein „Strang" ist hier KEIN Strang im Sinne des Planers: Die
 * Trinkwasserrechnung schneidet nach (Netzteil, Seite, Etage), und 79,2 % aller
 * Kaltwasserbauteile hängen in EINEM Netzteil (Befund S-11). Die Bezeichnung
 * sagt, was er ist.
 */
export interface SanitaerStrang {
  bereich: string | null;
  schluessel: string;
  bezeichnung: string | null;
  geschoss: string | null;
  teilstrecken: number;
  rohre: number;
  formstuecke: number;
  /** Formstücke OHNE Beiwertregel — ihr Anteil geht mit 0 Pa ein (S-02). */
  ohneZetaregel: number;
  laengeM: number;
  reibungPa: number;
  einzelPa: number;
  druckverlustPa: number;
  vMax: number | null;
  fuellungsgradMax: number | null;
  abflussMax: number | null;
  volumenstromMax: number | null;
  dnMin: number | null;
  dnMax: number | null;
  istMassgebend: boolean;
  beanstandet: number;
  kreise: string[];
}

export interface SanitaerStraenge {
  straenge: SanitaerStrang[];
  jeBereich: {
    bereich: string;
    straenge: number;
    teilstrecken: number;
    beanstandet: number;
  }[];
  summe: { straenge: number; teilstrecken: number; ohneZetaregel: number };
  stand: string | null;
}

/**
 * Ein Posten eines Strangs — ein Rohr, ein Formstück, eine Armatur.
 *
 * Δp_R und Δp_E stehen GETRENNT: DIN 1988-300 Abschnitt 6.3 bemisst
 * Δp = Δp_R + Δp_E + Δp_Ap. In einer Spalte ist einem Posten nicht anzusehen,
 * ob er eine Rohrlänge oder ein Bogen ist.
 */
export interface SanitaerTeilstrecke {
  id: string;
  nummer: number | null;
  kennzeichen: string;
  kreis: string | null;
  elementId: number | null;
  art: string | null;
  familie: string | null;
  typ: string | null;
  bezeichnung: string | null;
  nennweite: number | null;
  innendurchmesser: number | null;
  laengeM: number | null;
  werkstoff: string | null;
  rauigkeitMm: number | null;
  rauigkeitHerkunft: string | null;
  zeta: number | null;
  bauform: string | null;
  zetaHerkunft: string | null;
  zetaQuelle: string | null;
  volumenstrom: number | null;
  abfluss: number | null;
  geschwindigkeit: number | null;
  gefaelle: number | null;
  leitungsart: string | null;

  /**
   * Die Fließrichtung (V-S1) und die Fallleitung (V-S2).
   *
   * `durchflussHerkunft` ist die wichtigste der Angaben: 'obergrenze' heißt,
   * dass der Durchfluss auf das gedeckelt ist, was das Rohr bei 2 m/s fasst —
   * dann KANN die Geschwindigkeit ihre eigene Grenze nicht überschreiten
   * (Befund S-19), und die Spalte daneben ist keine Messung, sondern eine
   * Schranke.
   *
   * `vorrohr_obergrenze` und `vorrohr_kumuliert` stehen an Formstücken und
   * Armaturen: Sie führen keinen eigenen Durchfluss, ihre Geschwindigkeit ist
   * die des zuletzt durchlaufenen Rohres. Bis zum 22.09.2026 stand dort gar
   * nichts — 409 von 11.321 Zeilen trugen eine Geschwindigkeit ohne jede
   * Herkunft (S5b).
   */
  richtungHerkunft: string | null;
  richtungUmgekehrt: boolean | null;
  durchflussHerkunft: string | null;
  summeDU: number | null;
  lueftungsart: string | null;
  lueftungsartHerkunft: string | null;
  abflussvermoegenLs: number | null;
  erforderlichDn: number | null;

  fuellungsgrad: number | null;
  fuellungsgradGrenze: number | null;
  druckverlustPa: number | null;
  reibungPa: number | null;
  einzelPa: number | null;
  summeDruckverlustPa: number | null;
  summeDruckverlustM: number | null;
  raumnummer: string | null;
  geschoss: string | null;
  bewertung: string | null;
  hinweis: string | null;
}

export interface SanitaerStrangteilstrecken {
  strang: {
    schluessel: string;
    bereich: string | null;
    bezeichnung: string | null;
    istMassgebend: boolean;
    berechnetAm: string | null;
    teilstrecken: number;
    rohre: number;
    formstuecke: number;
    reibungPa: number;
    einzelPa: number;
    summePa: number;
    laengeM: number;
    ohneZetaregel: number;
    kreise: { kreis: string; anzahl: number }[];
    rauigkeit: { herkunft: string; anzahl: number }[];
  };
  teilstrecken: SanitaerTeilstrecke[];
}

export interface SanitaerTeilstreckenposten {
  posten: SanitaerTeilstrecke & {
    bereich: string | null;
    strang: string | null;
    strangSchluessel: string | null;
    herkunft: string | null;
    berechnetAm: string | null;
  };
  bauteil: Record<string, unknown> | null;
  nachbarn: {
    id: string;
    nummer: number | null;
    art: string | null;
    typ: string | null;
    nennweite: number | null;
    geschwindigkeit: number | null;
    druckverlustPa: number | null;
    summeDruckverlustPa: number | null;
  }[];
  herkuenfte: {
    groesse: string;
    wert: number | null;
    herkunft: string;
    quelle: string | null;
  }[];
}

/**
 * Ein Netzteil oder Strang, wie ihn die Netzteilliste zeigt.
 *
 * Eigene Schnittstelle und nicht `SanitaerBerechnungszeile`: Diese Antwort
 * reicht den Rechenweg und die Teilstrecken NICHT mit durch — sie wären je
 * Zeile bis zu 2.655 Posten, und die Liste zeigt 500 Zeilen.
 */
export interface SanitaerNetzteilzeile {
  id: string;
  gattung: string;
  ordnung: number;
  bezeichnung: string;
  kreis: string | null;
  raumnummer: string | null;
  geschoss: string | null;
  revitElementId: number | null;
  kennwert: number | null;
  istMassgebend: boolean;
  bewertung: string;
  befund: string | null;
  bereich: string | null;
  werte: Record<string, unknown> | null;
}

export interface SanitaerNetzteile {
  zeilen: SanitaerNetzteilzeile[];
  jeKreis: {
    kreis: string;
    netzteile: number;
    beanstandet: number;
    hinweise: number;
    kennwertSumme: number;
    kennwertMax: number | null;
  }[];
  gekuerzt: boolean;
  summe: { gesamt: number; beanstandet: number };
  stand: string | null;
}

/**
 * Ein Berechnungslauf mit seinen Kennzahlen.
 *
 * `kennzahlen` ist untypisiert: Die fünf Bereiche tragen verschiedene Größen —
 * Trinkwasser den Spitzendurchfluss, Regenwasser die Dachflächendeckung,
 * Warmwasser die Zapfstellen ohne Netz. Fünf Schnittstellen dafür wären fünf
 * Stellen, die bei der nächsten Kennzahl auseinanderlaufen.
 */
export interface SanitaerBereichslauf {
  id: string;
  bereich: string;
  norm: string | null;
  zeilen: number;
  beanstandet: number;
  gerechnetAm: string;
  modellstand: string | null;
  standVeraltet: boolean;
  veraltetTage: number | null;

  /** PB44: Zwischenstand statt Bemessung — die Grundlage ist doppelt verknüpft. */
  bemessungGesperrt?: boolean;
  sperrgrund?: string | null;

  kennzahlen: Record<string, unknown> | null;
  hinweise: string[] | null;
}

export interface SanitaerBerechnungsanlagen {
  gruppen: {
    gattung: string;
    anzahl: number;
    beanstandet: number;
    zeilen: (SanitaerNetzteilzeile & {
      rechenweg: string[] | null;
    })[];
  }[];
  summe: { gesamt: number; beanstandet: number };
  stand: string | null;
}

export interface SanitaerGegenstandsuebersicht {
  artenGesamt: {
    art: string;
    bezeichnung: string;
    objekte: number;
    plaetze: number;
  }[];
  gegenstaende: {
    id: string;
    revitElementId: number;
    art: string | null;
    familie: string | null;
    typ: string | null;
    geschoss: string | null;
    modellebene: string | null;
    raumnummer: string | null;
    raumname: string | null;
    herkunftsmodell: string | null;
    plaetze: number | null;
    anschlusswertDU: number | null;
    vollstromKaltLs: number | null;
    vollstromWarmLs: number | null;
    anschlusswertHerkunft: string | null;
  }[];
  etagen: {
    etage: string;
    objekte: number;
    plaetze: number;
    summeDu: number;
    summeKaltLs: number;
    summeWarmLs: number;
    arten: {
      art: string;
      bezeichnung: string;
      objekte: number;
      plaetze: number;
      duJePlatz: number | null;
      kaltJePlatz: number | null;
      warmJePlatz: number | null;
      summeDu: number;
      summeKaltLs: number;
      summeWarmLs: number;
      norm: string | null;
    }[];
  }[];
  gesamt: {
    objekte: number;
    plaetze: number;
    summeDu: number;
    abflussLs: number;
    abflusskennzahlK: number;
    summeKaltLs: number;
    summeWarmLs: number;
    spitzeKaltLs: number | null;
    spitzeWarmLs: number | null;
    gebaeudeart: string | null;
    kurve: string | null;
  };
  normen: { trinkwasser: string; abwasser: string };

  /**
   * Modelle, die MEHRFACH VERKNÜPFT sind (PB44).
   *
   * Diese Übersicht dient der Prüfung des Spitzendurchflusses — sie ist die
   * Stelle, an der jemand die Summen nachrechnet. Steht ein Modell zweimal
   * da, gehen seine Gegenstände zweimal in jede Summe ein; gemessen am
   * 21.09.2026 waren das 1.530,00 von 3.307,50 DU = 46,3 %.
   *
   * Entdoppelt wird nichts: Welche Verknüpfung gilt, entscheidet der
   * Planstand. `summeEinfach` ist die Vergleichszahl, kein Vorschlag.
   */
  doppelverknuepfungen: Doppelverknuepfung[];
}

/** Ein Modell unter mehr als einer Verknüpfungskennung (PB44). */
export interface Doppelverknuepfung {
  ablage: string;
  modell: string;
  verknuepfungen: number;
  zeilen: number;
  bauteile: number;
  zeilenDoppelt: number;
  summe: number | null;
  summeEinfach: number | null;
  summeMehrfach: number | null;
  anteilProzent: number | null;
  summenname: string | null;
  werteGleich: boolean;
  vermerk: string;
}

interface AnlagenGeraet {
  id: string;
  revitElementId: number;
  familie: string | null;
  typ: string | null;
  raumnummer: string | null;
  raumname: string | null;
  geschoss: string | null;
  kreis: string | null;
  hersteller: string | null;
  produktreihe: string | null;
  geraeteart: string | null;

  /** Die Adresse — oder `null`, wenn keine vergeben ist (Paket S8). */
  aksSchluessel: string | null;
  /**
   * Welcher der fünf Fälle: `vergeben`, `fehlt` (Lücke), `bekommt_keine`
   * (F39, kein Mangel), `ueber_dem_umfang`, `nicht_entschieden`.
   */
  aksEinstufung: string;
  /** Der Grund im Klartext — nie leer, auch wenn eine Adresse dasteht. */
  aksGrund: string;
  /** Die Bauart, nach der der Umfang entschieden hat. */
  aksBauart: string;
  /** F39, F54 — oder `offen`, wo nichts entschieden ist. */
  aksFestlegung: string;
}

/** Eine Zeile der AKS-Bilanz (Paket S8) — eine Bauart. */
export interface SanitaerAksBilanzzeile {
  bauart: string;
  bestand: number;
  mitAdresse: number;
  ohneAdresse: number;
  einstufung: string;
  grund: string;
  festlegung: string;
  /** Zaehlt NUR, was in die AKS gehoert und keine traegt. */
  luecke: number;
  imUmfang: boolean;
}

interface Anlagenauslegung {
  kennung: string;
  typ: string | null;
  bauart: string;
  kreis: string | null;
  zulaufLs: number | null;
  foerderstromLs: number | null;
  foerderhoeheM: number | null;
  mindestfoerderhoeheM: number | null;
  druckleitungDn: number | null;
  geschwindigkeitMs: number | null;
  nutzvolumenL: number | null;
  p2Kw: number | null;
  befund: string | null;
  unterlage: string | null;
}

export interface SanitaerAnlagen {
  fettabscheider: {
    auslegungen: {
      kennung: string;
      qsLs: number | null;
      fd: number | null;
      ft: number | null;
      fr: number | null;
      ngGerechnet: number | null;
      ngGewaehlt: number | null;
      schlammfangL: number | null;
      fettspeicherL: number | null;
      gegenstaendeGesamt: number | null;
      norm: string | null;
      unterlage: string | null;
      gegenstaende: unknown;
    }[];
    imModell: AnlagenGeraet[];
  };
  hebeanlagen: { auslegungen: Anlagenauslegung[]; imModell: AnlagenGeraet[] };
  vorbehaelter: { auslegungen: Anlagenauslegung[]; imModell: AnlagenGeraet[] };
  /**
   * Gerechnet, nicht geliefert — deshalb keine `auslegungen`, sondern
   * `anlagen`: In der Auslegungstabelle steht, was der Fachplaner gegeben
   * hat, und ein Rechenergebnis dazwischen wäre beim nächsten Abgleich nicht
   * mehr von einer Vorgabe zu unterscheiden.
   */
  druckerhoehung: {
    anlagen: SanitaerDruckerhoehung[];
    imModell: AnlagenGeraet[];
    foerderhoeheGesamtM: number | null;
  };
  frischwasserstationen: { imModell: AnlagenGeraet[] };
  durchlauferhitzer: { imModell: AnlagenGeraet[] };
  /**
   * Die Adressen in BEIDE Richtungen (Paket S8, F39/F54): was in die AKS
   * gehoert und keine hat (`luecke`), und was nach F39 gar keine bekommt
   * (`bekommtKeine`) — kein Mangel und kein Nennerposten.
   */
  aks: {
    imUmfang: number;
    mitAdresse: number;
    luecke: number;
    bekommtKeine: number;
    nichtEntschieden: number;
    ueberDemUmfang: number;
    /** Zeilen in `msr_aks_umfang` — 0 hieße: die Grenze ist gar nicht da. */
    verzeichniszeilen: number;
    zeilen: SanitaerAksBilanzzeile[];
  };
  regenwasser: {
    rigolen: {
      bereich: string;
      flaecheM2: number | null;
      abgemindertM2: number | null;
      erforderlichM3: number | null;
      nettoM3: number | null;
      versickerrateLs: number | null;
      regendauerMin: number | null;
      regenspendeLsHa: number | null;
      entleerungszeitH: number | null;
      verfahren: string | null;
      norm: string | null;
      kfWert: string | null;
      haeufigkeitJahre: number | null;
      teilflaechen: unknown;
    }[];
    flaecheGesamtM2: number;
    speicherGesamtM3: number;
  };
}

export interface SanitaerRechenlauf {
  bereiche: {
    bereich: string;
    berechnungId: string | null;
    zeilen: number;
    beanstandet: number;
    fehler: string | null;
  }[];
  hinweise: string[];
  offenePunkte: string[];
}

// --- Die Sanitärprodukte ----------------------------------------------------

/**
 * Der Abgleich conVoid gegen das Architekturmodell.
 *
 * `befund` unterscheidet vier Fälle. Der teuerste ist `verschoben`: In beiden
 * Modellen vorhanden, aber an unterschiedlicher Stelle — im Modell fällt das
 * nicht auf, aber die Leitung passt nicht mehr durch.
 */
export interface AussparungAbgleichDaten {
  /**
   * Die Grenzen, nach denen eingestuft wurde — F90, Migration 0822.
   *
   * Sie stehen in der Antwort und nicht im Code der Seite: Wer die Zahl
   * ändert, sieht die Stufen sofort verschoben. `ausDerDatenbank: false`
   * heißt, dass der Parameter fehlt und mit dem Rückfallwert gerechnet
   * wurde — das gehört auf die Seite.
   */
  grenzen: {
    grenzeMm: number;
    andererOrtMm: number;
    herkunft: string;
    begruendung: string | null;
    ausDerDatenbank: boolean;
    klartext: string;
  };

  /** Die Summen stehen über der GANZEN Menge, nicht über der Seite. */
  jeBefund: { befund: string; stueck: number }[];
  jeStufe: { stufe: string; stueck: number }[];
  jeHerkunft: { herkunft: string; stueck: number }[];
  jeEntscheidung: { entscheidung: string; stueck: number }[];
  jeGeschoss: { geschoss: string; stueck: number }[];

  paare: number;
  bestaetigt: number;
  entschieden: number;
  /** Paare an stillgelegten Aussparungen — NICHT im Abgleich (F87). */
  stillgelegt: number;

  /** Die gefilterte Menge. `zeilen.length` ist die SEITE davon. */
  gesamt: number;
  seite: number;
  groesse: number;
  seiten: number;
  hoechstens: number;
  hinweise: string[];

  /**
   * Die Rangfolge der Gewerke — ANGEZEIGT, nicht angewandt.
   *
   * `GewerkFuehrend` bleibt bei kombinierten Gewerken leer: Ob aus
   * „RLTAHZGA" ein Sammelkürzel oder ein führendes Gewerk wird, ist eine
   * Festlegung des Bauherrn und keine Ableitung.
   */
  gewerkrang: {
    kuerzel: string;
    modellkuerzel: string | null;
    bezeichnung: string | null;
    rang: number;
    begruendung: string | null;
  }[];

  zeilen: {
    id: string;
    befund: string;
    /** kennzeichen · lage · gepflegt — „lage" ist eine Vermutung. */
    herkunft: string;
    abstandMm: number | null;
    /** toleranz · befund · anderer_ort — null heißt: kein Abstand gemessen. */
    stufe: string | null;
    versatzX: number | null;
    versatzY: number | null;
    versatzZ: number | null;
    richtung: string;
    groessenabweichungMm: number | null;
    bestaetigt: boolean;
    bestaetigtVon: string | null;
    bemerkung: string | null;
    /** Welcher Stand gilt — null heißt offen. Ändert KEIN Modell (F61). */
    entscheidung: string | null;
    entschiedenVon: string | null;
    entschiedenAm: string | null;
    begruendung: string | null;
    geschoss: string;
    planungsbereich: string;
    conVoid: {
      kennzeichen: string | null;
      geschoss: string | null;
      planungsbereich: string | null;
      form: string | null;
      breiteMm: number | null;
      hoeheMm: number | null;
      durchmesserMm: number | null;
      lageX: number | null;
      lageY: number | null;
      lageZ: number | null;
      zustand: string | null;
      gewerkRoh: string | null;
      gewerke: string | null;
      gewerkFuehrend: string | null;
      anforderungRoh: string | null;
      freigegebenVon: string | null;
      abgelehntVon: string | null;
      offenBei: string | null;
      revitElementId: number;
    } | null;
    architektur: {
      kennzeichen: string | null;
      geschoss: string | null;
      form: string | null;
      breiteMm: number | null;
      hoeheMm: number | null;
      durchmesserMm: number | null;
      lageX: number | null;
      lageY: number | null;
      lageZ: number | null;
      zustand: string | null;
      /** Der Rohtext des Architekten — nicht ausgewertet. */
      anmerkung: string | null;
      anmerkungFeld: string | null;
      pruefvermerk: string | null;
      pruefer: string | null;
      verknuepfung: string | null;
      revitElementId: number;
    } | null;
  }[];
}

/**
 * Die Übersicht der Aussparungen.
 *
 * `sperren` sagt, was fehlt, damit eine Brandschottliste geführt werden DARF.
 * Bei 4.164 von 4.449 gefüllten Feldern steht als Anforderung „TBC?" — eine
 * Liste, die das als Anforderung ausgibt, sieht aus wie ein Nachweis.
 */
export interface AussparungsUebersicht {
  gesamt: number;
  mitHost: number;
  mitRaum: number;
  mitDurchgang: number;
  ohneAnforderung: number;
  kombiniert: number;
  /** Nur die AKTIVEN Abschottungen — bis AS2 zählte das Feld alle Zeilen. */
  brandschotts: number;
  /** Im Modell entfernt oder nicht mehr aufgenommen — stillgelegt (F87). */
  entfernt: number;
  /** Nummern an stillgelegten Zeilen. Sie sind vergeben und bleiben es. */
  nummernReserviert: number;
  jeGeschoss: {
    geschoss: string | null;
    stueck: number;
    rund: number;
    eckig: number;
    horizontal: number;
    vertikal: number;
    mitAnforderung: number;
    dichtungM3: number;
  }[];
  jeZustand: {
    zustand: string | null;
    zustandRoh: string | null;
    stueck: number;
  }[];
  jeGewerk: {
    gewerkRoh: string | null;
    gewerke: string | null;
    stueck: number;
  }[];
  jeBauart: { bauart: string | null; stueck: number }[];
  sperren: {
    kennung: string;
    gewicht: 'blocker' | 'warnung';
    titel: string;
    text: string;
  }[];
}

export interface Aussparung {
  id: string;
  revitElementId: number;
  kennzeichen: string | null;
  geschoss: string | null;
  ebene: string | null;
  form: string | null;
  breiteMm: number | null;
  hoeheMm: number | null;
  durchmesserMm: number | null;
  tiefeMm: number | null;
  orientierung: string | null;
  gewerkRoh: string | null;
  gewerke: string | null;
  /** Bleibt leer, solange das Gewerk kombiniert ist — das ist eine Festlegung. */
  gewerkFuehrend: string | null;
  zustand: string | null;
  zustandRoh: string | null;
  freigegebenVon: string | null;
  abgelehntVon: string | null;
  offenBei: string | null;
  anforderungRoh: string | null;
  /** null, solange in anforderungRoh „TBC?" steht. */
  anforderungGeprueft: string | null;
  dichtungsvolumenM3: number | null;
  lageX: number | null;
  lageY: number | null;
  lageZ: number | null;
  hosts: {
    hostElementId: number | null;
    kategorie: string | null;
    typ: string | null;
    bauart: string | null;
    tragendeSchicht: string | null;
    dickeMm: number | null;
    feuerwiderstand: string | null;
  }[];
  raeume: {
    seite: string;
    raumnummer: string | null;
    raumname: string | null;
    befund: string | null;
  }[];
  durchgaenge: {
    elementId: number | null;
    art: string | null;
    gewerk: string | null;
    typ: string | null;
    nennweite: number | null;
    werkstoff: string | null;
    istBrennbar: boolean | null;
    systemtyp: string | null;
  }[];
  /**
   * Wann sie aufgehört hat, ein aktiver Durchbruch zu sein (F87).
   *
   * `null` heißt aktiv. Steht ein Datum da, ist die Zeile stillgelegt —
   * gelöscht wird nichts, und ihre Katasternummer bleibt reserviert.
   */
  entferntAm: string | null;
  entferntGrund: string | null;
  /** Die Katasternummern an dieser Aussparung — auch die reservierten. */
  nummern: {
    nummer: string;
    gewerk: string | null;
    medium: string | null;
    anforderung: string | null;
    entferntAm: string | null;
    entferntGrund: string | null;
  }[];
}

/**
 * Die Übersicht der Außenanlagen.
 *
 * `sperren` sagt, was fehlt, damit gerechnet werden DARF — nicht, was fehlt,
 * damit eine Zahl entsteht. Eine Zahl entsteht immer, und genau das ist die
 * Gefahr: Ein Rückhaltevolumen ohne kf-Wert und Einzugsfläche ist nicht
 * bemessen, sondern gesetzt.
 */
export interface AussenUebersicht {
  leitungen: {
    kreis: string | null;
    systemtyp: string | null;
    stueck: number;
    rohre: number;
    laengeM: number;
    mitGefaelle: number;
    /** Leitungen mit negativem Gefälle — sie laufen bergauf. */
    bergauf: number;
  }[];
  leerrohre: {
    art: string;
    stueck: number;
    laengeM: number;
    mitNennweite: number;
    mitTrasse: number;
  }[];
  schaechte: {
    gewerk: string;
    bauart: string | null;
    stueck: number;
    mitSystem: number;
  }[];
  anschluesse: { richtung: string | null; stueck: number }[];
  versickerung: {
    bauwerke: number;
    volumenModellM3: number;
    mitFlaeche: number;
    nachgewiesen: number;
    flaechen: number;
    flaechenBestaetigt: number;
  };
  hydranten: number;
  gesamt: number;
  sperren: {
    kennung: string;
    gewicht: 'blocker' | 'warnung';
    titel: string;
    text: string;
  }[];
}

/**
 * Ein Berechnungslauf der Außenanlagen.
 *
 * `annahmen` trägt die Herkunft mit: „norm" heißt, der Wert steht in einer
 * Norm; „auftrag", der Bauherr hat ihn festgelegt; „angenommen", wir haben
 * ihn gesetzt. Nur das Letzte ist eine Annahme im eigentlichen Sinn — und
 * sie trägt die Unsicherheit des Ergebnisses.
 */
export interface AussenBerechnungslauf {
  id: string;
  bereich: string;
  norm: string | null;
  zeilen: number;
  beanstandet: number;
  gerechnetAm: string;
  gerechnetVon: string | null;
  /** TS9a: jüngste fertige Außenaufnahme (Gewerkstand-Regel), ohne Aufnahme null - wie im Kopf der Excel-Mappe. */
  modellstand?: string | null;
  /** TS9a: Lauf vor dem Modellstand (Server). */
  standVeraltet?: boolean;
  annahmen: AussenAnnahme[] | null;
  kennzahlen: Record<string, unknown> | null;
  hinweise: string[] | null;
  zeilen2: {
    id: string;
    gattung: string;
    ordnung: number;
    bezeichnung: string | null;
    kreis: string | null;
    bereich: string | null;
    revitElementId: number | null;
    kennwert: number | null;
    istMassgebend: boolean;
    bewertung: string | null;
    befund: string | null;
    werte: Record<string, unknown> | null;
    rechenweg: string[] | null;
    teilstrecken: Record<string, unknown>[] | null;
  }[];
}

/**
 * Eine Annahme, wie sie am Lauf abgelegt ist.
 *
 * `herkunft = 'festlegung'` heißt: Der Fachplaner hat auf eine benannte Frage
 * geantwortet. Dann trägt `quelle` die Fundstelle — „Fachplaner 16.09.2026,
 * Frage 49". Ohne sie wäre „bestätigt" eine Behauptung.
 */
export interface AussenAnnahme {
  schluessel: string;
  wert: number | null;
  /** Textwerte wie „SML" oder „bemessung" — sie haben keine Zahl. */
  text?: string | null;
  einheit: string | null;
  norm: string | null;
  herkunft: string;
  geprueft: boolean;
  quelle?: string | null;
  istBestaetigt?: boolean;
}

/** Eine Teilstrecke der Freispiegelentwässerung. */
export interface AussenTeilstrecke {
  abschnittNr: number;
  elementId: number;
  systemname: string | null;
  nennweite: number | null;
  innendurchmesserMm: number | null;
  laengeM: number;
  gefaellePromille: number | null;
  mindestgefaelleSoll: number | null;
  vollstromLs: number | null;
  abflussLs: number | null;
  fuellungsgrad: number | null;
  geschwindigkeitMs: number | null;
  vollstromGeschwindigkeitMs: number | null;
  vonZ: number | null;
  nachZ: number | null;
  bewertung: string;
  befund: string | null;
  /** Neigung über 45° — steht NICHT im mittleren Gefälle des Strangs. */
  istFallstueck: boolean;
}

/** Die Kopfzeile eines Strangs. */
export interface AussenStrangzeile {
  id: string;
  bezeichnung: string | null;
  kreis: string | null;
  kennwert: number | null;
  istMassgebend: boolean;
  bewertung: string | null;
  befund: string | null;
  werte: Record<string, unknown> | null;
}

export interface AussenNachweisStraenge {
  id: string;
  bereich: string;
  norm: string | null;
  gerechnetAm: string;
  kennzahlen: Record<string, unknown> | null;
  annahmen: AussenAnnahme[] | null;
  kreise: {
    kreis: string;
    straenge: number;
    beanstandet: number;
    ohneAbfluss: number;
    laengeM: number;
  }[];
  straenge: AussenStrangzeile[];
}

export interface AussenNachweisStrang {
  id: string;
  bezeichnung: string | null;
  kreis: string | null;
  gattung: string;
  kennwert: number | null;
  istMassgebend: boolean;
  bewertung: string | null;
  befund: string | null;
  bereich: string;
  norm: string | null;
  gerechnetAm: string;
  werte: Record<string, unknown> | null;
  teilstrecken: AussenTeilstrecke[] | null;
  rechenweg: string[] | null;
}

/** Ein Versickerungsbauwerk mit beiden Rechenwegen. */
export interface AussenNachweisBauwerk {
  id: string;
  bezeichnung: string | null;
  bereich: string | null;
  revitElementId: number | null;
  kennwert: number | null;
  istMassgebend: boolean;
  bewertung: string | null;
  befund: string | null;
  werte: Record<string, unknown> | null;
  flaechen: Record<string, unknown>[] | null;
  rechenweg: string[] | null;
}

export interface AussenNachweisVersickerung {
  id: string;
  bereich: string;
  norm: string | null;
  gerechnetAm: string;
  kennzahlen: Record<string, unknown> | null;
  annahmen: AussenAnnahme[] | null;
  hinweise: string[] | null;
  bauwerke: AussenNachweisBauwerk[];
}

/**
 * Der Stand EINES der sieben Außenberichte.
 *
 * `aktuell` ist ein VERGLEICH und keine Behauptung: Es sagt, ob der
 * Rechenstand des Registereintrags derselbe ist wie der des jüngsten Laufs.
 * Eine alte Dokumentnummer neben einem neuen Lauf ist sonst nicht als alt
 * zu erkennen.
 */
export interface AussenBerichtsstand {
  bereich: string;
  titel: string;
  kuerzel: string;
  hatLauf: boolean;
  norm: string | null;
  gerechnetAm: string | null;
  zeilen: number | null;
  beanstandet: number | null;
  dokumentnummer: string | null;
  berichtErzeugtAm: string | null;
  erzeugtVon: string | null;
  datenhash: string | null;
  freigabestatus: string | null;
  aktuell: boolean;
}

export interface AussenNachweisHydrant {
  id: string;
  gattung: string;
  bezeichnung: string | null;
  revitElementId: number | null;
  kennwert: number | null;
  istMassgebend: boolean;
  bewertung: string | null;
  befund: string | null;
  werte: Record<string, unknown> | null;
  rechenweg: string[] | null;
}

export interface AussenNachweisHydranten {
  id: string;
  norm: string | null;
  gerechnetAm: string;
  kennzahlen: Record<string, unknown> | null;
  annahmen: AussenAnnahme[] | null;
  hinweise: string[] | null;
  hydranten: AussenNachweisHydrant[];
  anlage: AussenNachweisHydrant | null;
}

export interface AussenNachweisBefunde {
  laeufe: {
    id: string;
    bereich: string;
    norm: string | null;
    zeilen: number;
    beanstandet: number;
    gerechnetAm: string;
    hinweise: string[] | null;
  }[];
  befunde: {
    id: string;
    gattung: string;
    bezeichnung: string | null;
    kreis: string | null;
    revitElementId: number | null;
    bewertung: string | null;
    befund: string | null;
    rechenweg: string[] | null;
    bereich: string;
  }[];
}

/** Ein Leerrohr des Außennetzes. */
export interface AussenLeerrohr {
  id: string;
  revitElementId: number | null;
  art: string | null;
  familie: string | null;
  typ: string | null;
  nennweite: number | null;
  innendurchmesserMm: number | null;
  laengeMm: number | null;
  /** Ohne Trasse hängt das Rohr im Netz nicht — das ist ein Befund. */
  trasseId: string | null;
  belegung: string | null;
  belegungsgradProzent: number | null;
  kabelAnzahl: number | null;
  kabelQuerschnittMm2: number | null;
  verlegetiefeMm: number | null;
  bereich: string | null;
}

/** Ein Hydrant des Löschwassernetzes. */
export interface AussenHydrant {
  id: string;
  revitElementId: number | null;
  kennung: string | null;
  bauart: string | null;
  familie: string | null;
  typ: string | null;
  nennweite: string | null;
  lageX: number | null;
  lageY: number | null;
  lageZ: number | null;
  bereich: string | null;
  netzdruckBar: number | null;
  abgabeLMin: number | null;
  abgabeHerkunft: string | null;
  fliessdruckBar: number | null;
  befund: string | null;
}

/**
 * Ein Übergang zwischen Gebäude- und Außennetz.
 *
 * `zustand` unterscheidet „verbunden" von „vorschlag": Ein Vorschlag steht
 * in der Liste und wirkt NICHT im Netz. Beim Trinkwasser ist das Absicht —
 * der Hausanschluss ist EINE Stelle, und die Suche findet drei Kandidaten.
 */
export interface AussenUebergang {
  id: string;
  innenElementId: number | null;
  kreisInnen: string | null;
  aussenElementId: number | null;
  kreisAussen: string | null;
  abstandMm: number;
  hoehenversatzMm: number | null;
  zustand: string;
  bestaetigt: boolean;
  befund: string | null;
}

export interface AussenLeitung {
  id: string;
  revitElementId: number;
  art: string;
  familie: string | null;
  typ: string | null;
  systemname: string | null;
  systemtyp: string | null;
  kreis: string | null;
  nennweite: number | null;
  innendurchmesser: number | null;
  aussendurchmesser: number | null;
  laengeMm: number | null;
  /** In Promille, mit VORZEICHEN — negativ heißt bergauf. */
  gefaelle: number | null;
  mindestgefaelleSoll: number | null;
  mindestgefaelleOk: boolean | null;
  verlegetiefeMm: number | null;
  frostfreiOk: boolean | null;
  vonZ: number | null;
  nachZ: number | null;
  bereich: string | null;

  /**
   * Die HERKUNFT der drei abgeleiteten Größen — `modell`, `abgeleitet`,
   * `typname`, `regel`, `nachbarschaft`, `geometrie` …
   *
   * Sie lag seit der ersten Fassung an der Zeile und wurde nie
   * ausgeliefert. Eine abgeleitete Nennweite ist etwas anderes als eine
   * modellierte; die Spalte DN zeigt beide gleich, der Tooltip trennt sie.
   */
  kreisHerkunft: string | null;
  nennweiteHerkunft: string | null;
  gefaelleHerkunft: string | null;
}

/**
 * Ein Schacht mit ALLEN Anschlüssen.
 *
 * Der Kern des Datenmodells: Im Modell steht an ihm „SP 36,SP 35". Er ist ein
 * Knoten, an dem Teilströme zusammenkommen — eine 1:1-Zuordnung Schacht zu
 * System wäre falsch.
 */
export interface AussenSchacht {
  id: string;
  revitElementId: number;
  gewerk: string;
  bauart: string | null;
  familie: string | null;
  typ: string | null;
  kennung: string | null;
  nennweite: number | null;
  bauhoeheMm: number | null;
  /** Der rohe Text aus dem Modell, unaufgelöst. */
  systemnameRoh: string | null;
  lageX: number | null;
  lageY: number | null;
  lageZ: number | null;
  bereich: string | null;
  anschluesse: {
    system: string;
    /** null heißt: keine Leitung zu diesem System gefunden — ein BEFUND. */
    leitungId: string | null;
    leitungElementId: number | null;
    richtung: string | null;
    nennweite: number | null;
    sohleMm: number | null;
    herkunft: string;
    bestaetigt: boolean;
  }[];
}

/** Die Rückhaltung einer Einleitstelle nach DWA-A 117 (`aussen_rueckhaltungen`). */
export interface AussenRueckhaltungszeile {
  id: string;
  einleitstelle: string;
  norm: string | null;
  vorhandenM3: number | null;
  vorhandenHerkunft: string | null;
  /** Bleibt null, solange die untere Wasserbehörde nicht entschieden hat (F84, AUA-02). */
  drosselabflussLs: number | null;
  drosselHerkunft: string | null;
  obergrenzeFlaecheM2: number | null;
  flaecheHerkunft: string | null;
  wiederkehrzeitJahre: number | null;
  zuschlagsfaktorFz: number | null;
  erforderlichOhneDrosselM3: number | null;
  erforderlichM3: number | null;
  notwendigerDrosselLs: number | null;
  tragfaehigeFlaecheM2: number | null;
  massgebendeDauerMin: number | null;
  regenspendeLsHa: number | null;
  deckungProzent: number | null;
  bewertung: string | null;
  befund: string | null;
  gerechnetAm: string | null;
}

export interface AussenVersickerungsbauwerk {
  id: string;
  revitElementId: number | null;
  bauart: string;
  familie: string | null;
  typ: string | null;
  bezeichnung: string | null;
  volumenModellM3: number | null;
  erforderlichM3: number | null;
  versickerungsflaecheM2: number | null;
  kfWert: number | null;
  kfHerkunft: string | null;
  reduzierteFlaecheM2: number | null;
  regendauerMin: number | null;
  regenspendeLsHa: number | null;
  wiederkehrzeitJahre: number | null;
  drosselabflussLs: number | null;
  entleerungszeitH: number | null;
  auslastungProzent: number | null;
  nachweisstand: string;
  norm: string | null;
  befund: string | null;
  bereich: string | null;
  einzugsflaechen: {
    id: string;
    bezeichnung: string;
    flaecheM2: number | null;
    anteilProzent: number;
    abflussbeiwert: number | null;
    art: string | null;
    herkunftFlaeche: string;
    bestaetigt: boolean;
    bemerkung: string | null;
    /** A · Anteil · ψ — der Beitrag DIESER Teilfläche. */
    beitragM2: number | null;
  }[];
  /** Σ(A · Anteil · ψ). null, wenn keine Teilfläche eine Zahl trägt. */
  auM2: number | null;
  flaechenBestaetigt: boolean;
}

export interface SanitaerGeraet {
  id: string;
  revitElementId: number;
  familie: string | null;
  typ: string | null;
  raumnummer: string | null;
  raumname: string | null;
  geschoss: string | null;
  kreis: string | null;
  hersteller: string | null;
  produktreihe: string | null;
  geraeteart: string | null;
  produktHerkunft: string | null;
  werte: number;
  /** Wie viele gepflegte Werte vom Modellstand abweichen. */
  abweichend: number;
  verweis: string | null;

  /** Die Adresse — oder `null` (Paket S8). */
  aksSchluessel: string | null;
  /** `vergeben` · `fehlt` · `bekommt_keine` · `ueber_dem_umfang` · `nicht_entschieden` */
  aksEinstufung: string;
  /** Der Grund im Klartext, aus `msr_aks_umfang`. Nie leer. */
  aksGrund: string;
}

/**
 * Eine Druckerhöhungsanlage.
 *
 * Sie steht in keiner Bauteiltabelle: Aus geodätischer Höhe,
 * Mindestfließdruck und Reibung folgt die Förderhöhe, aus dem
 * Spitzendurchfluss die Baugröße. `herkunft` ist deshalb immer `gerechnet` —
 * und das ist eine Aussage, keine Verlegenheit: Solange der Fachplaner keine
 * Anlage liefert, ist die Rechnung die beste vorhandene Angabe.
 */
/** Eine Vorgabe aus `sanitaer_annahmen` (Projekt vor global), z. B. die Grenze `ruhedruck_max_bar`. */
export interface SanitaerAnnahmeGrenze {
  wert: number | null;
  einheit: string | null;
  herkunft: string;
  norm: string | null;
  geprueft: boolean;
  begruendung: string | null;
}

/** Ein Druckminderervorschlag je Zone (`sanitaer_druckminderer_vorschlaege`). */
export interface SanitaerDruckmindererVorschlag {
  id: string;
  berechnungId: string | null;
  zone: string;
  zonenbezeichnung: string | null;
  einbauort: string;
  einbauortZMm: number | null;
  eingangsdruckBar: number | null;
  ausgangsdruckBar: number;
  ruhedruckMaxBar: number;
  nennweite: number | null;
  tiefsteEntnahmestelleZMm: number | null;
  entnahmestellenUeberGrenze: number;
  entnahmestellenGesamt: number;
  hoechsterRuhedruckBar: number | null;
  norm: string | null;
  herkunft: string;
  geprueft: boolean;
  begruendung: string | null;
  berechnetAm: string | null;
}

export interface SanitaerDruckminderer {
  grenze: SanitaerAnnahmeGrenze | null;
  vorschlaege: SanitaerDruckmindererVorschlag[];
  entnahmestellenUeberGrenze: number;
  entnahmestellen: number;
}

/** Eine Zeile des Katalogs Gegenstandsart → Flüssigkeitskategorie → Sicherungsart. */
export interface SanitaerSicherungseinrichtung {
  id: string;
  projectId: string | null;
  art: string;
  bezeichnung: string;
  fluessigkeitskategorie: number | null;
  sicherungsart: string | null;
  sicherungsartBezeichnung: string | null;
  alternativen: string | null;
  norm: string | null;
  tabellenzitat: string | null;
  herkunft: string;
  geprueft: boolean;
  bemerkung: string | null;
}

/** Die Absicherung eines Gegenstands (`sanitaer_absicherungen`). */
export interface SanitaerAbsicherungszeile {
  id: string;
  berechnungId: string | null;
  gegenstandId: string | null;
  revitElementId: number | null;
  art: string | null;
  bezeichnung: string | null;
  raumnummer: string | null;
  geschoss: string | null;
  fluessigkeitskategorie: number | null;
  sicherungsartSoll: string | null;
  sicherungsartBezeichnung: string | null;
  sicherungsartIst: string | null;
  vorhanden: boolean | null;
  kreis: string | null;
  stichleitungRohre: number | null;
  stichleitungLaengeM: number | null;
  stichleitungInhaltL: number | null;
  stichleitungUeberGrenze: boolean | null;
  bewertung: string;
  befund: string | null;
  norm: string | null;
  herkunft: string;
  berechnetAm: string | null;
}

export interface SanitaerAbsicherungsliste {
  katalog: SanitaerSicherungseinrichtung[];
  gegenstaende: number;
  erforderlich: number;
  nichtAnwendbar: number;
  ungeklaert: number;
  ueberDreiLiter: number;
  inhaltGesamtL: number;
  jeSicherungsart: { sicherungsart: string; zahl: number }[];
  jeKategorie: { kategorie: number; zahl: number }[];
  zeilen: SanitaerAbsicherungszeile[];
}

export interface SanitaerDruckerhoehung {
  kennung: string;
  zone: string | null;
  bezeichnung: string;
  hersteller: string | null;
  produktreihe: string | null;
  baureihe: string | null;
  typ: string | null;
  pumpen: number | null;
  betriebspumpen: number | null;
  spitzeLs: number | null;
  foerderstromM3h: number | null;
  foerderstromJePumpeM3h: number | null;
  /** Der FEHLBETRAG — was der Versorgungsdruck nicht trägt. */
  foerderhoeheM: number | null;
  erforderlichM: number | null;
  versorgungsdruckM: number | null;
  geodaetischM: number | null;
  mindestfliessdruckM: number | null;
  reibungM: number | null;
  armaturenM: number | null;
  zapfstellen: number | null;
  hoechsteZapfstelleZ: number | null;
  eintrittZ: number | null;
  befund: string | null;
  herkunft: string;
  rechenweg: string[];
}

/**
 * Ein Feld der Produktseite.
 *
 * `farbe` kommt vom Server und wird hier NICHT noch einmal abgeleitet: Sie
 * entsteht seit PB53 in `Rueckschreibweg.Farbe`, und eine zweite Ableitung
 * wäre die Stelle, an der Farbe und Wahrheit auseinanderlaufen.
 *
 * Bis PB53 hieß es hier „folgt aus RevitParameter und IstGerechnet". Genau
 * das ist die Regel, die neunzehn Stellen in neunzehn Fassungen trugen —
 * ein Parameter sagt, ob ein Feld ins Modell zurück KÖNNTE, nicht ob es
 * SOLL. Wer diesen Satz abschreibt, baut die zwanzigste Fassung.
 *
 *   gruen  geht ins Modell zurück (Festlegung UND Möglichkeit)
 *   gelb   nur in der Datenbank
 *   blau   von der Plattform gerechnet
 *   grau   als falsch erkannt und entwertet
 */
export interface SanitaerProduktfeld {
  feldschluessel: string;
  bezeichnung: string;
  einheit: string | null;
  art: string;
  auswahl: string | null;
  wert: string | null;
  zahlwert: number | null;
  herkunft: string | null;
  farbe: 'gruen' | 'gelb' | 'blau' | 'grau';
  istGerechnet: boolean;
  istSchreibbar: boolean;
  revitParameter: string | null;
  imModell: string | null;
  abweichend: boolean;
  hinweis: string | null;
}

export interface SanitaerGeraeteseite {
  id: string;
  projectId: string;
  revitElementId: number;
  familie: string | null;
  typ: string | null;
  raumnummer: string | null;
  raumname: string | null;
  geschoss: string | null;
  kreis: string | null;
  systemname: string | null;
  nennweite: number | null;
  werkstoff: string | null;
  produkt: {
    id: string;
    bezeichnung: string;
    hersteller: string;
    produktreihe: string;
    geraeteart: string;
    herkunftsvermerk: string;
    quelle: string | null;
    abgerufenAm: string | null;
    stand: string | null;
    geprueft: boolean;
    hinweis: string | null;
    groesse: string | null;
    artikelnummer: string | null;
    kennwert: number | null;
  } | null;
  gruppen: { gruppe: string; felder: SanitaerProduktfeld[] }[];
  produktHerkunft: string | null;
  verweis: string | null;
}

export interface ElektroGeraet {
  id: string;
  revitElementId: number | null;
  bauteilart: string;
  familie: string | null;
  typ: string | null;
  bezeichnung: string | null;
  kennzeichen: string | null;
  hersteller: string | null;
  modell: string | null;
  raumnummer: string | null;
  raumname: string | null;
  geschoss: string | null;
  geschossgruppe: string | null;
  datenstand: string | null;
  werte: number;
  abweichend: number;
  verweis: string | null;
}

export interface ElektroGruppe {
  bauteilart: string;
  bezeichnung: string;
  bauteile: number;
  mitFabrikat: number;
  raeume: number;
  geschosse: number;
}

export interface ElektroBauteilseite {
  gesamt: number;
  seite: number;
  proSeite: number;
  seiten: number;
  bauteile: ElektroGeraet[];
}

export interface SanitaerGepflegterWert {
  feldschluessel: string;
  wert: string | null;
  zahlwert: number | null;
  herkunft: string;
  wertImModell: string | null;
  abweichend: boolean;
}

export interface Heizungseite {
  gesamt: number;
  seite: number;
  proSeite: number;
  /** Schlüssel der Bauteilart zu ihrer Bezeichnung. */
  arten: Record<string, string>;
  bauteile: {
    id: string;
    bauteilart: string;
    familie: string | null;
    typ: string | null;
    bezeichnung: string | null;
    geschoss: string | null;
    /** Die Etage — G01_UKRD und G01_OKRB fallen unter G01. */
    geschossgruppe?: string | null;
    raumnummer: string | null;
    raumname: string | null;
    /** modell | lage | hand */
    raumHerkunft: string | null;
    systemname: string | null;
    systemabkuerzung: string | null;
    kreis: string | null;
    hersteller: string | null;
    datenblattUrl?: string | null;
    herstellerUrl?: string | null;
    /** offen | zugewiesen | pruefen */
    datenstand?: string;
    datenstandGrund?: string | null;
    modell: string | null;
    kostengruppe: string | null;
    datenblattId: string | null;
    herkunft: string;
    gelesenAm: string | null;
    entferntAm: string | null;
  }[];
}

/** Ein Bauteil mit allen Werten. */
export interface Heizungbauteil {
  id: string;
  bauteilart: string;
  familie: string | null;
  typ: string | null;
  bezeichnung: string | null;
  uniqueId: string | null;
  ifcGuid: string | null;
  geschoss: string | null;
  raumnummer: string | null;
  raumname: string | null;
  raumHerkunft: string | null;
  lageX: number | null;
  lageY: number | null;
  lageZ: number | null;
  systemname: string | null;
  systemabkuerzung: string | null;
  hersteller: string | null;
  modell: string | null;
  datenblattId: string | null;
  gelesenAm: string | null;
  entferntAm: string | null;
  werte: {
    feldschluessel: string;
    bezeichnung: string;
    gruppe: string | null;
    einheit: string | null;
    art: string;
    auswahl: string | null;
    hinweis: string | null;
    istSchreibbar: boolean;
    /**
     * Geht dieses Feld ins Modell zurück? Die FERTIGE Antwort aus dem
     * Rechenkern — nicht `revitParameter !== null`. Zum Färben
     * `farbklasse(w.gehtInsModell)` aus `Farbregel.tsx`.
     */
    gehtInsModell: boolean;
    /** Der NAME des Zielparameters — für den Hinweis unter dem Feld. */
    revitParameter: string | null;
    wert: string | null;
    zahlwert: number | null;
    herkunft: string | null;
    wertImModell: string | null;
    weichtAb: boolean;
  }[];
}

export interface Heizungnetz {
  id: string;
  systemname: string;
  abkuerzung: string | null;
  klassifizierung: string | null;
  kreis: string | null;
  istVorlauf: boolean | null;
  elemente: number | null;
  volumen: number | null;
  medium: string | null;
  mediumTemperatur: number | null;
  gelesenAm: string | null;
}

export interface LueftungsbezugErgebnis {
  anschlusspunkte: number;
  zugeordnet: number;
  anlagen: number;
  mitFunktionseinheit: number;
  mitBetriebspunkt: number;
  nichtAmNetz: number;
  anschlusswerte: number;
  probe: boolean;
  hinweise: string[];
  zuordnungen: {
    bauteilId: string;
    revitElementId: number | null;
    plantId: string;
    anlage: string;
    kennzeichnung: string;
    standort: string;
    geraetetitel: string;
    gruppe: string | null;
    modus: string;
    abstandMm: number;
    guete: string;
    plantUnitId: string | null;
    betriebspunktId: string | null;
    amNetz: boolean;
    hinweis: string | null;
    leistungW: number | null;
    massenstromKgH: number | null;
    werte: {
      feldschluessel: string;
      zahlwert: number;
      herkunft: string;
    }[];
  }[];
  offen: { bauteilId: string; revitElementId: number | null; grund: string }[];
}

/** Ein Exportprofil mit seinen Fassungen. */
export interface Profil {
  id: string;
  name: string;
  description: string | null;
  versions: {
    id: string;
    version: string;
    revitVersion: number;
    releasedAt: string | null;
    isReleased: boolean;
    createdAt: string;
    createdBy: string | null;
  }[];
}

/** Eine Fassung mit allen Einstellungen. */
export interface Profilfassung {
  id: string;
  version: string;
  revitVersion: number;
  isReleased: boolean;
  settingsHash: string;
  /** Die vollständige Definition — Aufbau siehe ProfileDefinition.cs. */
  settings: Record<string, unknown>;
}

/** Ein Modell in der Planübersicht. */
export interface Planmodellzeile {
  id: string;
  name: string;
  discipline: string | null;
  modellschluessel: string | null;
  schluesselAm: string | null;
  aufAcc: boolean;
  plaene: number;
  inListe: number;
  plaeneKonflikt: number;
  revisionen: number;
  revisionenKonflikt: number;
  letzterIndex: string | null;
  letzteFolge: number | null;
  letztesDatum: string | null;
  offeneUebertragung: number;
  /** Stände, die noch Entwurf sind — sie werden nicht übertragen. */
  entwuerfe: number;
  uploads: number;
  letzterUpload: string | null;
  letztePhase: string | null;
  jeStatus: { status: string; anzahl: number }[];
}

/** Ein Plan eines Modells. */
export interface Planblatt {
  id: string;
  plannummer: string;
  planname: string | null;
  teilprojekt: string | null;
  ersteller: string | null;
  gattung: string | null;
  geschoss: string | null;
  phase: string | null;
  gewerk: string | null;
  laufnummer: string | null;
  /** P = zur Prüfung | V = Vorabzug | F = Freigegeben */
  freigabestatus: string | null;
  bauwerksteil: string | null;
  bereich: string | null;
  massstab: string | null;
  plandatum: string | null;
  bearbeiter: string | null;
  inListe: boolean;
  /** Ob der Plan in die Übergabeliste der Plattform gehört. */
  inPlanliste: boolean;
  /**
   * Der Index, den das Blatt trägt.
   *
   * Der Wert am Blatt hat Vorrang. Fehlt er, gilt der Stand des Plansatzes — der
   * Index gehört dem Satz, nicht dem einzelnen Blatt.
   */
  aktuellerIndex: string | null;
  /** Ob der Index aus dem Plansatz kommt und nicht vom Blatt. */
  indexAusSatz: boolean;
  titelblock: string | null;
  herkunft: string;
  uebertragenAm: string | null;
  hatKonflikt: boolean;
  konfliktHinweis: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Ein Indexstand. */
export interface Planrevision {
  id: string;
  folge: number;
  /** Aus dem Modell gelesen — Revit leitet ihn ab, er ist nur lesbar. */
  index: string | null;
  datum: string | null;
  beschreibung: string | null;
  ersteller: string | null;
  ausgegeben: boolean;
  plaeneAnzahl: number;
  herkunft: string;
  uebertragenAm: string | null;
  hatKonflikt: boolean;
  konfliktHinweis: string | null;
  /**
   * Fixiert — der Stand ist verbindlich.
   *
   * Bis dahin ein Entwurf: änderbar, löschbar, und er wird nicht ins Modell
   * geschrieben.
   */
  istFixiert: boolean;
  fixiertAm: string | null;
  fixiertVon: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Ein Modellupload — die Dokumentation der Übergaben. */
export interface Modellupload {
  id: string;
  folge: number;
  datum: string;
  leistungsphase: string | null;
  /** acc | conclude | sonstige */
  ziel: string | null;
  zielHinweis: string | null;
  bemerkung: string | null;
  ersteller: string | null;
  istFixiert: boolean;
  fixiertAm: string | null;
  fixiertVon: string | null;
  createdAt: string;
  createdBy: string | null;
}

/** Eine Lüftungsanlage in der Übersicht, mit ihren Unterlagen. */
export interface Anlagenzeile {
  id: string;
  anlage: string;
  ebene: string | null;
  aksSchluessel: string | null;
  aksGeschoss: string | null;
  raumnummer: string | null;
  bauteile: number;
  einheiten: number;
  datasheetId: string | null;
  hersteller: string | null;
  geraetebezeichnung: string | null;
  angebotsnummer: string | null;
  datenblattDatum: string | null;
  datenversion: string | null;
  importiertAm: string | null;
  hatDatenblatt: boolean;
  hatSkizze: boolean;
  dateiname: string | null;
  skizzeDateiname: string | null;
  fassungen: number;
}

/** Ein Quelldokument mit seiner Prüfsumme. */
export interface Anlagenquelle {
  rolle: string;
  dateiname: string;
  sha256: string | null;
  seiten: number | null;
}

/** Eine Anlage, die zu dem Gerät passen könnte. */
export interface Anlagenvorschlag {
  id: string;
  projectId: string;
  anlage: string;
  ebene: string | null;
  aksSchluessel: string | null;
  bauteile: number;
  einheiten: number;
  hatDatenblatt: boolean;
  /** Wie viele Wortbestandteile der Gerätebezeichnung im Anlagennamen stehen. */
  punkte: number;
}

export interface Anlagenpruefung {
  gueltig: boolean;
  bereits?: boolean;
  vorhandeneRevision?: {
    id: string; datasheetId: string; dataVersion: string; createdAt: string;
  } | null;
  dataVersion?: string;
  folge?: number;
  aenderungsart?: string;
  contentSha256?: string;
  quellRevision?: string;
  hersteller?: string;
  baureihe?: string | null;
  geraetebezeichnung?: string;
  angebotsnummer?: string | null;
  angebotsdatum?: string | null;
  projekt?: string | null;
  /** air_handling_unit | hydraulic_heat_recovery_station */
  produktklasse?: string | null;
  herstellerWebseite?: string | null;
  nennkapazitaet?: number | null;
  luftstroeme?: number;
  einheiten?: number;
  warnungen?: string[];
  pruefzustand?: string | null;
  quellen?: Anlagenquelle[];
  hinweise?: string[];
  anlagenvorschlaege?: Anlagenvorschlag[];
  fehler?: string[];
  message?: string;
}

/** Die Vorschau eines Anlagenimports. */
export interface Anlagenvorschau {
  anlage: string;
  geraetebezeichnung: string;
  dataVersion: string;
  angebotsnummer: string | null;
  /** Gesetzt, wenn dieselbe Version mit anderem Inhalt vorliegt. */
  konflikt: string | null;
  istGeraet: boolean;
  werte: {
    fieldKey: string;
    label: string;
    neu: string;
    bisher: string | null;
    /** neu | unveraendert | konflikt */
    zustand: string;
    revitParameter: string | null;
  }[];
  offen: { fieldKey: string; label?: string; grund: string }[];
  einheiten: {
    luftstrom: string; reihenfolge: number; art: string; bezeichnung: string;
    druckverlustPa: number | null; componentKey: string | null;
    werte: number; ohneFeld: number;
    /**
     * Die Betriebspunkte des Registers.
     *
     * Ein Change-Over hat zwei — Kühlen und Heizen. Sie stehen als eigene Zeilen
     * in `plant_unit_operating_points`, nicht als Felder mit Nummer.
     */
    punkte: {
      reihenfolge: number;
      modusDe: string | null;
      stimmig: boolean | null;
      werte: number;
      leistung: string | null;
      vorlauf: string | null;
      ruecklauf: string | null;
    }[];
  }[];
  betriebspunkte: number;
  druck: { luftstrom: string; internPa: number; ventilatorPa: number }[];
  unbekannteArten: string[];
  merkmaleOhneFeld: number;
  /**
   * Rechnerische Unstimmigkeiten des Herstellers.
   *
   * Bei drei Kühlern nennt robatherm eine Gesamtleistung, die nicht der Summe aus
   * sensibler und latenter entspricht — 700 kW gegen 271 + 271. Nicht korrigiert:
   * Wer eine Herstellerangabe zurechtrechnet, hat danach eine eigene Angabe.
   */
  unstimmigkeiten: {
    einheit: string | null;
    betriebsart: string | null;
    gesamt: string | null;
    summe: string | null;
    sensibel: string | null;
    latent: string | null;
  }[];
  bisherigeFassungen: {
    id: string; dataVersion: string; versionMajor: number; versionMinor: number;
    contentSha256: string; angebotsnummer: string | null; createdAt: string;
  }[];
}

export interface Anlagenfassungen {
  datasheetId?: string;
  aktuell: string | null;
  aktuelleDatenversion?: string | null;
  geraetebezeichnung?: string | null;
  fassungen: {
    id: string; dataVersion: string; folge: number; contentSha256: string;
    quellRevision: string; angebotsnummer: string | null;
    angebotsdatum: string | null; aenderungsart: string;
    createdAt: string; createdBy: string | null;
    einheiten: number; istAktuell: boolean;
  }[];
}

// --- Datenblattimporte -----------------------------------------------------

/** Ein eingelesenes Herstellerdokument. */
export interface Datenblattimport {
  id: string;
  hersteller: string;
  serie: string;
  produktart: string | null;
  dateiname: string;
  quellDateiname: string | null;
  quellSeiten: number | null;
  dokumentDatum: string | null;
  anzahlGroessen: number;
  anzahlVarianten: number;
  anzahlBetriebspunkte: number;
  anzahlWarnungen: number;
  hochgeladenAm: string;
  hochgeladenVon: string | null;
  /** Wie viele Datenblätter dieses Dokument verwenden. */
  verwendet: number;
}

export interface Importpruefung {
  gueltig: boolean;
  bereits?: boolean;
  id?: string;
  hersteller?: string;
  serie?: string;
  produktart?: string | null;
  anzahlGroessen?: number;
  anzahlVarianten?: number;
  anzahlBetriebspunkte?: number;
  fehler?: string[];
  hinweise?: string[];
  /** Warnungen des Erzeugers — sie gehören gelesen, nicht überblättert. */
  warnungen?: string[];
  zielfelder?: string[];
  nichtAbbildbar?: string[];
  /** Zielfelder, die es im Katalog nicht gibt. */
  unbekannteFelder?: string[];
  message?: string;
}

export interface Importgroesse {
  sizeKey: string | null;
  variantKey: string | null;
  nennGroesse: number | null;
  breite: number | null;
  hoehe: number | null;
  gehaeuselaenge: number | null;
  bezeichnung: string;
}

export interface Importtabelle {
  tableKey: string;
  art: string;
  anzahl: number;
  aussage: string | null;
}

export interface Importdetail extends Datenblattimport {
  warnungen: string[];
  groessen: Importgroesse[];
  varianten: { variantKey: string; bezeichnung: string }[];
  tabellen: Importtabelle[];
  zuordnungen: {
    direct: { fieldKey: string }[];
    conditional: { fieldKey: string; selection?: string; allowedValues?: unknown[] }[];
    notMapped: { quantity: string; reason: string }[];
  };
}

export interface Importpunkt {
  index: number;
  sizeKey: string | null;
  bezeichnung: string;
  bedingungen: string[];
  /** Schlüssel innerhalb des Punkts, etwa Ventilstellung 1–4. */
  konfigurationen: string[];
}

/** Die getroffene Auswahl. Nur Merkmale, keine Werte. */
export interface Importauswahl {
  sizeKey?: string | null;
  variantKey?: string | null;
  widthMm?: number | null;
  heightMm?: number | null;
  housingLengthMm?: number | null;
  tabelle?: string | null;
  punktIndex?: number | null;
  konfiguration?: string | null;
  eigeneWahl?: Record<string, string>;
}

/** Ein Bauteiltyp des Projekts, zur Auswahl statt zum Eintippen. */
export interface Importtyp {
  familie: string;
  typ: string;
  bauteile: number;
  /** Wie viele davon eine Luftmenge tragen — ohne sie kein Abgleich. */
  mitLuftmenge: number;
}

/**
 * Eine Zeile der Zuordnungsliste auf der JSON-Seite.
 *
 * Je Bauteiltyp: ob Herstellerdaten zugewiesen sind, aus welchem Dokument, wann
 * — und was seither passiert ist.
 */
export interface Zuordnungszeile {
  familie: string;
  typ: string;
  bauteile: number;
  mitLuftmenge: number;
  zugewiesen: number;
  interpoliert: number;
  mitBefund: number;
  mengeGeaendert: number;
  mengeVon: number | null;
  mengeBis: number | null;
  /** fehler | veraltet | teilweise | offen | zugewiesen */
  zustand: string;
  zugewiesenAm: string | null;
  zugewiesenVon: string | null;
  datenblattId: string | null;
  importId: string | null;
  importHersteller: string | null;
  importSerie: string | null;
  importDatei: string | null;
  /** Die getroffene Auswahl als JSON — damit „Aktualisieren" sie vorbelegen kann. */
  auswahl: string | null;
  /** Das Datenblatt kam über den PDF-Weg, nicht über JSON. */
  ausPdf: boolean;
}

export interface Zuordnungsliste {
  zeilen: Zuordnungszeile[];
  bauteile: number;
  zugewiesen: number;
  jeZustand: { zustand: string; typen: number; bauteile: number }[];
}

/** Der Abgleich der Bauteile über ihre Luftmenge. */
export interface Importabgleich {
  hersteller: string;
  serie: string;
  familie: string;
  typ: string;
  bauteile: number;
  zeilen: number;
  mengen: number[];
  quelle: string;
  felder: string[];
  genau: number;
  /** Zwischen zwei gedruckten Zeilen berechnet. */
  interpoliert: number;
  ausserhalb: number;
  ohneMenge: number;
  geschrieben: number;
  treffer: {
    id: string;
    name: string;
    luftmenge: number | null;
    /** genau | interpoliert | ausserhalb | ohne_menge */
    art: string;
    hinweis: string;
    werte: { fieldKey: string; wert: string }[];
  }[];
  hinweis: string;
}

/** Was sich seit dem letzten Datenblattabgleich geändert hat. */
export interface Abgleichstand {
  jeZustand: { zustand: string; anzahl: number }[];
  jeTyp: { zustand: string; familie: string | null; typ: string | null; anzahl: number }[];
  nachzufuehren: number;
  zugewiesen: number;
}

/** Ein Bauteil mit Befund aus dem Datenblattabgleich. */
export interface Bauteilbefund {
  gesamt: number;
  offen: number;
  jeTyp: {
    familie: string; typ: string; anzahl: number;
    von: number | null; bis: number | null; hinweis: string;
  }[];
  bauteile: {
    id: string; familie: string | null; typ: string | null;
    groesse: string | null; volumenstrom: number | null;
    raumnummer: string | null; raumname: string | null;
    ebene: string | null; aksSchluessel: string | null;
    fehlerHinweis: string | null; fehlerZustand: string | null;
    fehlerAm: string | null; fehlerVon: string | null;
    elementGuid: string;
  }[];
}

export interface Importvorschau {
  hersteller: string;
  serie: string;
  familie: string;
  typ: string;
  bauteile: number;
  datenblattVorhanden: boolean;
  bisherigeQuelle: string | null;
  werte: {
    fieldKey: string;
    neu: string;
    bisher: string | null;
    /** neu | unveraendert | konflikt */
    zustand: string;
    quelle: string;
    bedingung: string;
  }[];
  offen: { fieldKey: string; grund: string }[];
  neu: number;
  konflikte: number;
  unveraendert: number;
}

// --- Datenblätter ----------------------------------------------------------

/**
 * Was nach dem Hochladen zurückkommt.
 *
 * Der Verweis ist der gleichbleibende, signierte Link — nicht die Adresse des
 * Objektspeichers. Die läuft nach Minuten ab, und dieser Verweis steht später im
 * Revit-Modell und in der IFC-Ausgabe.
 */
export interface Datenblattablage {
  blatt: Datenblatt;
  verweis: string | null;
  /** Wie viele Bauteile den Verweis bekommen haben. */
  bauteile: number;
  message: string;
}

export interface Datenblatt {
  id: string;
  plantId: string | null;
  /** bauteil | anlage */
  bereich: string;
  geraetebezeichnung: string | null;
  angebotsnummer: string | null;
  datenblattDatum: string | null;
  familie: string;
  typ: string;
  hersteller: string | null;
  produktreihe: string | null;
  herstellerUrl: string | null;
  dateiname: string | null;
  dateiGroesse: number | null;
  /** offen | wartet | laeuft | gefunden | nicht_gefunden | geprueft */
  zustand: string;
  zustandHinweis: string | null;
  versuche: number;
  wartetSeit: string | null;
  gesuchtAm: string | null;
  geprueftAm: string | null;
  geprueftVon: string | null;
  hatDatei: boolean;
  hatVorschlaege: boolean;
  /** Wie viele Bauteile dieses Typs im Projekt stehen. */
  bauteile: number;
  /** Die Vorschläge als JSON. Leer, solange keine Auswertung lief. */
  vorschlaegeJson: string | null;
  zugewiesen: number;
  interpoliert: number;
  mitBefund: number;
  mengeGeaendert: number;
  // Die Geräteskizze — ein zweites Dokument, das anderes zeigt: Abmessungen und
  // Anordnung statt der Auslegung.
  skizzeDateiname: string | null;
  skizzeGroesse: number | null;
  hatSkizze: boolean;
  aktuelleDatenversion: string | null;
  aktuelleRevisionId: string | null;
  importiertAm: string | null;
  importiertVon: string | null;
  fassungen: number;
}

export interface DatenblattStand {
  blaetter: Datenblatt[];
  stand: { zustand: string; anzahl: number }[];
  /** Typen aus dem Bestand, zu denen noch kein Datensatz existiert. */
  ohneDatensatz: (Zuweisungszahlen & {
    familie: string; typ: string; bauteile: number;
  })[];
  typenGesamt: number;
}

/**
 * Wie viele Bauteile eines Typs Herstellerdaten tragen.
 *
 * Alles über Spalten gezählt und nicht aus dem HerkunftJson gelesen — bei 6622
 * Bauteilen ist eine JSON-Suche der falsche Weg.
 */
export interface Zuweisungszahlen {
  /** Bauteile mit Werten aus einem Datenblatt. */
  zugewiesen: number;
  /** Davon zwischen zwei gedruckten Zeilen berechnet. */
  interpoliert: number;
  /** Mit Befund: die Luftmenge liegt außerhalb der Tabelle. */
  mitBefund: number;
  /**
   * Die Luftmenge hat sich seit dem Abgleich geändert.
   *
   * Der gefährlichste Fall ohne Kennzeichnung: Der Wert steht da und sieht
   * richtig aus, gilt aber für die alte Menge.
   */
  mengeGeaendert: number;
}

export interface Feldvorschlag {
  feldSchluessel: string;
  wert: string;
  /** hoch | mittel | unsicher | auswahl */
  guete: string;
  quelle: string | null;
  beleg: string | null;
  /**
   * Mehrere Möglichkeiten, unter denen ein Mensch wählen muss.
   *
   * „72 °C oder 95 °C" ist eine brauchbare Angabe, nur die Wahl steht aus. Sie
   * wegzulassen verschenkt sie — deshalb kommen alle Möglichkeiten mit, und der
   * Dialog legt sie zur Auswahl vor.
   */
  alternativen: string[] | null;
}

export interface LueftungVorlage {
  id: string;
  templateKey: string;
  name: string;
  beschreibung: string | null;
  aksCode: string;
  nummerBreite: number;
  hatAntrieb: boolean;
  revitKategorie: string;
  familienMuster: string;
  vorrang: number;
  /** Vorgabewerte als JSON. Leer, solange keine gepflegt sind. */
  dataJson: string | null;
  /** Wie viele Bauteile diese Vorlage im Projekt tragen. */
  bauteile: number;
}

export interface LueftungBauteil {
  id: string;
  aksSchluessel: string | null;
  aksCode: string | null;
  aksNummer: number | null;
  luftart: string | null;
  revitKategorie: string;
  familie: string | null;
  typ: string | null;
  groesse: string | null;
  volumenstrom: number | null;
  ebene: string | null;
  raumnummer: string | null;
  raumname: string | null;
  raumQuelle: string | null;
  zuordnung: string;
  aksTraeger: string;
  modellpruefung: string | null;
  state: string;
  bauart: string | null;
  hatAntrieb: boolean;
  /** Gesetzt, wenn Werte aus einem Datenblatt stammen — dann „zugewiesen". */
  datenblattId: string | null;
  /** genau | interpoliert | ausserhalb */
  abgleichArt: string | null;
  abgleichMenge: number | null;
  hatFehler: boolean;
  /** Aus dem Datenblattimport — dieselbe Quelle wie der Feldwert. */
  hersteller: string | null;
  produktcode: string | null;
}

export interface ProfilEintrag {
  id: string;
  name: string;
  description: string | null;
  versions: { id: string; version: string; revitVersion: number }[];
}

export interface Zeitplan {
  id: string;
  name: string;
  /** Kommagetrennte Exportarten, etwa "1,2" */
  kinds: string;
  /** taeglich | woechentlich | monatlich */
  rhythmus: string;
  wochentag: number | null;
  monatstag: number | null;
  /** Ortszeit im Format hh:mm:ss */
  uhrzeit: string;
  benachrichtigung: string | null;
  isActive: boolean;
  modelId: string | null;
  modell: string | null;
  exportProfileVersionId: string;
  /** Gilt genau die hinterlegte Fassung, oder die jeweils freigegebene? */
  profilfassungFest: boolean;
  profil: string | null;
  /** Die am Zeitplan hinterlegte Fassung — der Anker auf das Profil. */
  hinterlegteFassung: string | null;
  /** Die Fassung, die der nächste Lauf tatsächlich nimmt. */
  geltendeFassung: string | null;
  lastRunAt: string | null;
  nextRunAt: string | null;
  lastJobNumber: string | null;
  lastError: string | null;
}

/** Ein Fehlschlag im Regelbetrieb, mit seiner Einstufung. */
export interface RegelbetriebFehlschlag {
  auftragsnummer: string;
  zeitpunkt: string;
  art: string;
  maschine: string;
  modell: string;
  herkunft: string;
  ursachentext: string;
  klasse: string;
  klassenbezeichnung: string;
  erklaert: boolean;
}

/**
 * Der Füllgrad EINES Feldes eines Katalogs (Vertiefungsplan V-P1).
 *
 * `farbe` ist die Farbregel: 'gruen' heißt Modellfeld (rückschreibbar),
 * 'gelb' heißt Datenfeld. Die Aussage gilt dem FELD und nicht dem WERT —
 * siehe `Farbregel.tsx`.
 */
export interface Feldfuellgrad {
  gewerk: string;
  feldschluessel: string;
  bezeichnung: string;
  gruppe: string | null;
  farbe: 'gruen' | 'gelb';
  istGerechnet: boolean;
  /**
   * Die Zahl der Bauteile, an denen das Feld laut Katalog GILT — der Nenner.
   *
   * Vier Kataloge führen dazu eine Bauteilartliste (ventilation_fields,
   * heizung_felder, elektro_felder, sanitaer_felder), drei nicht. `0` heißt
   * nicht „leer", sondern: Die Bauteilklasse, für die das Feld gilt, führt
   * die Tabelle gar nicht — siehe `anwendbarkeit`.
   */
  gesamt: number;
  /** Gefüllte Werte an ANWENDBAREN Bauteilen. Daraus rechnet `anteil`. */
  gefuellt: number;
  /** Je Herkunftswort die Zahl der Werte. 'ohne Angabe', wo keine geführt wird. */
  herkunft: Record<string, number>;
  offenGrund: string | null;
  anteil: number;
  /**
   * Gefüllte Werte an Bauteilen, für die das Feld laut Katalog NICHT gilt.
   *
   * Absichtlich NICHT im `anteil`: Sie zu streichen ließe echte Werte
   * verschwinden, sie mitzuzählen ergäbe über 100 %. Eine Zahl größer null
   * ist ein Befund am Katalog, nicht an den Daten.
   */
  ausserhalbAnwendbarkeit: number;
  /** Welche Geltung gegriffen hat: 'ohne Einschraenkung', 'nur BSK, ENK', … */
  anwendbarkeit: string;
  /** WOGEGEN gezählt wurde: 'je Bauteil', 'je Anlage', 'je Geraet', … */
  bezug: string;
  /** Wie viele Objekte dieser Art es im Projekt gibt. */
  bezugsgroesse: number;
}

/**
 * Eine Nennergruppe: alle Felder eines Gewerks, die gegen DASSELBE Objekt
 * zählen.
 *
 * Die Lüftung hat davon vier. Solange die Gewerkzeile nur „7.081 Bauteile"
 * nannte, las sich diese eine Zahl wie der Nenner aller 400 Felder — es sind
 * 99. Die übrigen zählen gegen 108 Anlagen, 121 Einheiten und gegen gar
 * nichts.
 */
export interface Nennergruppe {
  bezug: string;
  bezugsgroesse: number;
  felder: number;
  moeglich: number;
  gefuellt: number;
  anteil: number;
}

/** Der Füllgrad eines Gewerks. */
export interface Gewerkfuellgrad {
  gewerk: string;
  bezeichnung: string;
  katalogtabelle: string;
  bauteile: number;
  felderGesamt: number;
  felderGruen: number;
  felderGelb: number;
  moeglich: number;
  gefuellt: number;
  anteil: number;
  /** Werte an nicht anwendbaren Bauteilen, über alle Felder. Nicht im Anteil. */
  ausserhalbAnwendbarkeit: number;
  /** Felder mit Nenner 0, weil ihre Bauteilklasse in der Tabelle fehlt. */
  felderOhneBauteilklasse: number;
  /**
   * Felder mit Nenner 0, weil es für sie im Bestand ÜBERHAUPT keine Ablage
   * gibt — nicht die Bauteilart fehlt, sondern die Tabelle.
   *
   * Nicht dasselbe wie `felderOhneBauteilklasse`. Die Lüftung hat davon 32
   * (die Betriebspunktfelder); der Server liefert die Zahl seit jeher, die
   * Seite zeigte sie bis PB30 nicht — 400 Felder, davon 109 ohne
   * Bauteilklasse und 32 ohne Ablage, ging ohne sie nicht auf.
   */
  felderOhneAblage: number;
  /** WOGEGEN gezählt wird, je Bezugsobjekt. */
  nenner: Nennergruppe[];
  /**
   * Nur bei `mitFelder`; die kurze Fassung liefert stattdessen `felderLeer`.
   */
  felder?: Feldfuellgrad[];
  /** Felder ohne einen Wert — OHNE die, die keinen Nenner haben. */
  felderLeer?: number;
}

/** Der Füllgradbericht über alle Gewerke — die Zahlen hinter PAR-FGB. */
export interface Parameterfuellgrad {
  projektId: string;
  projektnummer: string;
  projektname: string;
  erhobenAm: string;
  /** Bauteile mal Felder — die Zahl der möglichen Werte. */
  moeglich: number;
  gefuellt: number;
  anteil: number;
  ausserhalbAnwendbarkeit: number;
  felderOhneBauteilklasse: number;
  /** Felder mit Nenner 0, weil es für sie gar keine Ablage gibt. */
  felderOhneAblage: number;
  gewerke: Gewerkfuellgrad[];
  hinweise: string[];
}

/**
 * Der Regelbetrieb der letzten Tage — die Abnahmefrage der Stufe 8.
 *
 * Nur die Felder, die die Karte braucht. Der Server liefert mehr (Tagestabelle,
 * Fassungsstände); wer sie braucht, nimmt das PDF.
 */
export interface Regelbetrieb {
  projektnummer: string;
  projektname: string;
  von: string;
  bis: string;
  tage: number;
  auftraegeGesamt: number;
  erfolgreich: number;
  fehlgeschlagen: number;
  abgebrochen: number;
  wartend: number;
  laufend: number;
  fehlschlaegeErklaert: number;
  fehlschlaegeUnerklaert: number;
  abnahmeErfuellt: boolean;
  fehlschlaege: RegelbetriebFehlschlag[];
  raumbuchschutz: RegelbetriebRaumbuchereignis[];
  raumbuchBremsen: number;
  raumbuchNichtQuelle: number;
  /** Seit v0.52.3: verworfene Türaufnahmen (TUEREN_NICHT_QUELLE, TUEREN_BREMSE). */
  tuerbuchschutz: RegelbetriebRaumbuchereignis[];
  tuerbuchBremsen: number;
  tuerbuchNichtQuelle: number;
  serverfassung: string;
}

/**
 * Eine verworfene Raum- oder Türaufnahme: nicht aus der Quelle des Buchs, oder
 * von der Bremse angehalten.
 */
export interface RegelbetriebRaumbuchereignis {
  auftragsnummer: string;
  zeitpunkt: string;
  code: 'RAEUME_BREMSE' | 'RAEUME_NICHT_QUELLE' | 'TUEREN_BREMSE' | 'TUEREN_NICHT_QUELLE' | string;
  modell: string;
  text: string;
}

/** Wie eine Rechnung zum aktiven Raumbuch steht (Raumstandpruefung). */
export interface Raumstand {
  rechnung: string;
  bezeichnung: string;
  laufId: string | null;
  gerechnetAm: string | null;
  raeume: number;
  entfernt: number;
  entferntSeit: string | null;
  aufAltemRaumstand: boolean;
  hinweis: string | null;
}

/** Ein Folgelauf im Katalog der Regelaufgaben (WE1). */
export interface RegelKatalogLauf {
  schluessel: string;
  titel: string;
  standardAktiv: boolean;
  /** Warum der Lauf ab Werk aus ist (z. B. „bis D-TR3 …“). */
  ausGrund: string | null;
  /** Hält mit eigener Bremse an (Rückgabe 3), statt zu schreiben. */
  bremsfaehig: boolean;
  braucht: string[] | null;
  /** Der feste Werkzeugaufruf - zur Ansicht. */
  aufruf: string | null;
  berichte: string[] | null;
}

export interface RegelKatalogSchritt {
  schluessel: string;
  titel: string;
  /** aufnahme | gesamt */
  art: 'aufnahme' | 'gesamt' | string;
  rolle: string | null;
  schrittfristMinuten: number;
  teile: { schluessel: string; titel: string; art: number }[];
  laeufe: RegelKatalogLauf[];
  haeltBeiBremse: string[];
}

export interface RegelKatalog {
  schritte: RegelKatalogSchritt[];
  nichtFreigegeben: { art: number; name: string }[];
  nichtFreigegebenGrund: string;
  gesperrteFensterUtc: string[];
  zeitzone: string;
}

export interface RegelZeitplanSchritt {
  schluessel: string;
  position?: number;
  istAktiv: boolean;
  aus: string[];
  an: string[];
  teileAus: string[];
}

export interface RegelZeitplan {
  id: string;
  projectId: string;
  name: string;
  istAktiv: boolean;
  /** Bitmaske Mo = 1, Di = 2, … So = 64. */
  wochentage: number;
  wochentageText: string;
  /** Ortszeit Europe/Berlin, HH:MM. */
  uhrzeit: string;
  fristUtc: string;
  verspaetungMinuten: number;
  benachrichtigung: string | null;
  wochenuebersicht: boolean;
  exportProfileId: string;
  version: number;
  naechsterLaufUtc: string | null;
  naechsterLaufOrtszeit: string | null;
  schritte: RegelZeitplanSchritt[];
  letzterLauf: {
    id: string; status: string; ausloeser: string; geplantFuerUtc: string | null; beendetAm: string | null;
  } | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface RegelZeitplanEingabe {
  name: string;
  istAktiv: boolean;
  wochentage: number;
  uhrzeit: string;
  fristUtc: string;
  verspaetungMinuten: number;
  benachrichtigung: string | null;
  wochenuebersicht: boolean;
  exportProfileId: string;
  schritte: RegelZeitplanSchritt[];
  version?: number;
}

export interface RegelLaufSchritt {
  id: string;
  position: number;
  schluessel: string;
  gruppe: string;
  /** aufnahme | werkzeug | berichte | pruefung */
  art: string;
  status: string;
  gestartetAm: string | null;
  beendetAm: string | null;
  exportJobId: string | null;
  rc: number | null;
  meldung: string | null;
  protokollSchluessel: string | null;
}

export interface RegelLauf {
  id: string;
  zeitplanId: string;
  /** zeitplan | probe | trocken */
  ausloeser: string;
  ausgeloestVon: string | null;
  grund: string | null;
  geplantFuerUtc: string | null;
  gestartetAm: string | null;
  beendetAm: string | null;
  status: string;
  zusammenfassung: string | null;
  meldungVersandt: boolean;
  schritte: RegelLaufSchritt[];
}

export interface ZeitplanEingabe {
  name: string;
  modelId?: string | null;
  exportProfileVersionId: string;
  kinds: number[];
  rhythmus: string;
  wochentag?: number | null;
  monatstag?: number | null;
  uhrzeit: string;
  benachrichtigung?: string | null;
  isActive?: boolean;
}

export interface Exportgruppe {
  kind: number;
  kindName: string;
  /** Auftragsnummer des jüngsten erfolgreichen Laufs. */
  aktuell: string | null;
  auftraege: {
    id: string;
    jobNumber: string;
    createdAt: string;
    finishedAt: string | null;
    status: number;
    failureReason: string | null;
    dateien: { id: string; fileName: string; sizeBytes: number; kind: string }[];
  }[];
}

/**
 * Eine Tür in der Baumansicht.
 *
 * Ohne Werte: Bei 1845 Türen wären das fast zwei Megabyte, und der Browser
 * bricht ab. Für den Baum genügen Nummer, Raum, Geschoss und Typ.
 */
export interface Tuer {
  id: string;
  tuernummer: string | null;
  /** F60 H2: Nummer des Hauses (NULL in der DB = Standardhaus). Optional, ältere Antworten tragen sie nicht. */
  hausNummer?: number | null;
  /** Der Raum, zu dem die Tür gehört - aus der Türnummer abgeleitet. */
  raumnummer: string | null;
  /** Die wievielte Tür des Raums. */
  tuerImRaum: number | null;
  ebene: string | null;
  tuertyp: string | null;
  /** neu | bekannt | entfernt */
  state: string;
  /** Ob überhaupt Werte gepflegt sind. */
  gepflegt: boolean;
  anforderungscode: string | null;
  /** zugeordnet | pruefen | nicht_im_plan */
  anforderungsstatus: string | null;
  anforderungsanzahl: number;
  anforderungspruefung: string | null;
  /** Aus dem Raumbuch nachgeschlagen, sofern der Raum dort bekannt ist. */
  raumname: string | null;
  /**
   * Das Geschoss, abgeleitet aus der Raumnummer: E0-008 liegt in E0.
   *
   * Fällt auf die Revit-Ebene zurück, wenn der Raum im Raumbuch fehlt.
   */
  geschoss: string;
  /** Beleg der automatischen Geschossableitung aus Raumzuordnung oder Ebene. */
  geschossquelle: string;
  /** Nur bei widerspruechlichen oder nicht ableitbaren Raumlagen gesetzt. */
  geschosspruefung: string | null;

  /**
   * Die Kennung, die der ARCHITEKT an seiner Tür vergeben hat — Parameter
   * TUER_ID im Architekturmodell.
   *
   * Ein FESTER Wert: gelesen, nie geschrieben. Er wird deshalb im Türbuch
   * angezeigt und nicht angeboten zum Bearbeiten.
   */
  arcTuerId: string | null;

  /**
   * Unter welchem Parameternamen die Kennung gefunden wurde, etwa `TUER_ID`
   * oder `TÜR_ID (Typ)`.
   *
   * „(Typ)" ist ein Warnzeichen: Eine Kennung am Typ ist für alle Türen dieses
   * Typs dieselbe und taugt dann nicht zum Unterscheiden.
   */
  arcTuerIdQuelle: string | null;

  /** Sicherheitszone des Von-Raums, roher Parameterwert (`SZ4 - BOH`). */
  vonSicherheitszone: string | null;
  nachSicherheitszone: string | null;

  /** innerhalb | uebergang | unbestimmt | einseitig | ohne_raum */
  zonenbewertung: string;

  /** Richtungsneutrales Paar für die Gruppierung, nur beim Übergang. */
  zonenpaar: string | null;

  /**
   * Ob an dieser Tür der Zutritt geregelt werden muss.
   *
   * Nur beim Übergang wahr — bei `unbestimmt` bewusst NICHT: Das wäre eine
   * erfundene Anforderung.
   */
  zutrittsrelevant: boolean;

  /** Was an der Zonenzuordnung offen ist, im Klartext. */
  zonenpruefung: string | null;

  // ── Die Spaltengruppe „Sicherheit" ────────────────────────────────────
  //
  // Sie stammt aus der ABLEITUNG (Migration 0430/0431) und nicht aus dem
  // Raumbuch: `vonSicherheitszone` oben ist der rohe Raumparameter,
  // `schutzzoneBandseite` hier ist die Zone der benannten Türseite mit
  // Schutzstufe, Status und offenen Punkten.

  /** SZ1A … SZ8, aus dem Raum auf der Bandseite. */
  schutzzoneBandseite: string | null;
  schutzzoneBandgegenseite: string | null;

  /** Ordnungszahl 1-8. SZ6 und SZ7 tragen beide die 7. */
  schutzstufeBandseite: number | null;
  schutzstufeBandgegenseite: number | null;

  /** Null heißt: nicht bestimmbar, weil eine Zone fehlt. */
  istZonenwechsel: boolean | null;

  /** Bandseite | Bandgegenseite | keine | nicht bestimmbar */
  sicherungsseite: string | null;

  /** TK-… aus dem Blatt Türklassen. */
  tuerklasse: string | null;

  /**
   * Der SCHLECHTESTE Status der Anforderungen dieser Tür.
   *
   * verified | assumption | missing | klaerung — nicht der häufigste: Eine Tür
   * mit dreizehn gesicherten Merkmalen und einem offenen Punkt ist eine Tür mit
   * einem offenen Punkt.
   */
  sicherheitStatus: string | null;

  /** Die OP-Nummern der klärungsbedürftigen Merkmale, kommagetrennt. */
  sicherheitOffenePunkte: string | null;

  /**
   * Ob die Zuordnung Bandseite/Bandgegenseite überhaupt eine Aussage trägt.
   *
   * Falsch bei der Ringabtastung — dort hängt die Reihenfolge an der
   * Abtastrichtung. Eine Seitenregel auf einer solchen Tür wäre auf einen
   * Zufall angewandt.
   */
  seitenSindGeometrisch: boolean;

  /** Ob für diese Tür eine Zutrittskontrolle abgeleitet wurde. */
  zutrittskontrolle: boolean;

  /** Ob eine türbezogene Kamera abgeleitet wurde. */
  tuerkamera: boolean;
}

/** Eine Tür mit allen Werten - wird beim Anklicken geladen. */
export interface TuerDetail extends Tuer {
  elementGuid: string;
  // Lage und verbundene Räume — ergänzend zur Zuordnung über die Türnummer,
  // die führend bleibt.
  posX: number | null;
  posY: number | null;
  posZ: number | null;
  vonRaumnummer: string | null;
  vonRaumname: string | null;
  nachRaumnummer: string | null;
  nachRaumname: string | null;
  /** revit | geometrie_seiten | geometrie_innen */
  raumVerfahren: string | null;
  arcDoorId: string | null;
  arcLinkModel: string | null;
  gebaeude: string | null;
  dataJson: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  updatedAt: string | null;
  updatedBy: string | null;
  anforderungszuordnung: Tueranforderungszuordnung | null;
  anforderungen: Tueranforderung[];
  feldpflege: Tuerfeldpflege | null;
}

export interface TuerAbleitungsergebnis {
  tueren: number;
  anforderungen: number;
  jeStatus: Record<string, number>;
  jeRegel: Record<string, number>;
  jeTuerklasse: Record<string, number>;
  jeZonenpaar: Record<string, number>;
  hinweise: string[];
}

/** Eine abgeleitete Anforderung mit der Regel, die sie erzeugt hat. */
export interface TuerSicherheitsanforderung {
  /** Der Feldname aus Blatt Tuerparameter, etwa `zuko_erforderlich`. */
  merkmal: string;
  wert: string | null;
  regelId: string;
  /** verified | assumption | missing | klaerung */
  status: string;
  offenerPunkt: string | null;
  /** Warum dieser Wert — im Klartext, für den Nachweis. */
  begruendung: string | null;
  gerechnetAm: string;
  regelgegenstand: string;
  regelwirkung: string;
  regelquelle: string | null;
  reihenfolge: number;
}

export interface TuerSicherheitDetail {
  tuer: {
    id: string;
    tuernummer: string | null;
    bandseiteRaumnummer: string | null;
    bandseiteRaumname: string | null;
    bandgegenseiteRaumnummer: string | null;
    bandgegenseiteRaumname: string | null;
    seitenSindGeometrisch: boolean;
    pruefabstandMm: number | null;
    facingFlipped: boolean | null;
    handFlipped: boolean | null;
    schutzzoneBandseite: string | null;
    schutzzoneBandgegenseite: string | null;
    schutzstufeBandseite: number | null;
    schutzstufeBandgegenseite: number | null;
    istZonenwechsel: boolean | null;
    sicherungsseite: string | null;
    tuerklasse: string | null;
    sicherheitStatus: string | null;
    sicherheitOffenePunkte: string | null;
    sicherheitGerechnetAm: string | null;
  };
  anforderungen: TuerSicherheitsanforderung[];
  offenePunkte: {
    nummer: string;
    gegenstand: string;
    sachverhalt: string | null;
    auswirkung: string | null;
    zustaendig: string | null;
    quelle: string | null;
    status: string | null;
  }[];
  tuerklasse: {
    tuerklasse: string;
    bezeichnung: string;
    anwendung: string | null;
    mechanik: string | null;
    zutritt: string | null;
    video: string | null;
    quelle: string | null;
  } | null;
}

export interface TuerSicherheitPruefliste {
  /** abgeleitet | noch nicht abgeleitet */
  stand: string;
  zusammenfassung: {
    raeume: number;
    raeumeOhneZone: number;
    tueren: number;
    anforderungen: number;
    jeStatus: Record<string, number>;
  };
  raeumeOhneSchutzzone: {
    number: string;
    name: string;
    level: string | null;
    /** Der alte Wert aus `sicherheitszone` — Rang, tbd oder leer. */
    bisher: string | null;
    zonenflaechentyp: string | null;
  }[];
  tuerenBeideSeitenUnbekannt: {
    tuernummer: string | null;
    ebene: string | null;
    bandseite: string | null;
    bandgegenseite: string | null;
  }[];
  widerspruchPanikbeschlag: {
    tuernummer: string | null;
    ebene: string | null;
    grund: string;
  }[];
  widerspruchBrandschutzMotorschloss: {
    tuernummer: string | null;
    ebene: string | null;
    brandschutz: string | null;
    schloss: string | null;
  }[];
  offenePunkte: {
    nummer: string;
    gegenstand: string | null;
    sachverhalt: string | null;
    zustaendig: string | null;
    anforderungen: number;
    tueren: number;
    beispiele: (string | null)[];
  }[];
  jeTuerklasse: { tuerklasse: string; tueren: number }[];
  jeZonenpaar: { zonenpaar: string; tueren: number }[];
  /** Was der Planer befüllen muss, damit die Ableitung vollständig wird. */
  pflegevorrat: {
    tuerenOhneFassadentyp: number;
    tuerenOhneBrandschutzanforderung: number;
    raeumeOhneZonenflaechentyp: number;
    raeumeSZ8OhneFassadenlage: number;
  };
}

export interface TuerSicherheitszonen {
  /** Alle Zonen, die an RÄUMEN vorkommen — auch solche ohne Tür. */
  zonen: string[];
  zusammenfassung: {
    tueren: number;
    /** Türen zwischen zwei verschiedenen Zonen — hier wird Zutritt geregelt. */
    uebergaenge: number;
    innerhalb: number;
    /** Mindestens eine Seite ohne entschiedene Zone (`tbd` oder leer). */
    unbestimmt: number;
    einseitig: number;
    ohneRaum: number;
  };
  uebergaenge: {
    zonenpaar: string;
    anzahl: number;
    tueren: (string | null)[];
  }[];
  offen: {
    tuernummer: string | null;
    vonZone: string | null;
    nachZone: string | null;
    hinweis: string | null;
  }[];
}

export interface Tuerfeldpflege {
  revision: number;
  tuertypVorher: string | null;
  tuertypFinal: string | null;
  aktualisierteFelderJson: string;
  anforderungsaktion: string;
  revitVerknuepfungsstatus: string;
  offenePruefposten: string | null;
  quelle: string;
  ausgefuehrtAm: string;
  ausgefuehrtDurch: string;
}

export interface Tueranforderung {
  id: string;
  revision: number;
  position: number;
  kategorie: string;
  komponente: string;
  menge: number;
  pflichtstatus: string;
  system: string;
  bemerkung: string | null;
  status: string;
  quelldokument: string;
  planstand: string;
  quelldatum: string;
}

export interface Tueranforderungszuordnung {
  id: string;
  revision: number;
  doorTemplateId: string | null;
  anforderungscode: string | null;
  status: 'zugeordnet' | 'pruefen' | 'nicht_im_plan';
  zuordnungsart: string;
  grundlage: string;
  quelldokument: string;
  planstand: string;
  quelldatum: string;
  zugeordnetAm: string;
  zugeordnetDurch: string;
}

export interface Tuerfeld {
  id: string;
  fieldKey: string;
  label: string;
  gruppe: string | null;
  unit: string | null;
  dataType: string;
  options: string | null;
  /** Der NAME des Zielparameters — für den Tooltip, nicht für die Farbe. */
  revitParameter: string | null;
  /**
   * Geht dieses Feld ins Modell zurück? Die FERTIGE Antwort aus dem
   * Rechenkern (`Rueckschreibweg.GehtInsModell`), nicht die Zutaten.
   *
   * Sie ist NICHT dasselbe wie `revitParameter !== null`: Ein Feld kann
   * einen Parameter tragen, aus dem nur GELESEN wird. Wer hier wieder
   * `revitParameter` befragt, baut die elfte Fassung derselben Regel — und
   * genau die war seit den Migrationen 0729–0731 falsch.
   *
   * Zum Färben `farbklasse(feld.gehtInsModell)` aus `Farbregel.tsx`.
   */
  gehtInsModell: boolean;
  sortOrder: number;
}

export interface Tuertyp {
  id: string;
  templateKey: string;
  name: string;
  beschreibung: string | null;
  eltRequirementStatus?: string | null;
  inheritsFrom?: string | null;
  projectId?: string | null;
  dataJson: string | null;
  isActive?: boolean;
  createdBy?: string;
  anforderungen?: Tueranforderung[];
}

export interface Tueranforderungsbericht {
  gesamt: number;
  zugeordnet: number;
  pruefen: number;
  nichtImPlan: number;
  tueren: {
    tuerId: string;
    revitId: string;
    tuernummer: string | null;
    tuertyp: string | null;
    bisherigerTuertyp: string | null;
    finalerTuertyp: string | null;
    anforderungscode: string | null;
    zuordnungsstatus: string;
    zuordnungsart: string;
    offenerPruefposten: string | null;
    aktualisierteFelder: string;
    anforderungsaktion: string | null;
    revitVerknuepfungsstatus: string;
    anforderungen: number;
    quelle: string | null;
    planstand: string | null;
    quelldatum: string | null;
  }[];
}

export interface TuerbuchDaten {
  tueren: Tuer[];
  vorlagen: Tuertyp[];
  /** F60-2: ab zwei aktiven Häusern - dann Haus in der Gliederung. */
  mehrhaus?: boolean;
}

export interface KonfigDatei {
  id: string;
  key: string;
  version: string;
  kind: string;
  fileName: string;
  sizeBytes: number;
  isReleased: boolean;
  projectId: string | null;
  createdAt: string;
  createdBy: string;
}

/** Die Rollen, die eine Konfigurationsdatei einnehmen kann. */
export const KonfigRollen: { wert: string; text: string; hinweis: string }[] = [
  { wert: 'ifc.pset', text: 'IFC-Eigenschaftensaetze',
    hinweis: 'Definiert, welche Parameter als Property Sets exportiert werden.' },
  { wert: 'ifc.parameterMapping', text: 'IFC-Parameterzuordnung',
    hinweis: 'Ordnet Revit-Parameter den IFC-Eigenschaften zu.' },
  { wert: 'ifc.classification', text: 'IFC-Klassifikation',
    hinweis: 'Zuordnung zu einem Klassifikationssystem.' },
  { wert: 'dwg.layerMapping', text: 'DWG-Layerzuordnung',
    hinweis: 'Bestimmt die Layernamen im DWG-Export.' },
  { wert: 'dwg.lineTypeMapping', text: 'DWG-Linientypen',
    hinweis: 'Zuordnung der Linientypen.' },
  { wert: 'dwg.hatchPattern', text: 'DWG-Schraffuren',
    hinweis: 'Zuordnung der Schraffurmuster.' },
];

// --- Was die API liefert ---------------------------------------------------

export interface Maschine {
  id: string;
  name: string;
  erreichbar: boolean;
  gemeldeterZustand: number;
  lastHeartbeat: string | null;
  agentVersion: string | null;
  /** MASCH-1 (F209 E6/E8): Art und Stufe (ältere Server: fehlt = Revit/Produktiv). */
  maschinenart?: Maschinenart;
  betriebsstufe?: Betriebsstufe;
  completedCount: number;
  failedCount: number;
  revitVersions?: string;
  lastSeenIp?: string | null;
  note?: string | null;
  auftrag?: { jobNumber: string; art: string; startedAt: string } | null;
}

/** FV1: Antwort von `GET /api/system/version` (`Systemversion` der API). */
export interface Systemversion {
  release: string;
  commit: string | null;
  bauzeitUtc: string | null;
  gesetzt: boolean;
}

/** TD-WR1: Antwort von `GET /api/system/rechte` - nur Wahrheitswerte über die anfragende Person. */
export interface Systemrechte {
  /**
   * Darf Aufträge beauftragen, die ins Modell schreiben (alle Rückgaben, Modellvervollständigung,
   * Sandboxvorbereitung, Beschriftungslauf): seit S12 nur ProjectAdmin und SystemAdmin.
   */
  modellSchreiben: boolean;
}

export interface Zustand {
  zeitpunkt: string;
  maschinen: Maschine[];
  warteschlange: {
    wartend: number;
    inArbeit: number;
    laengsteWartezeitSekunden: number | null;
  };
  auftraege24h: { gesamt: number; erfolgreich: number; fehlgeschlagen: number };
  warnungen: { stufe: string; text: string }[];
}

/** Eine Zeile des Berichtsregisters mit Ablagestand (`BerichtsfassungDto`, F1). */
export interface Registerfassung {
  dokumentnummer: string;
  gewerkKuerzel: string;
  artKuerzel: string;
  gewerk: string;
  berechnungsart: string;
  gegenstand: string;
  berechnungsstand: string;
  freigabestatus: 'Vorabzug' | 'Zwischenstand' | 'Freigegeben';
  berechnetAm: string;
  erzeugtAm: string;
  erzeugtVon: string;
  datenhash: string;
  dateiname: string | null;
  abgelegt: boolean;
  dateiBytes: number | null;
  dateiSha256: string | null;
  abgelegtAm: string | null;
  ablagehinweis: string | null;
}

/** N7a: die Schritte des Freigabewegs (Pfadteil der Route). */
export type Freigabeschritt = 'pruefen' | 'freigeben' | 'zurueckziehen';

/** N7b-2: Freigabestand einer Fassung (`BerichtsfreigabeStandDto`). */
export interface BerichtsfreigabeStand {
  dokumentnummer: string;
  freigabestatus: string;
  zustand: 'geprueft' | 'freigegeben' | null;
  vorbehalt: string | null;
  freigegebeneFassung: string | null;
  darfPruefen: boolean;
  darfFreigeben: boolean;
  darfZurueckziehen: boolean;
  begruendungNoetig: boolean;
}

/** Eine Seite der Registerliste. */
export interface BerichtsfassungenSeite {
  zeilen: Registerfassung[];
  gesamt: number;
  seite: number;
  groesse: number;
}

export interface Projekt {
  id: string;
  number: string;
  name: string;

  /** Die ACC-Region. Steht in der Kopfzeile unter der Projektbezeichnung. */
  accRegion?: string | null;

  /**
   * BIM12: Der Server hat den BIM-Kern freigeschaltet (Schalter `Bim:Schatten`). Nur dann steht „BIM-Modelle“
   * in der Navigation; ohne das Feld (älterer Server) bleibt der Eintrag weg.
   */
  bimModelle?: boolean;

  /**
   * MP-6 (F324): in welcher Datenbank das Projekt liegt - `projekt` = eigene Projekt-DB (Eintrag im Projektregister),
   * `dienst` = die Dienst-DB. Nur die Art, keine Verbindungsdaten. Ohne das Feld (älterer Server) keine Anzeige.
   */
  datenbank?: ProjektDatenbank;
}

/** MP-6 (F324): Werte des Feldes `Projekt.datenbank`. */
export type ProjektDatenbank = 'projekt' | 'dienst';

/** BIM12: Verweis auf ein anderes Modell des BIM-Registers (Host einer Verknüpfung). */
export interface BimModellVerweis {
  id: string;
  titel: string | null;
  kennung: string;
}

/** BIM12: die aktive Version eines BIM-Modells (`bim_modell.AktiveVersionId`). */
export interface BimModellVersion {
  id: string;
  folge: number;
  versionskennung: string;
  /** Aktiviert am, sonst angelegt am (ISO). */
  datum: string;
  status: string;
  /** Alle Elemente der Version (auch Typen, Ebenen, Räume, Systeme) - `ZaehlungenJson.elemente`. */
  elemente: number | null;
  /** Bauteile (Instanzen) - `ZaehlungenJson.elemente_je_art.instanz`. */
  bauteile: number | null;
  parameterwerte: number | null;
}

/**
 * BIM12 (F147/F148): eine Zeile der nur lesenden Liste „BIM-Modelle“ - eine Quelle des Modellregisters
 * `bim_modell` (eigenes TGA-Modell, Sandbox oder Fremddatei).
 */
export interface BimModell {
  id: string;
  /** `acc-<guid32>` | `datei-<32hex>`. */
  kennung: string;
  titel: string | null;
  /** eigen_tga | fremd | sandbox | koordination | referenz | unbestimmt */
  rolle: string;
  dateiformat: string;
  quelle: string;
  /** Registermodell (`models.Id`) bei eigenen Modellen, sonst null. */
  modelId: string | null;
  aktiveVersion: BimModellVersion | null;
  /** Host-Modelle, deren aktive Version dieses Modell verknüpft. */
  verknuepftIn: BimModellVerweis[];
  /** Host, über dessen Link die Quelle zuerst kam. */
  entdecktUeber: BimModellVerweis | null;
}

export interface ProjektDetail {
  id: string;
  number: string;
  name: string;
  isActive: boolean;
  accRegion: string | null;
  defaultModelSource: number;
  geo: GeoEinstellungen;
  modelle: Modell[];
  /** PT-0a: jeder Angemeldete sieht das Projekt (ab v0.52.x-Release mit 1720). */
  offenFuerAlle?: boolean;
  /** PT-0a: was die anfragende Person an den Rechten pflegen darf. */
  rechte?: ProjektRechte;
  /**
   * IFC-3d (F248): Der Server nimmt Aufträge „IFC aus Datenkern“ (Art 200) an (Schalter `Bim:IfcExport`). Nur dann
   * steht die Auswahl in der Modellzeile; ohne das Feld (älterer Server) bleibt sie weg.
   */
  ifcAusDatenkern?: boolean;
  /**
   * MP-6 (F324): Der Server nimmt die Engine-Aufnahme (Art 161, „Revit-Import (Engine)“) an (Schalter
   * `Bim:EngineEingang`). Nur dann steht der Knopf in der Modellzeile; ohne das Feld (älterer Server) bleibt er weg.
   */
  engineEingang?: boolean;
}

/** PT-0a (Projekttrennung): nur Wahrheitswerte über die anfragende Person. */
export interface ProjektRechte {
  mitgliederVerwalten: boolean;
  zugangSchalten: boolean;
}

export interface Projektmitglied {
  benutzerId: string;
  displayName: string;
  email: string;
  /** Ein deaktivierter Benutzer zählt nicht als Mitglied. */
  aktiv: boolean;
  /** Gespeichert, in PT-0a noch nicht ausgewertet. */
  canExport: boolean;
  canAdminister: boolean;
}

export interface BekannterBenutzer {
  benutzerId: string;
  displayName: string;
  email: string;
}

export interface Mitgliederliste {
  offenFuerAlle: boolean;
  zugangSchalten: boolean;
  mitglieder: Projektmitglied[];
  /** Angemeldet gewesen, aktiv, noch nicht Mitglied. */
  bekannte: BekannterBenutzer[];
}

export interface Modell {
  id: string;
  name: string;
  discipline: string | null;
  revitVersion: number;
  /** 1 = Objektspeicher, 2 = Autodesk Docs */
  source: number;
  accProjectGuid: string | null;
  accModelGuid: string | null;
  storageKey: string | null;
  isActive: boolean;
  /** Zeitlimit in Minuten. Leer heißt: Vorgabe des Agenten (90). */
  timeoutMinutes: number | null;
  /** Ob in diesem Modell die Raumdaten liegen. */
  istRaumbuchQuelle: boolean;
  /** Ob in diesem Modell die Türinformationen liegen. */
  istTuerbuchQuelle: boolean;
  /**
   * Welche Fachbereichsdaten in diesem Modell liegen - die Schlüssel aus
   * `disciplines`. Ersetzt die beiden Boolean-Felder darüber; diese bleiben
   * abgeleitet erhalten, damit ältere Ansichten weiterlaufen.
   */
  rollen: string[];
  /** F60 H2b: Haus des Modells; leer = Standardhaus. Optional, ältere Antworten tragen es nicht. */
  hausId?: string | null;
}

/** F60 H1/H2: ein Haus des Projekts, wie es `GET /api/projects/{id}/haeuser` liefert. */
export interface Haus {
  id: string;
  /** 1..99, angezeigt zweistellig („Haus 01“). */
  nummer: number;
  kuerzel: string;
  name: string;
  reihenfolge: number;
  istStandard: boolean;
  isActive: boolean;
  /** Modelle in diesem Haus (ohne Zuordnung: im Standardhaus). */
  modelle: number;
  /** Raumbuchquellen in diesem Haus; mehr als eine ist erlaubt (Bauabschnitte), wird aber angezeigt. */
  raumbuchquellen: number;
  tuerbuchquellen: number;
}

export interface HausEingabe {
  kuerzel?: string | null;
  name?: string | null;
  reihenfolge?: number | null;
  istStandard?: boolean | null;
  isActive?: boolean | null;
}

export interface ModellEingabe {
  name: string;
  discipline?: string | null;
  revitVersion?: number;
  source?: number;
  accProjectGuid?: string | null;
  accModelGuid?: string | null;
  storageKey?: string | null;
  /** Stilllegen oder wieder aufnehmen. */
  isActive?: boolean;
  /** Zeitlimit in Minuten. Null setzt auf die Vorgabe zurück. */
  timeoutMinutes?: number | null;
  istRaumbuchQuelle?: boolean;
  istTuerbuchQuelle?: boolean;
}

/**
 * Eine Sandbox-Kopie, wie sie `/api/projects/{id}/sandboxmodelle` liefert.
 *
 * `veraltetUnbekannt` ist der Regelfall: Nur eine nachweislich SPAETERE
 * Aufnahme des Quellmodells (`quellmodell.zuletztAufgenommenAm` nach
 * `sandboxErzeugtAm`) macht `istNachweislichVeraltet` wahr. Ein ruhendes
 * Feld ist kein Beleg dafür, dass am ACC-Modell nichts mehr geändert wurde.
 */
export interface Sandboxmodell {
  id: string;
  name: string;
  discipline: string | null;
  sandboxStand: number;
  sandboxErzeugtAm: string | null;
  sandboxAblage: string | null;
  sandboxQuellHash: string | null;
  darfAlsSandboxBeschriebenWerden: boolean;
  quellmodell: {
    id: string | null;
    vorhanden: boolean;
    name: string | null;
    discipline: string | null;
    zuletztAufgenommenAm: string | null;
  };
  istNachweislichVeraltet: boolean;
  veraltetUnbekannt: boolean;
}

/** Vorgabewerte fuer die Lage exportierter IFC-Dateien. Alle Laengen in Metern. */
export interface GeoEinstellungen {
  aktiv: boolean;
  ost: number | null;
  nord: number | null;
  hoehe: number | null;
  refElevation: number | null;
  epsgCode: string | null;
  crsName: string | null;
  weltOst: number | null;
  weltNord: number | null;
  weltHoehe: number | null;
  weltDrehung: number | null;
  hinweis: string | null;
}

export interface Auftrag {
  id: string;
  jobNumber: string;
  status: string | number;
  projectId?: string | null;
  projectName?: string;
  createdAt: string;
  finishedAt: string | null;
  warningCount: number;
  requestedByEmail?: string;
  environment?: 'PRODUCTION' | 'GOLDEN' | string;
  workload?: string | null;
  caseId?: string | null;
  acceptanceStatus?: string | null;
  /** Betroffene Modelle - bei mehr als zwei eine Anzahl. */
  modelle?: string;
  /** Verarbeitende Maschine. Leer, solange der Auftrag wartet. */
  maschine?: string;
  /**
   * Pausiert — die Abholung lässt den Auftrag liegen.
   *
   * Als Kennzeichen und nicht als Zustandswert: Der Zustand liegt am Teilauftrag,
   * das Pausieren gilt dem Auftrag.
   */
  istPausiert: boolean;
  pausiertAm: string | null;
  pausiertVon: string | null;
  /**
   * UI-A73 (A73): Warum der offene Auftrag nicht abgeholt wird, z. B. „wartet auf passende Maschine (Revit 2024)“
   * (Server: `Vergabebereitschaft`). Nur, wenn kein Teil verarbeitet wird und jeder offene Teil einen sicheren Grund
   * hat; sonst leer. Die Seiten zeigen ihn statt „läuft“ (`statusAnzeige`).
   */
  wartegrund?: string | null;
}

/**
 * Ein Auftrag mit Protokoll.
 *
 * Anders als in der Liste liegen die Kopfdaten hier unter `summary` und nicht
 * flach - die Detailantwort umschliesst sie. Die Seite las sie flach, deshalb
 * stand dort "Invalid Date" und der Zustand blieb leer.
 *
 * Und das Ereignis nennt seinen Zeitpunkt `at`, nicht `createdAt`.
 */
export interface AuftragDetail {
  summary: Auftrag;
  parts?: {
    id: string; modelName: string; kind: string; status: string;
    modelVersionLabel: string | null; attemptCount: number;
    startedAt: string | null; finishedAt: string | null;
    failureReason: string | null;
    /** Nur belegt, wenn der Teilauftrag an einem Arbeitsplatz laeuft. */
    arbeitsplatzName: string | null;
    /**
     * Zuletzt gemeldeter Fortschritt. Leer ist der Normalfall -- ein
     * Arbeitsplatz, der noch nichts gemeldet hat (oder ein
     * Maschinen-Teilauftrag), zeigt hier nichts an, kein Fehler.
     */
    arbeitsplatzFortschritt: string | null;
    arbeitsplatzFortschrittSchrittVon: number | null;
    arbeitsplatzFortschrittSchrittBis: number | null;
    arbeitsplatzFortschrittAt: string | null;
    /** UI-A73 (A73): Warum der wartende Teil von keiner Maschine abgeholt wird; leer ohne sicheren Grund. */
    wartegrund?: string | null;
  }[];
  events?: { at: string; level: string; code: string; message: string }[];
  artifacts?: {
    id: string; fileName: string; kind: string; sizeBytes: number;
    sha256: string; expiresAt: string | null;
  }[];
  failureReason?: string | null;
}

/**
 * Die Statuswerte der Plattform. Queued ist 0 - nicht 10, wie man vermuten
 * koennte.
 */
export const StatusText: Record<string, string> = {
  0: 'wartet',
  10: 'wird vorbereitet',
  20: 'laeuft',
  30: 'wird geprueft',
  40: 'wird gepackt',
  50: 'fertig',
  60: 'fehlgeschlagen',
  70: 'abgebrochen',
  80: 'Zeitueberschreitung',
  Queued: 'wartend',
  Preparing: 'wird vorbereitet',
  Running: 'läuft',
  Validating: 'wird geprüft',
  Packaging: 'wird gepackt',
  Succeeded: 'fertig',
  Failed: 'fehlgeschlagen',
  Cancelled: 'abgebrochen',
  TimedOut: 'Zeitueberschreitung',
};

export const StatusFarbe: Record<string, string> = {
  0: 'wartend',
  10: 'laufend',
  20: 'laufend',
  30: 'laufend',
  40: 'laufend',
  50: 'gut',
  60: 'schlecht',
  70: 'neutral',
  80: 'schlecht',
  Queued: 'wartend',
  Preparing: 'laufend',
  Running: 'laufend',
  Validating: 'laufend',
  Packaging: 'laufend',
  Succeeded: 'gut',
  Failed: 'schlecht',
  Cancelled: 'neutral',
  TimedOut: 'schlecht',
};

/**
 * Zustand im Sinne der Musterbibliothek (`StatusBadge`, Abschnitt 18).
 *
 * `StatusFarbe` oben nennt die Klassen des alten `.marke`-Stils
 * (`wartend`/`laufend`/`gut`/`schlecht`/`neutral`). Die Muster kennen nur
 * fünf Zustände; diese Abbildung übersetzt beides ineinander, damit nicht
 * jede Seite ihre eigene Übersetzung schreibt.
 *
 * Bewusst KEIN Import aus `muster/` - `api.ts` bleibt frei von Oberfläche;
 * der Werttyp ist mit `Zustand` strukturell gleich.
 */
export type ZustandWort = 'gut' | 'warnung' | 'fehler' | 'info' | 'neutral';

/**
 * Übersetzt eine Marken-/Statusfarbe des Bestands in einen Musterzustand.
 *
 * Abschnitt 18: Ein wartender oder laufender Vorgang ist KEIN Fehler; er ist
 * `info`. Rot bleibt dem tatsächlichen Problem vorbehalten.
 */
export function zustandAusFarbe(farbe: string | null | undefined): ZustandWort {
  switch (farbe) {
    case 'gut':
    case 'erfolg':
      return 'gut';
    case 'warnung':
    case 'wartend':
      return 'warnung';
    case 'schlecht':
    case 'fehler':
      return 'fehler';
    case 'laufend':
    case 'info':
      return 'info';
    default:
      return 'neutral';
  }
}

/** Auftragsstatus (Zahl oder Name) unmittelbar als Musterzustand. */
export function auftragZustand(status: unknown): ZustandWort {
  return zustandAusFarbe(StatusFarbe[String(status)]);
}

// === Elektro =================================================================

export interface Elektroanlagengruppe {
  kostengruppe: string;
  bezeichnung: string;
  oberer: string | null;
  reihenfolge: number;
  hinweis: string | null;
}

export interface Elektrobauteilart {
  schluessel: string;
  bezeichnung: string;
  kostengruppe: string;
  kategorie: string | null;
  wirdGefuehrt: boolean;
  brauchtDatenblatt: boolean;
  brauchtStromkreis: boolean;
  /** Zuordnung über elt_it statt elt_uv. */
  istItGewerk: boolean;
  reihenfolge: number;
  hinweis: string | null;
}

export interface Elektrofeld {
  feldschluessel: string;
  bezeichnung: string;
  gruppe: string;
  einheit: string | null;
  art: string;
  auswahl: string | null;
  bauteilarten: string | null;
  revitParameter: string | null;
  istSchreibbar: boolean;
  /** Wird am Fabrikat gepflegt und gilt für alle Exemplare. */
  giltJeTyp: boolean;
  reihenfolge: number;
  hinweis: string | null;
  /**
   * WOHER der Wert kommt: Der Parameter ist ein fremder, den die Fachplanung
   * im Modell führt — kein gemeinsamer Parameter aus unserer eigenen
   * Familienpflege („TuV_…"). 28 der 119 Felder mit Parameter.
   *
   * Das ist NICHT die Farbe. Wer danach färbt, färbt nach der Herkunft des
   * WERTES und nicht nach dem Rückweg des FELDES — zwei Aussagen, die
   * auseinandergehen (siehe `Farbregel.tsx`).
   */
  ausModellGelesen: boolean;
  /**
   * Geht dieses Feld ins Modell zurück? Die FERTIGE Antwort aus dem
   * Rechenkern (`Rueckschreibweg.GehtInsModell`), nicht die Zutaten.
   *
   * Sie ist NICHT dasselbe wie `revitParameter !== null`: Ein Feld kann
   * einen Parameter tragen, aus dem nur GELESEN wird. Wer hier wieder
   * `revitParameter` befragt, baut die nächste Fassung derselben Regel —
   * das Projekt hatte davon neunzehn.
   *
   * Zum Färben `farbklasse(feld.gehtInsModell)` aus `Farbregel.tsx`.
   */
  gehtInsModell: boolean;
}

export interface Elektrouebersicht {
  gesamt: number;
  mitRaum: number;
  mitVerteiler: number;
  mitKreis: number;
  verteiler: number;
  stromkreise: number;
  anlagengruppen: {
    kostengruppe: string; bezeichnung: string; oberer: string | null;
    anzahl: number;
  }[];
  arten: {
    bauteilart: string; bezeichnung: string; kostengruppe: string | null;
    anzahl: number; mitRaum: number; mitVerteiler: number; mitKreis: number;
    mitDatenblatt: number; offen: number; zugewiesen: number; zuPruefen: number;
  }[];
}

export interface Elektroseite {
  gesamt: number;
  seite: number;
  proSeite: number;
  arten: Record<string, string>;

  /**
   * Die Verteiler dieser Seite, nach Kennung.
   *
   * Als Verzeichnis und nicht an jeder Zeile: Bei hundert Leuchten am selben
   * Abgang stünde der Name hundertmal.
   */
  verteiler: Record<string, {
    kennzeichen: string; bezeichnung: string | null; art: string;
  }>;

  stromkreise: Record<string, {
    kennzeichen: string; bezeichnung: string | null;
    schutzorgan: string | null; istSicherheit: boolean;
  }>;

  /**
   * Die Räume aus dem Raumbuch, nach Nummer.
   *
   * Das Raumbuch ist die führende Quelle für den Namen. Am Bauteil steht, wie das
   * gelesene Modell den Raum nennt — weicht es ab, gilt das Raumbuch.
   */
  raeume: Record<string, { name: string; level: string | null }>;

  /**
   * TV11: Raumnummern, die mehrere Räume tragen. Sie fehlen in `raeume` - ihr Raumbuchname ist nicht eindeutig. Ältere
   * Antworten ohne das Feld gelten als leer.
   */
  raeumeMehrdeutig?: string[];
  bauteile: {
    id: string;
    bauteilart: string;
    kostengruppe: string | null;
    familie: string | null;
    typ: string | null;
    bezeichnung: string | null;
    kennzeichen: string | null;
    geschoss: string | null;
    geschossgruppe: string | null;
    raumnummer: string | null;
    raumname: string | null;
    raumHerkunft: string | null;
    quadrant: string | null;
    verteilerId: string | null;
    stromkreisId: string | null;
    kreisHerkunft: string | null;
    datenblattId: string | null;
    datenblattUrl: string | null;
    herstellerUrl: string | null;
    datenstand: string;
    datenstandGrund: string | null;
  }[];
}

export interface Elektrobauteil {
  id: string;
  bauteilart: string;
  kostengruppe: string | null;
  familie: string | null;
  typ: string | null;
  bezeichnung: string | null;
  kennzeichen: string | null;
  ifcGuid: string | null;
  elementId: number | null;
  geschoss: string | null;
  geschossgruppe: string | null;
  bearbeitungsbereich: string | null;
  raumnummer: string | null;
  raumname: string | null;
  raumHerkunft: string | null;
  quadrant: string | null;
  kreisHerkunft: string | null;
  datenblattUrl: string | null;
  herstellerUrl: string | null;
  datenstand: string;
  datenstandGrund: string | null;
  gelesenAm: string | null;
  entferntAm: string | null;
  verteiler: {
    id: string; kennzeichen: string; bezeichnung: string | null; art: string;
  } | null;
  stromkreis: {
    id: string; kennzeichen: string; bezeichnung: string | null;
    schutzorgan: string | null;
  } | null;
  werte: {
    feldschluessel: string; wert: string | null; zahlwert: number | null;
    herkunft: string;
  }[];
  /** Die Werte des Fabrikats — sie gelten für alle Exemplare. */
  typwerte: {
    feldschluessel: string; wert: string | null; zahlwert: number | null;
    herkunft: string;
  }[];
}

export interface Elektroverteiler {
  id: string;
  kennzeichen: string;
  bezeichnung: string | null;
  art: string;
  oberId: string | null;
  verteilerbereich: string | null;
  raumnummer: string | null;
  geschoss: string | null;

  /**
   * Der Quadrant, den er versorgt — die erste Zahl im Bereichsnamen.
   *
   * UV_2.0.7: Quadrant 2, Geschoss G00, Unterverteilung 7.
   */
  quadrant: string | null;

  /** Die Unterverteilernummer — die dritte Zahl. */
  verteilernummer: string | null;

  /** Das Modellbauteil, das diesen Verteiler darstellt. */
  bauteilId: string | null;

  /** Der Ort des Schranks in Metern, aus dem Modellbauteil. */
  lageX: number | null;
  lageY: number | null;
  lageZ: number | null;

  /**
   * Die Größe eines Feldes in Teilungseinheiten. 24, 36 oder 48 sind die gängigen
   * Bauformen.
   */
  teJeFeld: number | null;

  /** Wie viele Felder der Verteiler hat. */
  felder: number | null;

  /**
   * Was die Kreisbildung errechnet hat — getrennt vom Ist-Wert, damit die
   * Abweichung sichtbar bleibt.
   */
  teBedarf: number | null;
  felderBedarf: number | null;

  // --- Eingangsdaten für die Nachweise ---------------------------------------

  frequenz: number | null;
  phasenanzahl: number | null;

  /** Der MINIMALE Kurzschlussstrom — der maßgebende für die Abschaltbedingung. */
  kurzschlussstromMin: number | null;

  vorgelagertesSchutzorgan: string | null;
  neutralleiterKonzept: string | null;
  ueberspannungsschutz: string | null;

  schranksystem: string | null;
  schrankHersteller: string | null;
  schrankBaureihe: string | null;
  aufstellungsort: string | null;
  umgebungstemperaturMax: number | null;

  sammelschieneStrom: number | null;
  sammelschieneKurzschlussfest: number | null;
  verlustleistungZulaessig: number | null;
  norm61439Teil: string | null;

  nachweisHerkunft: string | null;
  bemessungsstrom: number | null;
  bemessungsspannung: number | null;
  kurzschlussstrom: number | null;
  schutzart: string | null;
  netzform: string | null;
  herkunft: string;

  /**
   * Nur bei `herkunft === 'virtuell'` gefüllt: Warum und wann die Plattform
   * diesen Knoten angelegt hat, und dass er im Modell nachzuführen ist
   * (Festlegung des Fachplaners vom 19.09.2026, `einspeisung-verketten
   * --virtuelle-nshv`).
   */
  hinweis: string | null;

  /** N_T1_T2 · N_T3_T4 · S_T1_T2 · S_T3_T4 · K1_K2 · PV1 — bei Trafo und NSHV. */
  trafogruppe: string | null;

  /** Was das GERÄT kann: 1250 kVA je Trafo. Keine Summe. */
  bemessungsleistung: number | null;

  /** Was ERWARTET wird, aus der Bilanz. */
  bedarfsleistung: number | null;

  /** Was die Abgänge liefern können, gedeckelt durch die Schutzorgane. */
  summeAnschlussleistung: number | null;

  /**
   * Der Gleichzeitigkeitsfaktor, mit dem DIESER Knoten seine Kinder
   * summiert hat (NSHV/Trafo/MS) — null an einer Unterverteilung.
   */
  bilanzGleichzeitigkeit: number | null;

  /** Bedarfsleistung bezogen auf Bemessungsleistung — bei einem Trafo die Auslastung. */
  auslastungProzent: number | null;

  /**
   * Wie die Bilanz dieses Knotens zustande kam — oder WARUM es keine gibt.
   *
   * Beginnt der Text mit `LUECKE: `, ist die Bilanz nicht null, sondern
   * nicht vorhanden: Der Kette fehlt eine Stufe darüber oder darunter. Das
   * sind zwei verschiedene Sachverhalte, und sie sahen in der Liste gleich
   * aus — eine leere Zelle.
   */
  bilanzKette: string | null;

  /** Weitere Räume mit Schrankfeldern derselben Verteilung. */
  weitereRaeume: string | null;

  bauteile: number;
  stromkreise: number;
}

export interface Elektroverteilerliste {
  verteiler: Elektroverteiler[];
  /**
   * Verteilerbereiche aus dem Raumbuch, für die noch kein Verteiler angelegt ist.
   *
   * Solange einer hier steht, bleiben die Bauteile dieser Räume ohne Zuordnung —
   * das ist die Arbeitsliste.
   */
  offeneBereiche: { bereich: string; raeume: number }[];

  /** 'strom' (ohne IT), 'it' oder 'alle' — welche Auswahl geliefert wurde. */
  auswahl?: string;

  /**
   * Wie viele IT-Verteiler das Projekt hat. Sie stehen NICHT in `verteiler`
   * (Auftrag Fachplaner 18.09.2026: eigener Abschnitt), und diese Zahl ist
   * der Hinweis darauf — sonst verschwänden 26 Verteiler wortlos.
   */
  itVerteiler?: number;

  /** Alle Verteiler des Projekts, auch die herausgelösten. */
  gesamt?: number;
}

/** Ein Betriebsmittel auf seinem Platz im 19"-Raster. */
export interface ItSchrankeinbau {
  vonHe: number;
  bisHe: number;
  ausserhalb: boolean;
  ragtUeber: boolean;
  kennzeichen: string;
  art: string;
  bezeichnung: string | null;
  ports: number | null;
  portsBelegt: number | null;
  poeBudgetW: number | null;
  poeBedarfW: number | null;
  hoeheneinheiten: number;
}

/** Ein Netzabgang nach Art — Datendose, Accesspoint, Kamera, Zutrittsleser. */
export interface ItAbgang {
  bauteilart: string;
  bezeichnung: string;
  /** 'daten' (KG 4571) oder 'sicherheit' — aus der Kostengruppe, keine
   *  Festlegung eines eigenen Netzes (offene Frage 7). */
  anlagengruppe: string;
  kostengruppe: string;
  stueck: number;
  portsJeStueck: number;
  ports: number;
  poeWattJeStueck: number;
  poeBedarfW: number;
  poeKlasse: string | null;
  leitungsart: string | null;
  portVon: number;
  portBis: number;
  patchfeldVon: string;
  patchfeldBis: string;
}

export interface ItSchrank {
  verteilerId: string;
  kennzeichen: string;
  bezeichnung: string | null;
  geschoss: string | null;
  raumnummer: string | null;
  herkunft: string;
  stand: string;

  heJeSchrank: number;
  heHoechste: number;
  ueberschritten: boolean;
  zaehlrichtung: string;

  anschluesseGesamt: number;
  portsGesamt: number;
  patchfelder: number;
  switches: number;
  switchesAusPorts: number;
  switchesAusPoe: number;
  poeGeraete: number;
  poeBedarfW: number;
  poeBudgetW: number;
  hoeheneinheitenBelegt: number;
  hoeheneinheitenReserve: number;
  anschlussleistungW: number;
  bedarfsleistungW: number;
  bemessungsstromA: number;
  bilanzKette: string;

  bestueckung: ItSchrankeinbau[];
  inDerFlanke: { kennzeichen: string; art: string; bezeichnung: string | null;
                 hoeheneinheiten: number }[];
  abgaenge: ItAbgang[];
  hinweise: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// Video / CCTV (Migration 0825, docs/CCTV.md)
// ═══════════════════════════════════════════════════════════════════════════

/** 'ok' | 'warnung' | 'fehler' | 'entfernt' */
export type CctvStatus = string;

/** Eine Kamera, wie sie in elektro_cctv_anschluesse steht. */
export interface CctvZeile {
  id: string;
  kennung: string;
  itKennung: string | null;
  geschoss: string | null;
  raumnummer: string | null;
  raumname: string | null;
  familie: string | null;
  typ: string | null;
  kameraart: string | null;
  /** 'hand' | 'vorgabe' | 'muster' | 'offen' */
  kameraartHerkunft: string | null;
  itSchrank: string | null;
  itSchrankId: string | null;
  /** 'bauteil' | 'zuweisung' */
  schrankHerkunft: string | null;
  patchfeldNr: string | null;
  patchfeldPort: number | null;
  switchNr: string | null;
  switchPort: number | null;
  leitungsart: string | null;
  laengeM: number | null;
  /** 'hand' | 'trasse' | 'schaetzung' | null */
  laengeHerkunft: string | null;
  laengeRechenweg: string | null;
  poeW: number | null;
  poeKlasse: string | null;
  /** 'hand' | 'vorgabe' | 'it_vorgabe' */
  poeHerkunft: string | null;
  oeffnungswinkel: number | null;
  blickrichtung: number | null;
  vlan: number | null;
  ipPlan: string | null;
  bemerkung: string | null;
  status: CctvStatus;
  befunde: number;
  entfernt: boolean;
}

export interface CctvKennzahlen {
  anzahl: number;
  poeSummeW: number;
  kabelmeter: number;
  ohneLaenge: number;
  fehler: number;
  warnungen: number;
  hinweise: number;
}

export interface CctvListe {
  zeilen: CctvZeile[];
  gesamt: number;
  kennzahlen: CctvKennzahlen;
  auswahl: {
    geschosse: string[];
    raeume: string[];
    schraenke: string[];
    typen: string[];
    kameraarten: string[];
    status: string[];
  };
  datenschutz: string;
}

export interface CctvBefund {
  regel: string;
  /** 'fehler' | 'warnung' | 'hinweis' */
  stufe: string;
  kennung: string | null;
  schrank: string | null;
  switch: string | null;
  text: string;
}

export interface CctvSwitchBilanz {
  schrankId: string;
  schrank: string;
  switch: string;
  sicherheit: boolean;
  geraete: number;
  kameras: number;
  poeW: number;
  budgetW: number;
  ueberschritten: boolean;
}

export interface CctvDetail {
  zeile: CctvZeile;
  stand: {
    erstelltAm: string; erstelltVon: string | null;
    geaendertAm: string | null; geaendertVon: string | null;
    entferntAm: string | null; herkunft: string;
    bauteilId: string | null; bauteilUniqueId: string | null;
  };
  befunde: CctvBefund[];
  switchBilanz: CctvSwitchBilanz | null;
  probelauf: {
    laengeM: number | null; laengeHerkunft: string | null; laengeRechenweg: string;
    kameraart: string; poeW: number; patchfeldNr: string | null; switchNr: string | null;
  } | null;
  regeln: {
    kabelMaxM: number; kabelWarnM: number; umwegfaktor: number; zuschlagM: number;
    poeBudgetWJeSwitch: number;
  };
  kameraarten: string[];
  datenschutz: string;
}

/** Handpflege: nicht genannte Felder bleiben, `leeren` gibt sie zurück. */
export interface CctvWerte {
  laengeM?: number | null;
  kameraart?: string | null;
  poeW?: number | null;
  poeKlasse?: string | null;
  vlan?: number | null;
  ipPlan?: string | null;
  oeffnungswinkel?: number | null;
  blickrichtung?: number | null;
  bemerkung?: string | null;
  leeren?: string[];
}

export interface CctvLaufergebnis {
  geschrieben: boolean;
  kameras: number;
  neu: number;
  entfallen: number;
  fehler: number;
  warnungen: number;
  hinweise: string[];
  message: string;
}

export interface CctvBefundliste {
  befunde: CctvBefund[];
  switche: CctvSwitchBilanz[];
  hinweise: string[];
  kernverteiler: string[];
  datenschutz: string;
}

/** Wonach die Kameraliste als PDF gruppiert (Etappe 3). */
export type CctvGruppierung = 'geschoss' | 'schrank';

/** Ein Schemablatt eines IT-Schranks als Objekt-URL. */
export interface CctvSchemaVorschau {
  url: string;
  blatt: number;
  blaetter: number;
}

export interface Elektroverteilerangabe {
  kennzeichen?: string | null;
  bezeichnung?: string | null;
  art?: string | null;
  oberId?: string | null;
  verteilerbereich?: string | null;
  raumnummer?: string | null;
  geschoss?: string | null;
  bemessungsstrom?: number | null;
  bemessungsspannung?: number | null;
  kurzschlussstrom?: number | null;
  schutzart?: string | null;
  netzform?: string | null;
}

/**
 * Eine Position der Verteilerstückliste (V-E2).
 *
 * `verlustHerkunft` ist die wichtigste Angabe der Zeile: 'hersteller' heißt
 * belegt, 'festlegung' heißt üblicher Wert. Ohne sie sähe eine abgeschätzte
 * Wärmebilanz aus wie eine gerechnete.
 */
export interface Elektrostuecklistenposition {
  nummer: number;
  geraeteart: string;
  bezeichnung: string;
  anzahl: number;
  teJeStueck: number | null;
  teGesamt: number | null;
  verlustJeStueckW: number | null;
  verlustGesamtW: number | null;
  verlustHerkunft: string | null;
  regel: string;
}

export interface Elektroklemme {
  leiste: string;
  nummer: number;
  ader: string;
  kabeltyp: string | null;
  querschnittMm2: number | null;
  ziel: string | null;
}

/** Die Wärmebilanz nach DIN EN 61439-1. */
export interface Elektroverteilerwaerme {
  verlustleistungGeraeteW: number;
  belastungsfaktorRdf: number;
  wirksameVerlustleistungW: number;
  kuehlflaecheM2: number;
  waermeuebergangWM2K: number;
  raumtemperaturC: number;
  grenztemperaturC: number;
  zulaessigeUebertemperaturK: number;
  uebertemperaturK: number;
  abfuhrUeberGehaeuseW: number;
  lueftungsbedarfW: number;
  luftstromM3H: number;
  verfahren: string;
  normbezug: string;
  befund: string;
}

/**
 * Der Belüftungsnachweis EINES Aufstellraums nach AGI J11.
 *
 * Beide Querschnitte stehen darin: `zuluftquerschnittM2` ist der
 * maßgebende (Festlegung des Fachplaners vom 19.09.2026 — es gilt die
 * Zuluftdichte des Belüftungsblatts), `querschnittQuellblattM2` der Wert,
 * den die Planungsunterlage mit ihren 1,19 kg/m³ druckt.
 *
 * Eine Fläche allein ist immer plausibel; erst die zweite zeigt, woran sie
 * hängt — und eine unbezifferte Abweichung von einer Planungsunterlage ist
 * ein Fehler, eine bezifferte eine Entscheidung.
 */
export interface Trafobelueftungsnachweis {
  raumnummer: string;
  geschoss: string | null;
  trafos: string[];
  ansaetze: {
    thermischHoeheM: number;
    lufterwaermungK: number;
    aussentemperaturC: number;
    /** NULL heißt: die Zuluftdichte des Blatts, ρ(ϑ_A). */
    luftdichteKgM3: number | null;
    spezWaermekapazitaetJKgK: number;
    ausflusszahl: number;
    abluftZuschlag: number;
    quellblattDichteKgM3: number;
  };
  verlustleistungKw: number;
  zulufttemperaturC: number;
  ablufttemperaturC: number;
  zuluftdichteKgM3: number;
  abluftdichteKgM3: number;
  luftdichteAngesetztKgM3: number;
  differenzdruckPa: number;
  geschwindigkeitMs: number;
  luftmengeM3H: number;
  zuluftquerschnittM2: number;
  abluftquerschnittM2: number;
  luftmengeQuellblattM3H: number;
  querschnittQuellblattM2: number;
  kette: string;
}

export interface Trafobelueftungslauf {
  verlustleistungGesamtKw: number;
  hinweise: string[];
  nachweise: Trafobelueftungsnachweis[];
}

export interface Elektroverteilerdoku {
  kennzeichen: string;
  teBelegt: number;
  teMitReserve: number;
  teJeFeld: number | null;
  felderBedarf: number | null;
  stueckliste: Elektrostuecklistenposition[];
  klemmen: Elektroklemme[];
  waerme: Elektroverteilerwaerme | null;
  befunde: string[];
}

/** Ein Betriebsmittel der Hutschiene (Paket VT1/VT3). */
export interface Elektroverteilerbestueckungszeile {
  kennzeichen: string;
  art: string;
  bezeichnung: string | null;
  bemessungsstromA: number | null;
  charakteristik: string | null;
  polzahl: number | null;
  ausloesestromMa: number | null;
  verbraucher: number | null;
  auslastungProzent: number | null;
  raumnummer: string | null;
}

/** Ein Stromkreis des Verteilers, wie ihn die Kreisbildung gebildet hat. */
export interface Elektroverteilerplankreis {
  kennzeichen: string;
  bezeichnung: string | null;
  verwendung: string | null;
  schutzorgan: string | null;
  bemessungsstromA: number | null;
  polzahl: number | null;
  querschnitt: number | null;
  raumnummer: string | null;
}

/** Ein Verteiler, der an diesem Verteiler hängt (`oberId`). */
export interface Elektroverteilernachgelagert {
  id: string;
  kennzeichen: string;
  bezeichnung: string | null;
  art: string;
  raumnummer: string | null;
  geschoss: string | null;
}

/**
 * Bestückung, Kreise und Kennzahlen eines Verteilers (Paket VT3) - dieselbe
 * Aufbereitung, aus der auch ELT-VTP entsteht.
 */
export interface Elektroverteilerplandaten {
  kennzeichen: string;
  bezeichnung: string | null;
  art: string;
  raumnummer: string | null;
  geschoss: string | null;
  fassung: number;
  berechnetAm: string | null;
  betriebsmittelGesamt: number;
  kreiseGebildet: number;
  verbraucherGesamt: number;
  hoechsteAuslastungProzent: number;
  sicherungenUeber80: number;
  reserveplaetze: number;
  reserveanteilProzent: number;
  teJeReihe: number;
  stromlaufplanVorhanden: boolean;
  hinweise: string[];
  bestueckung: Elektroverteilerbestueckungszeile[];
  kreise: Elektroverteilerplankreis[];

  /**
   * Warum die Bestückung leer ist — `null`, solange sie es nicht ist.
   *
   * Bis zum 19.09.2026 kam an dieser Stelle ein HTTP 404, und die Seite
   * zeigte einen Fehlerkasten für 48 Verteiler, an denen planmäßig nichts
   * zu bestücken ist. 404 heißt „diesen Verteiler gibt es nicht" — eine
   * Falschaussage, die einen Planer nach einem Mangel suchen lässt, den es
   * nicht gibt.
   *
   * `einspeiseebene` | `it` | `nicht-gebildet`
   */
  leergrund: string | null;

  /** Derselbe Grund als Satz, fertig zum Anzeigen. */
  leergrundText: string | null;

  /**
   * Was an diesem Verteiler hängt. An einer Einspeiseebene ist DAS der
   * Inhalt, den ein Planer erwartet — die NSHV trägt 74 nachgelagerte
   * Verteiler und keinen Verbraucher.
   */
  nachgelagerte: Elektroverteilernachgelagert[];

  /** Nur bei Art `it`: Zeilen in `elektro_it_bestueckung` (Migration 0590). */
  itBetriebsmittel: number;
}

export interface Elektrostromkreis {
  id: string;
  kennzeichen: string;
  bezeichnung: string | null;
  verwendung: string | null;
  schutzorgan: string | null;
  bemessungsstrom: number | null;
  polzahl: number | null;

  /**
   * Belegte Teilungseinheiten. Ein B16/1 eine, ein C16/3 drei — wer in Kreisen
   * statt in Einheiten rechnet, unterschätzt einen Verteiler mit
   * Drehstromabgängen um den Faktor drei.
   */
  teilungseinheiten: number | null;

  /** Die Einbaureihenfolge im Verteiler. */
  position: number | null;

  // --- Eingangsdaten für die Nachweise ---------------------------------------

  betriebsstrom: number | null;
  leistungsfaktor: number | null;
  gleichzeitigkeit: number | null;
  phase: string | null;

  leiterwerkstoff: string | null;
  belasteteAdern: number | null;
  verlegeart: string | null;
  haeufung: number | null;
  umgebungstemperaturC: number | null;
  cprKlasse: string | null;

  schutzorganHersteller: string | null;
  schutzorganTyp: string | null;
  kennlinie: string | null;
  ausschaltvermoegen: number | null;

  rcdTyp: string | null;
  rcdBemessungsstrom: number | null;
  rcdDifferenzstrom: number | null;
  rcdPolzahl: number | null;

  zusatzgeraet: string | null;
  klemmeN: string | null;
  klemmePe: string | null;

  /** 'vorschlag' | 'gepflegt' — entscheidet, ob das Gate ihn als geprüft ansieht. */
  nachweisHerkunft: string | null;

  /**
   * Woher der Abgang stammt: 'hand' | 'import' | 'abgeleitet' | 'msr'.
   *
   * 'msr' heißt: Er trägt einen Automationsschrank der Gebäudeautomation,
   * seine Leistung kommt aus msr_leistungsbilanz und wird bei jedem
   * Ableitungslauf nachgezogen.
   */
  herkunft?: string | null;

  /** Der ASP hinter dem Abgang — nur bei Herkunft 'msr' gesetzt. */
  msrAspId?: string | null;
  querschnitt: number | null;
  leitungsart: string | null;
  laenge: number | null;
  istSicherheit: boolean;
  hinweis: string | null;
  bauteile: number;
}

export interface Elektrostromkreisangabe {
  kennzeichen?: string | null;
  bezeichnung?: string | null;
  verwendung?: string | null;
  schutzorgan?: string | null;
  bemessungsstrom?: number | null;
  polzahl?: number | null;
  querschnitt?: number | null;
  leitungsart?: string | null;
  laenge?: number | null;
  istSicherheit?: boolean | null;
  hinweis?: string | null;
}

export interface Elektrotyp {
  bauteilart: string;
  bezeichnung: string;
  brauchtDatenblatt: boolean;
  familie: string | null;
  typ: string | null;
  anzahl: number;
  mitDatenblatt: number;
  datasheetId: string | null;
  verweis: string | null;
  herstellerUrl: string | null;
  offen: number;
  zugewiesen: number;
  zuPruefen: number;
  werte: { feldschluessel: string; wert: string | null; herkunft: string }[];
}

/** Ein Knoten im Anlagenbaum. */
export interface Elektroanlagenknoten {
  schluessel: string;
  name: string;
  hinweis: string | null;
  anzahl: number;
  arten: {
    bauteilart: string; bezeichnung: string; anzahl: number;
    ohneKreis: number; offen: number;
  }[];
  kinder: Elektroanlagenknoten[];
}

// --- Produktseite ------------------------------------------------------------

/** Eine Zeile der Geräteliste. */
export interface Geraetezeile {
  id: string;
  revitElementId: number | null;
  bauteilart: string;
  familie: string | null;
  typ: string | null;
  hersteller: string | null;
  modell: string | null;
  raumnummer: string | null;
  raumname: string | null;
  geschossgruppe: string | null;
  datenstand: string | null;
  lageX: number | null;
  lageY: number | null;
  lageZ: number | null;
  /** Anzahl gepflegter Werte. */
  werte: number;
  /** Anzahl Hydraulikkreise. Null heißt: noch keine Auslegung möglich. */
  kreise: number;
  /** Der öffentliche, signierte Verweis auf die Produktseite. */
  verweis: string | null;
}

/** Ein Betriebspunkt eines Hydraulikkreises. */
export interface Produktpunkt {
  id: string;
  modus: string;
  modusDe: string | null;
  /** Der Punkt, nach dem dimensioniert wird. Genau einer je Kreis. */
  istAuslegung: boolean;
  leistungW: number | null;
  eintrittC: number | null;
  austrittC: number | null;
  spreizung: number | null;
  massenstromKgH: number | null;
  volumenstromM3H: number | null;
  druckverlustPa: number | null;
  aufnahmeW: number | null;
  arbeitszahl: number | null;
  herkunft: string;
  /** Ob Leistung, Spreizung und Massenstrom zusammenpassen. */
  stimmig: boolean | null;
  quelle: string | null;
  hinweis: string | null;
}

/** Ein Hydraulikkreis eines Geräts. */
export interface Produktkreis {
  id: string;
  kreisart: string;
  bezeichnung: string | null;
  netzkreis: string | null;
  medium: string | null;
  glykolProzent: number | null;
  waermekapazitaetWsKgK: number | null;
  dichteKgM3: number | null;
  viskositaetMm2S: number | null;
  stoffwertBei: number | null;
  anschluss: string | null;
  /** Ob der Kreis im Modell an einer Leitung hängt. */
  amNetz: boolean;
  quelle: string | null;
  hinweis: string | null;
  betriebspunkte: Produktpunkt[];
}

export interface Produktfeld {
  feldschluessel: string;
  bezeichnung: string;
  einheit: string | null;
  art: string;
  wert: string | null;
  zahlwert: number | null;
  herkunft: string;
  /** gruen aus dem Modell · gelb nur Datenbank · blau gerechnet · grau entwertet */
  farbe: string;
  istGerechnet: boolean;
  imModell: string | null;
  abweichend: boolean;
  hinweis: string | null;
}

export interface Produktdaten {
  id: string;
  revitElementId: number | null;
  uniqueId: string | null;
  ifcGuid: string | null;
  bauteilart: string;
  bauteilbezeichnung: string | null;
  /** Bauform des Schemas: zweikreis, verbraucher, fancoil, armatur … */
  schemaart: string | null;
  familie: string | null;
  typ: string | null;
  bezeichnung: string | null;
  hersteller: string | null;
  modell: string | null;
  artikelnummer: string | null;
  raumnummer: string | null;
  raumname: string | null;
  geschoss: string | null;
  geschossgruppe: string | null;
  kostengruppe: string | null;
  kreis: string | null;
  lageX: number | null;
  lageY: number | null;
  lageZ: number | null;
  datenblattUrl: string | null;
  herstellerUrl: string | null;
  /**
   * Das Anlagenkennzeichen (AKS) — und der Grund, wenn keines dasteht
   * (Paket S8). Nur die Sanitaerseite liefert sie heute; bei den uebrigen
   * Gewerken bleiben sie `undefined` und die Kopfzeile zeigt nichts.
   */
  aksSchluessel?: string | null;
  aksEinstufung?: string;
  aksGrund?: string;
  aksBauart?: string;
  aksFestlegung?: string;

  /** Nur auf dem internen Weg gefüllt. */
  datenstand: string | null;
  datenstandGrund: string | null;
  gelesenAm: string | null;
  verweis: string | null;
  gruppen: { gruppe: string; felder: Produktfeld[] }[];

  /** Nur bei Lüftungskomponenten: die Stützpunkte der Baureihe. */
  kennfeld?: {
    groesse: string | null;
    /** Wo der Hersteller die Aerodynamik gibt statt eines Betriebspunkts. */
    freieFlaecheM2?: number | null;
    zeta?: number | null;
    volumenstromM3h: number | null;
    druckverlustPa: number | null;
    lwaDb: number | null;
    herkunft: string;
    quelle: string | null;
    geprueft: boolean;
    abgerufenAm: string | null;
    hatOktavband: boolean;
  }[];

  /** Der abgeleitete Betriebspunkt dieser Komponente. */
  betriebspunkt?: {
    volumenstromM3h: number | null;
    /** modell · kanalnetz — woher der Volumenstrom stammt. */
    volumenstromHerkunft?: string | null;
    dichteKgM3?: number | null;
    druckverlustPa: number | null;
    lwaDb: number | null;
    /** datenblatt · interpoliert · keine */
    herkunft: string;
    istGerechnet: boolean;
    faktor: number | null;
    hinweis: string;
  };
  lueftungsregister: {
    plantId: string;
    anlage: string | null;
    kennzeichnung: string | null;
    standort: string;
    gruppe: string | null;
    amNetz: boolean;
    abstandMm: number;
    guete: string;
    einheitId: string | null;
    einheitArt: string | null;
    einheit: string | null;
    betriebspunkte: {
      id: string;
      reihenfolge: number;
      modus: string | null;
      modusDe: string | null;
      stimmig: boolean | null;
      dataJson: string | null;
      herkunftJson: string | null;
    }[];
  } | null;
  kreise: Produktkreis[];
}

// --- Heiz-/Kühllast ----------------------------------------------------------

/** Ein Validierungsbefund der Heiz-/Kühllast. */
export interface Heizlastbefund {
  id: string;
  bereich: string;
  schluessel: string;
  /** fehler | warnung | hinweis — nur `fehler` verhindert eine Freigabe. */
  schwere: string;
  roomId: string | null;
  raumnummer: string | null;
  bauteilId: string | null;
  zuordnungId: string | null;
  titel: string;
  text: string | null;
  vorschlag: string | null;
  /** offen | behoben | akzeptiert */
  status: string;
  akzeptiertVon: string | null;
  akzeptiertAm: string | null;
  akzeptiertBegruendung: string | null;
  erkanntAm: string;
}

/**
 * Die Kreislaufverbunde eines Projekts.
 *
 * `geraeteplaetze` sind Plätze im Schema, KEINE Gerätezuordnung: Welches
 * körperliche Gerät auf welchem Platz sitzt, geht aus den Typdatenblättern
 * nicht hervor.
 */
export interface KvsUebersicht {
  gruppen: {
    anlage: string;
    saisons: {
      saison: string; ausrichtung: string | null; aussentempC: number | null;
      glykolProzent: number | null; zuluftM3h: number | null;
      zuluftKw: number | null; zuluftDruckverlustKpa: number | null;
      zuluftSoleM3h: number | null; abluftM3h: number | null;
      abluftKw: number | null; abluftDruckverlustKpa: number | null;
      abluftSoleM3h: number | null; rueckwaermzahlTrockenPct: number | null;
      energiewirkungsgradPct: number | null; zeichnungsdatum: string | null;
      datenstatus: string;
    }[];
    geraeteplaetze: {
      geraeteslot: string; luftart: string; volumenstromM3h: number | null;
      leistungKw: number | null; eintrittC: number | null;
      austrittC: number | null;
    }[];
  }[];
  typdatenblaetter: {
    angebotsnummer: string; bezeichnung: string; variante: string | null;
    volumenstromM3h: number | null; ventilatorAnzahl: number | null;
    ventilatorLeistungKw: number | null; systemwirkungsgradPct: number | null;
    sfpWsM3: number | null; sfpIntWm3s: number | null;
    euroventWinter: string | null; euroventSommer: string | null;
    /** istWrg trennt Rückgewinnung von Klimatisierung. */
    register: {
      luftart: string; komponente: string; istWrg: boolean;
      betriebspunkt: string | null; nennleistungKw: number | null;
      sensibelKw: number | null; latentKw: number | null;
      eintrittC: number | null; austrittC: number | null;
      mediumTemp1C: number | null; mediumTemp2C: number | null;
      glykolProzent: number | null;
    }[];
  }[];
  anlagen: {
    id: string; anlage: string; ebene: string | null;
    raumnummer: string | null; raumname: string | null;
    geraetebezeichnung: string | null; zuluft: string | null;
    abluft: string | null; systemgradProzent: number | null;
    /** Signierter Verweis auf revit.tplusv.de/a/… — ohne Anmeldung nutzbar. */
    verweis: string | null;
  }[];
}

/** Was ein Berechnungslauf zurückmeldet. */
export interface HeizlastLaufErgebnis {
  laufId: string;
  laufnummer: number;
  status: string;
  raeume: number;
  bauteile: number;
  normheizlastW: number;
  vollstaendigkeitProzent: number;
  /** true, solange irgendein Vorbehalt besteht — dann keine Freigabe. */
  istVorlaeufig: boolean;
  zusammenfassung: string[];
}

/** Ein Raum- oder Zonenergebnis eines Laufs. */
export interface HeizlastErgebnisRaum {
  id: string;
  roomId: string | null;
  raumnummer: string | null;
  raumname: string | null;
  ebene: string | null;
  nutzung: string | null;
  /** Gesetzt, wenn diese Zeile eine ZONE ist und keinen einzelnen Raum. */
  zone: string | null;
  istZone: boolean;
  flaecheM2: number | null;
  volumenM3: number | null;
  innentemperaturC: number | null;
  transmissionW: number;
  waermebrueckeW: number;
  /**
   * Die ERHITZERLEISTUNG dieses Raums — eine Anlagengröße.
   *
   * Seit der Festlegung des Fachplaners vom 16.09.2026 (Frage 14) NICHT
   * mehr Teil von normheizlastW.
   */
  lueftungW: number;
  infiltrationW: number;
  /** Hygienischer Mindestvolumenstrom nach EN 12831-1 6.3.3 (Frage 12). */
  mindestluftM3h: number | null;
  /** Sein Wärmeverlust — TEIL der Gebäudeheizlast. */
  mindestluftW: number | null;
  /**
   * Die Raumlast der Lüftung, wo die Zuluft kälter ist als der Raum
   * (Frage 14). Steht NEBEN der Gebäudeheizlast, nicht in ihr.
   */
  raumlueftungW: number | null;
  /** Der Erdreichanteil nach dem ALTEN Ansatz (b = 0,30) — Frage 13. */
  erdreichAltW: number | null;
  /** Die GEBÄUDEheizlast: ohne den Lüftungsanteil der Anlagen. */
  normheizlastW: number;
  spezifischWm2: number | null;
  /** Volumenbezogen — bei Hallen die aussagekräftigere Zahl. */
  spezifischWm3: number | null;
  /** Wärmeverlustkoeffizient Transmission [W/K] — temperaturfrei. */
  htWk: number | null;
  /** Wärmeverlustkoeffizient Lüftung [W/K]. */
  hvWk: number | null;
  /** Aufheizleistung Φ_RH bei unterbrochenem Betrieb. */
  aufheizleistungW: number | null;

  /** Der Erdreichanteil des HEUTIGEN Ansatzes — die Gegenzahl zur Norm. */
  erdreichW: number;

  // ═══ Die Vergleichsvariante je Raum (Migration 0488) ═══════════════════
  //
  // „Heizlast nach Norm mit üblichen Werten" — NICHT das Ergebnis. NULL
  // heißt „nicht gerechnet", 0 heißt „gerechnet und null"; bei genau diesen
  // Posten ist der Unterschied der ganze Befund.

  /** H-02: A · f_RH nach DIN/TS 12831-1. */
  vergleichAufheizW: number | null;

  /** H-03: n_min · V — nur wo keine mechanische Außenluft anliegt. */
  vergleichMindestluftM3h: number | null;
  vergleichMindestluftW: number | null;

  /** H-16: der Erdreichanteil nach EN ISO 13370. */
  vergleichErdreichW: number | null;

  /** Tritt NICHT an die Stelle von normheizlastW. */
  vergleichNormheizlastW: number | null;
  vergleichHinweis: string | null;
  aussenluftM3h: number | null;
  wrgGradNenn: number | null;
  wrgGradWirksam: number | null;
  wrgErsatzwert: boolean;
  anzahlBauteile: number;
  bauteileOhneUWert: number;
  vollstaendigkeitProzent: number | null;
  warnungen: string | null;
  /** Der Vorbehalt eines Ersatzkörpers — darf nicht verschwinden. */
  vorbehalt: string | null;
}

export interface HeizlastLaufDetail {
  lauf: HeizlastLaufKopf;
  gruppen: {
    gruppe: string | null; anzahl: number; flaecheM2: number;
    transmissionW: number; waermebrueckeW: number;
  }[];
  parameter: {
    schluessel: string; zahlwert: number | null; textwert: string | null;
    herkunft: string; istBestaetigt: boolean;
  }[];
  raeume: HeizlastErgebnisRaum[];
}

/** Ein Bauteil in der Bilanz eines Raums. */
export interface HeizlastErgebnisBauteil {
  id: string;
  /** AW, AF, DA, IW … — beschreibt die Lage, nicht das Bauteil. */
  kuerzel: string | null;
  /** N, NO, O … NULL bei waagerechten Flächen. */
  himmelsrichtung: string | null;
  azimutGrad: number | null;
  neigungGrad: number | null;
  /** A — vor Abzug der Öffnungen. */
  bruttoflaecheM2: number | null;
  abzugsflaecheM2: number | null;
  /** U · A · f_x [W/K]. */
  htWk: number | null;
  bauteilart: string | null;
  bauteilgruppe: string | null;
  typ: string | null;
  randbedingung: string | null;
  uWertWmk: number | null;
  /** „ersatz" heißt: nach Vorgabe des Fachplaners gesetzt, kein Modellwert. */
  uWertHerkunft: string | null;
  flaecheM2: number | null;
  innentemperaturC: number | null;
  bezugstemperaturC: number | null;
  deltaTK: number | null;
  korrekturfaktorB: number | null;
  waermebrueckeWm2k: number | null;
  transmissionW: number;
  waermebrueckeW: number;
  istGerechnet: boolean;
  hinweis: string | null;
}

/** Die Lüftungszeile eines Raums, mit der Anlage dahinter. */
export interface HeizlastRaumLueftungszeile {
  id: string;
  herkunft: string | null;
  zuluftM3h: number | null;
  abluftM3h: number | null;
  aussenluftM3h: number | null;
  zulufttemperaturHeizenC: number | null;
  wrgGradProzent: number | null;
  plantId: string | null;
  anlage: string | null;
}

export interface HeizlastRaumBilanz {
  raum: HeizlastErgebnisRaum;
  bauteile: HeizlastErgebnisBauteil[];
  lueftung: HeizlastRaumLueftungszeile[];
}

export interface HeizlastLaufKopf {
  id: string;

  // Die API gibt die Laufzeile als Ganzes zurück; das Projekt stand hier
  // bisher nur nicht geschrieben. Es wird gebraucht, wo ein Lauf ohne
  // Seitenkontext angezeigt wird - etwa im Jahresgang, der von dort aus das
  // Unterlagenpaket „Lasten" anbietet.
  projectId: string;

  laufnummer: number;
  bezeichnung: string | null;
  status: string;
  fall: string;
  aussentemperaturC: number | null;
  anzahlRaeume: number;
  /** Die GEBÄUDEheizlast — ohne den Lüftungsanteil (Frage 14). */
  normheizlastW: number | null;
  transmissionW: number | null;
  waermebrueckeW: number | null;
  /** Die Erhitzerleistung der Lüftungsanlagen — NICHT in normheizlastW. */
  lueftungW: number | null;
  infiltrationW: number | null;
  /** Hygienischer Mindestluftwechsel (Frage 12) — TEIL der Heizlast. */
  mindestluftW: number | null;
  /** Raumlast der Lüftung (Frage 14) — NICHT in normheizlastW. */
  raumlueftungW: number | null;
  /** Erdreichanteil nach dem alten Ansatz b = 0,30 (Frage 13). */
  erdreichAltW: number | null;
  vollstaendigkeitProzent: number | null;
  istVorlaeufig: boolean;
  vorbehalte: string | null;
  methodeVersion: string | null;
  beendetAm: string | null;

  // ═══ Die Vergleichsvariante (Migration 0488) ═══════════════════════════
  //
  // Sie steht NEBEN dem Ergebnis und nicht darin: Welcher Wert gilt, ist
  // eine Festlegung des Fachplaners. NULL heißt: nicht gerechnet.
  vergleichAufheizW: number | null;
  vergleichMindestluftW: number | null;
  vergleichErdreichW: number | null;
  erdreichHeuteW: number | null;
  vergleichNormheizlastW: number | null;
  vergleichHinweis: string | null;

  // --- Jahressimulation, erst nach ihrem Lauf belegt ----------------------
  jahresbedarfMwh: number | null;
  jahresgewinnMwh: number | null;
  heizstunden: number | null;
  /** Jahresarbeit geteilt durch Auslegungsleistung. */
  vollbenutzung: number | null;

  // --- TL1 (Migration 1280): Zweck und Kennzeichnung --------------------
  /** auslegung | variante | pruefung; null = vor 1280 gerechnet. */
  zweck?: string | null;
  istMassgebend?: boolean;
  massgebendGesetztVon?: string | null;
  massgebendGesetztAm?: string | null;
  massgebendBegruendung?: string | null;
}

/**
 * Standort und Klimastand.
 *
 * `datensatz` ist der LEITdatensatz — der einzige, den der Rechenkern für die
 * Pflichtangaben liest. `weitereDatensaetze` sind Auslegungstage, Messreihen
 * und Metadaten; ein Auslegungstag ist ein Lastfall, den man rechnet, kein
 * Datensatz, den man zuweist.
 */
/* ───────────────────────────────────────────────────────────────────────────
   Raumtemperaturen (Paket RT1)

   Die Vorlage des Fachplaners vom 26.06.2026 mit ihren Bändern, die Regeln,
   nach denen Räume ihnen zugeordnet werden, und das Ergebnis je Raum.

   Die drei Herkünfte sind der Kern: `fachplaner` (Bereich der Vorlage),
   `norm` (Normwert der Nutzung) und `fehlt` (KEIN Wert — gezählt und
   benannt). Ein erfundener Sollwert ist schlimmer als eine sichtbare Lücke.
   ─────────────────────────────────────────────────────────────────────── */

export interface RaumtempBereich {
  code: string;
  bereich: string;
  nutzung: string | null;
  winterUntenC: number | null;
  winterObenC: number | null;
  sommerUntenC: number | null;
  sommerObenC: number | null;
  feuchteUntenProzent: number | null;
  feuchteObenProzent: number | null;
  hinweis: string | null;
  istAuslegungsfall: boolean;
  quelle: string;
  stand: string | null;
}

export interface RaumtempVorlagehinweis {
  schluessel: string;
  wortlaut: string;
  quelle: string;
}

export interface RaumtempRegel {
  schluessel: string;
  reihenfolge: number;
  art: string;
  muster: string;
  /** ausschluss | bereich | einordnung | norm */
  ziel: string;
  bereichCode: string | null;
  einordnung: string | null;
  normTempHeizenC: number | null;
  normTempKuehlenC: number | null;
  normQuelle: string | null;
  guete: string;
  begruendung: string | null;
  /** Wie viele Räume diese Musterzeile getroffen hat. */
  treffer: number;
}

export interface RaumtempRaumzeile {
  roomId: string;
  raumnummer: string | null;
  raumname: string | null;
  ebene: string | null;
  /** fachplaner | norm | fehlt */
  herkunft: string;
  bereich: string | null;
  regel: string | null;
  muster: string | null;
  guete: string | null;
  winterUntenC: number | null;
  winterObenC: number | null;
  sommerUntenC: number | null;
  sommerObenC: number | null;
  bandlageHeizen: string | null;
  bandlageKuehlen: string | null;
  tempHeizenC: number | null;
  tempKuehlenC: number | null;
  bestandTempHeizenC: number | null;
  bestandTempKuehlenC: number | null;
  bestandHerkunft: string | null;
  abweichungHeizenK: number | null;
  abweichungKuehlenK: number | null;
  /** gleich | abweichung | neu | ohne_wert */
  abgleich: string | null;
  /** gesetzt | behalten | ohne_wert */
  wirkung: string | null;
  wirkungGrund: string | null;
  quelle: string | null;
  begruendung: string | null;
}

export interface RaumtempLaufErgebnis {
  raeume: number;
  fachplaner: number;
  norm: number;
  fehlt: number;
  gleich: number;
  abweichung: number;
  neu: number;
  geschriebenHeizen: number;
  geschriebenKuehlen: number;
  bestandBehalten: number;
  bandlageHeizen: string;
  bandlageKuehlen: string;
  hinweise: string[];
}

export interface RaumtempStand {
  /** unten | oben — Festlegung F70. */
  bandlageHeizen: string | null;
  bandlageKuehlen: string | null;
  quote: {
    raeume: number;
    fachplaner: number;
    norm: number;
    fehlt: number;
    gleich: number;
    abweichung: number;
    neu: number;
    gesetzt: number;
    behalten: number;
  };
  bereiche: RaumtempBereich[];
  hinweise: RaumtempVorlagehinweis[];
  regeln: RaumtempRegel[];
  raeume: RaumtempRaumzeile[];
}

export interface HeizlastStandortstand {
  standort: {
    herkunft: string;
    guete: string;
    bezeichnung: string | null;
    breiteGrad: number | null;
    laengeGrad: number | null;
    hoeheMuNn: number | null;
    quellEpsg: string | null;
    istGepflegt: boolean;
    istBestaetigt: boolean;
    quelle: string | null;
    hinweis: string | null;
  } | null;
  datensatz: {
    id: string;
    bezeichnung: string;
    station: string | null;
    stationskennung: string | null;
    verwendung: string;
    istLeitdatensatz: boolean;
    abstandKm: number | null;
    stationHoehe: number | null;
    version: string | null;
    stand: string | null;
    /** Solange gesetzt, trägt jeder Lauf `preliminary_climate_data`. */
    istVorlaeufig: boolean;
    datenstatus: string | null;
    quelle: string | null;
    zitierhinweis: string | null;
    hinweis: string | null;
  } | null;
  weitereDatensaetze: {
    id: string;
    bezeichnung: string;
    verwendung: string;
    station: string | null;
    stationskennung: string | null;
    abstandKm: number | null;
    datenstatus: string | null;
    stand: string | null;
    quelle: string | null;
    zitierhinweis: string | null;
    hinweis: string | null;
    zeitschritte: number;
  }[];
  klima: {
    feldschluessel: string;
    bezeichnung: string;
    gruppe: string;
    einheit: string | null;
    fuerHeizlast: boolean;
    fuerKuehllast: boolean;
    istPflicht: boolean;
    hinweis: string | null;
    wert: {
      zahlwert: number | null;
      textwert: string | null;
      herkunft: string;
      /** Ein Annahmewert verhindert die Freigabe. */
      istAnnahme: boolean;
      quelle: string | null;
      hinweis: string | null;
    } | null;
  }[];
}

/** Eine Mengenangabe aus einer externen Fachsimulation. */
export interface HeizlastExternwert {
  id: string;
  quelle: string;
  quelleStand: string | null;
  schluessel: string;
  /** personenbelegung | luftmenge | interne_last | kleinkaelte | warmwasser | annahme */
  kategorie: string;
  bereich: string | null;
  feldschluessel: string;
  bezeichnung: string;
  zahlwert: number | null;
  textwert: string | null;
  einheit: string | null;
  /** Der Statusbegriff der Quelle, wörtlich — z. B. `missing`. */
  datenstatus: string;
  istPrimaer: boolean;
  istHistorisch: boolean;
  /** raum_eindeutig | bereich_mehrere_raeume | nicht_zugeordnet | gebaeude */
  zuordnungsart: string;
  roomId: string | null;
  raumnummer: string | null;
  raumbuchCode: string | null;
  raeumeAnzahl: number | null;
  /** Der Raumbuchwert zum Abgleichszeitpunkt — Kopie, keine Quelle. */
  raumbuchWert: number | null;
  bemerkung: string | null;
  hinweis: string | null;
}

export interface HeizlastExternwerte {
  anzahl: number;
  jeZuordnungsart: { zuordnungsart: string; anzahl: number }[];
  jeDatenstatus: { datenstatus: string; anzahl: number }[];
  werte: HeizlastExternwert[];
}

/** Welche Räume ein Bereich der Fremdsimulation umfasst. */
export interface HeizlastBereich {
  bereich: string;
  raeume: number;
  /** eindeutig | plausibel | unklar */
  guete: string[];
  bestaetigt: number;
  raeumeMitPersonenzahl: number;
  personenSumme: number;
  zuordnungsregeln: string[];
  raumliste: {
    roomId: string;
    raumnummer: string | null;
    raumname: string | null;
    guete: string;
    hinweis: string | null;
    personen: number | null;
  }[];
}

/** Ein externer Vergleichswert. Kein Rechenkern liest ihn als Eingang. */
export interface HeizlastReferenzwert {
  id: string;
  quelle: string;
  quelleStand: string | null;
  /** kuehllast | heizlast */
  fall: string;
  szenario: string;
  bezeichnung: string;
  vergleichsebene: string;
  /** NULL heißt: der Bericht nennt keine Zahl. */
  leistungKw: number | null;
  textwert: string | null;
  datenstatus: string;
  /** Nur `true` darf als Vergleichsziel dienen. */
  istAktuell: boolean;
  toleranzProzent: number | null;
  bemerkung: string | null;
  handlungsbedarf: string | null;
}

export interface HeizlastReferenzwerte {
  toleranzProzent: number | null;
  hinweis: string;
  vergleichsfaehig: {
    fall: string; szenario: string;
    leistungKw: number; toleranzProzent: number | null;
  }[];
  werte: HeizlastReferenzwert[];
}

export interface HeizlastUebersicht {
  lesestand: {
    modellname: string | null;
    exportiertAm: string | null;
    anzahlBauteile: number;
    anzahlRaeume: number;
    anzahlSegmente: number;
    anzahlSchichten: number;
    status: string;
    createdAt: string;
  } | null;
  nachweis: {
    kennung: string;
    nachweisindex: string;
    stand: string | null;
    bezeichnung: string;
  } | null;
  bauteile: {
    bauteilart: string;
    anzahl: number;
    mitNachweistyp: number;
    flaecheM2: number;
  }[];
  zuordnungen: {
    randbedingung: string;
    anzahl: number;
    flaecheM2: number;
    mitUWert: number;
  }[];
  befunde: { schwere: string; status: string; anzahl: number }[];
  offeneFehler: number;
  istFreigabefaehig: boolean;
}

export interface Zuordnungsergebnis {
  erfolg: boolean;
  raeume: number;
  zuordnungen: number;
  ausBegrenzung: number;
  ausHost: number;
  ausUeberschneidung: number;
  ausXyFallback: number;
  vonHandErhalten: number;
  entfernt: number;
  ohneRandbedingung: number;
  hinweise: string[];
}

export interface GegVerknuepfungsergebnis {
  erfolg: boolean;
  nachweis: string | null;
  bauteile: number;
  ueberCode: number;
  ueberAlias: number;
  ueberTypname: number;
  ohneTreffer: number;
  ohneKennung: number;
  annahmen: number;
  erdkontakt: number;
  ohneWerte: number;
  befunde: number;
  hinweise: string[];
}

export interface Pruefergebnis {
  fehler: number;
  warnungen: number;
  hinweise: number;
  akzeptiert: number;
  zusammenfassung: string[];
}

// =============================================================================
//  Kühllast
// =============================================================================

export interface KuehllastLaufKopf {
  id: string;
  laufnummer: number;
  bezeichnung: string | null;
  status: string;
  fall: string;
  auslegungMonat: number | null;
  auslegungTag: number | null;
  aussentemperaturMaxC: number | null;
  anzahlRaeume: number;
  anzahlStunden: number;
  /** Die Gebäudespitze — NICHT die Summe der Raumspitzen. */
  spitzeTrockenW: number | null;
  spitzeFeuchtW: number | null;
  spitzeStunde: number | null;
  summeRaumspitzenW: number | null;
  /** Spitze geteilt durch Summe der Raumspitzen. */
  gleichzeitigkeit: number | null;
  opakW: number | null;
  transparentW: number | null;
  strahlungW: number | null;
  innenW: number | null;
  lueftungW: number | null;
  vollstaendigkeitProzent: number | null;
  istVorlaeufig: boolean;
  vorbehalte: string | null;
  methodeVersion: string | null;
  beendetAm: string | null;

  /**
   * Der führende Klimadatensatz (Temperatur) des Auslegungstags — seit
   * Migration 0564 geschrieben. NULL an jedem Lauf davor.
   */
  klimadatensatzId: string | null;
  /** S | M | L | XL — Raumtyp der Speichermassennäherung. */
  speichermasseKlasse: string | null;
  /**
   * AUSWEISGRÖSSE: um so viel senkte die Speichermasse die Gebäudespitze —
   * ob sie ANGEWENDET wurde, sagt der Parameter kl_speichermasse_gilt und
   * nicht dieses Feld.
   */
  speichermasseDaempfungW: number | null;
  /** AUSWEISGRÖSSE, dieselbe Regel: kl_nachtlueftung_gilt. */
  nachtlueftungEntlastungW: number | null;
  /**
   * Die RAUM-Entfeuchtungsaufgabe (Personen plus Zuluft NACH Behandlung).
   * spitzeFeuchtW daneben ist die rohe Außenluftfeuchte und KEINE Aufgabe
   * für den Raum — Befund H-28.
   */
  raumEntfeuchtungW: number | null;

  // --- TL1 (Migration 1280): Zweck und Kennzeichnung --------------------
  zweck?: string | null;
  istMassgebend?: boolean;
  massgebendGesetztVon?: string | null;
  massgebendGesetztAm?: string | null;
  massgebendBegruendung?: string | null;
}

export interface KuehllastErgebnisRaum {
  id: string;
  roomId: string | null;
  raumnummer: string | null;
  raumname: string | null;
  ebene: string | null;
  nutzung: string | null;
  zone: string | null;
  istZone: boolean;
  flaecheM2: number | null;
  volumenM3: number | null;
  solltemperaturC: number | null;
  personen: number | null;
  /** raumwert | profil — „profil" heißt: aus der Personendichte abgeleitet. */
  personenQuelle: string | null;
  spitzeTrockenW: number | null;
  spitzeFeuchtW: number | null;
  spitzeStunde: number | null;
  spezifischWm2: number | null;
  spezifischWm3: number | null;
  opakW: number | null;
  transparentW: number | null;
  strahlungW: number | null;
  personenTrockenW: number | null;
  personenFeuchtW: number | null;
  beleuchtungW: number | null;
  geraeteW: number | null;
  lueftungTrockenW: number | null;
  lueftungFeuchtW: number | null;
  aussenluftM3h: number | null;
  wrgGrad: number | null;
  anzahlBauteile: number;
  bauteileOhneUWert: number;
  fensterflaecheM2: number | null;
  fensterOhneGWert: number;
  vollstaendigkeitProzent: number | null;
  /**
   * Die Leistung, die die Kälteerzeugung zur Aufbereitung der Außenluft
   * aufbringen muss — NICHT die Raumkühllast. NULL heißt „keine
   * Kühl-Zulufttemperatur belegt, also keine Anlagenrechnung", nicht
   * „kein Bedarf" (das wäre 0).
   */
  anlagenKaelteleistungW: number | null;
  anlagenKaelteleistungSensibelW: number | null;
  anlagenKaelteleistungLatentW: number | null;
  /** Die eigene Spitzenstunde der Anlagenkälteleistung — NICHT spitzeStunde. */
  anlagenKaelteleistungStunde: number | null;

  /**
   * Die Nennkühlleistung der Raumendgeräte dieses Raums (Fancoil,
   * Heizkühldecke, Deckensegel) — Befund H-20, zweite Hälfte. NULL heißt
   * „kein Gerät im Raum"; 0 hieße „Gerät ohne Leistungsangabe".
   */
  installierteKuehlleistungW: number | null;
  /** Davon sensibel — die Größe, die gegen die trockene Raumlast zählt. */
  installierteKuehlleistungSensibelW: number | null;
  anzahlRaumendgeraete: number;
  /** Installiert sensibel geteilt durch die trockene Raumspitze. */
  deckungsgradProzent: number | null;
  /** raumnummer | NULL — wie die Geräte dem Raum zugeordnet wurden. */
  raumendgeraeteHerkunft: string | null;
  /** Siehe KuehllastLaufKopf.raumEntfeuchtungW (H-28). */
  raumEntfeuchtungW: number | null;

  warnungen: string | null;
  vorbehalt: string | null;
}

/** Eine Anlage im Vergleich Bedarf gegen installierte Kälteleistung. */
export interface KuehllastAnlagenVergleichAnlage {
  plantId: string;
  anlage: string;
  bedarfW: number;
  /** 0, wenn die Anlage kein Change-Over-Register hat — siehe hatRegister. */
  installiertW: number;
  /** False heißt: kein Change-Over-Register, installiertW ist nicht belastbar. */
  hatRegister: boolean;
  differenzW: number;
  uebersteigt: boolean;
}

/**
 * Der Vergleich Bedarf (Auslegungsfall) gegen installierte Kälteleistung
 * (Change-Over-Register aus plant_units, Herstellerdaten). Die Projektsumme
 * ist immer belastbar; jeAnlage nur dort, wo die Raum→Anlage-Zuordnung
 * (heizlast_raumlueftung.PlantId) gesetzt ist — deshalb ist jeAnlage oft
 * leer, auch wenn die Projektsumme steht.
 */
export interface KuehllastAnlagenVergleich {
  anzahlEinheiten: number;
  anzahlAnlagenInstalliert: number;
  bedarfGesamtW: number;
  installiertGesamtW: number;
  differenzGesamtW: number;
  uebersteigtGesamt: boolean;
  jeAnlage: KuehllastAnlagenVergleichAnlage[];
}

/** Ein Raum im Abgleich Raumkühllast gegen die Raumendgeräte (Befund H-20). */
export interface KuehllastRaumendgeraetZeile {
  raumnummer: string | null;
  raumname: string | null;
  ebene: string | null;
  /** Die trockene Raumspitze, auf 0 gekappt. */
  bedarfW: number;
  /** Die SENSIBLE Nennkühlleistung — die Größe, die gegen bedarfW zählt. */
  installiertSensibelW: number;
  installiertGesamtW: number;
  anzahl: number;
  deckungsgradProzent: number | null;
}

/**
 * Der Abgleich gegen die RAUMENDGERÄTE (Fancoil, Heizkühldecke,
 * Deckensegel) — nicht zu verwechseln mit KuehllastAnlagenVergleich: Dort
 * geht es um die Außenluftaufbereitung an der RLT-Anlage, hier um den Raum
 * selbst. Verglichen wird sensibel gegen trocken.
 */
export interface KuehllastRaumendgeraete {
  geraeteGesamt: number;
  /** Geräte, deren Raumnummer kein Raumergebnis dieses Laufs trifft. */
  geraeteOhneRaum: number;
  raeumeMitGeraet: number;
  raeumeUnterdeckt: number;
  raeumeOhneGeraetMitLast: number;
  installiertGesamtW: number;
  installiertSensibelGesamtW: number;
  bedarfGesamtW: number;
  deckungsgradGesamtProzent: number | null;
  unterdeckt: KuehllastRaumendgeraetZeile[];
  ohneGeraet: KuehllastRaumendgeraetZeile[];
  vorbehalt: string;
}

export interface KuehllastStunde {
  stunde: number;
  aussenC: number | null;
  opakW: number;
  transparentW: number;
  strahlungW: number;
  innenW: number;
  lueftungW: number;
  feuchtW: number;
  trockenW: number;
}

export interface KuehllastLaufDetail {
  lauf: KuehllastLaufKopf;
  raeume: KuehllastErgebnisRaum[];
  /** Der Gebäudetagesgang, serverseitig über alle Räume summiert. */
  tagesgang: KuehllastStunde[];
  /** NULL: keine Change-Over-Register im Projekt, kein Vergleich möglich. */
  anlagenVergleich: KuehllastAnlagenVergleich | null;
  /** NULL: kein externer Kälteleistungswert im Projekt (Befund H-20). */
  referenzVergleich: KuehllastReferenzvergleich | null;
  /** NULL: keine Raumendgeräte im Projekt (Befund H-20, zweite Hälfte). */
  raumendgeraete: KuehllastRaumendgeraete | null;
}

export interface KuehllastRaumDetail {
  raum: KuehllastErgebnisRaum;
  stunden: KuehllastStunde[];
}

export interface KuehllastJahresgang {
  tage: {
    monat: number; tag: number;
    maxTrockenW: number; mittelTrockenW: number;
    maxAussenC: number | null; arbeitKwh: number;
  }[];
  monate: {
    monat: number; maxTrockenW: number;
    arbeitKwh: number; kuehlstunden: number;
  }[];
  kennzahlen: {
    stunden: number; kuehlstunden: number; arbeitMwh: number;
    spitzeW: number; spitzeMonat: number; spitzeTag: number;
    spitzeStunde: number; spitzeAussenC: number | null;
    freieKuehlungStunden: number;
  } | null;

  /**
   * Liegt für das Projekt überhaupt eine Klimamessreihe vor?
   *
   * Der Unterschied, den die Oberfläche bis zum 16.09.2026 nicht machte: Ein
   * Lauf ohne Jahresgangzeilen hieß dort „es liegt keine Messreihe vor" —
   * auch dann, wenn die Messreihe 8.760 Stunden hat und nur der Lauf ohne
   * Jahresgang gerechnet wurde. false heißt: rechnen hilft nicht, es fehlen
   * die Klimadaten.
   */
  messreiheVorhanden: boolean;
}

/** Je Lauf die Zahl seiner Jahresgangstunden. */
export interface KuehllastJahresgangBestand {
  bestand: { laufId: string; stunden: number }[];
  messreiheVorhanden: boolean;
}

/** Das Ergebnis eines nachgerechneten Jahresgangs. */
export interface KuehllastJahresgangErgebnis {
  erfolg: boolean;
  laufId: string;
  laufnummer: number;
  raeume: number;
  stunden: number;
  kuehlstunden: number;
  spitzeKw: number;
  spitzeMonat: number;
  spitzeTag: number;
  spitzeStunde: number;
  arbeitMwh: number;
  /** Die im Lauf gespeicherte Gebäudespitze des Auslegungstags. */
  auslegungSpitzeKw: number;
  /** Derselbe Auslegungstag, mit der heutigen Hülle nachgerechnet. */
  gegenprobeSpitzeKw: number;
  /** Gegenprobe gegen gespeicherten Wert. Über 1 % trägt der Lauf einen Vorbehalt. */
  abweichungProzent: number;
  koeffizientenAusHeutigerHuelle: boolean;
  sekunden: number;
  vorbehalte: string[];
  zusammenfassung: string[];
}

export interface KuehllastLaufErgebnis {
  erfolg: boolean;
  laufId: string;
  laufnummer: number;
  raeume: number;
  spitzeKw: number;
  spitzeStunde: number;
  summeRaumspitzenKw: number;
  gleichzeitigkeit: number;
  vorbehalte: string[];
  zusammenfassung: string[];
  /** Die geschriebenen Jahresgangstunden. 0: ohne Jahresgang gerechnet. */
  jahresstunden: number;
}

/** Der Jahresgang des Heizwärmebedarfs. */
export interface HeizlastJahresgang {
  tage: {
    monat: number; tag: number; maxBedarfW: number;
    arbeitKwh: number; gewinnKwh: number; minAussenC: number | null;
  }[];
  monate: {
    monat: number; maxBedarfW: number; arbeitKwh: number;
    gewinnKwh: number; heizstunden: number;
  }[];
  kennzahlen: {
    stunden: number; heizstunden: number;
    bedarfMwh: number; gewinnMwh: number; verlustMwh: number;
    spitzeW: number; spitzeMonat: number; spitzeTag: number;
    spitzeStunde: number; spitzeAussenC: number | null;
  } | null;
}

export interface HeizlastSimulationsergebnis {
  erfolg: boolean;
  laufId: string;
  stunden: number;
  heizstunden: number;
  bedarfMwh: number;
  gewinnMwh: number;
  vollbenutzungH: number;
  zusammenfassung: string[];
}

/** Ein Brandschott an einem einzelnen Durchgang. */
export interface AussparungSchott {
  schottart: string;
  hersteller: string | null;
  system: string | null;
  zulassungNummer: string | null;
  zulassungArt: string | null;
  feuerwiderstand: string | null;
  /** 'gerechnet' heißt vollständig, 'offen' heißt: es fehlt etwas. */
  herkunft: string;
  befund: string | null;
  /**
   * Deckt die Zulassung diese Abschottung? DREI Zustände:
   * `true` gedeckt, `false` passt nicht, `null` ungeprüft.
   *
   * `null` ist kein fehlender Wert — es fehlt die Angabe, gegen die zu
   * prüfen wäre. Bis AS6 stand hier an ALLEN Zeilen `true`, und keine
   * Stelle im Rechenkern konnte je `false` schreiben.
   */
  zulassungPasst: boolean | null;
  /** Welches Kriterium verletzt ist bzw. welche Angabe fehlt. */
  zulassungBefund: string | null;
  /** Die Bauart des durchbrochenen Bauteils — `tt_platte` schlägt alles. */
  bauart: string | null;
}

/** Ein durchgehendes Medium mit seinem Schott. */
export interface AussparungMedium {
  elementId: number | null;
  art: string | null;
  gewerk: string | null;
  typ: string | null;
  nennweite: number | null;
  breiteMm: number | null;
  hoeheMm: number | null;
  werkstoff: string | null;
  istBrennbar: boolean | null;
  systemtyp: string | null;
  verknuepfung: string | null;
  schott: AussparungSchott | null;
  /** Die vergebene Nummer. Sie steht im Modell und ist nicht zurückzuholen. */
  nummer: {
    nummer: string; vergebenAm: string | null; medium: string | null;
  } | null;
}

/** Ein durchbrochenes Bauteil. */
export interface AussparungBauteil {
  kategorie: string | null;
  typ: string | null;
  bauart: string | null;
  /** 'schichtaufbau' ist belegt, 'kategorie' nur eingestuft. */
  bauartHerkunft: string | null;
  tragendeSchicht: string | null;
  dickeMm: number | null;
  feuerwiderstand: string | null;
  istTragend: boolean | null;
}

export interface AussparungBaumKnoten {
  id: string;
  revitElementId: number | null;
  kennzeichen: string | null;
  ebene: string | null;
  form: string | null;
  breiteMm: number | null;
  hoeheMm: number | null;
  durchmesserMm: number | null;
  tiefeMm: number | null;
  gewerke: string | null;
  gewerkFuehrend: string | null;
  zustand: string | null;
  anforderungRoh: string | null;
  anforderungGeprueft: string | null;
  bauteile: AussparungBauteil[];
  raeume: { seite: string; raumnummer: string | null;
            raumname: string | null }[];
  medien: AussparungMedium[];
}

export interface AussparungBaum {
  gesamt: number;

  /**
   * Der Stand, gegen den der Filter „seit der letzten Aufnahme" misst.
   *
   * Er steht in der Antwort, damit die Zahl nachrechenbar bleibt: „147
   * geändert" ohne den Stichtag ist eine Behauptung.
   */
  letzteAufnahme: string | null;
  geaendert: boolean;
  geschosse: {
    geschoss: string;
    anzahl: number;
    bauarten: {
      bauart: string;
      anzahl: number;
      aussparungen: AussparungBaumKnoten[];
    }[];
  }[];
}

/** Eine Zulassung eines Brandschottsystems. */
export interface BrandschutzZulassung {
  id: string;
  hersteller: string;
  system: string;
  art: string;
  nummer: string;
  feuerwiderstand: string | null;
  fuerBrennbar: boolean | null;
  fuerBauart: string | null;
  gueltigVon: string | null;
  gueltigBis: string | null;
  quelle: string | null;
  hinweis: string | null;
}

/**
 * Die Uebersicht der Kabeltrassen.
 *
 * Gegliedert nach Stromart und nicht nach Bauteilart: Die Frage ist nicht
 * 'welches Fabrikat', sondern 'was liegt zusammen drin'.
 */
export interface Kabeltrassenuebersicht {
  anzahl: number;
  laengeGesamtM: number;
  kabelwege: number;
  jeGruppe: {
    gruppe: string; bezeichnung: string; anzahl: number;
    laengeM: number; istKabelweg: boolean;
  }[];
  jeStromart: {
    stromart: string;
    bezeichnung: string;
    anzahl: number;
    laengeM: number;
    funktionserhalt: string;
    geschosse: number;
    maxBreiteMm: number | null;
    maxHoeheMm: number | null;
  }[];
  jeGeschoss: {
    geschoss: string; stromart: string; anzahl: number; laengeM: number;
  }[];
  abmessungen: {
    breiteMm: number | null; hoeheMm: number | null;
    anzahl: number; laengeM: number;
  }[];
  regeln: {
    id: string; stromart: string; bezeichnung: string; feld: string;
    muster: string; rang: number; funktionserhalt: string | null;
    normbezug: string | null; farbe: string | null;
    begruendung: string | null; herkunft: string; geprueft: boolean;
    istAktiv: boolean;
  }[];
  hinweise: string[];
}

export interface Kabeltrasse {
  id: string;
  kategorie?: string | null;
  gruppe?: string | null;
  revitElementId: number | null;
  familie: string | null;
  typ: string | null;
  systemname: string | null;
  stromart: string;
  bauteilart: string | null;
  breiteMm: number | null;
  hoeheMm: number | null;
  durchmesserMm?: number | null;
  laengeM: number | null;
  belegungProzent: number | null;
  funktionserhalt: string | null;
  geschoss: string | null;

  /** Die zusammengefasste Etage -- G00 statt G00_OKRB. */
  ebene?: string | null;

  planungsbereich: string | null;
  quadrant: string | null;

  raumnummer?: string | null;
  raumname?: string | null;

  /** 'lage' aus der Geometrie, 'nachbar' als Vermutung zweiter Ordnung. */
  raumHerkunft?: string | null;

  /** Der Servicetyp aus dem Modell -- NICHT dasselbe wie systemname. */
  systemtyp?: string | null;

  herkunft?: string | null;

  /** Die gelesenen Merkmalskandidaten als JSON — die Grundlage der Regel. */
  merkmale?: string | null;
}

/**
 * Der Bestandsbaum: Kategorie → Ebene → Abschnitt → Raum.
 *
 * Die EBENE und nicht das Rohgeschoss: Ausgezaehlt am 2026-08-17 gab es 19
 * Geschosswerte fuer acht Etagen, weil _OKRB, _UKRD und _ARC
 * Revit-Ebenenvarianten sind und keine Stockwerke. Ein Baum danach hat 19
 * Aeste, und die Trassen einer Etage liegen in dreien davon.
 */
export interface Kabeltrassenbestand {
  baum: {
    gruppe: string;
    bezeichnung: string;
    istKabelweg: boolean;
    anzahl: number;
    laengeM: number;
    ohneStromart: number;
    ebenen: {
      ebene: string;
      anzahl: number;
      laengeM: number;
      abschnitte: {
        abschnitt: string;
        anzahl: number;
        laengeM: number;
        raeume: {
          raumnummer: string | null;
          raumname: string | null;
          anzahl: number;
          laengeM: number;
        }[];
      }[];
    }[];
  }[];
  hinweise: string[];
}

/**
 * Ein Blatt der Trassenliste.
 *
 * Mit der GESAMTZAHL: Vorher endete die Liste bei 2.000 Zeilen, und zwar
 * schweigend -- ein Knoten mit 19.770 Bauteilen zeigte 2.000 und sah
 * vollstaendig aus.
 */
export interface Kabeltrassenblatt {
  gesamt: number;
  seite: number;
  proSeite: number;
  seiten: number;
  zeilen: Kabeltrasse[];
}

/**
 * Der Baum: Ebene → Abschnitt → Trassenart.
 *
 * Diese Reihenfolge und keine andere, weil es die Reihenfolge ist, in der
 * gearbeitet wird: Wer Abschnitt 3 im ersten Obergeschoss bearbeitet, braucht
 * dessen Trassen und nicht die des Gebaeudes.
 */
export interface Kabeltrassenbaum {
  baum: {
    ebene: string;
    anzahl: number;
    laengeM: number;
    abschnitte: {
      abschnitt: string;
      anzahl: number;
      laengeM: number;
      arten: {
        stromart: string;
        gruppe: string | null;
        bezeichnung: string;
        gruppenname: string;
        istKabelweg: boolean;
        anzahl: number;
        laengeM: number;
        ohneQuerschnitt: number;
      }[];
    }[];
  }[];
  hinweise: string[];
}

/**
 * Der Feldkatalog — mit der HERKUNFT, nicht mit dem Rückweg.
 *
 * „aus dem Modell": revitParameter gesetzt — der Wert steht im Modell.
 * „nur in der Plattform": revitParameter null — es gibt im Modell keinen
 * Ort dafür.
 *
 * Der Unterschied ist keine Farbenspielerei: Wer einen Plattformwert im
 * Modell sucht, sucht vergeblich; wer einen Modellwert hier ändert, ändert
 * ihn bis zum nächsten Lesen.
 *
 * `ausModellGelesen` ist NICHT die Antwort auf „geht dieses Feld ins Modell
 * zurück" (`Rueckschreibweg.GehtInsModell`). Gemessen am Stand 0733 tragen
 * 8 der 21 aktiven Trassenfelder einen Parameter und KEIN EINZIGES eine
 * Festlegung — über den Rückweg ist hier noch nichts entschieden. Die
 * Auskunft hieß bis PB53 `ausModell` und ließ beides offen.
 */
export interface Kabeltrassenfelder {
  felder: {
    feldschluessel: string;
    bezeichnung: string;
    gruppe: string | null;
    einheit: string | null;
    art: string;
    auswahl: string | null;
    gruppen: string | null;
    revitParameter: string | null;
    /** Woher der Wert kommt — siehe Kopf. Nicht der Rückweg. */
    ausModellGelesen: boolean;
    /** Die Festlegung „darf zurückgeschrieben werden" — heute überall false. */
    istSchreibbar: boolean;
    istGerechnet: boolean;
    hinweis: string | null;
  }[];
  werte: {
    id: string;
    trasseId: string | null;
    typschluessel: string | null;
    feldschluessel: string;
    wert: string | null;
    zahlwert: number | null;
    herkunft: string;
    quelle: string | null;
    geprueft: boolean;
    begruendung: string | null;
  }[];
  ausModellGelesen: number;
  ausPlattform: number;
}

/** Wo die Stromart im Modell stehen koennte — ausgezaehlt statt geraten. */
export interface Kabeltrassenerhebung {
  zeilen: {
    feld: string;
    parameter: string | null;
    gefuellt: number;
    gesamt: number;
    verschiedene: number;
    werte: string | null;
    gelesenAm: string | null;
  }[];
  hinweise: string[];
}

export interface Kabeltrassenprodukte {
  felder: {
    feldschluessel: string; bezeichnung: string; einheit: string | null;
    art: string; auswahl: string | null; gruppe: string | null;
    geraetearten: string | null; hinweis: string | null;
  }[];
  fabrikate: {
    id: string; geraeteart: string; typmuster: string | null;
    feldschluessel: string; wert: string | null; zahlwert: number | null;
    herkunft: string; quelle: string | null; geprueft: boolean;
    bemerkung: string | null;
  }[];
  hinweise: string[];
}

// --- Der Hüllenbaum ---------------------------------------------------------

export interface Huellenbaum {
  arten: Huellenart[];
  felder: Huellenfeld[];
}

export interface Huellenfeld {
  schluessel: string;
  bezeichnung: string;
  einheit?: string | null;
  gruppe?: string | null;
  art: string;
  bauteilarten?: string | null;
  hinweis?: string | null;
}

export interface Huellenart {
  schluessel: string;
  bezeichnung: string;
  gruppe: string;
  huellart: string;
  istOeffnung: boolean;
  hinweis?: string | null;
  anzahl: number;
  flaecheM2: number;
  /**
   * Welche Felder an dieser Bauteilart gelten — entschieden von der API aus
   * huelle_felder.Bauteilarten. Die Oberfläche rechnet das NICHT nach: Als die
   * Regel zweimal dastand, wich die zweite Fassung im Trennzeichen ab und
   * filterte jedes Feld weg.
   */
  anwendbareFelder: string[];
  typen: Huellentyp[];
}

/** Ein einzelnes Bauteil eines Typs. */
export interface Huellenbauteil {
  id: string;
  elementId?: number | null;
  geschoss?: string | null;
  bezeichnung?: string | null;
  quellmodell?: string | null;
  flaecheM2?: number | null;
  glasflaecheM2?: number | null;
  azimutGrad?: number | null;
  istRaumbegrenzend: boolean;
  uWert?: number | null;
  uHerkunft?: string | null;
  nachweisCode?: string | null;
  /** Warum die Schichtherleitung abbricht, falls sie es tut. */
  schichtenOhneLambda: number;
}

export interface Huellentyp {
  typ: string;
  familie?: string | null;
  bauteilcode?: string | null;
  anzahl: number;
  flaecheM2: number;
  glasflaecheM2: number;
  /** Bauteile ohne Verknüpfung in den Wärmeschutznachweis. */
  ohneNachweis: number;
  nachweis: Huellennachweis[];
  felder: Huellenfeldwert[];
}

export interface Huellennachweis {
  code: string;
  bezeichnung?: string | null;
  u?: number | null;
  g?: number | null;
}

/**
 * Der Stand eines Feldes an einem Typ.
 *
 * min und max statt eines Mittelwerts: Streuen die Bauteile eines Typs, ist die
 * Spanne die Wahrheit. Ein Mittelwert stünde an keinem Bauteil.
 */
export interface Huellenfeldwert {
  feld: string;
  min?: number | null;
  max?: number | null;
  mitWert: number;
  herkuenfte: string[];
  /** Die Festlegung des Fachplaners, falls eine gesetzt ist. */
  festwert?: number | null;
  festBegruendung?: string | null;
  festHerkunft?: string | null;
  festAngewendet?: number | null;
}

export interface Typwertergebnis {
  festlegungen: number;
  gesetzt: number;
  geaendert: number;
  abgeraeumt: number;
  ohneBauteil: number;
  hinweise: string[];
}

/**
 * Ein Gewerk, das ein Unterlagenpaket anbietet (Auftrag Fachplaner
 * 16.09.2026).
 *
 * `dateien` ist die Zahl, die ohne `art`-Filter entstünde — sie steht am
 * Knopf, damit niemand blind eine Viertelstunde wartet.
 */
export interface UnterlagenGewerk {
  gewerk: string;
  kuerzel: string;
  bezeichnung: string;
  dateien: number;
  arten: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// FUNKTIONALE LEISTUNGSBESCHREIBUNG (Plan Abschnitt 7, Spur F1)
// ═══════════════════════════════════════════════════════════════════════════
//
// Gegliedert nach den Kostengruppen der DIN 276, nicht nach Gewerken: Eine
// Gruppe kann Anlagen mehrerer Gewerke fuehren (434 Kaelte liegt im
// Heizungsmodell, 456 Sicherheit im Elektromodell).
//
// JEDER WERT TRAEGT SEINE HERKUNFT. `text: null` heisst: der Wert liegt
// nicht vor - und NICHT null, leer oder Durchschnitt. Die Seite zeigt dafuer
// einen Gedankenstrich, so wie das PDF.

/** modell · berechnung · datenblatt · festlegung · bestand · fehlt. */
export type FlbHerkunft =
  'modell' | 'berechnung' | 'datenblatt' | 'festlegung' | 'bestand' | 'fehlt';

export interface FlbKostengruppe {
  kostengruppe: string;
  /** 2 = Zwischenüberschrift (430), 3 = Kapitel (431). */
  ebene: number;
  obergruppe?: string | null;
  bezeichnung: string;
  gewerk?: string | null;
  gewerkKuerzel?: string | null;
  datenquelle?: string | null;
  hatAbfrage: boolean;
  istImProjekt: boolean;
  istFestlegung: boolean;
  istBestaetigt: boolean;
  festlegung?: string | null;
  hinweis?: string | null;
  /** Wie viele der sieben Abschnitte einen Text haben (Vorlage oder gepflegt). */
  abschnitte: number;
  /** Davon vom Fachplaner oder der KI projektbezogen gepflegt. */
  gepflegt: number;
}

export interface FlbLauf {
  id: string;
  gestartetAm: string;
  beendetAm?: string | null;
  gewerk?: string | null;
  gruppen: number;
  anlagen: number;
  komponenten: number;
  kennzahlen: number;
  offenePlatzhalter: number;
  kiSaetze: number;
  geschrieben: boolean;
}

export interface FlbBaum {
  gruppen: FlbKostengruppe[];
  letzterLauf?: FlbLauf | null;
}

export interface FlbKennzahl {
  schluessel: string;
  bezeichnung: string;
  /** `null` heisst: liegt nicht vor. */
  text?: string | null;
  einheit?: string | null;
  herkunft: FlbHerkunft;
}

export interface FlbSpalte {
  titel: string;
  zahl: boolean;
}

export interface FlbWert {
  text?: string | null;
  herkunft: FlbHerkunft;
}

export interface FlbAnlage {
  kennung: string;
  standort?: string | null;
  versorgungsbereich?: string | null;
  werte: FlbWert[];
}

export interface FlbKomponente {
  gruppe: string;
  bezeichnung: string;
  menge?: number | null;
  einheit: string;
  herkunft: FlbHerkunft;
  kennwert?: string | null;
}

export interface FlbAbschnitt {
  /** 1..7 nach Plan Abschnitt 7. */
  nummer: number;
  titel: string;
  text: string;
  /** vorlage · fachplaner · ki. */
  herkunft: string;
  istBestaetigt: boolean;
  normen?: string | null;
  quelle?: string | null;
  /** Schlüssel, für die kein Wert vorlag — im Text als `[fehlt: …]`. */
  offenePlatzhalter: string[];
}

export interface FlbKapitel {
  kostengruppe: string;
  bezeichnung: string;
  datenquelle?: string | null;
  istFestlegung: boolean;
  istBestaetigt: boolean;
  festlegung?: string | null;
  kennzahlen: FlbKennzahl[];
  spalten: FlbSpalte[];
  anlagen: FlbAnlage[];
  komponenten: FlbKomponente[];
  luecken: string[];
  /** TS14a: Abgleich mit dem Fachlastenbuch; fehlt bei Gruppen ohne Abgleich. */
  abgleich?: FlbAbgleich[];
  abschnitte: FlbAbschnitt[];
}

/** Ergebnis einer Abgleichzeile: eingehalten · abweichend · offen. */
export type FlbAbgleichergebnis = 'eingehalten' | 'abweichend' | 'offen';

/** Eine Zeile des Abgleichs mit dem Fachlastenbuch (TS14a). */
export interface FlbAbgleich {
  punkt: string;
  fundstelle: string;
  /** `null` heisst: im Projekt nicht hinterlegt. */
  fachlastenbuch?: string | null;
  /** `null` heisst: liegt nicht vor. */
  planung?: string | null;
  ergebnis: FlbAbgleichergebnis;
  hinweis?: string | null;
}

export interface FlbGewerk {
  gewerk: string;
  zahlen: Record<string, number>;
  hinweise: string[];
  gegenproben: { aussage: string; ergebnis: string }[];
  kapitel: FlbKapitel[];
}

/** Wo ein angestoßenes Raumbuch gerade steht. */
export interface RaumbuchAuftragStand {
  kennung: string;
  zustand: 'laeuft' | 'fertig' | 'gescheitert';
  geschoss?: string | null;
  stufe: string;
  fertig: number;
  gesamt: number;
  begonnen: string;
  beendet?: string | null;
  sekunden: number;
  bytes: number;
  dateiname?: string | null;
  fehler?: string | null;
}

/** Eine zuletzt gemessene Dauer. Geschoss '' heißt: alle Räume. */
export interface RaumbuchDauer {
  geschoss: string;
  sekunden: number;
  raeume: number;
  wann: string;
}

/**
 * TL1: Der maßgebende Auslegungslauf (Auslegungslauf.cs).
 *
 * `grundlage`: `gekennzeichnet` (Kennzeichnung mit Begründung),
 * `auslegungstemperatur` (Heizlast: jüngster Lauf mit der Auslegungstemperatur
 * des Leitklimadatensatzes) oder `juengster_fertiger` (Kühllast).
 */
export interface AuslegungslaufWahl {
  laufId: string;
  laufnummer: number;
  fall: string;
  aussentemperaturC: number | null;
  beendetAm: string | null;
  grundlage: 'gekennzeichnet' | 'auslegungstemperatur' | 'juengster_fertiger';
  hinweis: string | null;
}

/** TL1: Antwort der Kennzeichnung „maßgebend“. */
export interface KennzeichnungAntwort {
  ausgang: string;
  message: string;
  laufId: string | null;
  laufnummer: number | null;
  abgeloest: number[];
}

// --- Kältenachweis (TH-3, 17.10.2026) ---------------------------------------
// Die Antwort von GET /api/projects/{id}/kaelte/nachweis
// (Infrastructure/Kaelte/Kaeltenachweis.cs). Ergebnis: erfuellt |
// nicht_erfuellt | nicht_pruefbar | vorlaeufig | hinweis | entfaellt.

export interface KaeltenachweisErgebnis {
  bereich: string;
  regel: string;
  gegenstand: string;
  bezeichnung: string;
  wert: number | null;
  grenzwert: number | null;
  einheit: string | null;
  vergleich: string | null;
  ergebnis: string;
  vorlaeufig: boolean;
  rechenweg: string;
  normbezug: string | null;
  quellen: string | null;
  fachfrage: string | null;
  reihenfolge: number;
}

export interface KaeltenachweisProfilzeile {
  /** n | n_minus_1 */
  fall: string;
  stunde: number;
  aussenC: number | null;
  lastKw: number;
  maschinenKw: number;
  erzeugungKw: number;
  entzugKw: number;
  ladungKw: number;
  ungedecktKw: number;
  ladezustandKwh: number;
}

export interface KaeltenachweisAnnahme {
  parameter: string;
  wert: string | null;
  einheit: string | null;
  herkunft: string;
  quelle: string | null;
  bestaetigt: boolean;
  normbezug: string | null;
}

export interface KaeltenachweisBefund {
  /** F | W | H */
  schwere: string;
  regel: string;
  gegenstand: string;
  meldung: string;
}

export interface KaeltenachweisNormverweis {
  norm: string;
  ausgabe: string | null;
  abschnitt: string | null;
  anwendung: string;
}

export interface KaeltenachweisModell {
  kuehllastLaufnummer: number | null;
  kuehllastSpitzeKw: number | null;
  kuehllastVorlaeufig: boolean;
  lesart: string;
  datenstand: string;
  /** vorabzug | zwischenstand */
  freigabestatus: string;
  ergebnisse: KaeltenachweisErgebnis[];
  profil: KaeltenachweisProfilzeile[];
  annahmen: KaeltenachweisAnnahme[];
  befunde: KaeltenachweisBefund[];
  normbezug: KaeltenachweisNormverweis[];
}

export interface KaeltenachweisLauf {
  id: string;
  gestartetAm: string;
  beendetAm: string | null;
  kuehllastLaufnummer: number | null;
  kuehllastAm: string | null;
  kuehllastGrundlage: string | null;
  lesart: string;
  vorlaeufig: boolean;
  unbestaetigteVorgaben: number;
  datenhash: string | null;
  hinweise: string[];
}

export interface KaeltenachweisAntwort {
  lauf: KaeltenachweisLauf | null;
  modell: KaeltenachweisModell | null;
  zaehlung: { ergebnis: string; anzahl: number }[];
  massgebendeKuehllast: { laufnummer: number; grundlage: string; hinweis: string | null } | null;
  veraltet: boolean;
}

// --- Hydraulischer Abgleich (TH-4, 17.10.2026) ---------------------------------
// Die Antwort von GET /api/projects/{id}/heizung/abgleich
// (Infrastructure/Heizung/Abgleichnachweis.cs). Ergebnis: erfuellt |
// nicht_erfuellt | nicht_pruefbar | vorlaeufig | hinweis | entfaellt.

export interface AbgleichVentil {
  systemgruppe: string;
  elementId: number | null;
  verbraucherart: string | null;
  bezeichnung: string | null;
  raumnummer: string | null;
  geschoss: string | null;
  istMassgebend: boolean;
  massenstromKgH: number | null;
  volumenstromM3h: number | null;
  wegPa: number | null;
  abgleichPa: number | null;
  armaturPa: number | null;
  restPa: number | null;
  ventilSollPa: number | null;
  kvErforderlich: number | null;
  baureihe: string | null;
  voreinstellung: number | null;
  kvGewaehlt: number | null;
  ventilIstPa: number | null;
  autoritaet: number | null;
  autoritaetBewertung: string | null;
  /** voreinstellung | kv_wert | offen | ohne */
  einstellart: string;
  armaturen: string | null;
  ergebnis: string;
  vorlaeufig: boolean;
  grund: string | null;
  hinweis: string | null;
  reihenfolge: number;
}

export interface AbgleichArmatur {
  systemgruppe: string;
  kennung: string;
  /** strangregulierventil | dp_regler | einzel */
  art: string;
  ebene: number;
  vorlaufElementId: number | null;
  ruecklaufElementId: number | null;
  verbraucher: number;
  geschosse: string | null;
  volumenstromM3h: number;
  drosselungPa: number;
  kvErforderlich: number | null;
  sollwertPa: number | null;
  massgebenderVerbraucher: string | null;
  ergebnis: string;
  vorlaeufig: boolean;
  hinweis: string | null;
  reihenfolge: number;
}

export interface AbgleichPruefung {
  /** lauf | gruppe | pumpe */
  bereich: string;
  regel: string;
  gegenstand: string;
  bezeichnung: string;
  wert: number | null;
  grenzwert: number | null;
  einheit: string | null;
  vergleich: string | null;
  ergebnis: string;
  vorlaeufig: boolean;
  rechenweg: string;
  normbezug: string | null;
  quellen: string | null;
  fachfrage: string | null;
  reihenfolge: number;
}

export interface AbgleichModell {
  heizlastLaufnummer: number | null;
  heizlastAussentemperaturC: number | null;
  heizlastVorlaeufig: boolean;
  veraltet: boolean;
  verfahren: string;
  druckbasis: string;
  rundung: string;
  datenstand: string;
  /** vorabzug | zwischenstand */
  freigabestatus: string;
  ventile: AbgleichVentil[];
  armaturen: AbgleichArmatur[];
  pruefungen: AbgleichPruefung[];
  annahmen: KaeltenachweisAnnahme[];
  befunde: KaeltenachweisBefund[];
  normbezug: KaeltenachweisNormverweis[];
}

export interface AbgleichLauf {
  id: string;
  gestartetAm: string;
  beendetAm: string | null;
  heizlastLaufnummer: number | null;
  heizlastGrundlage: string | null;
  rohrnetzAm: string | null;
  vorlaeufig: boolean;
  unbestaetigteVorgaben: number;
  datenhash: string | null;
  hinweise: string[];
}

export interface AbgleichAntwort {
  lauf: AbgleichLauf | null;
  modell: AbgleichModell | null;
  zaehlung: { ergebnis: string; anzahl: number }[];
  massgebendeHeizlast: { laufnummer: number; grundlage: string; hinweis: string | null } | null;
  veraltet: boolean;
}

/** TL2: Eine Vorgabe der Heiz-/Kühllast mit Bestätigungsstand (Seite „Annahmen und Vorgaben“). */
export interface VorgabeZeile {
  art: 'parameter' | 'profil' | 'ersatzkoerper' | 'zone' | 'klima' | 'standort' | 'baustoff' | string;
  id: string | null;
  schluessel: string;
  bezeichnung: string | null;
  wert: string | null;
  zahlwert: number | null;
  einheit: string | null;
  herkunft: string | null;
  quelle: string | null;
  /** Gilt für alle Projekte - in TGAPlan nicht bestätigbar (HUL-24). */
  hausweit: boolean;
  bestaetigbar: boolean;
  istBestaetigt: boolean;
  bestaetigtVon: string | null;
  bestaetigtAm: string | null;
  bestaetigtQuelle: string | null;
  bestaetigtBegruendung: string | null;
  /** Im Heizlast-Auslegungslauf gelesen; null = Lauf ohne Momentaufnahme. */
  genutztHeizlast: boolean | null;
  genutztKuehllast: boolean | null;
}

/** TL2: Eine gelesene Vorgabe eines Laufs (Annahmenblatt, TX-STANDARD S3.4). */
export interface AnnahmeZeile {
  art: string;
  schluessel: string;
  bezeichnung: string | null;
  wert: string | null;
  zahlwert: number | null;
  einheit: string | null;
  herkunft: string | null;
  quelle: string | null;
  zweck: 'ergebnis' | 'vergleich' | 'abgleich' | 'kennung' | 'geladen' | string;
  anzahl: number | null;
  anzahlRaeume: number | null;
  istBestaetigt: boolean;
  bestaetigtVon: string | null;
  bestaetigtAm: string | null;
  bestaetigtQuelle: string | null;
  bestaetigtBegruendung: string | null;
  vorgabeId: string | null;
}

/** TL2: Die Annahmen eines Laufs. */
export interface Annahmenstand {
  laufId: string;
  laufnummer: number;
  fall: 'heizlast' | 'kuehllast' | string;
  /** false = Lauf vor TL2, ausgewiesen rekonstruiert. */
  momentaufnahme: boolean;
  zeilen: AnnahmeZeile[];
  unbestaetigt: number;
  herleitung: string[];
}

/** TL2: Kurzstand je Auslegungslauf für den Hinweis der Seiten. */
export interface AnnahmenStandLauf {
  laufId: string;
  laufnummer: number;
  beendetAm: string | null;
  momentaufnahme: boolean;
  unbestaetigt: number;
  zeilen: number;
  herleitung: string[];
}

export interface AnnahmenStandProjekt {
  heizlast: AnnahmenStandLauf | null;
  kuehllast: AnnahmenStandLauf | null;
}

/** TL2: Anfrage „bestätigen/zurücknehmen“. Bei einer Zone ist `schluessel` der Bereich. */
export interface VorgabeBestaetigung {
  art: string;
  id: string | null;
  schluessel: string | null;
  begruendung: string;
  quelle: string;
  zuruecknehmen: boolean;
}
// ═══ TV5 Lüftung „Prüfungen sichtbar“ (Anfang): Typen ═══════════════════════
//
// Spiegel der Records aus LueftungPruefungListen.cs (camelCase). Die Zeilen
// sind genau die Zeilen von PDF und Excel; `ziel` ist der Verweis der Seite.

export type LueftungPruefbericht = 'geraete' | 'bsk' | 'entrauchung' | 'luftmengen';
export type LueftungPruefliste = 'pruefung' | 'luftmengen' | 'befunde';

/** Der Lauf, auf dem eine Liste beruht (Kopf, nicht Datenhash). */
export interface LueftungLaufangabe {
  lauf: string;
  werkzeug: string;
  gerechnetAm: string;
  modellstand: string | null;
  datenstand: string;
}

export interface LueftungElementziel {
  plantId: string | null;
  componentId: string | null;
}

export interface LueftungSeitenzeile<Z> {
  zeile: Z;
  ziel: LueftungElementziel;
}

export interface LueftungAnnahme {
  parameter: string;
  wert: string | null;
  einheit: string | null;
  herkunft: string;
  quelle: string | null;
  bestaetigt: boolean;
  normbezug: string | null;
}

/**
 * TV7a-2 (Plan TV §22 c): die Vorbehalte und Annahmen eines Lüftungsberichts (LUE-SCH, LUE-RSC, LUE-DRV), wie sie im
 * Vorbehaltskasten und im Annahmenblatt des PDF stehen - die Seite zeigt genau diese Sätze.
 */
export interface LueftungBerichtsvorbehalte {
  vorbehalte: LueftungVorbehalt[];
  annahmen: LueftungAnnahme[];
}

export interface LueftungNormverweis {
  norm: string;
  ausgabe: string | null;
  abschnitt: string | null;
  anwendung: string;
}

/**
 * TV5b: ein Vorbehalt des Datenmodells (Record `Vorbehalt` aus Tuv.Berichte.Layout) - derselbe Eintrag wie im
 * Vorbehaltskasten des PDF. `art` ist die Zahl der Aufzählung: 0 Annahmen, 1 Fachfrage, 2 Raumstand,
 * 3 Veraltet, 4 Sonstiges (Reihenfolge des Kastens, `muster/Vorbehalte.tsx`).
 */
export interface LueftungVorbehalt {
  art: number;
  text: string;
  verweis: string | null;
}

export interface LueftungBefundzeile {
  /** 0 Fehler, 1 Warnung, 2 Hinweis (Reihenfolge des Standards). */
  rang: number;
  rangText: string;
  regel: string;
  gegenstand: string;
  meldung: string;
}

export interface LueftungPruefantwort<M, Z> {
  lauf: LueftungLaufangabe | null;
  datenhash: string | null;
  /** Vorabzug | Zwischenstand (S3.5); bei den Befunden ohne eigenen Bericht null. */
  status: string | null;
  modell: M | null;
  zeilen: LueftungSeitenzeile<Z>[];
}

export type LueftungJ9Stand = 'erfuellt' | 'nicht_erfuellt' | 'nicht_pruefbar' | 'entfaellt';

export interface LueftungGeraetezeile {
  anlage: string;
  geraetetyp: string;
  regel: string;
  variante: string;
  gruppe: string;
  bezeichnung: string;
  stand: LueftungJ9Stand;
  schwere: string;
  istwert: number | null;
  istText: string | null;
  grenzwert: number | null;
  grenzText: string | null;
  einheit: string | null;
  text: string;
  norm: string;
  fundstelle: string | null;
  datenquelle: string | null;
  datenblattAm: string | null;
  regelBestaetigt: boolean;
  normwertPruefen: boolean;
}

export interface LueftungGeraetemodell {
  anlagen: number;
  regeln: number;
  zaehlung: { gesamt: number; erfuellt: number; nichtErfuellt: number; nichtPruefbar: number; entfaellt: number };
  annahmen: LueftungAnnahme[];
  normen: LueftungNormverweis[];
  befunde: LueftungBefundzeile[];
  vorbehalte: LueftungVorbehalt[];
}

export interface LueftungBskzeile {
  kennung: string;
  familie: string | null;
  typ: string | null;
  baureihe: string | null;
  erkennung: string;
  aksCode: string | null;
  aksSchluessel: string | null;
  anlage: string | null;
  raumnummer: string | null;
  ebene: string | null;
  msrAnlage: string | null;
  msrAks: string | null;
  motorantrieb: boolean | null;
  msrDatenpunkte: number;
  stand: 'konsistent' | 'befund';
  befunde: string | null;
  text: string;
}

export interface LueftungBskmodell {
  klappen: number;
  konsistent: number;
  mitBefund: number;
  motorisch: number;
  annahmen: LueftungAnnahme[];
  normen: LueftungNormverweis[];
  befunde: LueftungBefundzeile[];
  vorbehalte: LueftungVorbehalt[];
}

export interface LueftungEntrauchungszeile {
  ebene: 'anlage' | 'klappe' | 'zentrale';
  anlage: string | null;
  msrKennung: string | null;
  msrGewerk: string | null;
  kennung: string | null;
  familie: string | null;
  typ: string | null;
  aksCode: string | null;
  msrBauteilart: string | null;
  klappenModell: number | null;
  klappenMsr: number | null;
  stand: 'konsistent' | 'befund' | 'hinweis';
  befunde: string | null;
  text: string;
}

export interface LueftungEntrauchungsmodell {
  zeilenzahl: number;
  anlagen: number;
  klappen: number;
  zentralen: number;
  mitBefund: number;
  annahmen: LueftungAnnahme[];
  normen: LueftungNormverweis[];
  befunde: LueftungBefundzeile[];
  vorbehalte: LueftungVorbehalt[];
}

export interface LueftungBrandschutzantwort {
  lauf: LueftungLaufangabe | null;
  bskDatenhash: string | null;
  bskStatus: string | null;
  bsk: LueftungBskmodell | null;
  bskZeilen: LueftungSeitenzeile<LueftungBskzeile>[];
  entrauchungDatenhash: string | null;
  entrauchungStatus: string | null;
  entrauchung: LueftungEntrauchungsmodell | null;
  entrauchungZeilen: LueftungSeitenzeile<LueftungEntrauchungszeile>[];
}

/** TV4: nur_untergrenze - das Soll ist nur eine Untergrenze, das Ist hält sie ein (Auslegung offen). */
export type LueftungBewertung =
  | 'fehlt' | 'kein_soll' | 'unter_soll' | 'im_rahmen' | 'ueber_soll' | 'unplausibel' | 'nur_untergrenze';

export interface LueftungLuftmengenzeile {
  raumnummer: string | null;
  raumname: string | null;
  ebene: string | null;
  bereich: string | null;
  raeumeAnzahl: number | null;
  nutzungsart: string | null;
  personen: number | null;
  personenHerkunft: string | null;
  flaecheM2: number | null;
  volumenM3: number | null;
  sollM3h: number | null;
  sollHerkunft: string | null;
  sollRueckfall: boolean;
  istZuluftM3h: number | null;
  istAbluftM3h: number | null;
  istM3h: number | null;
  istQuelle: string | null;
  abweichungProzent: number | null;
  bewertung: LueftungBewertung;
  kennzeichnungen: string | null;
  hinweis: string | null;
  /** TV4/TV5c: auslegung | untergrenze | extern | sanitaer | bereich; null vor TV4. */
  sollArt: string | null;
  /** TV4/TV5c: CO₂-Abschätzung nach ASR A3.6 in ppm (nur mit belastbarer Personenzahl). */
  co2Ppm: number | null;
  /** TV4/TV5c: bis_1000 | bis_2000 | ueber_2000. */
  co2Stufe: string | null;
}

/** TV5c: das Ergebnis einer Variante - die Stufen der Hauptzeile, dazu nicht_rechenbar. */
export type LueftungVariantenbewertung = LueftungBewertung | 'nicht_rechenbar';

/** TV5c: eine Variante zu einer offenen Fachfrage (TV4) - gerechnet, NICHT entschieden. */
export interface LueftungLuftmengenvariante {
  variante: string;
  fachfrage: string | null;
  raumnummer: string | null;
  raumname: string | null;
  bereich: string | null;
  personen: number | null;
  sollM3h: number | null;
  istM3h: number | null;
  abweichungProzent: number | null;
  bewertung: LueftungVariantenbewertung;
  co2Ppm: number | null;
  annahmen: string | null;
}

export interface LueftungVariantenzaehlung {
  variante: string;
  fachfrage: string | null;
  gesamt: number;
  unterSoll: number;
  imRahmen: number;
  ueberSoll: number;
  fehlt: number;
  unplausibel: number;
  nichtRechenbar: number;
}

export interface LueftungLuftmengenmodell {
  raeume: number;
  jeBewertung: { bewertung: LueftungBewertung; anzahl: number; mitRueckfallSoll: number }[];
  sollRueckfall: number;
  istAusRaumbuchSumme: number;
  annahmen: LueftungAnnahme[];
  normen: LueftungNormverweis[];
  befunde: LueftungBefundzeile[];
  vorbehalte: LueftungVorbehalt[];
  /** TV5c: Räume je Sollart ("" = ohne Angabe, Lauf vor TV4). */
  jeSollArt: { sollArt: string; anzahl: number }[];
  /** TV5c: CO₂-Abschätzungen nach Stufe. */
  co2: { mitWert: number; bis1000: number; bis2000: number; ueber2000: number };
  /** TV5c: die Varianten je Art gezählt. */
  jeVariante: LueftungVariantenzaehlung[];
  /** TV5c: alle Varianten des Laufs, ohne Kappung. */
  varianten: LueftungLuftmengenvariante[];
}

export interface LueftungNetzbefundzeile {
  schwere: 'fehler' | 'warnung' | 'hinweis';
  regel: string;
  anlage: string | null;
  weg: string | null;
  kreis: string | null;
  /** Revit-Elementnummer. */
  kennung: number;
  art: string | null;
  bauform: string | null;
  raumnummer: string | null;
  istwert: number | null;
  grenzwert: number | null;
  einheit: string;
  text: string;
  geteilt: number | null;
}

export interface LueftungRegelbezug {
  regel: string;
  bezeichnung: string;
  normbezug: string;
  bestaetigt: boolean | null;
}

export interface LueftungNetzbefundmodell {
  gesamt: number;
  fehler: number;
  warnungen: number;
  hinweise: number;
  jeRegel: { regel: string; fehler: number; warnungen: number; hinweise: number }[];
  regeln: LueftungRegelbezug[];
  annahmen: LueftungAnnahme[];
  vorbehalte: LueftungVorbehalt[];
}
// ═══ TV5 (Ende) ═══════════════════════════════════════════════════════════════

// ═══ TH-2 (Anfang): Typen Erzeugung und Absicherung ═══════════════════════

export interface HeizungBilanzzeile {
  ebene: string;
  bezug: string;
  bezeichnung: string | null;
  norm: string;
  heizlastLaufnummer: number | null;
  normheizlastKw: number | null;
  transmissionKw: number | null;
  lueftungKw: number | null;
  infiltrationKw: number | null;
  gleichzeitigkeitProzent: number;
  gleichzeitigkeitHerkunft: string;
  heizlastGleichzeitigKw: number | null;
  trinkwasserKw: number | null;
  trinkwasserHerkunft: string | null;
  aufheizProzent: number | null;
  aufheizKw: number | null;
  erforderlichKw: number | null;
  installiertKw: number | null;
  installiertHerkunft: string | null;
  erzeuger: number;
  erzeugerOhneLeistung: number;
  listeKw: number | null;
  deckungsgradProzent: number | null;
  reserveKw: number | null;
  vollstaendigkeit: string | null;
  bewertung: string;
  vorbehalt: string | null;
  hinweis: string | null;
}

export interface HeizungKaskadenzeile {
  ebene: string;
  bezug: string;
  stufe: number;
  folgeregel: string;
  bezeichnung: string | null;
  herkunft: string;
  stufeKw: number | null;
  kumuliertKw: number | null;
  deckungProzent: number | null;
  istGrundlast: boolean;
  bewertung: string;
  hinweis: string | null;
}

export interface HeizungErzeugerzeile {
  systemgruppe: string | null;
  bauteilart: string | null;
  bezeichnung: string | null;
  raumnummer: string | null;
  seiten: string;
  installiertKw: number | null;
  leistungHerkunft: string | null;
  listeKennung: string | null;
  listeKw: number | null;
  abweichungKw: number | null;
  absicherungBewertung: string | null;
  bewertung: string;
  hinweis: string | null;
}

export interface HeizungAbsicherungszeile {
  systemgruppe: string | null;
  bauteilart: string;
  bezeichnung: string | null;
  raumnummer: string | null;
  leistungKw: number | null;
  leistungHerkunft: string | null;
  ansprechdruckBar: number | null;
  zulaessigerDruckBar: number | null;
  zulaessigeTemperaturC: number | null;
  vorlaufC: number | null;
  svEintrittDn: number | null;
  svAustrittDn: number | null;
  bewertung: string;
  hinweis: string | null;
}

export interface HeizungAusdehnungszeile {
  systemgruppe: string;
  bezeichnung: string | null;
  vorlaufC: number | null;
  glykolProzent: number | null;
  anlageninhaltL: number;
  leistungKw: number;
  ausdehnungProzent: number | null;
  ausdehnungL: number | null;
  wasservorlageL: number;
  statischeHoeheM: number;
  vordruckBar: number;
  ansprechdruckBar: number;
  enddruckBar: number;
  ansprechdruckStufeBar: number | null;
  nennvolumenErfL: number | null;
  nennvolumenModellL: number | null;
  gefaesseModell: number;
  bewertung: string;
  vdi2035Stufe: string | null;
  vdi2035Anforderung: string | null;
  leitungNennweiteDn: number | null;
  leitungKriterium: string | null;
  leitungBewertung: string | null;
  hinweis: string | null;
}

export interface HeizungPruefzeile {
  regel: string;
  regelbezeichnung: string;
  ebene: string;
  systemgruppe: string;
  erzeuger: string | null;
  /** erfuellt | nicht_erfuellt | nicht_pruefbar | entfaellt | hinweis */
  stand: string;
  schwere: string;
  istwert: number | null;
  istText: string | null;
  grenzwert: number | null;
  grenzText: string | null;
  einheit: string | null;
  text: string;
  norm: string | null;
  regelBestaetigt: boolean;
  normwertPruefen: boolean;
}

export interface HeizungVorbehalt {
  /** Fachlicher Schlüssel: veraltet | hzk29 | fernwaerme | en378 */
  schluessel: string;
  /** Art des gemeinsamen Vorbehaltskastens (muster/Vorbehalte): annahmen | fachfrage | raumstand | veraltet | sonstiges */
  art: string;
  /** Satz im Wortlaut des Kastens, z. B. „HZK-29: …“ */
  text: string;
  /** erscheint als „(siehe …)“ */
  verweis: string | null;
  fachfrage: string | null;
}

export interface HeizungJahresganglage {
  laufnummer: number;
  grundlage: string;
  stunden: number;
  stundenOhneWert: number;
  hoechstwertKw: number | null;
  jahresarbeitMwh: number | null;
  volllaststunden: number | null;
  installiertKw: number | null;
  stundenUeberInstalliert: number | null;
}

export interface HeizungAnnahme {
  parameter: string;
  wert: string | null;
  einheit: string | null;
  herkunft: string;
  quelle: string | null;
  bestaetigt: boolean;
  normbezug: string | null;
}

export interface HeizungBefund {
  /** 0 Fehler, 1 Warnung, 2 Hinweis */
  rang: number;
  rangText: string;
  regel: string;
  gegenstand: string;
  meldung: string;
}

export interface HeizungNormverweis {
  norm: string;
  ausgabe: string | null;
  abschnitt: string | null;
  anwendung: string;
}

export interface HeizungErzeugungModell {
  heizlastLaufnummer: number | null;
  heizlastAussentemperaturC: number | null;
  heizlastGrundlage: string | null;
  veraltet: boolean;
  /** Vorabzug | Zwischenstand */
  freigabestatus: string;
  bilanz: HeizungBilanzzeile[];
  kaskade: HeizungKaskadenzeile[];
  erzeuger: HeizungErzeugerzeile[];
  absicherung: HeizungAbsicherungszeile[];
  ausdehnung: HeizungAusdehnungszeile[];
  pruefungen: HeizungPruefzeile[];
  jahresgang: HeizungJahresganglage | null;
  vorbehalte: HeizungVorbehalt[];
  annahmen: HeizungAnnahme[];
  befunde: HeizungBefund[];
  normbezug: HeizungNormverweis[];
}

export interface HeizungErzeugungStaende {
  erzeugerAm: string | null;
  ausdehnungAm: string | null;
  absicherungAm: string | null;
  leitungAm: string | null;
  pruefungAm: string | null;
  pruefungDatenhash: string | null;
}

export interface HeizungErzeugungAntwort {
  stand: HeizungErzeugungStaende;
  modell: HeizungErzeugungModell | null;
  massgebendeHeizlast: { laufnummer: number; aussentemperaturC: number | null; grundlage: string; hinweis: string | null } | null;
}

export interface HeizungJahresgangwerte {
  laufnummer: number;
  grundlage: string;
  zeitpunkte: string[];
  bedarfKw: (number | null)[];
  aussenC: (number | null)[];
}

export interface KaelteJahresgangwerte {
  laufnummer: number;
  grundlage: string;
  zeitpunkte: string[];
  lastKw: (number | null)[];
  installiertKw: number | null;
  groessteMaschineKw: number | null;
  nMinus1Kw: number | null;
}

// ═══ TH-2 (Ende) ═══════════════════════════════════════════════════════════

/**
 * SA-4d (main 21.10.2026): was die Linienbildung neu bildet und was stehen bleibt - für die Rückfrage vor dem
 * Schreiben. Linien je Art (neu und bisher), die stehenden Linien mit Grund, die Meldergruppen, die Teilnehmer mit
 * anderer Linie oder Adresse und die davon mit gespeicherter Meldernummer.
 */
export interface Linienzusammenfassung {
  linienNeu: Record<string, number>;
  linienBisher: Record<string, number>;
  stehend: {
    kennzeichen: string; art: string; zentrale: string;
    teilnehmer: number; handTeilnehmer: number; grund: string;
  }[];
  gruppenNeu: number;
  gruppenBisher: number;
  gruppenStehend: number;
  umgehaengt: number;
  meldernummernBetroffen: number;
  handOhneLinie: number;
}

/** SA-4d: die Trassenlängen in der Probe - je Art gemessen und wie viele eine andere Länge bekommen. */
export interface Laengenzusammenfassung {
  jeArt: Record<string, number>;
  geaendert: number;
}

/**
 * SA-4e (main 21.10.2026): was „Zentralen zuordnen" schreiben würde - neu zugeordnete Teilnehmer, Wechsel der Zentrale
 * (davon mit Linie an der alten Zentrale), unveränderte, von Hand zugeordnete und offene.
 */
export interface Zuordnungszusammenfassung {
  neu: number;
  wechsel: number;
  linieBleibt: number;
  unveraendert: number;
  hand: number;
  offen: number;
  versorgungsbereiche: number;
  jeWechsel: { von: string; nach: string; anzahl: number; linieBleibt: number }[];
}

/**
 * SA-4e Teil 2 (main 21.10.2026): was „Zentralen erkennen" anlegen würde - erkannte Gruppen (Raum und Bezeichnung), davon
 * schon angelegte (eine Zentrale trägt ein Bauteil der Gruppe oder steht mit derselben Art im Raum), im selben Raum
 * zusammengefasste und anzulegende, je Art.
 */
export interface Erkennungszusammenfassung {
  erkannt: number;
  schonAngelegt: number;
  anzulegen: number;
  zusammengefasst: number;
  jeArt: { art: string; erkannt: number; schonAngelegt: number; anzulegen: number }[];
}

/**
 * SA-4f (main 21.10.2026): was „SV zuordnen" schreiben würde - neu zugeordnete SV-Verteiler, Wechsel der Anlage,
 * unveränderte (nicht geschrieben), geschützte (von Hand oder Bestand vor 1621, Herkunft unbekannt) und offene.
 */
export interface SvZuordnungszusammenfassung {
  neu: number;
  wechsel: number;
  unveraendert: number;
  geschuetzt: number;
  offen: number;
  jeWechsel: { von: string; nach: string; anzahl: number }[];
  /** Herkunft `hand` oder `unbekannt`. */
  jeGeschuetzt: { anlage: string; herkunft: string; anzahl: number; endstromkreise: number }[];
}

/**
 * SA-4f (main 21.10.2026): was „Bestückung bilden" schreiben würde - neu angelegte Baugruppen, angepasste berechnete
 * Mengen, unveränderte und geschützte (gepflegt oder Bestand vor 1622) mit der gepflegten und der gerechneten Menge.
 */
export interface Bestueckungszusammenfassung {
  zentrale: string;
  neu: number;
  angepasst: number;
  unveraendert: number;
  geschuetzt: number;
  jeNeu: { bezeichnung: string; menge: number }[];
  jeAngepasst: { bezeichnung: string; vorher: number; nachher: number }[];
  /** Herkunft `hand` oder `unbekannt`. */
  jeGeschuetzt: { bezeichnung: string; herkunft: string; gepflegt: number; gerechnet: number }[];
}

// ═══ TL10: GEG-Abgleich (Antwort von GET /api/projects/{id}/heizlast/geg-abgleich) ═══

/** Ein Bauteiltyp des GEG-Nachweises mit dem, was das Modell daraus macht. */
export interface GegAbgleichTyp {
  typCode: string;
  kategorie: string | null;
  bezeichnung: string | null;
  /** verified | verified_inconsistent | verified_ground_contact | verified_ground_contact_incomplete | assumption | missing */
  status: string;
  erdkontakt: boolean;
  transparent: boolean;
  referenzTyp: string | null;
  uAnlage2: number | null;
  uBilanz: number | null;
  uBilanzFlag: string | null;
  ufgEffektiv: number | null;
  uSystemBezeichnung: string | null;
  uSystem: number | null;
  gWert: number | null;
  uVergleich: number | null;
  uVergleichQuelle: string | null;
  bauteile: number;
  flaecheNettoM2: number;
  zuordnungen: number;
  flaecheWirksamM2: number;
  flaecheAussenluftM2: number;
  flaecheErdreichM2: number;
  flaecheSonstigeM2: number;
  flaecheOhneUM2: number;
  uRechnung: number | null;
  uRechnungMin: number | null;
  uRechnungMax: number | null;
  uHerkunft: string | null;
  anteilNachweisProzent: number | null;
  deltaU: number | null;
  /** gleich | abweichend | ohne_modell | ohne_nachweiswert | nicht_vergleichbar */
  bewertung: string;
  reihenfolge: number;
}

export interface GegAbgleichModell {
  nachweis: { kennung: string; index: string; bezeichnung: string; stand: string | null; quelle: string | null };
  typen: GegAbgleichTyp[];
  bilanzansaetze: {
    schluessel: string; bezeichnung: string; zahlwert: number | null; einheit: string | null; giltFuer: string | null;
    bemerkung: string | null; status: string;
  }[];
  hinweise: { typCode: string; hinweis: string; status: string; handlungsempfehlung: string | null }[];
  ohneTyp: { bauteilart: string; bauteile: number; flaecheNettoM2: number; flaecheGegenAussenM2: number }[];
  /** Befunde nach TX-STANDARD S3.3; `rang` 0 Fehler, 1 Warnung, 2 Hinweis (Befundrang). */
  befunde: { rang: number | string; regel: string; gegenstand: string; meldung: string; rangText?: string }[];
  freigabestatus: string;
}

export interface GegAbgleichAntwort {
  modell: GegAbgleichModell | null;
  stand: string | null;
  hinweis: string | null;
}

// ═══ AC1a: ACC-Anbindung (Antwort von GET /api/acc/verbindung) ═══

/** Die Verbindung der Plattform zu Autodesk. Zeiten als ISO-Text (UTC). */
export interface AccVerbindung {
  /** `false`: Schlüssel oder APS-App fehlen auf dem Server - `einrichtungsFehler` sagt, was. */
  eingerichtet: boolean;
  einrichtungsFehler: string | null;
  /** verbunden | fehler | widerrufen | getrennt */
  status: string;
  /** Anzeigename des Autodesk-Kontos, das zugestimmt hat. */
  konto: string | null;
  verbundenAm: string | null;
  verbundenVon: string | null;
  erneuertAm: string | null;
  zugriffBis: string | null;
  /** Ohne Erneuerung verfiele die Zustimmung zu diesem Zeitpunkt (APS: 15 Tage). */
  zustimmungGiltBis: string | null;
  fehlerCode: string | null;
  fehler: string | null;
  fehlerAm: string | null;
  bereiche: string | null;
  /** Die Callback-URL, die in der APS-App eingetragen sein muss. */
  rueckrufadresse: string | null;
  rechte: { verbinden: boolean };
}

// ═══ AC1b: ACC durchblättern ═══

export interface AccHub { id: string; name: string; region: string | null; art: string | null }
export interface AccHinweis { code: string; text: string }
export interface AccProjektEintrag { id: string; name: string; art: string | null; wurzelordner: string | null }

// ═══ RS1a/RS1b (F436/F438): Revit Server - nur lesend durchsuchen, Modell mit ModelGUID übernehmen ═══

/** Ein Revit Server aus dem Register der Konfiguration (ohne Adresse - die bleibt auf dem Server). */
export interface RsServer { kennung: string; anzeigename: string; jahr: number }
export interface RsOrdner { name: string }
/** Modell im Ordner: Größe in Byte, Produktversion des Servers, Sperrstatus (0 = frei). */
export interface RsModell { name: string; groesse: number | null; produktversion: string | null; sperrstatus: number | null }
export interface RsInhalt { ordner: RsOrdner[]; modelle: RsModell[]; freierPlatz: number | null }
/** `modelInfo` + `history`: die ModelGUID ist die Identität des Modells in TGAPlan. */
export interface RsModellInfo { modelGuid: string; geaendert: string | null; groesse: number | null; versionen: number }
export interface RsUebernommen { id: string; name: string; rsModelGuid: string; revitVersion: number }
export interface AccOrdner { id: string; name: string }
/** Eine Version in ACC; bei Revit-Cloud-Modellen mit den Kennungen für den Cloud-Pfad. */
export interface AccVersion {
  id: string;
  nummer: number;
  name: string | null;
  am: string | null;
  dateityp: string | null;
  art: string | null;
  projectGuid: string | null;
  modelGuid: string | null;
  itemId: string | null;
  istCloudModell: boolean;
}
export interface AccDatei { id: string; name: string; art: string | null; tip: AccVersion | null }
export interface AccInhalt { ordner: AccOrdner[]; dateien: AccDatei[] }

// ═══ AC1c: ACC-Zuordnung ═══

export interface AccModellStand {
  id: string;
  name: string;
  discipline: string | null;
  /** 1 = Objektspeicher, 2 = Autodesk Docs */
  source: number;
  accProjectGuid: string | null;
  accModelGuid: string | null;
  accItemUrn: string | null;
  accVersionUrn: string | null;
  accVersionNummer: number | null;
  accVersionStand: string | null;
  accVersionGeprueftAm: string | null;
  /** Bis zu dieser Nummer gilt eine Version als bekannt. */
  accVersionQuittiert: number | null;
  accZugeordnetAm: string | null;
  accZugeordnetVon: string | null;
}

export interface AccStand {
  projekt: {
    id: string; number: string; name: string; accHubId: string | null; accProjectId: string | null;
    accProjektName: string | null; accRegion: string | null; accZugeordnetAm: string | null; zugeordnet: boolean;
  };
  modelle: AccModellStand[];
}

/** zugeordnet | sicher | pruefen | mehrdeutig | kein_treffer | nicht_gefunden */
export interface AccModellVorschlag {
  modelId: string;
  name: string;
  status: string;
  treffer: AccVersion | null;
  grund: string;
}

export type AccVorschlaege =
  | { art: 'projekt'; projektVorschlaege: { hub: AccHub; projekt: AccProjektEintrag; status: string; grund: string }[] }
  | { art: 'modelle'; modelle: AccModellVorschlag[]; neu: AccVersion[] };

// ═══ AC1d: neue ACC-Versionen ═══

export interface AccEinstellung {
  versionsabfrage: boolean;
  autoAufnahme: boolean;
  exportProfileVersionId: string | null;
  /** ExportKind-Zahlen, nur lesende Arten. */
  arten: number[];
  letzteAbfrageAm: string | null;
  letzteAbfrageErgebnis: string | null;
}

/** offen | auftrag | erledigt | ersetzt */
export interface AccVersionshinweis {
  id: string;
  modelId: string;
  modell: string | null;
  versionNummer: number;
  versionStand: string | null;
  erkanntAm: string;
  status: string;
  erledigtAm: string | null;
  erledigtVon: string | null;
  grund: string | null;
  exportJobId: string | null;
  auftrag: string | null;
}

export interface AccVersionsstand { einstellung: AccEinstellung; hinweise: AccVersionshinweis[] }
export interface AccPruefung { geprueft: number; neu: string[]; fehler: string[]; auftragNummer: string | null }
